"use client";

import { useState, useMemo, useRef, useCallback, useEffect, Suspense } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  Ban,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  Copy,
  Upload,
  Building2,
  FileText,
  Pencil,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  Shield,
  Lock,
  Search,
  ChevronUp,
  CheckSquare,
  Zap,
  UserPlus,
  Users,
  X,
  Calendar,
  Plus,
  Tag,
  Coins,
  Radio,
  Link2,
  Layers,
  ArrowUpDown,
  Loader2,
  RefreshCw,
  TrendingUp,
  Image as ImageIcon,
  Trash2,
  Wallet,
  Star,
  Scale,
  Eye,
  MessageSquare,
  Send as SendIcon,
  Unlink2,
  Unlink,
  CircleArrowDown,
  CircleArrowUp,
} from "lucide-react";
import {
  CfUsdtCoin,
  CfProfit,
  CfNaira,
  CfBills,
  CfClipboard,
  CfActivityLog,
  CfScanner,
} from "@/components/section-icons";
import {
  PageWrapper,
  PageHeader,
  SectionCard,
  VerificationBadge,
  TabBar,
  Button,
  ProgressBar,
} from "@/components/ui";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { Trade, counterparties, settlementEntries, SettlementEntry, treasuryAccounts, treasuryTransactions, TreasuryTransaction, monoBankStatements, MonoBankStatement, activeStaff, hasPermission, type AuditLogEntry, type BankTransaction, type Counterparty, type TradeAttachment, type OcrExtractedData, type PayerDetail, type ReceiptFlag, POSITIVE_TAGS, NEGATIVE_TAGS, POSITIVE_TAG_IDS, computeSentiment, type TradeReview, type Dispute, DISPUTE_REASONS, type DisputePriority, hydrateBankTransactions, hydrateTreasuryAccounts, type BalanceAdjustment } from "@/lib/mock-data";
import { postAuditEntry, diffChanges } from "@/lib/audit";
import { generateForensicReport } from "@/lib/generate-forensic-pdf";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Tooltip } from "@/components/tooltip";
import { EvidenceLightbox } from "@/components/evidence-lightbox";
import { BankIcon } from "@/components/bank-icon";
import { useToast } from "@/contexts/toast-context";
import { formatCurrency, truncateHash, formatDate, formatDateTime, formatDateCompact, timeAgo, copyText, NIGERIAN_BANKS } from "@/lib/utils";
import { useScanner } from "@/hooks/use-scanner";
import type { DetectedTransaction } from "@/lib/scanner/types";
import { WalletManagementModal } from "@/components/wallet-management-modal";
import { computeLedgerAccounting, computePeriodPnL, isToday, isYesterday, isWithinDays, type TradeAccounting, type PeriodPnL, type AccountingWarning } from "@/lib/ledger-accounting";
import type { Expense, ExpenseCategory } from "@/lib/expenses-store";
import { NIBSS_CODE_MAP } from "@/lib/ocr/bank-parsers";
import { playWarningBuzz, playDangerAlarm } from "@/lib/sounds";

// --- Helpers ---

/** Local datetime string for datetime-local inputs (no TZ offset) */
function localNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}

/** Persist a bank transaction link/unlink to the store */
async function persistBankTxLink(txId: string, linkedTradeId: number | undefined, linkedSettlementId: number | undefined) {
  const res = await fetch("/api/bank-transactions", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: txId, linkedTradeId: linkedTradeId ?? null, linkedSettlementId: linkedSettlementId ?? null }),
  });
  if (!res.ok) throw new Error(`Failed to persist bank tx link for ${txId}`);
  return res;
}

/** Re-fetch counterparty balances from the server and patch the in-memory array */
async function refreshCounterpartyBalances() {
  try {
    const res = await fetch("/api/counterparties");
    const data = await res.json();
    if (data.counterparties) {
      for (const fresh of data.counterparties) {
        const cp = counterparties.find((c: { id: number }) => c.id === fresh.id);
        if (cp) {
          cp.balanceNgn = fresh.balanceNgn;
          cp.balanceUsdt = fresh.balanceUsdt;
        }
      }
    }
  } catch {}
}

// --- Types ---

type SortColumn = "counterparty" | "direction" | "usdtAmount" | "rate" | "ngnAmount" | "chain" | "status" | "createdAt";


// --- Constants ---

const PAGE_SIZE = 25;

const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  bank_fees: "Bank Fees",
  gas_fees: "Gas / Network Fees",
  salaries: "Salaries & Wages",
  rent: "Rent",
  utilities: "Utilities",
  marketing: "Marketing",
  equipment: "Equipment",
  regulatory: "Regulatory / Compliance",
  other: "Other",
};

function genId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === "x" ? r : (r & 0x3) | 0x8).toString(16); });
}

// --- Animation Variants ---

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -6 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] as const },
  },
};

const sectionFadeIn = (delay: number) => ({
  initial: { opacity: 0, y: 10 } as const,
  animate: { opacity: 1, y: 0 } as const,
  transition: {
    duration: 0.4,
    ease: [0.23, 1, 0.32, 1] as const,
    delay,
  },
});

const iconSpring = {
  whileHover: { scale: 1.1 },
  transition: { type: "spring" as const, stiffness: 400, damping: 15 },
};

const drawerContentVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};
const drawerItemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] as const } },
};

const reviewCardSlideUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, stiffness: 300, damping: 25, staggerChildren: 0.04 },
  },
  exit: { opacity: 0, y: 40, transition: { duration: 0.2 } },
};

const tagChipVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 400, damping: 20 },
  },
};

const reviewSuccessVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 300, damping: 20 },
  },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } },
};

// --- Smart Suggestions ---

function getSmartSuggestions(trade: Trade, tradePayerDetails: PayerDetail[]): Set<string> {
  const suggestions = new Set<string>();
  if (trade.settlementTimeSecs != null && trade.settlementTimeSecs < 300) {
    suggestions.add("paid_fast");
    suggestions.add("coin_landed_quick");
  }
  if (trade.settlementTimeSecs != null && trade.settlementTimeSecs > 7200) {
    suggestions.add("slow_payment");
  }
  // Check if payer name doesn't match counterparty name
  if (trade.counterpartyName && tradePayerDetails.length > 0) {
    const cpNameLower = trade.counterpartyName.toLowerCase();
    const hasMismatch = tradePayerDetails.some(
      (pd) => pd.payerName && !pd.payerName.toLowerCase().includes(cpNameLower) && !cpNameLower.includes(pd.payerName.toLowerCase())
    );
    if (hasMismatch) {
      suggestions.add("third_party_account");
      suggestions.add("name_mismatch");
    }
  }
  return suggestions;
}

// --- Clipboard Helper ---

function copyToClipboard(text: string): Promise<void> {
  return copyText(text);
}

// --- Helpers ---

const FILTER_TABS = ["All", "Pending", "Settled", "Disputed", "Expired"] as const;

const DATE_PRESETS = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
] as const;

function isRecent(dateStr: string): boolean {
  return Date.now() - new Date(dateStr).getTime() < 7 * 86_400_000;
}

function csvEscape(val: string | number | undefined | null): string {
  const s = String(val ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

const getName = (t: Trade) => t.counterpartyName || "Unknown";

/** Get WAT calendar date string (YYYY-MM-DD) for consistent day boundaries */
function watDateKey(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" }); // YYYY-MM-DD
}

function getDateGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();

  const tradeDateKey = watDateKey(date);
  const todayKey = watDateKey(now);

  // Calculate day difference using WAT date keys
  const tradeDay = new Date(tradeDateKey + "T00:00:00");
  const todayDay = new Date(todayKey + "T00:00:00");
  const diffDays = Math.round((todayDay.getTime() - tradeDay.getTime()) / 86_400_000);

  const shortDate = date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "Africa/Lagos" });
  if (diffDays === 0) return `Today — ${shortDate}`;
  if (diffDays === 1) return `Yesterday — ${shortDate}`;
  if (diffDays < 7) return `${diffDays} days ago — ${shortDate}`;
  return formatDate(dateStr);
}

function groupTradesByDate(trades: Trade[]): Map<string, Trade[]> {
  const groups = new Map<string, Trade[]>();
  for (const trade of trades) {
    const key = getDateGroup(trade.createdAt);
    const existing = groups.get(key);
    if (existing) {
      existing.push(trade);
    } else {
      groups.set(key, [trade]);
    }
  }
  return groups;
}

function looksLikeTxHash(input: string): boolean {
  const cleaned = input.trim().toLowerCase();
  if (cleaned.startsWith("0x")) return /^0x[a-f0-9]{64}$/.test(cleaned);
  return /^[a-f0-9]{64}$/.test(cleaned);
}

function looksLikeAddress(input: string): boolean {
  const cleaned = input.trim();
  // TRON: Base58, starts with T, ~34 chars
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(cleaned)) return true;
  // EVM: 0x + 40 hex chars
  if (/^0x[a-fA-F0-9]{40}$/i.test(cleaned)) return true;
  return false;
}

function relativeTime(isoStr: string): string {
  const secs = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}


function getDirectionConfig(direction: Trade["direction"]) {
  switch (direction) {
    case "buy":
      return {
        icon: ArrowDownLeft,
        colorClass: "bg-success/10 text-success",
        borderClass: "border-l-success/40",
        label: "Buy",
      };
    case "sell":
      return {
        icon: ArrowUpRight,
        colorClass: "bg-danger/10 text-danger",
        borderClass: "border-l-danger/40",
        label: "Sell",
      };
    case "lend":
      return {
        icon: ArrowUpRight,
        colorClass: "bg-warning/10 text-warning",
        borderClass: "border-l-warning/40",
        label: "Lend",
      };
    case "borrow":
      return {
        icon: ArrowDownLeft,
        colorClass: "bg-accent/10 text-accent",
        borderClass: "border-l-accent/40",
        label: "Borrow",
      };
    case "return":
      return {
        icon: ArrowUpRight,
        colorClass: "bg-muted/20 text-muted",
        borderClass: "border-l-muted/40",
        label: "Return",
      };
    case "personal":
      return {
        icon: ArrowDownLeft,
        colorClass: "bg-surface-3 text-muted",
        borderClass: "border-l-muted/40",
        label: "Personal",
      };
    case "lock":
      return {
        icon: Lock,
        colorClass: "bg-purple-500/10 text-purple-400",
        borderClass: "border-l-purple-400/40",
        label: "Lock",
      };
    case "middleman":
      return {
        icon: ArrowUpDown,
        colorClass: "bg-purple-500/10 text-purple-400",
        borderClass: "border-l-purple-400/40",
        label: "Middleman",
      };
  }
}

function formatSettlementTimer(createdAt: string): string {
  const elapsed = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / 1000
  );
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m elapsed`;
  }
  return `${mins}m ${secs}s elapsed`;
}

function generateCsv(trades: Trade[]): string {
  const header =
    "ID,Direction,Counterparty,USDT Amount,Rate,NGN Amount,Status,Verification,Chain,TX Hash,Sender Address,Sender Entity,Created At,Settled At,Buy Rate,Sell Rate,Buy Counterparty,Sell Counterparty,Middleman Profit\n";
  const rows = trades.map((t) =>
    [
      t.id,
      t.direction,
      t.direction === "middleman"
        ? `${t.buyCounterpartyName || ""} → ${t.sellCounterpartyName || ""}`
        : t.counterpartyName || (t.viaExchange ? t.exchangeName : "Unknown"),
      t.usdtAmount,
      t.rate || "",
      t.ngnAmount || "",
      t.status,
      t.verificationLevel,
      t.chain,
      t.txHash || "",
      t.senderAddress || "",
      t.senderEntity || "",
      t.createdAt,
      t.settledAt || "",
      t.buyRate || "",
      t.sellRate || "",
      t.buyCounterpartyName || "",
      t.sellCounterpartyName || "",
      t.middlemanProfit || "",
    ].map(csvEscape).join(",")
  );
  return header + rows.join("\n");
}

// --- Table Sub-Components ---

const COL_COUNT = 11;

function chainLabel(chain: Trade["chain"]) {
  return { tron: "TRC-20", bsc: "BEP-20", ethereum: "ERC-20" }[chain];
}

function statusLabel(status: Trade["status"]) {
  return { pending: "Pending", settled: "Paid", disputed: "Disputed", expired: "Expired" }[status];
}

function settlementDotColor(status: string) {
  return { pending: "bg-muted/40", partial: "bg-warning", complete: "bg-success", overpaid: "bg-danger" }[status] ?? "bg-muted/40";
}

function settlementPct(settled: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((settled / total) * 100);
}

/** For middleman "through_you" trades the total naira is BOTH legs combined, not just ngnAmount */
function getTradeNgnTotal(trade: Trade): number {
  if (trade.direction === "middleman" && trade.nairaFlow === "through_you" && trade.buyRate && trade.sellRate) {
    return (trade.buyRate * trade.usdtAmount) + (trade.sellRate * trade.usdtAmount);
  }
  if (trade.direction === "middleman" && trade.nairaFlow === "direct") {
    return Math.abs(trade.middlemanProfit || 0);
  }
  return trade.ngnAmount || 0;
}

function playSuccessSound() {
  try {
    const ctx = new AudioContext();
    const playTone = (freq: number, start: number, dur: number, gain: number, type: OscillatorType = "triangle") => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      g.gain.setValueAtTime(gain, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    // Three-tone ascending major chord arpeggio (C6-E6-G6) with shimmer
    playTone(1047, 0, 0.18, 0.12, "triangle");
    playTone(1319, 0.08, 0.18, 0.10, "triangle");
    playTone(1568, 0.16, 0.25, 0.10, "triangle");
    // Soft high shimmer on top
    playTone(2093, 0.2, 0.3, 0.04, "sine");
  } catch {
    // Audio not available
  }
}

function TradeGroup({
  dateLabel,
  trades,
  expandedId,
  setExpandedId,
  toast,
  selectedIds,
  onSelectToggle,
  focusedIdx,
  flatIndexOffset,
  onAssignCounterparty,
  onUpdateTrade,
  onLinkToLock,
  linkedTradeMap,
  accountingMap,
  highlightedId,
  lastRates,
}: {
  dateLabel: string;
  trades: Trade[];
  expandedId: number | null;
  setExpandedId: (id: number | null) => void;
  toast: (msg: string, variant?: "success" | "error" | "info") => void;
  selectedIds: Set<number>;
  onSelectToggle: (id: number) => void;
  focusedIdx: number | null;
  flatIndexOffset: number;
  onAssignCounterparty: (tradeId: number) => void;
  onUpdateTrade: (id: number, updates: { rate?: number; direction?: Trade["direction"]; status?: Trade["status"]; buyRate?: number; sellRate?: number; middlemanProfit?: number; buyCounterpartyId?: number | null; buyCounterpartyName?: string; sellCounterpartyId?: number | null; sellCounterpartyName?: string }) => void;
  onLinkToLock: (trade: Trade) => void;
  linkedTradeMap: Map<number, { lockId: number; lockType: string }>;
  accountingMap: Map<number, TradeAccounting>;
  highlightedId: number | null;
  lastRates: { buyRate: number | null; sellRate: number | null };
}) {
  return (
    <>
      {/* Date group separator */}
      <tr>
        <td colSpan={COL_COUNT} className="px-5 py-2.5 bg-surface-2 border-b border-border">
          <span className="text-[11px] font-bold text-foreground/70 uppercase tracking-wider">{dateLabel}</span>
          <span className="text-[11px] text-muted/60 ml-2">{trades.length} trade{trades.length !== 1 ? "s" : ""}</span>
        </td>
      </tr>
      {trades.map((trade, i) => (
        <TradeRow
          key={trade.id}
          trade={trade}
          isActive={expandedId === trade.id}
          onToggle={() => setExpandedId(expandedId === trade.id ? null : trade.id)}
          toast={toast}
          isSelected={selectedIds.has(trade.id)}
          onSelect={() => onSelectToggle(trade.id)}
          isFocused={focusedIdx === flatIndexOffset + i}
          onAssignCounterparty={onAssignCounterparty}
          onUpdateTrade={onUpdateTrade}
          onLinkToLock={onLinkToLock}
          linkedLock={linkedTradeMap.get(trade.id)}
          tradeAccounting={accountingMap.get(trade.id)}
          isHighlighted={highlightedId === trade.id}
          lastRates={lastRates}
        />
      ))}
    </>
  );
}

// Inflow = USDT received/incoming. Outflow = USDT sent/outgoing.
// Buy/Borrow can never become Sell/Lend, and vice versa.
// Lock, Return, Personal appear on both sides.
const INFLOW_DIRECTIONS: Trade["direction"][] = ["buy", "borrow", "return", "personal"];
const OUTFLOW_DIRECTIONS: Trade["direction"][] = ["sell", "lend", "return", "personal"];

function getDirectionOptions(current: Trade["direction"]): Trade["direction"][] {
  if (current === "sell" || current === "lend") return OUTFLOW_DIRECTIONS;
  if (current === "buy" || current === "borrow") return INFLOW_DIRECTIONS;
  // lock, return, personal — keep whichever side they're already on
  return [...INFLOW_DIRECTIONS, ...OUTFLOW_DIRECTIONS.filter(d => !INFLOW_DIRECTIONS.includes(d))];
}

function TradeRow({
  trade,
  isActive,
  onToggle,
  toast,
  isSelected,
  onSelect,
  isFocused,
  onAssignCounterparty,
  onUpdateTrade,
  onLinkToLock,
  linkedLock,
  tradeAccounting,
  isHighlighted,
  lastRates,
}: {
  trade: Trade;
  isActive: boolean;
  onToggle: () => void;
  toast: (msg: string, variant?: "success" | "error" | "info") => void;
  isSelected: boolean;
  onSelect: () => void;
  isFocused: boolean;
  onAssignCounterparty: (tradeId: number) => void;
  onUpdateTrade: (id: number, updates: { rate?: number; direction?: Trade["direction"]; status?: Trade["status"]; buyRate?: number; sellRate?: number; middlemanProfit?: number; buyCounterpartyId?: number | null; buyCounterpartyName?: string; sellCounterpartyId?: number | null; sellCounterpartyName?: string }) => void;
  onLinkToLock: (trade: Trade) => void;
  linkedLock?: { lockId: number; lockType: string };
  tradeAccounting?: TradeAccounting;
  isHighlighted?: boolean;
  lastRates: { buyRate: number | null; sellRate: number | null };
}) {
  const [editingRate, setEditingRate] = useState(false);
  const [rateValue, setRateValue] = useState(String(trade.rate ?? ""));
  const [editingMmRate, setEditingMmRate] = useState<"buy" | "sell" | null>(null);
  const [mmRateValue, setMmRateValue] = useState("");
  const [shakeRate, setShakeRate] = useState(false);
  const [showDirPicker, setShowDirPicker] = useState(false);

  // Close direction picker on Escape key
  useEffect(() => {
    if (!showDirPicker) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setShowDirPicker(false); e.stopPropagation(); }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showDirPicker]);

  const tradeEntries = settlementEntries.filter(e => e.tradeId === trade.id);
  // Rate is locked when any settlement entry for this trade is linked to a treasury bank tx
  const hasLinkedBankTx = tradeEntries.some((e) => e.treasuryAccountId != null);
  const coinEntries = tradeEntries.filter(e => e.side === "coin");
  const nairaEntries = tradeEntries.filter(e => e.side === "naira");
  const coinPct = settlementPct(trade.coinAmountSettled, trade.usdtAmount);
  const tradeNgnTotal = getTradeNgnTotal(trade);
  const nairaPct = tradeNgnTotal ? settlementPct(trade.nairaAmountSettled, tradeNgnTotal) : 0;
  const hasCombinedReceipts = coinEntries.some(e => e.batchId);
  const combinedReceiptCount = hasCombinedReceipts ? coinEntries.filter(e => e.batchId).length : 0;

  const dir = getDirectionConfig(trade.direction);
  const sourceDisplay = trade.senderEntity
    ? trade.senderEntity
    : trade.senderAddress
      ? truncateHash(trade.senderAddress, 4)
      : null;
  const sourceRaw = trade.senderAddress || trade.senderEntity || null;

  const isUnassigned = !trade.counterpartyName && !trade.buyCounterpartyName && !trade.sellCounterpartyName;

  const handleRateSave = () => {
    const parsed = parseFloat(rateValue.replace(/,/g, ""));
    if (!isNaN(parsed) && parsed > 0) {
      const oldRate = trade.rate;
      onUpdateTrade(trade.id, { rate: parsed });
      postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "rate_updated", details: `Rate changed from ₦${oldRate?.toLocaleString() ?? "--"} to ₦${parsed.toLocaleString()}`, changes: [{ field: "rate", from: oldRate ?? null, to: parsed }], channel: "dashboard", timestamp: new Date().toISOString() });
      toast("Rate updated");
      // Rate deviation warning
      const ref = trade.direction === "buy" ? lastRates.buyRate : lastRates.sellRate;
      if (ref != null && ref > 0) {
        const pct = ((parsed - ref) / ref) * 100;
        if (Math.abs(pct) > 5) {
          toast(`Rate ₦${parsed.toLocaleString()} is ${Math.abs(pct).toFixed(1)}% ${pct > 0 ? "above" : "below"} your last ${trade.direction} rate (₦${ref.toLocaleString()})`, "info");
        }
      }
    }
    setEditingRate(false);
  };

  const handleMmRateSave = () => {
    if (!editingMmRate) return;
    const parsed = parseFloat(mmRateValue.replace(/,/g, ""));
    if (!isNaN(parsed) && parsed > 0) {
      const oldVal = editingMmRate === "buy" ? trade.buyRate : trade.sellRate;
      const otherRate = editingMmRate === "buy" ? (trade.sellRate ?? 0) : (trade.buyRate ?? 0);
      const newBuyRate = editingMmRate === "buy" ? parsed : (trade.buyRate ?? 0);
      const newSellRate = editingMmRate === "sell" ? parsed : (trade.sellRate ?? 0);
      const newProfit = (newSellRate - newBuyRate) * trade.usdtAmount;
      onUpdateTrade(trade.id, {
        ...(editingMmRate === "buy" ? { buyRate: parsed } : { sellRate: parsed }),
        middlemanProfit: newProfit,
      });
      postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "rate_updated", details: `${editingMmRate === "buy" ? "Buy" : "Sell"} rate changed from ₦${oldVal?.toLocaleString() ?? "--"} to ₦${parsed.toLocaleString()}`, changes: [{ field: editingMmRate === "buy" ? "buyRate" : "sellRate", from: oldVal ?? null, to: parsed }], channel: "dashboard", timestamp: new Date().toISOString() });
      toast(`${editingMmRate === "buy" ? "Buy" : "Sell"} rate updated`);
      // Rate deviation warning
      const ref = editingMmRate === "buy" ? lastRates.buyRate : lastRates.sellRate;
      if (ref != null && ref > 0) {
        const pct = ((parsed - ref) / ref) * 100;
        if (Math.abs(pct) > 5) {
          toast(`Rate ₦${parsed.toLocaleString()} is ${Math.abs(pct).toFixed(1)}% ${pct > 0 ? "above" : "below"} your last ${editingMmRate} rate (₦${ref.toLocaleString()})`, "info");
        }
      }
    }
    setEditingMmRate(null);
  };

  return (
    <>
      <tr
        data-trade-id={trade.id}
        onClick={onToggle}
        className={`border-b border-border hover:bg-surface-2/40 cursor-pointer group ${isFocused ? "ring-1 ring-accent/40 ring-inset" : ""} ${isActive ? "bg-accent/[0.06]" : ""} ${hasCombinedReceipts ? "bg-accent/[0.03]" : ""} ${trade.isRefund ? "bg-warning/[0.03]" : ""} ${isHighlighted ? "trade-row-highlight" : ""}`}
        style={{
          transition: "background-color 1.5s ease-out, box-shadow 0.1s ease",
          ...(isHighlighted ? { backgroundColor: "color-mix(in srgb, var(--accent) 15%, transparent)" } : {}),
          ...(isActive ? { boxShadow: "inset 3px 0 0 var(--accent)" } : hasCombinedReceipts ? { boxShadow: "inset 3px 0 0 var(--accent)" } : trade.isRefund ? { boxShadow: "inset 3px 0 0 var(--warning)" } : {}),
        }}
      >
        {/* Checkbox column — hidden by default, visible on row hover or when selected */}
        <td className="pl-3 pr-1 py-3.5 w-8" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onSelect}
            className={`flex items-center justify-center w-4 h-4 rounded transition-opacity ${
              isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
          >
            {isSelected
              ? <CheckSquare className="w-3.5 h-3.5 text-accent" />
              : <CheckSquare className="w-3.5 h-3.5 text-muted/30" />}
          </button>
        </td>
        <td className="pl-2.5 pr-0 py-3.5 whitespace-nowrap">
          <span className="text-[13px] font-semibold text-foreground">
            {trade.direction === "middleman"
              ? `${(trade.buyCounterpartyName || "Seller").toUpperCase()} → ${(trade.sellCounterpartyName || "Buyer").toUpperCase()}`
              : (trade.counterpartyName || "Unknown").toUpperCase()}
          </span>
          {hasCombinedReceipts && (
            <span className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded border border-accent/20">
              <Layers className="w-2.5 h-2.5" />Combined &middot; {combinedReceiptCount} receipts
            </span>
          )}
          {trade.isRefund && (
            <span className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded border border-warning/20">
              Refund{trade.refundOfTradeId ? ` of #${trade.refundOfTradeId}` : ""}
            </span>
          )}
          {/* KYC warning badge for unverified counterparties */}
          {(trade.counterpartyId || trade.buyCounterpartyId || trade.sellCounterpartyId) && (() => {
            const cpIds = [trade.counterpartyId, trade.buyCounterpartyId, trade.sellCounterpartyId].filter(Boolean) as number[];
            const unverified = cpIds.some(id => { const cp = counterparties.find(c => c.id === id); return cp && !cp.kycVerified; });
            if (unverified) {
              return (
                <span className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20" title="Counterparty has no KYC verification">
                  <Shield className="w-2.5 h-2.5" />No KYC
                </span>
              );
            }
            return null;
          })()}
          {/* Blocked badge */}
          {(trade.counterpartyId || trade.buyCounterpartyId || trade.sellCounterpartyId) && (() => {
            const cpIds = [trade.counterpartyId, trade.buyCounterpartyId, trade.sellCounterpartyId].filter(Boolean) as number[];
            const blocked = cpIds.map(id => counterparties.find(c => c.id === id)).find(cp => cp && cp.isBlocked);
            if (blocked) {
              return (
                <span className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold text-danger bg-danger/10 px-1.5 py-0.5 rounded border border-danger/20" title={blocked.blockReason ? `Blocked: ${blocked.blockReason}` : "Blocked"}>
                  <Ban className="w-2.5 h-2.5" />Blocked
                </span>
              );
            }
            return null;
          })()}
          {linkedLock ? (
            <span
              className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold text-purple-400 bg-purple-500/10 px-1 py-0.5 rounded border border-purple-500/20 cursor-default"
              title={`Lock ${linkedLock.lockType === "lock_in" ? "IN" : "OUT"} #${linkedLock.lockId}`}
            >
              <Lock className="w-2.5 h-2.5" />#{linkedLock.lockId}
            </span>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onLinkToLock(trade); }}
              className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-purple-500/10 text-muted hover:text-purple-400"
              title="Link to lock"
            >
              <Lock className="w-3 h-3" />
            </button>
          )}
        </td>
        <td className="pl-1.5 pr-2.5 py-3.5 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {sourceDisplay ? (
              <button
                className="inline-flex items-center gap-1.5 bg-surface-2 rounded px-2 py-0.5 hover:bg-surface-3 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(sourceRaw || "");
                  toast("Source copied");
                }}
                title={sourceRaw || ""}
              >
                <code className="text-[12px] text-foreground font-mono">{sourceDisplay}</code>
                <Copy className="w-3 h-3 text-muted/50" />
              </button>
            ) : (
              <span className="text-[13px] text-muted">--</span>
            )}
            {isUnassigned && (
              <button
                className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5 text-[10px] font-semibold text-accent bg-accent/10 px-1.5 py-0.5 rounded border border-accent/20 hover:bg-accent/20"
                onClick={(e) => { e.stopPropagation(); onAssignCounterparty(trade.id); }}
                title="Assign counterparty"
              >
                <Users className="w-3 h-3" />Assign
              </button>
            )}
          </div>
        </td>
        <td className="px-2.5 py-3.5 relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => hasPermission(activeStaff, "change_direction") && setShowDirPicker(!showDirPicker)}
            className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded transition-all ${dir.colorClass} ${hasPermission(activeStaff, "change_direction") ? "cursor-pointer hover:ring-1 hover:ring-foreground/20" : "cursor-default opacity-70"}`}
            title={!hasPermission(activeStaff, "change_direction") ? "No permission" : undefined}
          >
            {dir.label}
          </button>
          <AnimatePresence>
            {showDirPicker && (() => {
              const sameSideOptions = getDirectionOptions(trade.direction);
              // Always show opposite-side directions as refund options
              const isInflow = trade.direction === "buy" || trade.direction === "borrow";
              const isOutflow = trade.direction === "sell" || trade.direction === "lend";
              const refundOptions: Trade["direction"][] =
                isInflow ? ["sell", "lend"] : isOutflow ? ["buy", "borrow"] : [];
              // Find the batchId for the combined group (if any)
              const activeBatchId = hasCombinedReceipts ? coinEntries.find(e => e.batchId)?.batchId : undefined;

              return (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDirPicker(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.12 }}
                    className="absolute top-full left-2 mt-1 z-50 bg-surface rounded-lg py-1.5 shadow-lg min-w-[140px]"
                    style={{ border: "1px solid var(--border)" }}
                  >
                    {/* Current direction label + close button */}
                    <div className="px-3 py-1 mb-0.5 flex items-center justify-between gap-3">
                      <span className="text-[9px] font-bold text-muted uppercase tracking-wider">Current: {dir.label}</span>
                      <button onClick={() => setShowDirPicker(false)} className="text-muted hover:text-foreground transition-colors p-0.5 -mr-1 rounded hover:bg-surface-2" title="Close" aria-label="Close">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="h-px bg-border mx-2 mb-0.5" />
                    {/* Same-side direction options */}
                    {sameSideOptions.map((d) => {
                      const dc = getDirectionConfig(d);
                      return (
                        <button
                          key={d}
                          onClick={() => {
                            const oldDir = getDirectionConfig(trade.direction).label;
                            onUpdateTrade(trade.id, { direction: d });
                            setShowDirPicker(false);
                            postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "direction_changed", details: `Direction changed from ${oldDir.toLowerCase()} to ${dc.label.toLowerCase()}`, changes: [{ field: "direction", from: trade.direction, to: d }], channel: "dashboard", timestamp: new Date().toISOString() });
                            toast(`Direction changed to ${dc.label}`);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-[11px] font-semibold uppercase hover:bg-surface-2 transition-colors flex items-center gap-2 ${
                            trade.direction === d ? "text-accent" : "text-foreground"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${dc.colorClass}`} />
                          {dc.label}
                        </button>
                      );
                    })}
                    {/* Refund options (opposite direction) — only for combined trades */}
                    {refundOptions.length > 0 && (
                      <>
                        <div className="h-px bg-border mx-2 my-0.5" />
                        <div className="px-3 py-1">
                          <span className="text-[9px] font-bold text-warning uppercase tracking-wider">Refund</span>
                        </div>
                        {refundOptions.map((d) => {
                          const dc = getDirectionConfig(d);
                          return (
                            <button
                              key={`refund-${d}`}
                              onClick={() => {
                                setShowDirPicker(false);
                                // Open the drawer to access refund flow
                                if (!isActive) onToggle();
                              }}
                              className="w-full text-left px-3 py-1.5 text-[11px] font-semibold uppercase hover:bg-warning/10 transition-colors flex items-center gap-2 text-warning"
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${dc.colorClass}`} />
                              {dc.label} — Refund
                            </button>
                          );
                        })}
                      </>
                    )}
                  </motion.div>
                </>
              );
            })()}
          </AnimatePresence>
        </td>
        <td className="px-2.5 py-3.5 text-[13px] text-foreground tabular-nums whitespace-nowrap">
          {trade.usdtAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </td>
        <td className="px-2.5 py-3.5 text-[13px] text-foreground tabular-nums whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {trade.direction === "middleman" ? (
            <div className="flex items-center gap-1">
              {editingMmRate === "buy" ? (
                <input
                  autoFocus
                  type="text"
                  value={mmRateValue}
                  onChange={(e) => setMmRateValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleMmRateSave(); if (e.key === "Escape") setEditingMmRate(null); }}
                  onBlur={handleMmRateSave}
                  className="w-16 bg-surface-2 rounded px-1.5 py-0.5 text-[12px] text-foreground tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/40"
                  style={{ border: "1px solid var(--border)" }}
                />
              ) : (
                <button
                  onClick={() => {
                    if (!hasPermission(activeStaff, "change_rate")) return;
                    if (hasLinkedBankTx) { toast("Unlink this trade from treasury ledger first before editing the rate", "error"); setShakeRate(true); setTimeout(() => setShakeRate(false), 600); return; }
                    setMmRateValue(String(trade.buyRate ?? "")); setEditingMmRate("buy");
                  }}
                  className={`rounded px-1 py-0.5 -mx-1 transition-colors text-[12px] ${shakeRate ? "animate-shake-hard" : ""} ${hasPermission(activeStaff, "change_rate") ? "hover:bg-surface-2 cursor-text" : "cursor-default"}`}
                  title={hasLinkedBankTx ? "Unlink from treasury ledger to edit rate" : hasPermission(activeStaff, "change_rate") ? "Click to edit buy rate" : "No permission"}
                >
                  {trade.buyRate ? `₦${trade.buyRate.toLocaleString()}` : <span className="text-muted">--</span>}
                </button>
              )}
              <span className="text-muted">/</span>
              {editingMmRate === "sell" ? (
                <input
                  autoFocus
                  type="text"
                  value={mmRateValue}
                  onChange={(e) => setMmRateValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleMmRateSave(); if (e.key === "Escape") setEditingMmRate(null); }}
                  onBlur={handleMmRateSave}
                  className="w-16 bg-surface-2 rounded px-1.5 py-0.5 text-[12px] text-foreground tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/40"
                  style={{ border: "1px solid var(--border)" }}
                />
              ) : (
                <button
                  onClick={() => {
                    if (!hasPermission(activeStaff, "change_rate")) return;
                    if (hasLinkedBankTx) { toast("Unlink this trade from treasury ledger first before editing the rate", "error"); setShakeRate(true); setTimeout(() => setShakeRate(false), 600); return; }
                    setMmRateValue(String(trade.sellRate ?? "")); setEditingMmRate("sell");
                  }}
                  className={`rounded px-1 py-0.5 -mx-1 transition-colors text-[12px] ${shakeRate ? "animate-shake-hard" : ""} ${hasPermission(activeStaff, "change_rate") ? "hover:bg-surface-2 cursor-text" : "cursor-default"}`}
                  title={hasLinkedBankTx ? "Unlink from treasury ledger to edit rate" : hasPermission(activeStaff, "change_rate") ? "Click to edit sell rate" : "No permission"}
                >
                  {trade.sellRate ? `₦${trade.sellRate.toLocaleString()}` : <span className="text-muted">--</span>}
                </button>
              )}
            </div>
          ) : (
            <>
              {editingRate ? (
                <input
                  autoFocus
                  type="text"
                  value={rateValue}
                  onChange={(e) => setRateValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRateSave();
                    if (e.key === "Escape") setEditingRate(false);
                  }}
                  onBlur={handleRateSave}
                  className="w-20 bg-surface-2 rounded px-2 py-0.5 text-[13px] text-foreground tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/40"
                  style={{ border: "1px solid var(--border)" }}
                />
              ) : (
                <button
                  onClick={() => {
                    if (!hasPermission(activeStaff, "change_rate")) return;
                    if (hasLinkedBankTx) {
                      toast("Unlink this trade from treasury ledger first before editing the rate", "error");
                      setShakeRate(true);
                      setTimeout(() => setShakeRate(false), 600);
                      return;
                    }
                    setRateValue(String(trade.rate ?? ""));
                    setEditingRate(true);
                  }}
                  className={`rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 transition-colors ${shakeRate ? "animate-shake-hard" : ""} ${hasPermission(activeStaff, "change_rate") ? "hover:bg-surface-2 cursor-text" : "cursor-default"}`}
                  title={hasLinkedBankTx ? "Unlink from treasury ledger to edit rate" : hasPermission(activeStaff, "change_rate") ? "Click to edit rate" : "No permission"}
                >
                  {trade.rate ? trade.rate.toLocaleString() : <span className="text-muted">--</span>}
                </button>
              )}
              {trade.direction === "sell" && tradeAccounting?.wacAtSale != null && (
                <span className="block text-[10px] text-muted mt-0.5">
                  cost ₦{tradeAccounting.wacAtSale.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </>
          )}
        </td>
        <td className="px-2.5 py-3.5 text-[13px] text-foreground tabular-nums whitespace-nowrap">
          <span>{trade.direction === "middleman"
            ? (trade.middlemanProfit != null
                ? <span className={trade.middlemanProfit >= 0 ? "text-success" : "text-danger"}>
                    {trade.middlemanProfit >= 0 ? "+" : "-"}₦{Math.abs(trade.middlemanProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} profit
                  </span>
                : <span className="text-muted">--</span>)
            : trade.ngnAmount ? trade.ngnAmount.toLocaleString() : <span className="text-muted">--</span>}</span>
          {trade.direction === "sell" && tradeAccounting?.grossProfit != null && tradeAccounting.grossProfit !== 0 && (
            <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
              tradeAccounting.grossProfit >= 0
                ? "text-success bg-success/10"
                : "text-danger bg-danger/10"
            }`}>
              {tradeAccounting.grossProfit >= 0 ? "+" : ""}{formatCurrency(Math.round(Math.abs(tradeAccounting.grossProfit)), "NGN")}
            </span>
          )}
        </td>
        <td className="px-2.5 py-3.5 text-[13px] text-foreground whitespace-nowrap">
          {chainLabel(trade.chain)}
        </td>
        <td className="px-2.5 py-3.5 whitespace-nowrap">
          {trade.txHash ? (
            <button
              className="inline-flex items-center gap-1.5 bg-surface-2 rounded px-2 py-0.5 hover:bg-surface-3 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(trade.txHash || "");
                toast("TX hash copied");
              }}
              title={trade.txHash}
            >
              <code className="text-[12px] text-foreground font-mono">{truncateHash(trade.txHash, 4)}</code>
              <Copy className="w-3 h-3 text-muted/50" />
            </button>
          ) : (
            <span className="text-[13px] text-muted">--</span>
          )}
        </td>
        <td className="px-2.5 py-3.5 whitespace-nowrap">
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center gap-0.5">
              <div className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${settlementDotColor(trade.coinStatus)}`} />
                <span className={`text-[11px] font-semibold tabular-nums ${trade.coinStatus === "overpaid" ? "text-danger" : "text-foreground"}`}>{settlementPct(trade.coinAmountSettled, trade.usdtAmount)}%</span>
              </div>
              <span className="text-[9px] text-muted uppercase">USDT</span>
            </div>
            {tradeNgnTotal ? (
              <div className="flex flex-col items-center gap-0.5">
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${settlementDotColor(trade.nairaStatus)}`} />
                  <span className={`text-[11px] font-semibold tabular-nums ${trade.nairaStatus === "overpaid" ? "text-danger" : "text-foreground"}`}>{settlementPct(trade.nairaAmountSettled, tradeNgnTotal)}%</span>
                </div>
                <span className="text-[9px] text-muted uppercase">NGN</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-0.5">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-muted/20" />
                  <span className="text-[11px] text-muted">--</span>
                </div>
                <span className="text-[9px] text-muted uppercase">NGN</span>
              </div>
            )}
          </div>
        </td>
        <td className="px-2.5 py-3.5 text-right text-[13px] text-foreground tabular-nums whitespace-nowrap">
          {formatDateCompact(trade.createdAt)}
        </td>
      </tr>
    </>
  );
}

// --- Trade Detail Drawer ---

function TradeDetailDrawer({
  trade,
  onClose,
  toast,
  onUpdateTrade,
  onCreateTrade,
  onAssignCounterparty,
  onLinkToLock,
  linkedLock,
  scannerTransactions,
  onScannerMatch,
  tradeAccounting,
  allTrades,
}: {
  trade: Trade;
  onClose: () => void;
  toast: (msg: string, variant?: "success" | "error" | "info") => void;
  onUpdateTrade: (id: number, updates: { rate?: number; direction?: Trade["direction"]; status?: Trade["status"]; buyRate?: number; sellRate?: number; middlemanProfit?: number; buyCounterpartyId?: number | null; buyCounterpartyName?: string; sellCounterpartyId?: number | null; sellCounterpartyName?: string }) => void;
  onCreateTrade: (trade: Trade) => void;
  onAssignCounterparty: (tradeId: number) => void;
  onLinkToLock: (trade: Trade) => void;
  linkedLock?: { lockId: number; lockType: string };
  scannerTransactions: DetectedTransaction[];
  onScannerMatch: (txId: string, tradeId: number) => Promise<void>;
  tradeAccounting?: TradeAccounting;
  allTrades: Trade[];
}) {
  const [showCombineReceipts, setShowCombineReceipts] = useState(false);
  const [editingMmCp, setEditingMmCp] = useState<"buy" | "sell" | null>(null);
  const [mmCpSearch, setMmCpSearch] = useState("");
  const [selectedCoinTxIds, setSelectedCoinTxIds] = useState<Set<string>>(new Set());
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [showRefundSelect, setShowRefundSelect] = useState<{ batchId: string; refundDirection: Trade["direction"] } | null>(null);
  const [selectedRefundTxIds, setSelectedRefundTxIds] = useState<Set<string>>(new Set());
  const [showResolutionPanel, setShowResolutionPanel] = useState<{
    side: "naira" | "coin";
    type: "overpayment" | "underpayment";
  } | null>(null);
  const [resolutionTab, setResolutionTab] = useState<"from_bank" | "from_scanner" | "use_credit" | null>(null);
  const [resolutionBankId, setResolutionBankId] = useState<number | null>(null);
  const [resolutionSelectedTxIds, setResolutionSelectedTxIds] = useState<Set<string>>(new Set());
  const [creditSubAction, setCreditSubAction] = useState<"apply_to_trade" | null>(null);
  const [creditTargetTradeId, setCreditTargetTradeId] = useState<number | null>(null);
  // Resolution "Add Payment" form state (separate from inline drawer form)
  const [resAddAmount, setResAddAmount] = useState("");
  const [resAddMethod, setResAddMethod] = useState<SettlementEntry["paymentMethod"]>("bank_transfer");
  const [resAddRef, setResAddRef] = useState("");
  const [resAddSource, setResAddSource] = useState("");
  const [resPayerName, setResPayerName] = useState("");
  const [resPayerAccount, setResPayerAccount] = useState("");
  const [resPayerBank, setResPayerBank] = useState("");
  const [resNotes, setResNotes] = useState("");
  const [resManualExpanded, setResManualExpanded] = useState(false);
  // Resolution USDT combine state (separate from inline combine)
  const [resCombineSearch, setResCombineSearch] = useState("");
  const [resCombineFilterDir, setResCombineFilterDir] = useState<"all" | "incoming" | "outgoing">("all");
  const [resCombineSortAsc, setResCombineSortAsc] = useState(false);
  // Cross-currency conversion rate for resolution (NGN excess → crypto, or USDT excess → bank)
  const [resCrossRate, setResCrossRate] = useState("");
  const [resCrossRateMode, setResCrossRateMode] = useState<"trade" | "custom">("trade");
  const [showAddNaira, setShowAddNaira] = useState(false);
  const [showBankPicker, setShowBankPicker] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState<"naira" | "coin" | false>(false);
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [selectedTxIds, setSelectedTxIds] = useState<Set<string>>(new Set());
  const [addAmount, setAddAmount] = useState("");
  const [addMethod, setAddMethod] = useState<SettlementEntry["paymentMethod"]>("bank_transfer");
  const [addRef, setAddRef] = useState("");
  const [addSource, setAddSource] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [confirmRemove, setConfirmRemove] = useState<SettlementEntry | null>(null);
  // Search/filter state for Combine Receipts and Refund Selection panels
  const [combineSearch, setCombineSearch] = useState("");
  const [combineFilterDir, setCombineFilterDir] = useState<"all" | "incoming" | "outgoing">("all");
  const [combineSortAsc, setCombineSortAsc] = useState(false);
  const [refundSearch, setRefundSearch] = useState("");
  const [refundFilterDir, setRefundFilterDir] = useState<"all" | "incoming" | "outgoing">("all");
  const [refundSortAsc, setRefundSortAsc] = useState(false);
  const [showRefundWarning, setShowRefundWarning] = useState(false);
  const [combineConfirm, setCombineConfirm] = useState<{ sameDirCount: number; oppDirCount: number; totalApply: number; totalRefund: number; execute: () => void } | null>(null);
  const [combineSuccess, setCombineSuccess] = useState<string | null>(null);

  // Notes & Screenshots state
  const [tradeNotes, setTradeNotes] = useState("");
  const [tradeAttachments, setTradeAttachments] = useState<TradeAttachment[]>([]);
  const [notesSaving, setNotesSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  // Payer details state (OCR + form)
  const [ocrResult, setOcrResult] = useState<OcrExtractedData | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [payerName, setPayerName] = useState("");
  const [payerAccount, setPayerAccount] = useState("");
  const [payerBank, setPayerBank] = useState("");
  const [tradePayerDetails, setTradePayerDetails] = useState<PayerDetail[]>([]);
  const [receiptFlags, setReceiptFlags] = useState<ReceiptFlag[]>([]);
  const [ocrImageHash, setOcrImageHash] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [matchWarning, setMatchWarning] = useState<string | null>(null);
  // Sound + vibrate when warning overlays appear
  useEffect(() => { if (matchWarning) { playWarningBuzz(); if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } }, [matchWarning]);
  useEffect(() => { if (duplicateWarning) { playDangerAlarm(); if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]); } }, [duplicateWarning]);
  const [bankMismatchWarning, setBankMismatchWarning] = useState<string | null>(null);
  const [bankDetectedConfirm, setBankDetectedConfirm] = useState<string | null>(null);
  const [ocrDetectedBankRaw, setOcrDetectedBankRaw] = useState<string | null>(null);
  const [addAccountId, setAddAccountId] = useState<number | null>(null);
  const [addSessionId, setAddSessionId] = useState("");
  const [addDate, setAddDate] = useState(localNow());
  const [payerExpanded, setPayerExpanded] = useState(true);
  const [showAddSaveConfirm, setShowAddSaveConfirm] = useState(false);
  // Resolution modal OCR & screenshot state (separate from Add Payment)
  const [resOcrResult, setResOcrResult] = useState<OcrExtractedData | null>(null);
  const [resOcrImageHash, setResOcrImageHash] = useState<string | null>(null);
  const [resTradeAttachments, setResTradeAttachments] = useState<TradeAttachment[]>([]);
  // Resolution modal bank detection state
  const [resDuplicateWarning, setResDuplicateWarning] = useState<string | null>(null);
  const [resMatchWarning, setResMatchWarning] = useState<string | null>(null);
  useEffect(() => { if (resMatchWarning) { playWarningBuzz(); if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } }, [resMatchWarning]);
  useEffect(() => { if (resDuplicateWarning) { playDangerAlarm(); if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]); } }, [resDuplicateWarning]);
  const [resBankMismatchWarning, setResBankMismatchWarning] = useState<string | null>(null);
  const [resBankDetectedConfirm, setResBankDetectedConfirm] = useState<string | null>(null);
  const [resOcrDetectedBankRaw, setResOcrDetectedBankRaw] = useState<string | null>(null);
  const [resAccountId, setResAccountId] = useState<number | null>(null);
  const [resSessionId, setResSessionId] = useState("");
  const [resDate, setResDate] = useState(localNow());
  const [resPayerExpanded, setResPayerExpanded] = useState(true);
  const [showResSaveConfirm, setShowResSaveConfirm] = useState(false);

  // Review card state
  const [showReviewCard, setShowReviewCard] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [reviewNote, setReviewNote] = useState("");
  const [showNoteField, setShowNoteField] = useState(false);
  const [anonymous, setAnonymous] = useState(true);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewExists, setReviewExists] = useState<boolean | null>(null);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);

  // Dispute state
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState<string>("");
  const [disputeDescription, setDisputeDescription] = useState("");
  const [disputePriority, setDisputePriority] = useState<DisputePriority>("medium");
  const [disputeEvidence, setDisputeEvidence] = useState<string[]>([]);
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [tradeDisputes, setTradeDisputes] = useState<Dispute[]>([]);
  const [tradeRefundAdjustments, setTradeRefundAdjustments] = useState<BalanceAdjustment[]>([]);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState<string | null>(null);
  const [resolveType, setResolveType] = useState<"refund" | "partial_refund" | "dismissed">("refund");
  const [resolveAmount, setResolveAmount] = useState("");
  const [resolveCurrency, setResolveCurrency] = useState<"NGN" | "USDT">("NGN");
  const [resolveNote, setResolveNote] = useState("");
  const [submittingResolve, setSubmittingResolve] = useState(false);
  const [disputeUploading, setDisputeUploading] = useState(false);
  const disputeFileInputRef = useRef<HTMLInputElement>(null);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Block / Blacklist state
  const [showTradeBlockConfirm, setShowTradeBlockConfirm] = useState(false);
  const [showTradeUnblockConfirm, setShowTradeUnblockConfirm] = useState(false);
  const [showTradeBlacklistConfirm, setShowTradeBlacklistConfirm] = useState(false);
  const [tradeBlockReason, setTradeBlockReason] = useState("");

  const tradeCp = trade.counterpartyId ? counterparties.find(c => c.id === trade.counterpartyId) : null;

  const handleTradeBlock = async () => {
    if (!tradeCp || !tradeBlockReason.trim()) return;
    try {
      await fetch(`/api/counterparties/${tradeCp.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isBlocked: true, blockReason: tradeBlockReason.trim(), blockedAt: new Date().toISOString() }),
      });
      tradeCp.isBlocked = true;
      tradeCp.blockReason = tradeBlockReason.trim();
      tradeCp.blockedAt = new Date().toISOString();
      toast("Counterparty blocked", "success");
      setShowTradeBlockConfirm(false);
      setTradeBlockReason("");
    } catch {
      toast("Failed to block", "error");
    }
  };

  const handleTradeUnblock = async () => {
    if (!tradeCp) return;
    try {
      await fetch(`/api/counterparties/${tradeCp.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isBlocked: false, blockReason: null, blockedAt: null }),
      });
      tradeCp.isBlocked = false;
      tradeCp.blockReason = null;
      tradeCp.blockedAt = null;
      toast("Counterparty unblocked", "success");
      setShowTradeUnblockConfirm(false);
    } catch {
      toast("Failed to unblock", "error");
    }
  };

  const handleTradeBlacklist = async () => {
    if (!tradeCp) return;
    try {
      await fetch(`/api/counterparties/${tradeCp.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isBlacklisted: true }),
      });
      tradeCp.isBlacklisted = true;
      toast("Counterparty blacklisted", "success");
      setShowTradeBlacklistConfirm(false);
    } catch {
      toast("Failed to blacklist", "error");
    }
  };

  const smartSuggestions = useMemo(
    () => getSmartSuggestions(trade, tradePayerDetails),
    [trade, tradePayerDetails]
  );

  // Load notes/attachments + payer details when drawer opens
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [notesRes, payerRes, flagsRes] = await Promise.all([
          fetch(`/api/uploads/notes?tradeId=${trade.id}`),
          fetch(`/api/payer-details?tradeId=${trade.id}`),
          fetch(`/api/receipt-verify?tradeId=${trade.id}`),
        ]);
        if (!cancelled) {
          if (notesRes.ok) {
            const data = await notesRes.json();
            setTradeNotes(data.notes ?? "");
            setTradeAttachments(data.attachments ?? []);
          }
          if (payerRes.ok) {
            const data = await payerRes.json();
            setTradePayerDetails(data.details ?? []);
          }
          if (flagsRes.ok) {
            const data = await flagsRes.json();
            setReceiptFlags(data.flags ?? []);
          }
        }
      } catch {
        // silently fail
      }
    })();
    return () => { cancelled = true; };
  }, [trade.id]);

  // Fetch disputes for this trade
  useEffect(() => {
    let cancelled = false;
    setDisputeLoading(true);
    fetch(`/api/trades/${trade.id}/disputes`)
      .then((r) => r.ok ? r.json() : { disputes: [] })
      .then((data) => {
        if (!cancelled) setTradeDisputes(data.disputes ?? []);
      })
      .catch(() => {
        if (!cancelled) setTradeDisputes([]);
      })
      .finally(() => {
        if (!cancelled) setDisputeLoading(false);
      });
    return () => { cancelled = true; };
  }, [trade.id]);

  // Fetch balance adjustments linked to this trade (for refund indicators)
  useEffect(() => {
    let cancelled = false;
    setTradeRefundAdjustments([]);
    if (trade.counterpartyId) {
      fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`)
        .then((r) => r.ok ? r.json() : { adjustments: [] })
        .then((data) => {
          if (!cancelled) {
            const linked = ((data.adjustments || []) as BalanceAdjustment[]).filter((a: BalanceAdjustment) => a.linkedTradeId === trade.id);
            setTradeRefundAdjustments(linked);
          }
        })
        .catch(() => {
          if (!cancelled) setTradeRefundAdjustments([]);
        });
    }
    return () => { cancelled = true; };
  }, [trade.id, trade.counterpartyId]);

  // Auto-check if review exists for settled trades
  useEffect(() => {
    if (trade.status !== "settled") {
      setReviewExists(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/trades/${trade.id}/review`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) {
          setReviewExists(data.exists);
          if (!data.exists) {
            setShowReviewCard(true);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setReviewExists(null);
      });
    return () => { cancelled = true; };
  }, [trade.id, trade.status]);

  const handleSubmitReview = useCallback(async () => {
    if (!trade.counterpartyId || selectedTags.size === 0) return;
    setSubmittingReview(true);
    try {
      const res = await fetch(`/api/counterparties/${trade.counterpartyId}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tags: Array.from(selectedTags),
          tradeId: trade.id,
          note: reviewNote || null,
          anonymous,
          tradeAmount: trade.usdtAmount,
          settlementTimeSecs: trade.settlementTimeSecs,
          counterpartyName: trade.counterpartyName,
        }),
      });
      if (res.ok) {
        setReviewSuccess(true);
        setReviewExists(true);
        setTimeout(() => {
          setReviewSuccess(false);
          setShowReviewCard(false);
          setReviewSubmitted(true);
        }, 2000);
      }
    } catch {
      toast("Failed to submit review", "error");
    }
    setSubmittingReview(false);
  }, [trade, selectedTags, reviewNote, anonymous, toast]);

  const handleSubmitDispute = useCallback(async () => {
    if (!disputeReason || !disputeDescription || !disputePriority) return;
    setSubmittingDispute(true);
    try {
      const res = await fetch(`/api/trades/${trade.id}/disputes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: disputeReason,
          description: disputeDescription,
          priority: disputePriority,
          counterpartyId: trade.counterpartyId,
          counterpartyName: trade.counterpartyName,
          evidence: disputeEvidence,
          createdBy: activeStaff.name,
          staffRole: activeStaff.role,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setTradeDisputes((prev) => [...prev, data.dispute]);
        // Update trade status to "disputed"
        if (trade.status !== "disputed") {
          onUpdateTrade(trade.id, { status: "disputed" });
        }
        postAuditEntry({
          tradeId: trade.id,
          staffId: activeStaff.id,
          staffName: activeStaff.name,
          action: "dispute_created",
          details: `Filed dispute: ${disputeReason}`,
          changes: [{ field: "dispute_reason", from: null, to: disputeReason }],
          channel: "dashboard",
          timestamp: new Date().toISOString(),
        });
        toast("Dispute filed successfully", "success");
        setShowDisputeModal(false);
        setDisputeReason("");
        setDisputeDescription("");
        setDisputePriority("medium");
        setDisputeEvidence([]);
      } else {
        toast("Failed to file dispute", "error");
      }
    } catch {
      toast("Failed to file dispute", "error");
    }
    setSubmittingDispute(false);
  }, [trade, disputeReason, disputeDescription, disputePriority, disputeEvidence, toast, activeStaff, onUpdateTrade]);

  const handleResolveDispute = useCallback(async (disputeId: string) => {
    if (!resolveNote.trim()) return;
    setSubmittingResolve(true);
    try {
      const resolution = {
        type: resolveType,
        amount: resolveType !== "dismissed" ? parseFloat(resolveAmount) || 0 : undefined,
        currency: resolveType !== "dismissed" ? resolveCurrency : undefined,
        note: resolveNote,
        resolvedBy: activeStaff.name,
        resolvedAt: new Date().toISOString(),
      };
      const res = await fetch(`/api/trades/${trade.id}/disputes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          disputeId,
          status: resolveType === "dismissed" ? "dismissed" : "resolved",
          resolution,
          staffRole: activeStaff.role,
          authorName: activeStaff.name,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setTradeDisputes((prev) => prev.map((d) => (d.id === disputeId ? data.dispute : d)));
        postAuditEntry({
          tradeId: trade.id,
          staffId: activeStaff.id,
          staffName: activeStaff.name,
          action: "dispute_resolved",
          details: `Dispute ${resolveType === "dismissed" ? "dismissed" : "resolved"}: ${resolveType}`,
          changes: [{ field: "dispute_status", from: "open", to: resolveType === "dismissed" ? "dismissed" : "resolved" }],
          channel: "dashboard",
          timestamp: new Date().toISOString(),
        });
        toast(`Dispute ${resolveType === "dismissed" ? "dismissed" : "resolved"}`, "success");
        setShowResolveModal(null);
        setResolveType("refund");
        setResolveAmount("");
        setResolveNote("");
      } else {
        toast("Failed to resolve dispute", "error");
      }
    } catch {
      toast("Failed to resolve dispute", "error");
    }
    setSubmittingResolve(false);
  }, [trade, resolveType, resolveAmount, resolveCurrency, resolveNote, toast, activeStaff]);

  const handleDisputeEvidenceUpload = useCallback(async (file: File) => {
    const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
    if (!ALLOWED.includes(file.type)) {
      toast("Invalid file type. Only JPEG, PNG, and WebP are allowed", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast("File too large. Maximum 5 MB", "error");
      return;
    }
    setDisputeUploading(true);
    try {
      const presignRes = await fetch("/api/uploads/presign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentType: file.type, size: file.size, tradeId: trade.id }),
      });
      if (!presignRes.ok) { toast("Upload failed", "error"); setDisputeUploading(false); return; }
      const { presignedUrl, publicUrl, key, attachmentId } = await presignRes.json();
      try { await fetch(presignedUrl, { method: "PUT", headers: { "Content-Type": file.type, "Content-Length": String(file.size) }, body: file }); } catch { /* mock mode */ }
      const confirmRes = await fetch("/api/uploads/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attachmentId, tradeId: trade.id, filename: key, originalName: file.name, url: publicUrl, contentType: file.type, size: file.size, uploadedBy: "dashboard" }),
      });
      if (confirmRes.ok) {
        setDisputeEvidence((prev) => [...prev, publicUrl]);
        toast("Evidence uploaded", "success");
      } else {
        toast("Upload failed", "error");
      }
    } catch {
      toast("Upload failed", "error");
    }
    setDisputeUploading(false);
  }, [trade.id, toast]);

  const handleMarkUnderReview = useCallback(async (disputeId: string) => {
    try {
      const res = await fetch(`/api/trades/${trade.id}/disputes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disputeId, status: "under_review", staffRole: activeStaff.role, authorName: activeStaff.name }),
      });
      if (res.ok) {
        const data = await res.json();
        setTradeDisputes((prev) => prev.map((d) => (d.id === disputeId ? data.dispute : d)));
        postAuditEntry({
          tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name,
          action: "dispute_under_review", details: "Dispute marked under review",
          changes: [{ field: "dispute_status", from: "open", to: "under_review" }],
          channel: "dashboard", timestamp: new Date().toISOString(),
        });
        toast("Dispute marked under review", "success");
      } else {
        const err = await res.json().catch(() => ({}));
        toast((err as { error?: string }).error || "Failed to update dispute", "error");
      }
    } catch { toast("Failed to update dispute", "error"); }
  }, [trade, activeStaff, toast]);

  const handleReopenDispute = useCallback(async (disputeId: string) => {
    try {
      const res = await fetch(`/api/trades/${trade.id}/disputes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disputeId, status: "open", staffRole: activeStaff.role, authorName: activeStaff.name }),
      });
      if (res.ok) {
        const data = await res.json();
        setTradeDisputes((prev) => prev.map((d) => (d.id === disputeId ? data.dispute : d)));
        postAuditEntry({
          tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name,
          action: "dispute_reopened", details: "Dispute re-opened",
          changes: [{ field: "dispute_status", from: data.dispute.status, to: "open" }],
          channel: "dashboard", timestamp: new Date().toISOString(),
        });
        toast("Dispute re-opened", "success");
      } else {
        const err = await res.json().catch(() => ({}));
        toast((err as { error?: string }).error || "Failed to re-open dispute", "error");
      }
    } catch { toast("Failed to re-open dispute", "error"); }
  }, [trade, activeStaff, toast]);

  const handleAddComment = useCallback(async (disputeId: string) => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await fetch(`/api/trades/${trade.id}/disputes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disputeId, comment: { text: commentText.trim() }, staffRole: activeStaff.role, authorName: activeStaff.name }),
      });
      if (res.ok) {
        const data = await res.json();
        setTradeDisputes((prev) => prev.map((d) => (d.id === disputeId ? data.dispute : d)));
        setCommentText("");
        toast("Comment added", "success");
      } else { toast("Failed to add comment", "error"); }
    } catch { toast("Failed to add comment", "error"); }
    setSubmittingComment(false);
  }, [trade, commentText, activeStaff, toast]);

  const handleRemoveEvidence = useCallback(async (disputeId: string, url: string) => {
    try {
      const res = await fetch(`/api/trades/${trade.id}/disputes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disputeId, removeEvidence: url, staffRole: activeStaff.role, authorName: activeStaff.name }),
      });
      if (res.ok) {
        const data = await res.json();
        setTradeDisputes((prev) => prev.map((d) => (d.id === disputeId ? data.dispute : d)));
        toast("Evidence removed", "success");
      } else { toast("Failed to remove evidence", "error"); }
    } catch { toast("Failed to remove evidence", "error"); }
  }, [trade, activeStaff, toast]);

  const handleSaveNotes = useCallback(async () => {
    setNotesSaving(true);
    try {
      const res = await fetch("/api/uploads/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tradeId: trade.id, notes: tradeNotes }),
      });
      if (res.ok) {
        postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "note_updated", details: `Updated trade notes`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });
        toast("Notes saved", "success");
      } else {
        toast("Failed to save notes", "error");
      }
    } catch {
      toast("Failed to save notes", "error");
    }
    setNotesSaving(false);
  }, [trade.id, tradeNotes, toast, activeStaff]);

  const formatDuplicateMsg = useCallback((data: { source: string; tradeId?: number; direction?: string; amount?: number; loggedAt?: string; loggedBy?: string; payerName?: string }) => {
    const ago = data.loggedAt ? timeAgo(data.loggedAt) : "";
    const by = data.loggedBy ? (data.loggedBy === activeStaff.name ? "you" : data.loggedBy) : "";
    if (data.source === "trade") {
      return `This screenshot was already used on Trade #${data.tradeId}${by ? ` by ${by}` : ""}${ago ? ` (${ago})` : ""}`;
    }
    return `This screenshot was already logged as ${data.direction} — ₦${Number(data.amount).toLocaleString()}${by ? ` by ${by}` : ""}${ago ? ` (${ago})` : ""}`;
  }, [activeStaff.name]);

  // Re-check duplicate hash when ocrImageHash changes (matches bank-ledger pattern)
  useEffect(() => {
    if (!ocrImageHash) { setDuplicateWarning(null); return; }
    let cancelled = false;
    fetch("/api/receipt-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "check-hash", screenshotHash: ocrImageHash, direction: "credit" }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        if (data.duplicate) {
          setDuplicateWarning(formatDuplicateMsg(data));
        } else {
          setDuplicateWarning(null);
        }
      })
      .catch(() => { if (!cancelled) setDuplicateWarning(null); });
    return () => { cancelled = true; };
  }, [ocrImageHash, formatDuplicateMsg]);

  // Re-check duplicate hash for Resolution modal (separate from Add Payment)
  useEffect(() => {
    if (!resOcrImageHash) { setResDuplicateWarning(null); return; }
    let cancelled = false;
    fetch("/api/receipt-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "check-hash", screenshotHash: resOcrImageHash, direction: "credit" }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        if (data.duplicate) {
          setResDuplicateWarning(formatDuplicateMsg(data));
        } else {
          setResDuplicateWarning(null);
        }
      })
      .catch(() => { if (!cancelled) setResDuplicateWarning(null); });
    return () => { cancelled = true; };
  }, [resOcrImageHash, formatDuplicateMsg]);

  // Cross-check: does a matching bank transaction already exist? (receipt ↔ statement dedup)
  const runMatchCheck = useCallback((amt: number, date: string) => {
    if (!amt || !date || date.length < 10) return;
    fetch("/api/receipt-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "check-transaction-match", amount: amt, date }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.match) {
          const srcMap: Record<string, string> = { pdf_import: "statement import", csv_import: "statement import", excel_import: "statement import", manual: "manual log", "bank-ledger": "manual log", "trade-ledger": "trade ledger" };
          const src = srcMap[data.source] || "existing record";
          const by = data.loggedBy ? ` by ${data.loggedBy}` : "";
          setMatchWarning(`A matching ₦${Number(data.amount).toLocaleString()} ${data.direction} on ${data.date.slice(0, 10)} already exists (from ${src}${by})`);
        } else {
          setMatchWarning(null);
        }
      })
      .catch(() => setMatchWarning(null));
  }, []);

  // Debounced match check when Add Payment amount/date changes
  useEffect(() => {
    const amt = parseFloat((addAmount || "").replace(/,/g, ""));
    if (!amt || !addDate || addDate.length < 10) { setMatchWarning(null); return; }
    const timer = setTimeout(() => runMatchCheck(amt, addDate), 400);
    return () => clearTimeout(timer);
  }, [addAmount, addDate, runMatchCheck]);

  // Debounced match check for Resolution modal
  const runResMatchCheck = useCallback((amt: number, date: string) => {
    if (!amt || !date || date.length < 10) return;
    fetch("/api/receipt-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "check-transaction-match", amount: amt, date }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.match) {
          const srcMap: Record<string, string> = { pdf_import: "statement import", csv_import: "statement import", excel_import: "statement import", manual: "manual log", "bank-ledger": "manual log", "trade-ledger": "trade ledger" };
          const src = srcMap[data.source] || "existing record";
          const by = data.loggedBy ? ` by ${data.loggedBy}` : "";
          setResMatchWarning(`A matching ₦${Number(data.amount).toLocaleString()} ${data.direction} on ${data.date.slice(0, 10)} already exists (from ${src}${by})`);
        } else {
          setResMatchWarning(null);
        }
      })
      .catch(() => setResMatchWarning(null));
  }, []);

  useEffect(() => {
    const amt = parseFloat((resAddAmount || "").replace(/,/g, ""));
    if (!amt || !resDate || resDate.length < 10) { setResMatchWarning(null); return; }
    const timer = setTimeout(() => runResMatchCheck(amt, resDate), 400);
    return () => clearTimeout(timer);
  }, [resAddAmount, resDate, runResMatchCheck]);

  const handleScreenshotUpload = useCallback(async (file: File) => {
    const ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!ALLOWED.includes(file.type)) {
      toast("Invalid file type. Only JPEG, PNG, WebP, and PDF are allowed", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast("File too large. Maximum 5 MB", "error");
      return;
    }

    const isPdf = file.type === "application/pdf";

    // For PDFs: validate it's not a bank statement before accepting
    if (isPdf) {
      setUploading(true);
      try {
        const validateForm = new FormData();
        validateForm.append("file", file);
        const res = await fetch("/api/bank-receipts/validate-pdf", { method: "POST", body: validateForm });
        if (res.ok) {
          const data = await res.json();
          if (!data.valid) {
            toast(data.error || "This PDF looks like a bank statement. Use Import Statement instead.", "error");
            setUploading(false);
            return;
          }
        }
      } catch {
        // Validation failed — allow the file through (fail open)
      }
      setUploading(false);
    }

    setUploading(true);
    try {
      // 1. Get presigned URL
      const presignRes = await fetch("/api/uploads/presign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contentType: file.type,
          size: file.size,
          tradeId: trade.id,
        }),
      });
      if (!presignRes.ok) {
        const err = await presignRes.json();
        toast(err.error ?? "Upload failed", "error");
        setUploading(false);
        return;
      }
      const { presignedUrl, publicUrl, key, attachmentId } = await presignRes.json();

      // 2. PUT file to R2 (silently fails in mock mode)
      try {
        await fetch(presignedUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type, "Content-Length": String(file.size) },
          body: file,
        });
      } catch {
        // Mock mode — PUT to placeholder URL will fail, that's expected
      }

      // 3. Confirm upload
      const confirmRes = await fetch("/api/uploads/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attachmentId,
          tradeId: trade.id,
          filename: key,
          originalName: file.name,
          url: publicUrl,
          contentType: file.type,
          size: file.size,
          uploadedBy: "dashboard",
        }),
      });
      if (confirmRes.ok) {
        const { attachment } = await confirmRes.json();
        // Mark PDF attachments so thumbnails render correctly
        if (isPdf) attachment.isPdf = true;
        setTradeAttachments((prev) => [...prev, attachment]);
        postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "screenshot_uploaded", details: `Uploaded ${isPdf ? "PDF receipt" : "screenshot"}: ${file.name}`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });

        // For PDFs: compute hash for duplicate detection but skip OCR (can't vision-read a PDF)
        if (isPdf) {
          setOcrLoading(false);
          setOcrResult(null);
          setOcrImageHash(null);
          setDuplicateWarning(null); setMatchWarning(null); setBankMismatchWarning(null); setBankDetectedConfirm(null); setOcrDetectedBankRaw(null);
          try {
            const arrayBuf = await file.arrayBuffer();
            const hashBuf = await crypto.subtle.digest("SHA-256", arrayBuf);
            const hashArr = Array.from(new Uint8Array(hashBuf));
            const hash = hashArr.map(b => b.toString(16).padStart(2, "0")).join("");
            setOcrImageHash(hash);
            const dupRes = await fetch("/api/receipt-verify", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "check-hash", screenshotHash: hash, direction: "credit" }),
            });
            if (dupRes.ok) {
              const dupData = await dupRes.json();
              if (dupData.duplicate) {
                setDuplicateWarning(formatDuplicateMsg(dupData));
                toast("Duplicate receipt detected", "error");
              }
            }
          } catch { /* allow manual save on check failure */ }
          toast("PDF receipt attached — fill in the details below", "info");
          setUploading(false);
          return;
        }

        toast("Screenshot uploaded — extracting payer details...", "success");

        // Run OCR in the background (non-blocking — upload already succeeded)
        setOcrLoading(true);
        setOcrImageHash(null);
        setDuplicateWarning(null); setMatchWarning(null); setBankMismatchWarning(null); setBankDetectedConfirm(null); setOcrDetectedBankRaw(null);
        const ocrForm = new FormData();
        ocrForm.append("image", file);
        fetch("/api/ocr", { method: "POST", body: ocrForm })
          .then((res) => res.ok ? res.json() : Promise.reject())
          .then(async (ocrData) => {
            if (ocrData.success && ocrData.data) {
              setOcrResult(ocrData.data);
              // Auto-populate payer details (sender info)
              if (ocrData.data.senderName) setPayerName((prev) => prev || ocrData.data.senderName);
              if (ocrData.data.senderAccountNumber) setPayerAccount((prev) => prev || ocrData.data.senderAccountNumber);
              if (ocrData.data.senderBank) setPayerBank((prev) => prev || ocrData.data.senderBank);
              // Auto-collapse payer details when OCR fills the form
              if (ocrData.data.senderName || ocrData.data.senderAccountNumber || ocrData.data.senderBank) setPayerExpanded(false);
              // Auto-populate transaction fields
              if (ocrData.data.amount) setAddAmount((prev) => prev || ocrData.data.amount.toLocaleString());
              if (ocrData.data.reference) setAddRef((prev) => prev || ocrData.data.reference);
              if (ocrData.data.sessionId) setAddSessionId((prev) => prev || ocrData.data.sessionId);
              // Auto-populate transaction date if available
              if (ocrData.data.transactionDate) {
                const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})[:.]?(\d{2})?/.exec(ocrData.data.transactionDate);
                if (m) {
                  setAddDate(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || "00"}`);
                }
              }
              // Immediately run cross-check with OCR-extracted values
              if (ocrData.data.amount && ocrData.data.transactionDate) {
                runMatchCheck(ocrData.data.amount, ocrData.data.transactionDate);
              }

              // Auto-match bank account from OCR + confirmation/mismatch
              const detectedBankLabel = ocrData.data.bankDetected || ocrData.data.senderBank || "";
              const detectedBank = detectedBankLabel.toLowerCase().replace(/[^a-z0-9]/g, "");
              setOcrDetectedBankRaw(detectedBankLabel || null);
              setBankDetectedConfirm(null);
              setBankMismatchWarning(null);
              const bankAccts = treasuryAccounts.filter(a => a.type === "bank");
              if (detectedBank && bankAccts.length > 0) {
                const match = bankAccts.find((acc) => {
                  const accName = (acc.bankName + " " + acc.label).toLowerCase().replace(/[^a-z0-9]/g, "");
                  return accName.includes(detectedBank) || detectedBank.includes(accName.split(" ")[0]);
                });
                if (match) {
                  setAddSource(match.label);
                  setAddAccountId(match.id);
                  setBankDetectedConfirm(match.bankName || detectedBankLabel);
                } else {
                  setBankMismatchWarning(`Screenshot shows "${detectedBankLabel}" but no matching bank found in your accounts. Please verify.`);
                }
              }
              const fieldCount = [ocrData.data.amount, ocrData.data.senderName, ocrData.data.senderAccountNumber, ocrData.data.sessionId, ocrData.data.reference, ocrData.data.transactionDate].filter(Boolean).length;
              toast(`Payer details extracted (${fieldCount}/6 fields found) — please verify`, "success");

              // Check for duplicate screenshot immediately, and store hash for re-checks
              if (ocrData.imageHash) {
                setOcrImageHash(ocrData.imageHash);
                try {
                  const dupRes = await fetch("/api/receipt-verify", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ action: "check-hash", screenshotHash: ocrData.imageHash, direction: "credit" }),
                  });
                  if (dupRes.ok) {
                    const dupData = await dupRes.json();
                    if (dupData.duplicate) {
                      setDuplicateWarning(formatDuplicateMsg(dupData));
                      toast("Duplicate screenshot detected", "error");
                    }
                  }
                } catch { /* allow manual save on check failure */ }
              }
            }
          })
          .catch(() => {
            toast("Could not extract payer details — fill manually", "info");
          })
          .finally(() => setOcrLoading(false));
      } else {
        toast("Failed to confirm upload", "error");
      }
    } catch {
      toast("Upload failed", "error");
    }
    setUploading(false);
  }, [trade.id, toast, activeStaff, addAmount, formatDuplicateMsg]);

  // Resolution modal screenshot handler — same as above but fills res* state
  const resScreenshotInputRef = useRef<HTMLInputElement>(null);
  const handleResScreenshotUpload = useCallback(async (file: File) => {
    const ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!ALLOWED.includes(file.type)) { toast("Invalid file type. Only JPEG, PNG, WebP, and PDF are allowed", "error"); return; }
    if (file.size > 5 * 1024 * 1024) { toast("File too large. Max 5 MB", "error"); return; }

    const isPdf = file.type === "application/pdf";

    // For PDFs: validate it's not a bank statement before accepting
    if (isPdf) {
      setUploading(true);
      try {
        const validateForm = new FormData();
        validateForm.append("file", file);
        const res = await fetch("/api/bank-receipts/validate-pdf", { method: "POST", body: validateForm });
        if (res.ok) {
          const data = await res.json();
          if (!data.valid) {
            toast(data.error || "This PDF looks like a bank statement. Use Import Statement instead.", "error");
            setUploading(false);
            return;
          }
        }
      } catch { /* fail open */ }
      setUploading(false);
    }

    setUploading(true);
    try {
      const presignRes = await fetch("/api/uploads/presign", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ contentType: file.type, size: file.size, tradeId: trade.id }) });
      if (!presignRes.ok) { toast("Upload failed", "error"); setUploading(false); return; }
      const { presignedUrl, publicUrl, key, attachmentId } = await presignRes.json();
      try { await fetch(presignedUrl, { method: "PUT", headers: { "Content-Type": file.type, "Content-Length": String(file.size) }, body: file }); } catch { /* mock mode */ }
      const confirmRes = await fetch("/api/uploads/confirm", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attachmentId, tradeId: trade.id, filename: key, originalName: file.name, url: publicUrl, contentType: file.type, size: file.size, uploadedBy: "dashboard" }) });
      if (confirmRes.ok) {
        const { attachment } = await confirmRes.json();
        if (isPdf) attachment.isPdf = true;
        setResTradeAttachments(prev => [...prev, attachment]);

        // For PDFs: compute hash for duplicate detection but skip OCR
        if (isPdf) {
          setOcrLoading(false);
          setResOcrResult(null);
          setResOcrImageHash(null);
          setResDuplicateWarning(null); setResMatchWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null);
          try {
            const arrayBuf = await file.arrayBuffer();
            const hashBuf = await crypto.subtle.digest("SHA-256", arrayBuf);
            const hashArr = Array.from(new Uint8Array(hashBuf));
            const hash = hashArr.map(b => b.toString(16).padStart(2, "0")).join("");
            setResOcrImageHash(hash);
            const dupRes = await fetch("/api/receipt-verify", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "check-hash", screenshotHash: hash, direction: "credit" }),
            });
            if (dupRes.ok) {
              const dupData = await dupRes.json();
              if (dupData.duplicate) {
                setResDuplicateWarning(formatDuplicateMsg(dupData));
                toast("Duplicate receipt detected", "error");
              }
            }
          } catch { /* allow manual save on check failure */ }
          toast("PDF receipt attached — fill in the details below", "info");
          setUploading(false);
          return;
        }

        toast("Screenshot uploaded — extracting payer details...", "success");
        setOcrLoading(true);
        setResOcrImageHash(null);
        setResDuplicateWarning(null); setResMatchWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null);
        const ocrForm = new FormData();
        ocrForm.append("image", file);
        fetch("/api/ocr", { method: "POST", body: ocrForm })
          .then(res => res.ok ? res.json() : Promise.reject())
          .then(async (ocrData) => {
            if (ocrData.success && ocrData.data) {
              setResOcrResult(ocrData.data);
              // Auto-populate payer details (sender info)
              if (ocrData.data.senderName) setResPayerName((prev) => prev || ocrData.data.senderName);
              if (ocrData.data.senderAccountNumber) setResPayerAccount((prev) => prev || ocrData.data.senderAccountNumber);
              if (ocrData.data.senderBank) setResPayerBank((prev) => prev || ocrData.data.senderBank);
              // Auto-collapse payer details when OCR fills the form
              if (ocrData.data.senderName || ocrData.data.senderAccountNumber || ocrData.data.senderBank) setResPayerExpanded(false);
              // Auto-populate transaction fields
              if (ocrData.data.amount) setResAddAmount((prev) => prev || ocrData.data.amount.toLocaleString());
              if (ocrData.data.reference) setResAddRef((prev) => prev || ocrData.data.reference);
              if (ocrData.data.sessionId) setResSessionId((prev) => prev || ocrData.data.sessionId);
              // Auto-populate transaction date if available
              if (ocrData.data.transactionDate) {
                const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})[:.]?(\d{2})?/.exec(ocrData.data.transactionDate);
                if (m) {
                  setResDate(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || "00"}`);
                }
              }
              // Immediately run cross-check with OCR-extracted values
              if (ocrData.data.amount && ocrData.data.transactionDate) {
                runResMatchCheck(ocrData.data.amount, ocrData.data.transactionDate);
              }

              // Auto-match bank account from OCR + confirmation/mismatch
              const detectedBankLabel = ocrData.data.bankDetected || ocrData.data.senderBank || "";
              const detectedBank = detectedBankLabel.toLowerCase().replace(/[^a-z0-9]/g, "");
              setResOcrDetectedBankRaw(detectedBankLabel || null);
              setResBankDetectedConfirm(null);
              setResBankMismatchWarning(null);
              const bankAccts = treasuryAccounts.filter(a => a.type === "bank");
              if (detectedBank && bankAccts.length > 0) {
                const match = bankAccts.find((acc) => {
                  const accName = (acc.bankName + " " + acc.label).toLowerCase().replace(/[^a-z0-9]/g, "");
                  return accName.includes(detectedBank) || detectedBank.includes(accName.split(" ")[0]);
                });
                if (match) {
                  setResAddSource(match.label);
                  setResAccountId(match.id);
                  setResBankDetectedConfirm(match.bankName || detectedBankLabel);
                } else {
                  setResBankMismatchWarning(`Screenshot shows "${detectedBankLabel}" but no matching bank found in your accounts. Please verify.`);
                }
              }
              const fieldCount = [ocrData.data.amount, ocrData.data.senderName, ocrData.data.senderAccountNumber, ocrData.data.sessionId, ocrData.data.reference, ocrData.data.transactionDate].filter(Boolean).length;
              toast(`Payer details extracted (${fieldCount}/6 fields found) — please verify`, "success");

              // Check for duplicate screenshot immediately, and store hash for re-checks
              if (ocrData.imageHash) {
                setResOcrImageHash(ocrData.imageHash);
                try {
                  const dupRes = await fetch("/api/receipt-verify", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ action: "check-hash", screenshotHash: ocrData.imageHash, direction: "credit" }),
                  });
                  if (dupRes.ok) {
                    const dupData = await dupRes.json();
                    if (dupData.duplicate) {
                      setResDuplicateWarning(formatDuplicateMsg(dupData));
                      toast("Duplicate screenshot detected", "error");
                    }
                  }
                } catch { /* allow manual save on check failure */ }
              }
            }
          })
          .catch(() => toast("Could not extract payer details — fill manually", "info"))
          .finally(() => setOcrLoading(false));
      }
    } catch { toast("Upload failed", "error"); }
    setUploading(false);
  }, [trade.id, toast, resAddAmount, formatDuplicateMsg]);

  const handleDeleteAttachment = useCallback(async (attachmentId: string) => {
    try {
      const att = tradeAttachments.find(a => a.id === attachmentId);
      const res = await fetch(
        `/api/uploads/${attachmentId}?tradeId=${trade.id}`,
        { method: "DELETE" }
      );
      if (res.ok) {
        setTradeAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
        postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "screenshot_deleted", details: `Deleted screenshot: ${att?.originalName || attachmentId}`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });
        toast("Attachment deleted", "success");
      } else {
        toast("Failed to delete attachment", "error");
      }
    } catch {
      toast("Failed to delete attachment", "error");
    }
  }, [trade.id, toast, tradeAttachments, activeStaff]);

  // Close drawer on Escape key (skip if sub-modal is open)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (lightboxImages) { setLightboxImages(null); return; }
        if (lightboxUrl) { setLightboxUrl(null); return; }
        if (showAddNaira) { setShowAddNaira(false); return; }
        if (showBankPicker) { setShowBankPicker(false); setSelectedBankId(null); setSelectedTxIds(new Set()); return; }
        if (showBalanceModal) { setShowBalanceModal(false); return; }
        if (showReviewCard) { setShowReviewCard(false); return; }
        if (showResolveModal) { setShowResolveModal(null); return; }
        if (showDisputeModal) { setShowDisputeModal(false); return; }
        if (!confirmRemove && !combineConfirm && !combineSuccess && !showRefundWarning) {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [confirmRemove, combineConfirm, combineSuccess, showRefundWarning, lightboxImages, lightboxUrl, showAddNaira, showBankPicker, showBalanceModal, showReviewCard, showResolveModal, showDisputeModal, onClose]);

  // Fetch audit entries when Activity Log is expanded
  useEffect(() => {
    if (!showActivityLog) return;
    fetch(`/api/audit-log?tradeId=${trade.id}`)
      .then(r => r.ok ? r.json() : [])
      .then((entries: AuditLogEntry[]) => setAuditEntries(entries))
      .catch(() => {});
  }, [showActivityLog, trade.id]);

  const tradeEntries = settlementEntries.filter(e => e.tradeId === trade.id);
  const coinEntries = tradeEntries.filter(e => e.side === "coin");
  const nairaEntries = tradeEntries.filter(e => e.side === "naira");
  const coinPct = settlementPct(trade.coinAmountSettled, trade.usdtAmount);
  const drawerNgnTotal = getTradeNgnTotal(trade);
  const nairaPct = drawerNgnTotal ? settlementPct(trade.nairaAmountSettled, drawerNgnTotal) : 0;
  const hasCombinedReceipts = coinEntries.some(e => e.batchId);
  const combinedReceiptCount = hasCombinedReceipts ? coinEntries.filter(e => e.batchId).length : 0;

  // Recalculate settlement totals from actual entries (prevents drift)
  const recalcSettlement = () => {
    // Read fresh from module-level array to avoid stale closures
    const currentEntries = settlementEntries.filter(e => e.tradeId === trade.id);
    // Skip isCreditToBalance entries — they don't affect settled amounts (tracked as balance adjustments instead)
    const calcEntries = currentEntries.filter(e => !e.isCreditToBalance);
    const coinEntries = calcEntries.filter(e => e.side === "coin");
    const coinTotal = coinEntries.filter(e => !e.isRefund).reduce((s, e) => s + e.amount, 0);
    const coinRefunds = coinEntries.filter(e => e.isRefund).reduce((s, e) => s + e.amount, 0);
    const nairaTotal = calcEntries.filter(e => e.side === "naira" && !e.isRefund).reduce((s, e) => s + e.amount, 0);
    const nairaRefunds = calcEntries.filter(e => e.side === "naira" && e.isRefund).reduce((s, e) => s + e.amount, 0);
    // Mutate the canonical trade object directly
    const canonical = trade;
    // For scanner trades (txHash present), preserve scanner-detected coin settlement
    // when there are no user-created coin-side entries — the on-chain delivery is the source of truth.
    if (coinEntries.length > 0) {
      canonical.coinAmountSettled = Math.max(0, coinTotal - coinRefunds);
    }
    // If no coin entries exist AND trade has no txHash, reset to 0 (manual trade with no coin settlement)
    else if (!canonical.txHash) {
      canonical.coinAmountSettled = 0;
    }
    // Otherwise: scanner trade with no coin entries → keep existing coinAmountSettled from scanner
    canonical.coinStatus = canonical.coinAmountSettled <= 0 ? "pending" : canonical.coinAmountSettled > canonical.usdtAmount ? "overpaid" : canonical.coinAmountSettled >= canonical.usdtAmount ? "complete" : "partial";
    canonical.nairaAmountSettled = Math.max(0, nairaTotal - nairaRefunds);
    if (canonical.direction === "middleman") {
      // Middleman: check all legs
      const nairaEnts = currentEntries.filter(e => e.side === "naira");
      if (canonical.nairaFlow === "direct") {
        const profitSettled = nairaEnts.filter(e => e.middlemanLeg === "profit" || !e.middlemanLeg).reduce((s, e) => s + e.amount, 0);
        canonical.nairaStatus = profitSettled <= 0 ? "pending" : profitSettled >= Math.abs(canonical.middlemanProfit || 0) ? "complete" : "partial";
      } else {
        const buySettled = nairaEnts.filter(e => e.middlemanLeg === "buy").reduce((s, e) => s + e.amount, 0);
        const sellSettled = nairaEnts.filter(e => e.middlemanLeg === "sell").reduce((s, e) => s + e.amount, 0);
        const buyDone = buySettled >= (canonical.buyRate || 0) * canonical.usdtAmount;
        const sellDone = sellSettled >= (canonical.sellRate || 0) * canonical.usdtAmount;
        canonical.nairaStatus = (buyDone && sellDone) ? "complete" : (buySettled > 0 || sellSettled > 0) ? "partial" : "pending";
      }
    } else {
      const ngnTotal = canonical.ngnAmount || 0;
      canonical.nairaStatus = canonical.nairaAmountSettled <= 0 ? "pending" : canonical.nairaAmountSettled > ngnTotal ? "overpaid" : canonical.nairaAmountSettled >= ngnTotal ? "complete" : "partial";
    }
    const coinDone = canonical.coinStatus === "complete" || canonical.coinStatus === "overpaid";
    const nairaDone = canonical.nairaStatus === "complete" || canonical.nairaStatus === "overpaid";
    if (coinDone && nairaDone) {
      canonical.status = "settled";
      canonical.settledAt = canonical.settledAt || new Date().toISOString();
      const settledAt = new Date(canonical.settledAt).getTime();
      const createdAt = new Date(canonical.createdAt).getTime();
      canonical.settlementTimeSecs = Math.round((settledAt - createdAt) / 1000);
    } else if (canonical.status === "settled") {
      canonical.status = "pending";
      canonical.settledAt = null;
      canonical.settlementTimeSecs = null;
    }
    // Derive payment metadata from most recent naira-side entry
    const nairaEnts = calcEntries
      .filter(e => e.side === "naira" && !e.isRefund)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (nairaEnts.length > 0) {
      canonical.nairaPaymentMethod = nairaEnts[0].paymentMethod;
      canonical.nairaBankRef = nairaEnts[0].reference;
      canonical.nairaBankVerified = nairaEnts.some(e => !!e.treasuryAccountId);
    } else {
      canonical.nairaPaymentMethod = null;
      canonical.nairaBankRef = null;
      canonical.nairaBankVerified = false;
    }
    // Sync back if trade prop is a different ref
    if (canonical !== trade) {
      trade.coinAmountSettled = canonical.coinAmountSettled;
      trade.coinStatus = canonical.coinStatus;
      trade.nairaAmountSettled = canonical.nairaAmountSettled;
      trade.nairaStatus = canonical.nairaStatus;
      trade.status = canonical.status;
      trade.settledAt = canonical.settledAt;
      trade.settlementTimeSecs = canonical.settlementTimeSecs;
      trade.nairaPaymentMethod = canonical.nairaPaymentMethod;
      trade.nairaBankRef = canonical.nairaBankRef;
      trade.nairaBankVerified = canonical.nairaBankVerified;
    }
  };

  const resetAddPaymentState = () => {
    setAddAmount(""); setAddRef(""); setAddSource(""); setAddMethod("bank_transfer");
    setPayerName(""); setPayerAccount(""); setPayerBank(""); setOcrResult(null); setOcrImageHash(null);
    setDuplicateWarning(null); setMatchWarning(null); setBankMismatchWarning(null); setBankDetectedConfirm(null); setOcrDetectedBankRaw(null); setAddAccountId(null);
    setAddSessionId(""); setAddDate(localNow()); setPayerExpanded(true); setShowAddSaveConfirm(false);
    setTradeAttachments([]);
  };

  const handleAddEntry = async (side: "coin" | "naira") => {
    // Block on duplicate screenshot
    if (duplicateWarning) { toast(duplicateWarning, "error"); return; }
    const parsed = parseFloat(addAmount.replace(/,/g, ""));
    if (isNaN(parsed) || parsed <= 0) return;
    const currency = side === "coin" ? "USDT" as const : "NGN" as const;
    const activeLeg = (trade as Record<string, unknown>).__activeLeg as "buy" | "sell" | "profit" | undefined;
    const entry: SettlementEntry = {
      id: Date.now(),
      tradeId: trade.id,
      side,
      amount: parsed,
      currency,
      paymentMethod: addMethod,
      sourceDestination: addSource || (side === "coin" ? "Wallet" : "Bank Account"),
      reference: addRef || `REF-${Date.now()}`,
      createdAt: new Date().toISOString(),
      ...(addMethod === "bank_transfer" && addAccountId ? { treasuryAccountId: addAccountId } : {}),
      ...(trade.direction === "middleman" && side === "naira" && activeLeg ? { middlemanLeg: activeLeg } : {}),
    };
    // Save payer details for naira bank transfers
    if (side === "naira" && payerName.trim()) {
      const payerDetailId = genId();
      entry.payerDetailId = payerDetailId;
      const payerDetail: PayerDetail = {
        id: payerDetailId,
        settlementId: entry.id,
        tradeId: trade.id,
        payerName: payerName.trim(),
        payerAccountNumber: payerAccount.trim(),
        payerBank: payerBank,
        amount: parsed,
        reference: addRef || "",
        source: ocrResult ? "ocr" : "manual",
        ocrConfidence: ocrResult?.confidence,
        ocrRawText: ocrResult?.rawText,
        ...(ocrImageHash ? { screenshotHash: ocrImageHash } : {}),
        createdAt: new Date().toISOString(),
      };
      setTradePayerDetails((prev) => [...prev, payerDetail]);
      fetch("/api/payer-details", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payerDetail) })
        .then(() =>
          fetch("/api/receipt-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "verify", detail: payerDetail }) })
        )
        .then((res) => res?.ok ? res.json() : null)
        .then((data) => {
          if (data?.flags?.length > 0) {
            setReceiptFlags((prev) => [...prev, ...data.flags]);
            const critical = data.flags.filter((f: ReceiptFlag) => f.severity === "critical");
            if (critical.length > 0) {
              toast(`⚠ Receipt alert: ${critical[0].message}`, "error");
            } else {
              toast(`Receipt check: ${data.flags[0].message}`, "info");
            }
          }
        })
        .catch(() => {});
    }
    const beforeSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
    settlementEntries.push(entry);
    recalcSettlement();
    const afterSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
    resetAddPaymentState();
    setShowAddNaira(false);
    // Persist to disk BEFORE triggering re-render (avoids race with scanner.reload)
    const addSettlementSync = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
    const persistPromises: Promise<unknown>[] = [
      fetch("/api/trades/settlements", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: [entry] }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...addSettlementSync }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...addSettlementSync }) }),
    ];
    // Create bank transaction for naira bank_transfer payments with a selected bank account
    if (side === "naira" && addMethod === "bank_transfer" && addAccountId) {
      const watDate = addDate.includes("+") ? addDate : `${addDate}+01:00`;
      const bankTx = {
        id: String(Date.now()),
        accountId: addAccountId,
        type: (trade.direction === "middleman" && activeLeg === "buy" ? "debit" : trade.direction === "buy" || trade.direction === "borrow" ? "debit" : "credit") as "credit" | "debit",
        amount: parsed,
        currency: "NGN",
        narration: entry.reference,
        counterpartyName: payerName.trim() || undefined,
        date: watDate,
        loggedAt: new Date().toISOString(),
        linkedTradeId: trade.id,
        linkedSettlementId: entry.id,
        sessionId: addSessionId || undefined,
        screenshotUrl: tradeAttachments[0]?.url || undefined,
        screenshotHash: ocrImageHash || undefined,
        payerName: payerName.trim() || undefined,
        payerAccount: payerAccount.trim() || undefined,
        payerBank: payerBank || undefined,
        beneficiaryName: ocrResult?.beneficiaryName || undefined,
        beneficiaryAccountNumber: ocrResult?.beneficiaryAccountNumber || undefined,
        loggedBy: activeStaff.name,
        source: "trade-ledger" as const,
        note: tradeNotes.trim() || undefined,
      };
      persistPromises.push(
        fetch("/api/bank-transactions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(bankTx) })
      );
      // Push into local monoBankStatements so bank picker reflects immediately
      monoBankStatements.push(bankTx as unknown as MonoBankStatement);
    }
    try {
      await Promise.all(persistPromises.map(p => p.then(async (r) => { if (r instanceof Response && !r.ok) throw new Error(`Persist failed: ${r.status}`); })));
    } catch (persistErr) {
      console.error("Settlement persist failed:", persistErr);
      toast("Settlement saved locally but failed to sync to disk — refresh and retry", "error");
    }
    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "payment_added", details: `Added ${side === "coin" ? parsed.toLocaleString() + " USDT" : "₦" + parsed.toLocaleString()} ${entry.paymentMethod.replace(/_/g, " ")} via ${entry.sourceDestination}${activeLeg ? ` (${activeLeg} leg)` : ""}`, changes: diffChanges(beforeSnap, afterSnap, ["coinAmountSettled", "coinStatus", "nairaAmountSettled", "nairaStatus", "status"]), channel: "dashboard", timestamp: new Date().toISOString() });
    onUpdateTrade(trade.id, {}); // trigger re-render
    toast("Payment entry added");
  };


  const handleResolutionAddPayment = async () => {
    // Block on duplicate screenshot
    if (resDuplicateWarning) { toast(resDuplicateWarning, "error"); return; }
    const parsed = parseFloat(resAddAmount.replace(/,/g, "")) || 0;
    if (parsed <= 0) return;
    const isOverpayment = showResolutionPanel?.type === "overpayment";
    const isBuyLike = trade.direction === "buy" || trade.direction === "borrow";
    const activeLeg = trade.direction === "middleman" ? (trade as Record<string, unknown>).__activeLeg as "buy" | "sell" | "profit" | undefined : undefined;

    // Cross-currency: bank manual entry for USDT excess → convert NGN to USDT
    const isCrossCurrency = resolutionTab === "from_bank" && showResolutionPanel?.side !== "naira";
    const crossRate = resCrossRateMode === "trade" ? (trade.rate || 0) : (parseFloat(resCrossRate.replace(/,/g, "")) || 0);

    // Overpayment refunds → create balance adjustment instead of settlement entry
    if (isOverpayment) {
      const cp = trade.counterpartyId ? counterparties.find(c => c.id === trade.counterpartyId) : null;
      if (!trade.counterpartyId || !cp) return;

      let adjAmount = parsed;
      let adjCurrency: "NGN" | "USDT" = "NGN";
      const adjPaymentMethod = resAddMethod as "bank_transfer" | "cash" | "crypto_wallet";
      if (isCrossCurrency && crossRate > 0) {
        adjAmount = parseFloat((parsed / crossRate).toFixed(2));
        adjCurrency = "USDT";
      }

      const tradeSummary = `Refund from ${trade.direction === "buy" ? "Buy" : trade.direction === "sell" ? "Sell" : trade.direction} ${trade.usdtAmount.toLocaleString()} USDT`;
      const adjReference = isCrossCurrency && crossRate > 0
        ? `₦${parsed.toLocaleString()} @ ₦${crossRate.toLocaleString()}/USDT · ${resAddRef || `REFUND-${Date.now()}`}`
        : (resAddRef || `REFUND-${Date.now()}`);

      await fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "payout",
          amount: adjAmount,
          currency: adjCurrency,
          paymentMethod: adjPaymentMethod,
          destination: resAddSource || (resAddMethod === "cash" ? "Cash" : resAddMethod === "p2p" ? "P2P" : "Bank Account"),
          reference: adjReference,
          linkedTradeId: trade.id,
          linkedTradeSummary: tradeSummary,
          ...(isCrossCurrency && crossRate > 0 ? { crossCurrencyRate: crossRate } : {}),
        }),
      }).catch(() => {});

      // Refresh linked adjustments state
      fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`)
        .then(r => r.ok ? r.json() : { adjustments: [] })
        .then(data => {
          const linked = ((data.adjustments || []) as BalanceAdjustment[]).filter((a: BalanceAdjustment) => a.linkedTradeId === trade.id);
          setTradeRefundAdjustments(linked);
        })
        .catch(() => {});

      const totalLabel = adjCurrency === "NGN" ? `₦${adjAmount.toLocaleString()}` : `${adjAmount.toLocaleString()} USDT`;
      postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "refund_logged", details: `Refund payout of ${totalLabel} on trade #${trade.id} — balance adjustment for ${cp.name}`, changes: [{ field: "balance_adjustment", from: null, to: `payout ${totalLabel}` }], channel: "dashboard", timestamp: new Date().toISOString() });

      setShowResolutionPanel(null);
      setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAddMethod("bank_transfer");
      setResPayerName(""); setResPayerAccount(""); setResPayerBank(""); setResNotes("");
      setResDuplicateWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null); setResAccountId(null);
      setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true); setShowResSaveConfirm(false);
      setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null);
      setResCrossRate(""); setResCrossRateMode("trade");
      onUpdateTrade(trade.id, {});
      toast(`Refund of ${totalLabel} applied as balance payout for ${cp.name}`);
      return;
    }

    // --- Normal payment (underpayment) path — unchanged ---

    let entrySide: "naira" | "coin" = "naira";
    let entryAmount = parsed;
    let entryCurrency: "NGN" | "USDT" = "NGN";
    if (isCrossCurrency && crossRate > 0) {
      entrySide = "coin";
      entryAmount = parseFloat((parsed / crossRate).toFixed(2));
      entryCurrency = "USDT";
    }

    const entry: SettlementEntry = {
      id: Date.now(),
      tradeId: trade.id,
      side: entrySide,
      amount: entryAmount,
      currency: entryCurrency,
      paymentMethod: resAddMethod,
      sourceDestination: resAddSource || (resAddMethod === "cash" ? "Cash" : resAddMethod === "p2p" ? "P2P" : "Bank Account"),
      reference: isCrossCurrency && crossRate > 0
        ? `₦${parsed.toLocaleString()} @ ₦${crossRate.toLocaleString()}/USDT · ${resAddRef || `PAY-${Date.now()}`}`
        : (resAddRef || `PAY-${Date.now()}`),
      ...(isCrossCurrency && crossRate > 0 ? { crossCurrencyRate: crossRate } : {}),
      createdAt: new Date().toISOString(),
      ...(resAddMethod === "bank_transfer" && resAccountId ? { treasuryAccountId: resAccountId } : {}),
      ...(trade.direction === "middleman" && activeLeg ? { middlemanLeg: activeLeg } : {}),
    };

    // Handle payer details
    const payerDetails: PayerDetail[] = [];
    if (resAddMethod === "bank_transfer" && resPayerName) {
      const payerDetailId = genId();
      entry.payerDetailId = payerDetailId;
      payerDetails.push({
        id: payerDetailId, settlementId: entry.id, tradeId: trade.id,
        payerName: resPayerName, payerAccountNumber: resPayerAccount, payerBank: resPayerBank,
        amount: parsed, reference: resAddRef, source: resOcrResult ? "ocr" : "manual",
        ...(resOcrImageHash ? { screenshotHash: resOcrImageHash } : {}),
        createdAt: new Date().toISOString(),
      });
    }

    const resBeforeSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
    settlementEntries.push(entry);
    recalcSettlement();
    const resAfterSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };

    const syncData = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
    const resPersistPromises: Promise<unknown>[] = [
      fetch("/api/trades/settlements", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: [entry] }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...syncData }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...syncData }) }),
    ];
    // Create bank transaction for naira bank_transfer resolution with a selected bank account
    if (resAddMethod === "bank_transfer" && resAccountId && entrySide === "naira") {
      const resWatDate = resDate.includes("+") ? resDate : `${resDate}+01:00`;
      const resBankTx = {
        id: String(Date.now()),
        accountId: resAccountId,
        type: (isBuyLike ? "credit" : "debit") as "credit" | "debit",
        amount: parsed,
        currency: "NGN",
        narration: entry.reference,
        counterpartyName: resPayerName.trim() || undefined,
        date: resWatDate,
        loggedAt: new Date().toISOString(),
        linkedTradeId: trade.id,
        linkedSettlementId: entry.id,
        sessionId: resSessionId || undefined,
        screenshotUrl: resTradeAttachments[0]?.url || undefined,
        screenshotHash: resOcrImageHash || undefined,
        payerName: resPayerName.trim() || undefined,
        payerAccount: resPayerAccount.trim() || undefined,
        payerBank: resPayerBank || undefined,
        beneficiaryName: resOcrResult?.beneficiaryName || undefined,
        beneficiaryAccountNumber: resOcrResult?.beneficiaryAccountNumber || undefined,
        loggedBy: activeStaff.name,
        source: "trade-ledger" as const,
        note: resNotes.trim() || undefined,
      };
      resPersistPromises.push(
        fetch("/api/bank-transactions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(resBankTx) })
      );
      monoBankStatements.push(resBankTx as unknown as MonoBankStatement);
    }
    await Promise.all(resPersistPromises).catch(() => {});
    if (payerDetails.length > 0) {
      setTradePayerDetails(prev => [...prev, ...payerDetails]);
      fetch("/api/payer-details", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ details: payerDetails }) }).catch(() => {});
    }

    // Save notes if present
    if (resNotes.trim()) {
      fetch("/api/uploads/notes", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, notes: resNotes.trim() }) }).catch(() => {});
    }

    const logLabel = isCrossCurrency && crossRate > 0
      ? `₦${parsed.toLocaleString()} (${entryAmount.toLocaleString()} USDT @ ₦${crossRate.toLocaleString()})`
      : `₦${parsed.toLocaleString()}`;
    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "payment_added", details: `Payment of ${logLabel} on trade #${trade.id}`, changes: diffChanges(resBeforeSnap, resAfterSnap, ["coinAmountSettled", "coinStatus", "nairaAmountSettled", "nairaStatus", "status"]), channel: "dashboard", timestamp: new Date().toISOString() });

    setShowResolutionPanel(null);
    setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAddMethod("bank_transfer");
    setResPayerName(""); setResPayerAccount(""); setResPayerBank(""); setResNotes("");
    setResDuplicateWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null); setResAccountId(null);
    setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true); setShowResSaveConfirm(false);
    setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null);
    setResCrossRate(""); setResCrossRateMode("trade");
    onUpdateTrade(trade.id, {});
    toast(`Payment of ${logLabel} logged.`);
  };

  // --- Resolution handlers ---

  const handleRefundFromTransaction = async () => {
    if (!showResolutionPanel || resolutionSelectedTxIds.size === 0) return;
    const settleSide = showResolutionPanel.side;
    const isNairaSide = settleSide === "naira";
    const isBuyLike = trade.direction === "buy" || trade.direction === "borrow";
    const cp = trade.counterpartyId ? counterparties.find(c => c.id === trade.counterpartyId) : null;
    if (!trade.counterpartyId || !cp) return;

    // Determine cross-currency rate when resolving via opposite-currency transactions
    const crossRate = resCrossRateMode === "trade" ? (trade.rate || 0) : (parseFloat(resCrossRate.replace(/,/g, "")) || 0);

    // Build trade summary for the adjustment
    const tradeSummary = `Refund from ${trade.direction === "buy" ? "Buy" : trade.direction === "sell" ? "Sell" : trade.direction} ${trade.usdtAmount.toLocaleString()} USDT`;

    let totalRefundAmount = 0;
    let refundCurrency: "NGN" | "USDT" = isNairaSide ? "NGN" : "USDT";
    let refundPaymentMethod: "bank_transfer" | "crypto_wallet" = "bank_transfer";
    let refundReference = "";
    let refundDestination = "";
    let txCount = 0;

    if (resolutionTab === "from_bank") {
      // Bank tab — selecting NGN bank statements
      const isCrossCurrency = !isNairaSide; // USDT excess resolved via NGN bank
      const txType = isBuyLike ? "credit" : "debit";
      const selected = monoBankStatements.filter(s => resolutionSelectedTxIds.has(s.id) && s.type === txType && !s.linkedTradeId);
      refundPaymentMethod = "bank_transfer";
      for (const stmt of selected) {
        if (isCrossCurrency && crossRate > 0) {
          // Cross-currency: convert NGN bank amount to USDT
          const usdtAmount = parseFloat((stmt.amount / crossRate).toFixed(2));
          totalRefundAmount += usdtAmount;
          refundCurrency = "USDT";
          refundReference = `₦${stmt.amount.toLocaleString()} @ ₦${crossRate.toLocaleString()}/USDT · ${stmt.narration.slice(0, 25)}`;
        } else {
          totalRefundAmount += stmt.amount;
          refundCurrency = "NGN";
          refundReference = stmt.narration.slice(0, 40);
        }
        refundDestination = treasuryAccounts.find(a => a.id === stmt.accountId)?.label || "Unknown";
        // Link bank tx to trade for audit trail (no settlement entry)
        stmt.linkedTradeId = trade.id;
        persistBankTxLink(stmt.id, trade.id, undefined);
        txCount++;
      }
    } else if (resolutionTab === "from_scanner") {
      // Crypto tab — selecting USDT scanner/treasury transactions
      const isCrossCurrency = isNairaSide; // NGN excess resolved via USDT crypto
      refundPaymentMethod = "crypto_wallet";
      for (const uid of resolutionSelectedTxIds) {
        if (uid.startsWith("rsc-")) {
          const txId = uid.replace("rsc-", "");
          const stx = scannerTransactions.find(t => t.id === txId);
          if (stx && !stx.matchedTradeId) {
            if (isCrossCurrency && crossRate > 0) {
              const ngnAmount = parseFloat((stx.amount * crossRate).toFixed(2));
              totalRefundAmount += ngnAmount;
              refundCurrency = "NGN";
              refundReference = `${stx.amount.toLocaleString()} USDT @ ₦${crossRate.toLocaleString()} · ${stx.txHash.slice(0, 15)}`;
            } else {
              totalRefundAmount += stx.amount;
              refundCurrency = "USDT";
              refundReference = stx.txHash;
            }
            refundDestination = `${stx.chain.toUpperCase()} · ${stx.txHash.slice(0, 10)}...`;
            (stx as Record<string, unknown>).matchedTradeId = trade.id;
            txCount++;
          }
        } else if (uid.startsWith("rtr-")) {
          const txId = uid.replace("rtr-", "");
          const tx = treasuryTransactions.find(t => t.id === txId);
          if (tx && tx.currency === "USDT" && !tx.linkedTradeId) {
            if (isCrossCurrency && crossRate > 0) {
              const ngnAmount = parseFloat((tx.amount * crossRate).toFixed(2));
              totalRefundAmount += ngnAmount;
              refundCurrency = "NGN";
              refundReference = `${tx.amount.toLocaleString()} USDT @ ₦${crossRate.toLocaleString()} · ${tx.narration.slice(0, 20)}`;
            } else {
              totalRefundAmount += tx.amount;
              refundCurrency = "USDT";
              refundReference = tx.narration.slice(0, 40);
            }
            refundDestination = treasuryAccounts.find(a => a.id === tx.accountId)?.label || "Wallet";
            tx.linkedTradeId = trade.id;
            persistBankTxLink(tx.id, trade.id, undefined);
            txCount++;
          }
        }
      }
    }

    if (txCount === 0 || totalRefundAmount <= 0) return;

    // Create balance adjustment (payout) instead of settlement entry
    const adjustmentBody = {
      type: "payout" as const,
      amount: totalRefundAmount,
      currency: refundCurrency,
      paymentMethod: refundPaymentMethod,
      destination: refundDestination,
      reference: refundReference,
      linkedTradeId: trade.id,
      linkedTradeSummary: tradeSummary,
      ...(refundCurrency !== (isNairaSide ? "NGN" : "USDT") ? { crossCurrencyRate: crossRate } : {}),
    };

    await fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(adjustmentBody),
    }).catch(() => {});

    // Refresh linked adjustments state
    fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`)
      .then(r => r.ok ? r.json() : { adjustments: [] })
      .then(data => {
        const linked = ((data.adjustments || []) as BalanceAdjustment[]).filter((a: BalanceAdjustment) => a.linkedTradeId === trade.id);
        setTradeRefundAdjustments(linked);
      })
      .catch(() => {});

    const totalLabel = refundCurrency === "NGN" ? `₦${totalRefundAmount.toLocaleString()}` : `${totalRefundAmount.toLocaleString()} USDT`;
    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "refund_logged", details: `Refund payout of ${totalLabel} via ${txCount} transaction(s) on trade #${trade.id} — balance adjustment for ${cp.name}`, changes: [{ field: "balance_adjustment", from: null, to: `payout ${totalLabel}` }], channel: "dashboard", timestamp: new Date().toISOString() });

    setShowResolutionPanel(null);
    setResolutionSelectedTxIds(new Set());
    setResolutionBankId(null);
    setResCrossRate(""); setResCrossRateMode("trade");
    onUpdateTrade(trade.id, {});
    toast(`Refund of ${totalLabel} applied as balance payout for ${cp.name}`);
  };

  const handleCreditToTrade = async () => {
    if (!showResolutionPanel || !creditTargetTradeId) return;
    const side = showResolutionPanel.side;
    const isNairaSide = side === "naira";
    const tradeToTradeNgnTotal = getTradeNgnTotal(trade);
    const excessAmount = isNairaSide
      ? (tradeToTradeNgnTotal ? trade.nairaAmountSettled - tradeToTradeNgnTotal : 0)
      : (trade.coinAmountSettled - trade.usdtAmount);
    if (excessAmount <= 0) return;

    const targetTrade = allTrades.find(t => t.id === creditTargetTradeId);
    if (!targetTrade) return;

    // Refund entry on source trade
    const refundEntry: SettlementEntry = {
      id: Date.now(),
      tradeId: trade.id,
      side: isNairaSide ? "naira" : "coin",
      amount: excessAmount,
      currency: isNairaSide ? "NGN" : "USDT",
      paymentMethod: isNairaSide ? "bank_transfer" : "crypto_wallet",
      sourceDestination: `Applied to Trade #${creditTargetTradeId}`,
      reference: `XFER-${Date.now()}`,
      isRefund: true,
      createdAt: new Date().toISOString(),
    };

    // Payment entry on target trade
    const paymentEntry: SettlementEntry = {
      id: Date.now() + 1,
      tradeId: creditTargetTradeId,
      side: isNairaSide ? "naira" : "coin",
      amount: excessAmount,
      currency: isNairaSide ? "NGN" : "USDT",
      paymentMethod: isNairaSide ? "bank_transfer" : "crypto_wallet",
      sourceDestination: `Credit from Trade #${trade.id}`,
      reference: `XFER-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };

    settlementEntries.push(refundEntry);
    settlementEntries.push(paymentEntry);
    recalcSettlement();

    // Recalc target trade inline
    const targetEntries = settlementEntries.filter(e => e.tradeId === creditTargetTradeId);
    const tCoinTotal = targetEntries.filter(e => e.side === "coin" && !e.isRefund).reduce((s, e) => s + e.amount, 0);
    const tCoinRefunds = targetEntries.filter(e => e.side === "coin" && e.isRefund).reduce((s, e) => s + e.amount, 0);
    const tNairaTotal = targetEntries.filter(e => e.side === "naira" && !e.isRefund).reduce((s, e) => s + e.amount, 0);
    const tNairaRefunds = targetEntries.filter(e => e.side === "naira" && e.isRefund).reduce((s, e) => s + e.amount, 0);
    targetTrade.coinAmountSettled = Math.max(0, tCoinTotal - tCoinRefunds);
    targetTrade.coinStatus = targetTrade.coinAmountSettled <= 0 ? "pending" : targetTrade.coinAmountSettled > targetTrade.usdtAmount ? "overpaid" : targetTrade.coinAmountSettled >= targetTrade.usdtAmount ? "complete" : "partial";
    targetTrade.nairaAmountSettled = Math.max(0, tNairaTotal - tNairaRefunds);
    const tNgnTotal = targetTrade.ngnAmount || 0;
    targetTrade.nairaStatus = targetTrade.nairaAmountSettled <= 0 ? "pending" : targetTrade.nairaAmountSettled > tNgnTotal ? "overpaid" : targetTrade.nairaAmountSettled >= tNgnTotal ? "complete" : "partial";
    const tCoinDone = targetTrade.coinStatus === "complete" || targetTrade.coinStatus === "overpaid";
    const tNairaDone = targetTrade.nairaStatus === "complete" || targetTrade.nairaStatus === "overpaid";
    if (tCoinDone && tNairaDone) { targetTrade.status = "settled"; targetTrade.settledAt = targetTrade.settledAt || new Date().toISOString(); }

    const srcSync = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
    const tgtSync = { nairaAmountSettled: targetTrade.nairaAmountSettled, nairaStatus: targetTrade.nairaStatus, coinAmountSettled: targetTrade.coinAmountSettled, coinStatus: targetTrade.coinStatus, status: targetTrade.status, settledAt: targetTrade.settledAt, nairaPaymentMethod: targetTrade.nairaPaymentMethod, nairaBankRef: targetTrade.nairaBankRef, nairaBankVerified: targetTrade.nairaBankVerified };
    await Promise.all([
      fetch("/api/trades/settlements", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: [refundEntry, paymentEntry] }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...srcSync }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...srcSync }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: creditTargetTradeId, ...tgtSync }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: creditTargetTradeId, ...tgtSync }) }),
    ]).catch(() => {});

    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "credit_transferred", details: `Transferred ${isNairaSide ? "₦" + excessAmount.toLocaleString() : excessAmount.toLocaleString() + " USDT"} from trade #${trade.id} to trade #${creditTargetTradeId}`, changes: [{ field: isNairaSide ? "nairaAmountSettled" : "coinAmountSettled", from: isNairaSide ? trade.nairaAmountSettled + excessAmount : trade.coinAmountSettled + excessAmount, to: isNairaSide ? trade.nairaAmountSettled : trade.coinAmountSettled }], channel: "dashboard", timestamp: new Date().toISOString() });

    setShowResolutionPanel(null);
    setCreditTargetTradeId(null);
    setCreditSubAction(null);
    onUpdateTrade(trade.id, {});
    toast(`${isNairaSide ? "₦" + excessAmount.toLocaleString() : excessAmount.toLocaleString() + " USDT"} applied to Trade #${creditTargetTradeId}`);
  };

  const applyCounterpartyCredit = async (side: "naira" | "coin") => {
    if (!trade.counterpartyId) return;
    const isNairaSide = side === "naira";
    const applyNgnTotal = getTradeNgnTotal(trade);
    const shortfall = isNairaSide
      ? (applyNgnTotal ? applyNgnTotal - trade.nairaAmountSettled : 0)
      : (trade.usdtAmount - trade.coinAmountSettled);
    if (shortfall <= 0) return;

    const cp = counterparties.find(c => c.id === trade.counterpartyId);
    if (!cp) return;
    const availableCredit = isNairaSide ? cp.balanceNgn : cp.balanceUsdt;
    if (availableCredit <= 0) return;

    const applyAmount = Math.min(shortfall, availableCredit);

    // Find the most recent auto-credit refund for this counterparty to link via sourceTradeId
    const creditRefund = [...settlementEntries].reverse().find(e =>
      e.isRefund && e.creditCounterpartyId === trade.counterpartyId && e.sourceTradeId
    );
    const entry: SettlementEntry = {
      id: Date.now(),
      tradeId: trade.id,
      side: isNairaSide ? "naira" : "coin",
      amount: applyAmount,
      currency: isNairaSide ? "NGN" : "USDT",
      paymentMethod: isNairaSide ? "bank_transfer" : "crypto_wallet",
      sourceDestination: "Counterparty Credit",
      reference: `CREDIT-APPLY-${Date.now()}`,
      creditCounterpartyId: trade.counterpartyId,
      createdAt: new Date().toISOString(),
      balanceDeltaNgn: isNairaSide ? -applyAmount : 0,
      balanceDeltaUsdt: isNairaSide ? 0 : -applyAmount,
      sourceTradeId: creditRefund?.sourceTradeId,
    };

    settlementEntries.push(entry);
    recalcSettlement();

    // Decrease counterparty credit
    if (isNairaSide) cp.balanceNgn -= applyAmount;
    else cp.balanceUsdt -= applyAmount;

    const syncData = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
    await Promise.all([
      fetch("/api/trades/settlements", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: [entry] }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...syncData }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...syncData }) }),
      fetch(`/api/counterparties/${trade.counterpartyId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ balanceNgn: cp.balanceNgn, balanceUsdt: cp.balanceUsdt }) }),
    ]).catch(() => {});

    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "credit_applied", details: `Applied ${isNairaSide ? "₦" + applyAmount.toLocaleString() : applyAmount.toLocaleString() + " USDT"} from ${cp.name}'s credit to trade #${trade.id}`, changes: [{ field: isNairaSide ? "nairaAmountSettled" : "coinAmountSettled", from: isNairaSide ? trade.nairaAmountSettled - applyAmount : trade.coinAmountSettled - applyAmount, to: isNairaSide ? trade.nairaAmountSettled : trade.coinAmountSettled }], counterpartyId: trade.counterpartyId ?? undefined, channel: "dashboard", timestamp: new Date().toISOString() });

    onUpdateTrade(trade.id, {});
    toast(`${isNairaSide ? "₦" + applyAmount.toLocaleString() : applyAmount.toLocaleString() + " USDT"} applied from ${cp.name}'s credit`);
  };

  const handleApplyCreditToShortfall = async () => {
    if (!showResolutionPanel || !trade.counterpartyId) return;
    await applyCounterpartyCredit(showResolutionPanel.side);
    setShowResolutionPanel(null);
  };

  const handleTagTransaction = async (tx: TreasuryTransaction, side: "coin" | "naira", batchId?: string) => {
    const entry: SettlementEntry = {
      id: Date.now(),
      tradeId: trade.id,
      side,
      amount: tx.amount,
      currency: tx.currency as "USDT" | "NGN",
      paymentMethod: "bank_transfer",
      sourceDestination: treasuryAccounts.find(a => a.id === tx.accountId)?.label || "Unknown",
      reference: tx.narration.slice(0, 40),
      treasuryAccountId: tx.accountId,
      batchId,
      createdAt: new Date().toISOString(),
    };
    // Extract payer details from narration for naira treasury transactions
    if (side === "naira" && tx.narration) {
      const narration = tx.narration;
      let pName: string | null = null;
      let pAcct: string | null = null;
      let pRef: string | null = null;
      // NIP/TRF pattern
      const nipMatch = /(?:NIP|MC|NIBSS)\s*\/?\s*(?:TRF\s+)?(?:FRM|FROM)?\s*([A-Za-z\s.'-]{2,40})\s*\/\s*(\d{10})\s*\/?\s*(.*)/i.exec(narration);
      if (nipMatch) { pName = nipMatch[1].trim(); pAcct = nipMatch[2]; if (nipMatch[3]?.trim()) pRef = nipMatch[3].trim().slice(0, 40); }
      if (!pName) { const trfMatch = /(?:TRF|TRANSFER)\s+FROM\s+([A-Za-z\s.'-]{2,40})/i.exec(narration); if (trfMatch) pName = trfMatch[1].trim(); }
      if (!pAcct) { const acctMatch = /\b(\d{10})\b/.exec(narration); if (acctMatch) pAcct = acctMatch[1]; }
      if (!pName) pName = (tx as Record<string, unknown>).counterpartyName as string || null;
      if (pName) {
        const payerDetailId = genId();
        entry.payerDetailId = payerDetailId;
        const bankLabel = treasuryAccounts.find(a => a.id === tx.accountId)?.bankName || treasuryAccounts.find(a => a.id === tx.accountId)?.label || "";
        const payerDetail: PayerDetail = { id: payerDetailId, settlementId: entry.id, tradeId: trade.id, payerName: pName, payerAccountNumber: pAcct || "", payerBank: bankLabel, amount: tx.amount, reference: pRef || tx.narration.slice(0, 40), source: "treasury", createdAt: new Date().toISOString() };
        setTradePayerDetails((prev) => [...prev, payerDetail]);
        fetch("/api/payer-details", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payerDetail) }).catch(() => {});
      }
    }
    settlementEntries.push(entry);
    tx.linkedTradeId = trade.id;
    tx.linkedSettlementId = entry.id;
    persistBankTxLink(tx.id, trade.id, entry.id);
    recalcSettlement();
    setShowBankPicker(false);
    // Persist to disk BEFORE triggering re-render (avoids race with scanner.reload)
    const tagSettlementSync = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
    await Promise.all([
      fetch("/api/trades/settlements", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: [entry] }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...tagSettlementSync }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...tagSettlementSync }) }),
    ]).catch(() => {});
    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "treasury_tagged", details: `Tagged ${tx.currency === "USDT" ? tx.amount.toLocaleString() + " USDT" : "₦" + tx.amount.toLocaleString()} from ${entry.sourceDestination}`, changes: [{ field: tx.currency === "USDT" ? "coinAmountSettled" : "nairaAmountSettled", from: tx.currency === "USDT" ? trade.coinAmountSettled - tx.amount : trade.nairaAmountSettled - tx.amount, to: tx.currency === "USDT" ? trade.coinAmountSettled : trade.nairaAmountSettled }], channel: "dashboard", timestamp: new Date().toISOString() });
    onUpdateTrade(trade.id, {});
    toast("Transaction tagged to trade");
  };

  // Recalculate settlement for any trade (used for cascade removal)
  const recalcTradeSettlement = (t: Trade) => {
    const currentEntries = settlementEntries.filter(e => e.tradeId === t.id);
    const calcEntries = currentEntries.filter(e => !e.isCreditToBalance);
    const coinTotal = calcEntries.filter(e => e.side === "coin" && !e.isRefund).reduce((s, e) => s + e.amount, 0);
    const coinRefunds = calcEntries.filter(e => e.side === "coin" && e.isRefund).reduce((s, e) => s + e.amount, 0);
    const nairaTotal = calcEntries.filter(e => e.side === "naira" && !e.isRefund).reduce((s, e) => s + e.amount, 0);
    const nairaRefunds = calcEntries.filter(e => e.side === "naira" && e.isRefund).reduce((s, e) => s + e.amount, 0);
    t.coinAmountSettled = Math.max(0, coinTotal - coinRefunds);
    t.coinStatus = t.coinAmountSettled <= 0 ? "pending" : t.coinAmountSettled > t.usdtAmount ? "overpaid" : t.coinAmountSettled >= t.usdtAmount ? "complete" : "partial";
    t.nairaAmountSettled = Math.max(0, nairaTotal - nairaRefunds);
    if (t.direction === "middleman") {
      const nairaEnts = currentEntries.filter(e => e.side === "naira");
      if (t.nairaFlow === "direct") {
        const profitSettled = nairaEnts.filter(e => e.middlemanLeg === "profit" || !e.middlemanLeg).reduce((s, e) => s + e.amount, 0);
        t.nairaStatus = profitSettled <= 0 ? "pending" : profitSettled >= Math.abs(t.middlemanProfit || 0) ? "complete" : "partial";
      } else {
        const buySettled = nairaEnts.filter(e => e.middlemanLeg === "buy").reduce((s, e) => s + e.amount, 0);
        const sellSettled = nairaEnts.filter(e => e.middlemanLeg === "sell").reduce((s, e) => s + e.amount, 0);
        const buyDone = buySettled >= (t.buyRate || 0) * t.usdtAmount;
        const sellDone = sellSettled >= (t.sellRate || 0) * t.usdtAmount;
        t.nairaStatus = (buyDone && sellDone) ? "complete" : (buySettled > 0 || sellSettled > 0) ? "partial" : "pending";
      }
    } else {
      const ngnTotal = t.ngnAmount || 0;
      t.nairaStatus = t.nairaAmountSettled <= 0 ? "pending" : t.nairaAmountSettled > ngnTotal ? "overpaid" : t.nairaAmountSettled >= ngnTotal ? "complete" : "partial";
    }
    const coinDone = t.coinStatus === "complete" || t.coinStatus === "overpaid";
    const nairaDone = t.nairaStatus === "complete" || t.nairaStatus === "overpaid";
    if (coinDone && nairaDone) { t.status = "settled"; t.settledAt = t.settledAt || new Date().toISOString(); }
    else if (t.status === "settled") { t.status = "pending"; t.settledAt = null; }
  };

  const handleRemoveEntry = async (entry: SettlementEntry) => {
    const rmBeforeSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
    // Remove from settlementEntries array
    const idx = settlementEntries.findIndex(e => e.id === entry.id);
    if (idx !== -1) settlementEntries.splice(idx, 1);
    // Unlink any tagged bank transaction — fetch fresh from API to avoid stale in-memory state
    if (entry.treasuryAccountId) {
      try {
        const freshRes = await fetch("/api/bank-transactions");
        if (freshRes.ok) {
          const freshData = await freshRes.json();
          const allTxs: BankTransaction[] = freshData.transactions || [];
          // Primary: match by linkedSettlementId (set by new linking code)
          let linked = allTxs.filter(tx => tx.linkedSettlementId === entry.id);
          // Fallback: for transactions linked before linkedSettlementId was tracked,
          // match by linkedTradeId + accountId + amount
          if (linked.length === 0) {
            linked = allTxs.filter(tx =>
              tx.linkedTradeId === trade.id &&
              tx.accountId === entry.treasuryAccountId &&
              Math.abs(tx.amount - entry.amount) < 0.01
            );
          }
          for (const tx of linked) {
            // Update in-memory for immediate UI (check both arrays)
            const memTx = treasuryTransactions.find(t => t.id === tx.id) || monoBankStatements.find(s => s.id === tx.id);
            if (memTx) { memTx.linkedTradeId = undefined; memTx.linkedSettlementId = undefined; }
            // Persist to server
            await persistBankTxLink(tx.id, undefined, undefined);
          }
        }
      } catch { /* proceed — best-effort unlink */ }
    }

    // Bidirectional cascade: if this entry has a sourceTradeId, also remove its counterpart
    let cascadedEntry: SettlementEntry | undefined;
    let cascadedTrade: Trade | undefined;
    if (entry.sourceTradeId && entry.creditCounterpartyId) {
      if (!entry.isRefund) {
        // Removing a credit-application entry → find the refund on the source trade
        const pairIdx = settlementEntries.findIndex(e =>
          e.tradeId === entry.sourceTradeId && e.isRefund && e.creditCounterpartyId === entry.creditCounterpartyId
        );
        if (pairIdx !== -1) {
          cascadedEntry = settlementEntries[pairIdx];
          settlementEntries.splice(pairIdx, 1);
          cascadedTrade = allTrades.find(t => t.id === entry.sourceTradeId);
        }
      } else {
        // Removing a refund entry → find the application on another trade
        const pairIdx = settlementEntries.findIndex(e =>
          e.sourceTradeId === entry.tradeId && !e.isRefund && e.creditCounterpartyId === entry.creditCounterpartyId
        );
        if (pairIdx !== -1) {
          cascadedEntry = settlementEntries[pairIdx];
          settlementEntries.splice(pairIdx, 1);
          cascadedTrade = allTrades.find(t => t.id === cascadedEntry!.tradeId);
        }
      }
    }

    recalcSettlement();
    // Also recalc the cascaded trade if present
    if (cascadedTrade) recalcTradeSettlement(cascadedTrade);

    const rmAfterSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
    // If this was a credit-to-balance entry, delete the adjustment then re-fetch server balances
    if (entry.isCreditToBalance && entry.creditCounterpartyId) {
      await fetch(`/api/counterparties/${entry.creditCounterpartyId}/adjustments`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: entry.reference }),
      }).catch(() => {});
      await refreshCounterpartyBalances();
    }
    // Clean up any balance adjustments linked to this trade (refund payouts)
    // When the payment that created the overpayment is unlinked, the refund adjustment is orphaned
    if (trade.counterpartyId) {
      try {
        const adjRes = await fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`);
        if (adjRes.ok) {
          const adjData = await adjRes.json();
          const linkedAdjs = ((adjData.adjustments || []) as BalanceAdjustment[]).filter((a: BalanceAdjustment) => a.linkedTradeId === trade.id);
          for (const adj of linkedAdjs) {
            await fetch(`/api/counterparties/${trade.counterpartyId}/adjustments`, {
              method: "DELETE",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ adjustmentId: adj.id }),
            }).catch(() => {});
          }
          if (linkedAdjs.length > 0) {
            setTradeRefundAdjustments([]);
            await refreshCounterpartyBalances();
          }
        }
      } catch { /* best-effort cleanup */ }
    }
    // Unlink any orphaned bank statements still pointing at this trade
    // Covers: old refund links (linkedSettlementId pointing to deleted entry) AND
    // new refund links (linkedTradeId set but no linkedSettlementId)
    try {
      const freshBankRes = await fetch("/api/bank-transactions");
      if (freshBankRes.ok) {
        const freshBankData = await freshBankRes.json();
        const allBankTxs: BankTransaction[] = freshBankData.transactions || [];
        const remainingEntryIds = new Set(settlementEntries.filter(e => e.tradeId === trade.id).map(e => e.id));
        const orphanedBankTxs = allBankTxs.filter(tx =>
          tx.linkedTradeId === trade.id &&
          (!tx.linkedSettlementId || !remainingEntryIds.has(tx.linkedSettlementId))
        );
        for (const tx of orphanedBankTxs) {
          const memTx = monoBankStatements.find(s => s.id === tx.id);
          if (memTx) { memTx.linkedTradeId = undefined; memTx.linkedSettlementId = undefined; }
          await persistBankTxLink(tx.id, undefined, undefined);
        }
      }
    } catch { /* best-effort cleanup */ }
    // Reverse any balance effect from this entry using stored deltas
    if (entry.creditCounterpartyId && (entry.balanceDeltaNgn || entry.balanceDeltaUsdt)) {
      const cp = counterparties.find(c => c.id === entry.creditCounterpartyId);
      if (cp) {
        // Subtract the delta to reverse it (positive delta was a credit addition, negative was outstanding)
        if (entry.balanceDeltaNgn) cp.balanceNgn -= entry.balanceDeltaNgn;
        if (entry.balanceDeltaUsdt) cp.balanceUsdt -= entry.balanceDeltaUsdt;
        // Also reverse the cascaded entry's balance delta
        if (cascadedEntry) {
          if (cascadedEntry.balanceDeltaNgn) cp.balanceNgn -= cascadedEntry.balanceDeltaNgn;
          if (cascadedEntry.balanceDeltaUsdt) cp.balanceUsdt -= cascadedEntry.balanceDeltaUsdt;
        }
        fetch(`/api/counterparties/${entry.creditCounterpartyId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ balanceNgn: cp.balanceNgn, balanceUsdt: cp.balanceUsdt }) }).catch(() => {});
      }
    }
    // Persist to disk BEFORE triggering re-render (avoids race with scanner.reload)
    const settlementSync = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
    const deletePromises: Promise<Response>[] = [
      fetch("/api/trades/settlements", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entryId: entry.id }) }),
      fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...settlementSync }) }),
      fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...settlementSync }) }),
    ];
    // Persist cascaded deletion + trade sync
    if (cascadedEntry) {
      deletePromises.push(fetch("/api/trades/settlements", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entryId: cascadedEntry.id }) }));
      if (cascadedTrade) {
        const cascSync = { nairaAmountSettled: cascadedTrade.nairaAmountSettled, nairaStatus: cascadedTrade.nairaStatus, coinAmountSettled: cascadedTrade.coinAmountSettled, coinStatus: cascadedTrade.coinStatus, status: cascadedTrade.status, settledAt: cascadedTrade.settledAt, nairaPaymentMethod: cascadedTrade.nairaPaymentMethod, nairaBankRef: cascadedTrade.nairaBankRef, nairaBankVerified: cascadedTrade.nairaBankVerified };
        deletePromises.push(fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: cascadedTrade.id, ...cascSync }) }));
        deletePromises.push(fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: cascadedTrade.id, ...cascSync }) }));
      }
    }
    await Promise.all(deletePromises).catch(() => {});
    postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "payment_removed", details: `Removed ${entry.side === "coin" ? entry.amount.toLocaleString() + " USDT" : "₦" + entry.amount.toLocaleString()} ${entry.paymentMethod.replace(/_/g, " ")} via ${entry.sourceDestination}${cascadedEntry ? ` (cascade: also removed entry on trade #${cascadedEntry.tradeId})` : ""}`, changes: diffChanges(rmBeforeSnap, rmAfterSnap, ["coinAmountSettled", "coinStatus", "nairaAmountSettled", "nairaStatus", "status"]), channel: "dashboard", timestamp: new Date().toISOString() });
    onUpdateTrade(trade.id, {});
    toast(cascadedEntry ? "Payment entries removed (cascaded)" : "Payment entry removed");
  };

  const dir = getDirectionConfig(trade.direction);

  return (
    <>
      {/* Backdrop overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[55] bg-black/30"
        onClick={onClose}
      />
      {/* Drawer panel */}
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Trade details"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed top-0 right-0 bottom-0 z-[56] w-full sm:w-[85%] md:w-[60%] lg:w-[50%] max-w-[720px] bg-surface shadow-2xl flex flex-col"
        style={{ borderLeft: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-surface border-b border-border">
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${dir.colorClass}`}>{dir.label}</span>
              <span className="text-sm font-semibold text-foreground">
                {trade.direction === "middleman"
                  ? `${(trade.buyCounterpartyName || "Seller").toUpperCase()} \u2192 ${(trade.sellCounterpartyName || "Buyer").toUpperCase()}`
                  : (trade.counterpartyName || "Unknown").toUpperCase()}
              </span>
              <span className="text-xs text-muted">#{trade.id}</span>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-lg hover:bg-surface-2 text-muted hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Summary strip */}
          <div className="px-5 py-2 bg-surface-2/50 border-t border-border flex items-center gap-3 text-sm">
            <span className="font-semibold text-foreground tabular-nums">{trade.usdtAmount.toLocaleString()} USDT</span>
            <span className="text-muted/40">&middot;</span>
            <span className="font-semibold text-foreground tabular-nums">₦{trade.rate?.toLocaleString() ?? "—"}</span>
            <span className="text-muted/40">&middot;</span>
            <span className="font-semibold text-foreground tabular-nums">{drawerNgnTotal ? `₦${drawerNgnTotal.toLocaleString()}` : "—"}</span>
            {(() => {
              const hasOverpay = trade.coinStatus === "overpaid" || trade.nairaStatus === "overpaid";
              const allDone = (trade.coinStatus === "complete" || trade.coinStatus === "overpaid") && (trade.nairaStatus === "complete" || trade.nairaStatus === "overpaid");
              const hasPartial = trade.coinStatus === "partial" || trade.nairaStatus === "partial";
              const bg = hasOverpay ? "color-mix(in srgb, var(--danger) 12%, transparent)" : allDone ? "color-mix(in srgb, var(--success) 12%, transparent)" : hasPartial ? "color-mix(in srgb, var(--warning) 12%, transparent)" : "var(--surface-3)";
              const dot = hasOverpay ? "bg-danger" : allDone ? "bg-success" : hasPartial ? "bg-warning" : "bg-muted";
              const txt = hasOverpay ? "text-danger" : allDone ? "text-success" : hasPartial ? "text-warning" : "text-muted";
              const label = hasOverpay ? "Overpaid" : allDone ? "Settled" : hasPartial ? "Partial" : "Pending";
              return (
                <span className={`ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full`} style={{ background: bg }}>
                  <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                  <span className={txt}>{label}</span>
                </span>
              );
            })()}
          </div>
        </div>
        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <motion.div className="px-5 py-5" variants={drawerContentVariants} initial="hidden" animate="visible">
                  {/* Settlement Timeline */}
                  {(() => {
                    type TlEvent = { time: Date; label: string; detail: string; color: string };
                    const events: TlEvent[] = [];
                    events.push({
                      time: new Date(trade.createdAt),
                      label: "Created",
                      detail: new Date(trade.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
                      color: "bg-muted",
                    });
                    for (const entry of tradeEntries) {
                      const t = new Date(entry.createdAt);
                      const isCoin = entry.side === "coin";
                      const amt = isCoin
                        ? `${entry.amount.toLocaleString()} USDT`
                        : `₦${entry.amount.toLocaleString()}`;
                      events.push({
                        time: t,
                        label: entry.isRefund ? `${isCoin ? "USDT" : "NGN"} Refund` : amt,
                        detail: t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
                        color: entry.isRefund ? "bg-danger" : isCoin ? "bg-accent" : "bg-warning",
                      });
                    }
                    if (trade.settledAt) {
                      events.push({
                        time: new Date(trade.settledAt),
                        label: "Settled",
                        detail: new Date(trade.settledAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
                        color: "bg-success",
                      });
                    }
                    events.sort((a, b) => a.time.getTime() - b.time.getTime());
                    if (events.length <= 1) return null;
                    const totalMs = trade.settledAt
                      ? new Date(trade.settledAt).getTime() - new Date(trade.createdAt).getTime()
                      : null;
                    const fmtDelta = (ms: number) => {
                      const mins = Math.round(ms / 60000);
                      if (mins < 1) return "<1m";
                      if (mins < 60) return `${mins}m`;
                      const hrs = Math.floor(mins / 60);
                      const rm = mins % 60;
                      return rm > 0 ? `${hrs}h ${rm}m` : `${hrs}h`;
                    };
                    return (
                      <motion.div
                        className="mb-4 rounded-lg px-3 py-2.5 overflow-x-auto"
                        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                        variants={drawerItemVariants}
                      >
                        <div className="flex items-center gap-0 min-w-max">
                          {events.map((ev, i) => (
                            <div key={i} className="flex items-center">
                              {i > 0 && (
                                <div className="flex items-center mx-1">
                                  <div className="w-4 h-px bg-border" />
                                  <span className="text-[8px] text-muted/50 mx-0.5 tabular-nums">
                                    {fmtDelta(ev.time.getTime() - events[i - 1].time.getTime())}
                                  </span>
                                  <div className="w-4 h-px bg-border" />
                                </div>
                              )}
                              <div className="flex flex-col items-center gap-0.5 px-1">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${ev.color}`} />
                                <span className="text-[10px] font-semibold text-foreground whitespace-nowrap">{ev.label}</span>
                                <span className="text-[9px] text-muted whitespace-nowrap tabular-nums">{ev.detail}</span>
                              </div>
                            </div>
                          ))}
                          {totalMs !== null && (
                            <span className="ml-2 text-[9px] text-success font-semibold whitespace-nowrap">({fmtDelta(totalMs)})</span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })()}
                  {/* Dispute Banner */}
                  {(trade.status === "disputed" || tradeDisputes.length > 0) && !disputeLoading && (
                    <motion.div variants={drawerItemVariants} className="mb-4">
                      {tradeDisputes.map((d) => (
                        <div
                          key={d.id}
                          className="rounded-xl p-4 mb-3 last:mb-0"
                          style={{ background: "color-mix(in srgb, var(--danger) 8%, var(--surface-2))", border: "1px solid color-mix(in srgb, var(--danger) 25%, var(--border))" }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-danger/15 flex items-center justify-center">
                                <AlertTriangle className="w-4 h-4 text-danger" />
                              </div>
                              <div>
                                <span className="text-[13px] font-bold text-foreground">{d.reason}</span>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                    d.status === "open" ? "text-danger bg-danger/15" :
                                    d.status === "under_review" ? "text-warning bg-warning/15" :
                                    d.status === "resolved" ? "text-success bg-success/15" :
                                    "text-muted bg-surface-3"
                                  }`}>{d.status.replace("_", " ")}</span>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                    d.priority === "critical" ? "text-danger bg-danger/10" :
                                    d.priority === "high" ? "text-warning bg-warning/10" :
                                    d.priority === "medium" ? "text-accent bg-accent/10" :
                                    "text-muted bg-surface-3"
                                  }`}>{d.priority}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-[12px] text-muted leading-relaxed mb-3">{d.description}</p>
                          {/* Mini timeline */}
                          <div className="flex items-center gap-0 mb-3">
                            <div className="flex flex-col items-center gap-0.5 px-1">
                              <span className="w-2 h-2 rounded-full bg-danger" />
                              <span className="text-[9px] font-semibold text-foreground whitespace-nowrap">Filed</span>
                              <span className="text-[8px] text-muted whitespace-nowrap tabular-nums">{new Date(d.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center mx-1">
                              <div className={`w-6 h-px ${d.status !== "open" ? "bg-warning" : "bg-border"}`} />
                            </div>
                            <div className="flex flex-col items-center gap-0.5 px-1">
                              <span className={`w-2 h-2 rounded-full ${d.status !== "open" ? "bg-warning" : "bg-border"}`} />
                              <span className={`text-[9px] font-semibold whitespace-nowrap ${d.status !== "open" ? "text-foreground" : "text-muted/40"}`}>Under Review</span>
                            </div>
                            <div className="flex items-center mx-1">
                              <div className={`w-6 h-px ${d.status === "resolved" || d.status === "dismissed" ? "bg-success" : "bg-border"}`} />
                            </div>
                            <div className="flex flex-col items-center gap-0.5 px-1">
                              <span className={`w-2 h-2 rounded-full ${d.status === "resolved" || d.status === "dismissed" ? "bg-success" : "bg-border"}`} />
                              <span className={`text-[9px] font-semibold whitespace-nowrap ${d.status === "resolved" || d.status === "dismissed" ? "text-foreground" : "text-muted/40"}`}>{d.status === "dismissed" ? "Dismissed" : "Resolved"}</span>
                            </div>
                          </div>
                          {/* Dispute history */}
                          {d.history && d.history.length > 0 && (
                            <details className="mb-3 group">
                              <summary className="text-[10px] font-semibold text-muted cursor-pointer hover:text-foreground transition-colors select-none">
                                History ({d.history.length})
                              </summary>
                              <div className="mt-1.5 space-y-1 pl-1">
                                {d.history.map((h, hi) => (
                                  <div key={hi} className="text-[10px] text-muted flex items-center gap-1.5">
                                    <span className="tabular-nums text-muted/60">{new Date(h.at).toLocaleDateString()}</span>
                                    <span className="text-muted/40">·</span>
                                    <span className="font-medium text-foreground/80">{h.by}</span>
                                    <span className="text-muted/40">·</span>
                                    <span>{h.action.replace(/_/g, " ")}{h.from && h.to ? ` ${h.from} → ${h.to}` : ""}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                          {/* Evidence thumbnails */}
                          {d.evidence.length > 0 && (
                            <div className="flex gap-1.5 mb-3 flex-wrap">
                              {d.evidence.map((url, i) => (
                                <div key={i} className="relative group/ev">
                                  <img
                                    src={url}
                                    alt={`Evidence ${i + 1}`}
                                    className="w-10 h-10 rounded-md object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                    style={{ border: "1px solid var(--border)" }}
                                    onClick={() => { setLightboxImages(d.evidence); setLightboxIndex(i); }}
                                  />
                                  {(d.status === "open" || d.status === "under_review") && hasPermission(activeStaff, "dispute") && (
                                    <button
                                      onClick={() => handleRemoveEvidence(d.id, url)}
                                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-danger text-white flex items-center justify-center opacity-0 group-hover/ev:opacity-100 transition-opacity"
                                    >
                                      <X className="w-2.5 h-2.5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Comments thread */}
                          {d.comments && d.comments.length > 0 && (
                            <div className="mb-3 space-y-1.5">
                              {d.comments.map((c) => (
                                <div key={c.id} className="rounded-lg px-3 py-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    <span className="text-[10px] font-bold text-foreground">{c.author}</span>
                                    <span className="text-[9px] text-muted">{timeAgo(c.createdAt)}</span>
                                  </div>
                                  <p className="text-[11px] text-muted leading-relaxed">{c.text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {(d.status === "open" || d.status === "under_review") && hasPermission(activeStaff, "dispute") && (
                            <div className="flex gap-1.5 mb-3">
                              <input
                                type="text"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && commentText.trim()) handleAddComment(d.id); }}
                                placeholder="Add a comment..."
                                className="flex-1 bg-surface-2 rounded-lg px-3 py-1.5 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
                                style={{ border: "1px solid var(--border)" }}
                              />
                              <button
                                onClick={() => handleAddComment(d.id)}
                                disabled={!commentText.trim() || submittingComment}
                                className="px-2.5 py-1.5 rounded-lg bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-40 transition-colors"
                              >
                                {submittingComment ? <Loader2 className="w-3 h-3 animate-spin" /> : <SendIcon className="w-3 h-3" />}
                              </button>
                            </div>
                          )}
                          {/* Resolution details */}
                          {d.resolution && (
                            <div className="rounded-lg p-3 mt-2" style={{ background: "color-mix(in srgb, var(--success) 6%, var(--surface))", border: "1px solid color-mix(in srgb, var(--success) 20%, var(--border))" }}>
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                                <span className="text-[11px] font-bold text-success capitalize">{d.resolution.type.replace(/_/g, " ")}</span>
                                {d.resolution.amount != null && d.resolution.amount > 0 && (
                                  <span className="text-[11px] font-semibold text-foreground tabular-nums ml-1">
                                    {d.resolution.currency === "NGN" ? "₦" : ""}{d.resolution.amount.toLocaleString()}{d.resolution.currency === "USDT" ? " USDT" : ""}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-muted">{d.resolution.note}</p>
                              <p className="text-[9px] text-muted/70 mt-1">Resolved by {d.resolution.resolvedBy} · {new Date(d.resolution.resolvedAt).toLocaleDateString()}</p>
                            </div>
                          )}
                          {/* Action buttons */}
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            {d.status === "open" && hasPermission(activeStaff, "dispute") && (
                              <button
                                onClick={() => handleMarkUnderReview(d.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground hover:bg-surface-3/80 transition-colors"
                                style={{ border: "1px solid var(--border)" }}
                              >
                                <Eye className="w-3 h-3" />
                                Mark Under Review
                              </button>
                            )}
                            {(d.status === "open" || d.status === "under_review") && hasPermission(activeStaff, "dispute") && (
                              <button
                                onClick={() => { setShowResolveModal(d.id); setResolveType("refund"); setResolveAmount(""); setResolveNote(""); setResolveCurrency("NGN"); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground hover:bg-surface-3/80 transition-colors"
                                style={{ border: "1px solid var(--border)" }}
                              >
                                <Scale className="w-3 h-3" />
                                Resolve Dispute
                              </button>
                            )}
                            {(d.status === "resolved" || d.status === "dismissed") && hasPermission(activeStaff, "dispute") && (
                              <button
                                onClick={() => handleReopenDispute(d.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-warning hover:bg-warning/10 transition-colors"
                                style={{ border: "1px solid color-mix(in srgb, var(--warning) 30%, var(--border))" }}
                              >
                                <RefreshCw className="w-3 h-3" />
                                Re-open
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {tradeDisputes.length === 0 && trade.status === "disputed" && (
                        <div className="rounded-xl p-4" style={{ background: "color-mix(in srgb, var(--danger) 8%, var(--surface-2))", border: "1px solid color-mix(in srgb, var(--danger) 25%, var(--border))" }}>
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-danger" />
                            <span className="text-[12px] font-semibold text-danger">This trade is disputed</span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                  <motion.div className="grid grid-cols-2 gap-4" variants={drawerItemVariants}>
                    {/* Coin Delivery Side */}
                    <div className="bg-surface-2/40 rounded-xl p-4 space-y-3" style={{ border: "1px solid var(--border)" }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="section-icon section-icon-accent"><CfUsdtCoin /></div>
                          <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">USDT Delivery</h4>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${trade.coinStatus === "overpaid" ? "text-danger bg-danger/10" : trade.coinStatus === "complete" ? "text-success bg-success/10" : trade.coinStatus === "partial" ? "text-warning bg-warning/10" : "text-muted bg-surface-3"}`}>{trade.coinStatus === "overpaid" ? `${coinPct}% OVER` : `${coinPct}%`}</span>
                        </div>
                      </div>
                      <ProgressBar percentage={coinPct} />
                      <p className="text-[11px] text-muted tabular-nums mt-2">{trade.coinAmountSettled.toLocaleString()} / {trade.usdtAmount.toLocaleString()} USDT</p>
                      {/* Scanner on-chain data — always visible for scanner trades regardless of settlement entries */}
                      {trade.txHash && coinEntries.length === 0 && (
                        <div className="space-y-1.5">
                          <div className="rounded-lg px-3 py-2 bg-surface-3/50">
                            {/* Row 1: icon + amount + method + chain */}
                            <div className="flex items-center gap-2">
                              <CircleArrowDown className="w-3.5 h-3.5 text-success/60 flex-shrink-0" />
                              <span className="text-[11px] font-semibold text-foreground tabular-nums">{trade.coinAmountSettled.toLocaleString()} USDT</span>
                              <span className="text-[9px] text-muted/30">·</span>
                              <span className="text-[10px] text-muted">crypto wallet</span>
                              {trade.chain && <span className="text-[9px] font-bold text-accent bg-accent/10 px-1 py-0.5 rounded uppercase">{trade.chain}</span>}
                            </div>
                            {/* Row 2: tx hash · sender address */}
                            <div className="flex items-center gap-1.5 mt-0.5 pl-[22px]">
                              <button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(trade.txHash || ""); toast("Tx hash copied"); }}>{truncateHash(trade.txHash, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button>
                              {trade.senderAddress && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted">{truncateHash(trade.senderAddress, 4)}</span></>}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Coin entry list — grouped by batchId */}
                      {coinEntries.length > 0 && (() => {
                        // Build grouped display items
                        type CoinDisplayItem =
                          | { kind: "single"; entry: SettlementEntry }
                          | { kind: "combined"; batchId: string; entries: SettlementEntry[]; totalAmount: number };
          
                        const batchMap = new Map<string, SettlementEntry[]>();
                        const singles: SettlementEntry[] = [];
                        for (const entry of coinEntries) {
                          if (entry.batchId) {
                            const group = batchMap.get(entry.batchId);
                            if (group) group.push(entry);
                            else batchMap.set(entry.batchId, [entry]);
                          } else {
                            singles.push(entry);
                          }
                        }
          
                        const displayItems: CoinDisplayItem[] = [];
                        for (const entry of singles) {
                          displayItems.push({ kind: "single", entry });
                        }
                        for (const [bid, entries] of batchMap) {
                          if (entries.length === 1) {
                            // Degraded batch — render as single
                            displayItems.push({ kind: "single", entry: entries[0] });
                          } else {
                            displayItems.push({
                              kind: "combined",
                              batchId: bid,
                              entries,
                              totalAmount: entries.reduce((sum, e) => sum + e.amount, 0),
                            });
                          }
                        }
                        // Sort by earliest createdAt
                        displayItems.sort((a, b) => {
                          const aDate = a.kind === "single" ? a.entry.createdAt : a.entries[0].createdAt;
                          const bDate = b.kind === "single" ? b.entry.createdAt : b.entries[0].createdAt;
                          return new Date(aDate).getTime() - new Date(bDate).getTime();
                        });
          
                        return (
                          <div className="space-y-1.5">
                            {displayItems.map(item => {
                              if (item.kind === "single") {
                                const entry = item.entry;
                                return (
                                  <div key={entry.id} className={`rounded-lg px-3 py-2 group/entry transition-colors duration-150 ${entry.isRefund ? "bg-warning/5 border border-warning/20 hover:bg-warning/8" : "bg-surface-3/50 hover:bg-surface-3/60"}`}>
                                    {/* Row 1: icon + tag + amount + method */}
                                    <div className="flex items-center gap-2">
                                      {entry.isRefund ? <CircleArrowUp className="w-3.5 h-3.5 text-warning/60 flex-shrink-0" /> : <CircleArrowDown className="w-3.5 h-3.5 text-success/60 flex-shrink-0" />}
                                      {entry.isRefund && <span className="text-[9px] font-bold text-warning bg-warning/10 px-1 py-0.5 rounded uppercase">Refund</span>}
                                      <span className="text-[11px] font-semibold text-foreground tabular-nums">{entry.isRefund ? "-" : ""}{entry.amount.toLocaleString()} {entry.currency}</span>
                                      <span className="text-[9px] text-muted/30">·</span>
                                      <span className="text-[10px] text-muted">{entry.paymentMethod.replace(/_/g, " ")}</span>
                                    </div>
                                    {/* Row 2: ref · address */}
                                    <div className="flex items-center gap-1.5 mt-0.5 pl-[22px]">
                                      <button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(entry.reference); toast("Reference copied"); }}>{truncateHash(entry.reference, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button>
                                      {entry.sourceDestination && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted truncate">{entry.sourceDestination}</span></>}
                                    </div>
                                  </div>
                                );
                              }
          
                              // Combined group
                              const isExpanded = expandedBatchId === item.batchId;
                              const refundEntries = item.entries.filter(e => e.isRefund);
                              const receiptEntries = item.entries.filter(e => !e.isRefund);
                              const netAmount = receiptEntries.reduce((s, e) => s + e.amount, 0) - refundEntries.reduce((s, e) => s + e.amount, 0);
                              return (
                                <div key={item.batchId}>
                                  {/* Collapsed summary row */}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setExpandedBatchId(isExpanded ? null : item.batchId); }}
                                    className="w-full flex items-center justify-between bg-accent/5 rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-accent/10"
                                    style={{ border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)" }}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Layers className="w-3 h-3 text-accent flex-shrink-0" />
                                      <span className="text-[11px] font-semibold text-foreground tabular-nums">{netAmount.toLocaleString()} USDT</span>
                                      <span className="text-[10px] font-semibold text-accent bg-accent/10 px-1.5 py-0.5 rounded">{receiptEntries.length} receipts</span>
                                      {refundEntries.length > 0 && <span className="text-[10px] font-semibold text-warning bg-warning/10 px-1.5 py-0.5 rounded">{refundEntries.length} refund{refundEntries.length > 1 ? "s" : ""}</span>}
                                    </div>
                                    <ChevronDown className={`w-3 h-3 text-muted transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                                  </button>
          
                                  {/* Expanded sub-rows */}
                                  <AnimatePresence>
                                    {isExpanded && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{
                                          height: { duration: 0.2, ease: [0.23, 1, 0.32, 1] as const },
                                          opacity: { duration: 0.15 },
                                        }}
                                        className="overflow-hidden"
                                      >
                                        <div className="pl-7 space-y-0.5 py-1">
                                          {item.entries.map(entry => (
                                            <div key={entry.id} className={`rounded-lg px-3 py-2 group/entry transition-colors duration-150 ${entry.isRefund ? "bg-warning/5 border border-warning/20 hover:bg-warning/8" : "bg-surface-3/30 hover:bg-surface-3/50"}`}>
                                              {/* Row 1: icon + tag + amount + method */}
                                              <div className="flex items-center gap-2">
                                                {entry.isRefund ? <CircleArrowUp className="w-3.5 h-3.5 text-warning/60 flex-shrink-0" /> : <CircleArrowDown className="w-3.5 h-3.5 text-success/60 flex-shrink-0" />}
                                                {entry.isRefund && <span className="text-[9px] font-bold text-warning bg-warning/10 px-1 py-0.5 rounded uppercase">Refund</span>}
                                                <span className="text-[11px] font-semibold text-foreground tabular-nums">{entry.isRefund ? "-" : ""}{entry.amount.toLocaleString()} {entry.currency}</span>
                                                <span className="text-[9px] text-muted/30">·</span>
                                                <span className="text-[10px] text-muted">{entry.paymentMethod.replace(/_/g, " ")}</span>
                                              </div>
                                              {/* Row 2: ref · address */}
                                              <div className="flex items-center gap-1.5 mt-0.5 pl-[22px]">
                                                <button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(entry.reference); toast("Reference copied"); }}>{truncateHash(entry.reference, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button>
                                                {entry.sourceDestination && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted truncate">{entry.sourceDestination}</span></>}
                                              </div>
                                            </div>
                                          ))}
                                          {/* Refund selection panel — triggered from direction picker */}
                                          {showRefundSelect?.batchId === item.batchId && (() => {
                                            const refundDir = showRefundSelect.refundDirection;
                                            const refundDirConfig = getDirectionConfig(refundDir);
                                            // Merge scanner transactions + treasury USDT transactions into one list
                                            const rScannerTxs = scannerTransactions
                                              .map(stx => ({
                                                uid: `rsc-${stx.id}`,
                                                source: "scanner" as const,
                                                amount: stx.amount,
                                                label: truncateHash(stx.txHash, 4),
                                                detail: `${stx.chain.toUpperCase()} · from ${truncateHash(stx.counterpartyAddress, 4)}`,
                                                direction: stx.direction as "incoming" | "outgoing",
                                                date: stx.timestamp,
                                                tagged: stx.matchedTradeId ? stx.matchedTradeId : null,
                                                searchText: `${stx.txHash} ${stx.counterpartyAddress} ${stx.chain}`.toLowerCase(),
                                                raw: stx,
                                              }));
                                            const rTreasuryTxs = treasuryTransactions
                                              .filter(tx => tx.currency === "USDT")
                                              .map(tx => ({
                                                uid: `rtr-${tx.id}`,
                                                source: "treasury" as const,
                                                amount: tx.amount,
                                                label: tx.narration.slice(0, 30),
                                                detail: treasuryAccounts.find(a => a.id === tx.accountId)?.label || "Wallet",
                                                direction: (tx.type === "credit" ? "incoming" : "outgoing") as "incoming" | "outgoing",
                                                date: tx.date,
                                                tagged: tx.linkedTradeId ? tx.linkedTradeId : null,
                                                searchText: `${tx.narration} ${treasuryAccounts.find(a => a.id === tx.accountId)?.label || ""} ${tx.counterpartyName || ""}`.toLowerCase(),
                                                raw: tx,
                                              }));
                                            // Apply search, direction filter, and sort
                                            const rRawTxs = [...rScannerTxs, ...rTreasuryTxs];
                                            const rSearchLower = refundSearch.toLowerCase().trim();
                                            const rFilteredTxs = rRawTxs.filter(t => {
                                              if (rSearchLower && !t.searchText.includes(rSearchLower) && !t.label.toLowerCase().includes(rSearchLower) && !t.detail.toLowerCase().includes(rSearchLower) && !String(t.amount).includes(rSearchLower)) return false;
                                              if (refundFilterDir !== "all" && t.direction !== refundFilterDir) return false;
                                              return true;
                                            });
                                            const rAllTxs = rFilteredTxs.sort((a, b) => refundSortAsc
                                              ? new Date(a.date).getTime() - new Date(b.date).getTime()
                                              : new Date(b.date).getTime() - new Date(a.date).getTime());
                                            const rSelectableTxs = rAllTxs;
                                            const rSelectedTotal = rSelectableTxs.filter(t => selectedRefundTxIds.has(t.uid)).reduce((sum, t) => sum + t.amount, 0);
          
                                            return (
                                              <div className="bg-warning/5 rounded-lg p-3 space-y-3 mt-1" onClick={(e) => e.stopPropagation()} style={{ border: "1px solid color-mix(in srgb, var(--warning) 30%, transparent)" }}>
                                                <div className="flex items-center justify-between">
                                                  <p className="text-[10px] font-bold text-warning uppercase tracking-wider">Select transactions for refund</p>
                                                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${refundDirConfig.colorClass}`}>{refundDirConfig.label} — Refund</span>
                                                </div>
                                                <p className="text-[10px] text-muted">These will be recorded as a refund in the combined group and as a separate refund trade row.</p>
          
                                                {/* Search bar */}
                                                <div className="relative">
                                                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted pointer-events-none" />
                                                  <input
                                                    type="text"
                                                    value={refundSearch}
                                                    onChange={(e) => setRefundSearch(e.target.value)}
                                                    placeholder="Search by tx hash, address, reference..."
                                                    className="w-full bg-surface-2 rounded px-2 pl-6 py-1.5 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-warning/40"
                                                    style={{ border: "1px solid var(--border)" }}
                                                  />
                                                  {refundSearch && (
                                                    <button onClick={() => setRefundSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground">
                                                      <X className="w-3 h-3" />
                                                    </button>
                                                  )}
                                                </div>
          
                                                {/* Direction filter + sort */}
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-0.5 bg-surface-2 rounded p-0.5" style={{ border: "1px solid var(--border)" }}>
                                                    {(["all", "incoming", "outgoing"] as const).map(dir => (
                                                      <button
                                                        key={dir}
                                                        onClick={() => setRefundFilterDir(dir)}
                                                        className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-colors ${
                                                          refundFilterDir === dir ? "bg-warning/15 text-warning" : "text-muted hover:text-foreground"
                                                        }`}
                                                      >
                                                        {dir === "all" ? "All" : dir === "incoming" ? "IN" : "OUT"}
                                                      </button>
                                                    ))}
                                                  </div>
                                                  <button
                                                    onClick={() => setRefundSortAsc(!refundSortAsc)}
                                                    className="inline-flex items-center gap-1 text-[9px] font-semibold text-muted hover:text-foreground transition-colors"
                                                    title={refundSortAsc ? "Oldest first" : "Newest first"}
                                                  >
                                                    <ArrowUpDown className="w-3 h-3" />
                                                    {refundSortAsc ? "Oldest" : "Newest"}
                                                  </button>
                                                </div>
          
                                                {/* Running total */}
                                                <div className="flex items-center justify-between rounded px-2.5 py-1.5 bg-surface-2" style={{ border: "1px solid var(--border)" }}>
                                                  <span className="text-[11px] font-semibold tabular-nums text-foreground">Refund total: {rSelectedTotal.toLocaleString()} USDT</span>
                                                  <span className="text-[10px] text-muted tabular-nums">{selectedRefundTxIds.size} selected</span>
                                                </div>
          
                                                {/* Transaction list */}
                                                <div className="max-h-64 overflow-auto space-y-0.5">
                                                  {rAllTxs.length === 0 ? (
                                                    <p className="text-[11px] text-muted py-2">{refundSearch || refundFilterDir !== "all" ? "No transactions match your filters" : "No USDT transactions found"}</p>
                                                  ) : rAllTxs.map(tx => {
                                                    const isOtherTag = !!tx.tagged && tx.tagged !== trade.id;
                                                    const isSelected = selectedRefundTxIds.has(tx.uid);
                                                    return (
                                                      <button
                                                        key={tx.uid}
                                                        onClick={() => {
                                                          const next = new Set(selectedRefundTxIds);
                                                          if (next.has(tx.uid)) { next.delete(tx.uid); } else { next.add(tx.uid); }
                                                          setSelectedRefundTxIds(next);
                                                        }}
                                                        className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-colors ${
                                                          isSelected ? "bg-warning/10" :
                                                          "hover:bg-surface-2"
                                                        }`}
                                                      >
                                                        <span className="flex-shrink-0">
                                                          {isSelected
                                                            ? <CheckSquare className="w-3 h-3 text-warning" />
                                                            : <CheckSquare className="w-3 h-3 text-muted/30" />}
                                                        </span>
                                                        <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.source === "scanner" ? "bg-accent/10 text-accent" : "bg-surface-3 text-muted"}`}>
                                                          {tx.source === "scanner" ? "ON-CHAIN" : "WALLET"}
                                                        </span>
                                                        <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.direction === "incoming" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
                                                          {tx.direction === "incoming" ? "IN" : "OUT"}
                                                        </span>
                                                        {isOtherTag && (
                                                          <span className="text-[9px] font-bold text-warning bg-warning/10 px-1 py-0.5 rounded flex-shrink-0">
                                                            Trade #{tx.tagged}
                                                          </span>
                                                        )}
                                                        <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(tx.date)}</span>
                                                        <span className="text-[11px] text-foreground truncate flex-1 min-w-0" title={tx.label}>{tx.label}</span>
                                                        <span className="text-[10px] text-muted truncate max-w-[80px]">{tx.detail}</span>
                                                        <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">{tx.amount.toLocaleString()} USDT</span>
                                                      </button>
                                                    );
                                                  })}
                                                </div>
          
                                                {/* Action buttons */}
                                                <div className="flex items-center justify-between pt-1">
                                                  <Button variant="ghost" size="sm" onClick={() => { setShowRefundSelect(null); setSelectedRefundTxIds(new Set()); }}>Cancel</Button>
                                                  <Button
                                                    variant="primary"
                                                    size="sm"
                                                    disabled={selectedRefundTxIds.size === 0}
                                                    onClick={() => {
                                                      const selected = rSelectableTxs.filter(t => selectedRefundTxIds.has(t.uid));
                                                      let totalRefunded = 0;
                                                      for (const tx of selected) {
                                                        // Create refund settlement entry inside the combined group
                                                        const refundEntry: SettlementEntry = {
                                                          id: Date.now() + Math.random(),
                                                          tradeId: trade.id,
                                                          side: "coin",
                                                          amount: tx.amount,
                                                          currency: "USDT",
                                                          paymentMethod: tx.source === "scanner" ? "crypto_wallet" : "bank_transfer",
                                                          sourceDestination: tx.detail,
                                                          reference: tx.label,
                                                          batchId: item.batchId,
                                                          isRefund: true,
                                                          createdAt: new Date().toISOString(),
                                                        };
                                                        settlementEntries.push(refundEntry);
                                                        // Mark source transaction as linked
                                                        if (tx.source === "scanner") {
                                                          (tx.raw as DetectedTransaction).matchedTradeId = trade.id;
                                                        } else {
                                                          const ttx = tx.raw as TreasuryTransaction;
                                                          ttx.linkedTradeId = trade.id;
                                                          ttx.linkedSettlementId = refundEntry.id;
                                                          persistBankTxLink(ttx.id, trade.id, refundEntry.id);
                                                        }
                                                        totalRefunded += tx.amount;
                                                      }
                                                      // Recalculate settlement from entries
                                                      recalcSettlement();
                                                      // Create separate refund trade row in the ledger
                                                      const refundTrade: Trade = {
                                                        id: Date.now(),
                                                        counterpartyName: trade.counterpartyName,
                                                        counterpartyId: trade.counterpartyId,
                                                        direction: refundDir,
                                                        usdtAmount: totalRefunded,
                                                        rate: trade.rate,
                                                        ngnAmount: trade.rate ? totalRefunded * trade.rate : null,
                                                        txHash: null,
                                                        chain: trade.chain,
                                                        viaExchange: false,
                                                        exchangeName: null,
                                                        usdtVerified: false,
                                                        nairaPaymentMethod: null,
                                                        nairaBankVerified: false,
                                                        nairaReceiptPath: null,
                                                        nairaBankRef: null,
                                                        senderConfirmed: false,
                                                        receiverConfirmed: false,
                                                        status: "pending",
                                                        verificationLevel: "unverified",
                                                        settlementTimeSecs: null,
                                                        createdAt: new Date().toISOString(),
                                                        settledAt: null,
                                                        senderAddress: null,
                                                        senderEntity: null,
                                                        coinStatus: "pending",
                                                        coinAmountSettled: 0,
                                                        nairaStatus: "pending",
                                                        nairaAmountSettled: 0,
                                                        isRefund: true,
                                                        refundOfTradeId: trade.id,
                                                      };
                                                      onCreateTrade(refundTrade);
                                                      postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "refund_added", details: `Refund of ${totalRefunded.toLocaleString()} USDT (${selected.length} tx) — refund trade #${refundTrade.id} created as ${getDirectionConfig(refundDir).label}`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });
                                                      setShowRefundSelect(null);
                                                      setSelectedRefundTxIds(new Set());
                                                      onUpdateTrade(trade.id, {});
                                                      toast(`Refund of ${totalRefunded.toLocaleString()} USDT recorded — ${selected.length} transaction${selected.length !== 1 ? "s" : ""}`);
                                                    }}
                                                  >
                                                    Confirm Refund ({selectedRefundTxIds.size} selected{selectedRefundTxIds.size > 0 ? ` — ${rSelectableTxs.filter(t => selectedRefundTxIds.has(t.uid)).reduce((sum, t) => sum + t.amount, 0).toLocaleString()} USDT` : ""})
                                                  </Button>
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* Standalone Refund Selection — for non-combined trades */}
                      {showRefundSelect && showRefundSelect.batchId.startsWith("refund-") && (() => {
                        const refundDir = showRefundSelect.refundDirection;
                        const refundDirConfig = getDirectionConfig(refundDir);
                        const rScannerTxs = scannerTransactions
                          .map(stx => ({
                            uid: `rsc-${stx.id}`,
                            source: "scanner" as const,
                            amount: stx.amount,
                            label: truncateHash(stx.txHash, 4),
                            detail: `${stx.chain.toUpperCase()} · from ${truncateHash(stx.counterpartyAddress, 4)}`,
                            direction: stx.direction as "incoming" | "outgoing",
                            date: stx.timestamp,
                            tagged: stx.matchedTradeId ? stx.matchedTradeId : null,
                            searchText: `${stx.txHash} ${stx.counterpartyAddress} ${stx.chain}`.toLowerCase(),
                            raw: stx,
                          }));
                        const rTreasuryTxs = treasuryTransactions
                          .filter(tx => tx.currency === "USDT")
                          .map(tx => ({
                            uid: `rtr-${tx.id}`,
                            source: "treasury" as const,
                            amount: tx.amount,
                            label: tx.narration.slice(0, 30),
                            detail: treasuryAccounts.find(a => a.id === tx.accountId)?.label || "Wallet",
                            direction: (tx.type === "credit" ? "incoming" : "outgoing") as "incoming" | "outgoing",
                            date: tx.date,
                            tagged: tx.linkedTradeId ? tx.linkedTradeId : null,
                            searchText: `${tx.narration} ${treasuryAccounts.find(a => a.id === tx.accountId)?.label || ""} ${tx.counterpartyName || ""}`.toLowerCase(),
                            raw: tx,
                          }));
                        const rRawTxs = [...rScannerTxs, ...rTreasuryTxs];
                        const rSearchLower = refundSearch.toLowerCase().trim();
                        const rFilteredTxs = rRawTxs.filter(t => {
                          if (rSearchLower && !t.searchText.includes(rSearchLower) && !t.label.toLowerCase().includes(rSearchLower) && !t.detail.toLowerCase().includes(rSearchLower) && !String(t.amount).includes(rSearchLower)) return false;
                          if (refundFilterDir !== "all" && t.direction !== refundFilterDir) return false;
                          return true;
                        });
                        const rAllTxs = rFilteredTxs.sort((a, b) => refundSortAsc
                          ? new Date(a.date).getTime() - new Date(b.date).getTime()
                          : new Date(b.date).getTime() - new Date(a.date).getTime());
                        const rSelectableTxs = rAllTxs;
                        const rSelectedTotal = rSelectableTxs.filter(t => selectedRefundTxIds.has(t.uid)).reduce((sum, t) => sum + t.amount, 0);
          
                        return (
                          <div className="bg-warning/5 rounded-lg p-3 space-y-3" onClick={(e) => e.stopPropagation()} style={{ border: "1px solid color-mix(in srgb, var(--warning) 30%, transparent)" }}>
                            <div className="flex items-center justify-between">
                              <p className="text-[10px] font-bold text-warning uppercase tracking-wider">Select transactions for refund</p>
                              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${refundDirConfig.colorClass}`}>{refundDirConfig.label} — Refund</span>
                            </div>
                            <p className="text-[10px] text-muted">Opposite direction selected — these will be filed as a refund under this trade.</p>
          
                            {/* Search bar */}
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted pointer-events-none" />
                              <input
                                type="text"
                                value={refundSearch}
                                onChange={(e) => setRefundSearch(e.target.value)}
                                placeholder="Search by tx hash, address, reference..."
                                className="w-full bg-surface-2 rounded px-2 pl-6 py-1.5 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-warning/40"
                                style={{ border: "1px solid var(--border)" }}
                              />
                              {refundSearch && (
                                <button onClick={() => setRefundSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
          
                            {/* Direction filter + sort */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-0.5 bg-surface-2 rounded p-0.5" style={{ border: "1px solid var(--border)" }}>
                                {(["all", "incoming", "outgoing"] as const).map(d => (
                                  <button
                                    key={d}
                                    onClick={() => setRefundFilterDir(d)}
                                    className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-colors ${
                                      refundFilterDir === d ? "bg-warning/15 text-warning" : "text-muted hover:text-foreground"
                                    }`}
                                  >
                                    {d === "all" ? "All" : d === "incoming" ? "IN" : "OUT"}
                                  </button>
                                ))}
                              </div>
                              <button
                                onClick={() => setRefundSortAsc(!refundSortAsc)}
                                className="inline-flex items-center gap-1 text-[9px] font-semibold text-muted hover:text-foreground transition-colors"
                              >
                                <ArrowUpDown className="w-3 h-3" />
                                {refundSortAsc ? "Oldest" : "Newest"}
                              </button>
                            </div>
          
                            {/* Running total */}
                            <div className="flex items-center justify-between rounded px-2.5 py-1.5 bg-surface-2" style={{ border: "1px solid var(--border)" }}>
                              <span className="text-[11px] font-semibold tabular-nums text-foreground">Refund total: {rSelectedTotal.toLocaleString()} USDT</span>
                              <span className="text-[10px] text-muted tabular-nums">{selectedRefundTxIds.size} selected</span>
                            </div>
          
                            {/* Transaction list */}
                            <div className="max-h-64 overflow-auto space-y-0.5">
                              {rAllTxs.length === 0 ? (
                                <p className="text-[11px] text-muted py-2">{refundSearch || refundFilterDir !== "all" ? "No transactions match your filters" : "No USDT transactions found"}</p>
                              ) : rAllTxs.map(tx => {
                                const isOtherTag = !!tx.tagged && tx.tagged !== trade.id;
                                const isSelected = selectedRefundTxIds.has(tx.uid);
                                return (
                                  <button
                                    key={tx.uid}
                                    onClick={() => {
                                      const next = new Set(selectedRefundTxIds);
                                      if (next.has(tx.uid)) { next.delete(tx.uid); } else { next.add(tx.uid); }
                                      setSelectedRefundTxIds(next);
                                    }}
                                    className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-colors ${
                                      isSelected ? "bg-warning/10" :
                                      "hover:bg-surface-2"
                                    }`}
                                  >
                                    <span className="flex-shrink-0">
                                      {isSelected
                                        ? <CheckSquare className="w-3 h-3 text-warning" />
                                        : <CheckSquare className="w-3 h-3 text-muted/30" />}
                                    </span>
                                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.source === "scanner" ? "bg-accent/10 text-accent" : "bg-surface-3 text-muted"}`}>
                                      {tx.source === "scanner" ? "ON-CHAIN" : "WALLET"}
                                    </span>
                                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.direction === "incoming" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
                                      {tx.direction === "incoming" ? "IN" : "OUT"}
                                    </span>
                                    {isOtherTag && (
                                      <span className="text-[9px] font-bold text-warning bg-warning/10 px-1 py-0.5 rounded flex-shrink-0">
                                        Trade #{tx.tagged}
                                      </span>
                                    )}
                                    <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(tx.date)}</span>
                                    <span className="text-[11px] text-foreground truncate flex-1 min-w-0" title={tx.label}>{tx.label}</span>
                                    <span className="text-[10px] text-muted truncate max-w-[80px]">{tx.detail}</span>
                                    <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">{tx.amount.toLocaleString()} USDT</span>
                                  </button>
                                );
                              })}
                            </div>
          
                            {/* Action buttons */}
                            <div className="flex items-center justify-between pt-1">
                              <Button variant="ghost" size="sm" onClick={() => { setShowRefundSelect(null); setSelectedRefundTxIds(new Set()); }}>Cancel</Button>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={selectedRefundTxIds.size === 0}
                                onClick={() => {
                                  const selected = rSelectableTxs.filter(t => selectedRefundTxIds.has(t.uid));
                                  let totalRefunded = 0;
                                  for (const tx of selected) {
                                    const refundEntry: SettlementEntry = {
                                      id: Date.now() + Math.random(),
                                      tradeId: trade.id,
                                      side: "coin",
                                      amount: tx.amount,
                                      currency: "USDT",
                                      paymentMethod: tx.source === "scanner" ? "crypto_wallet" : "bank_transfer",
                                      sourceDestination: tx.detail,
                                      reference: tx.label,
                                      isRefund: true,
                                      createdAt: new Date().toISOString(),
                                    };
                                    settlementEntries.push(refundEntry);
                                    if (tx.source === "scanner") {
                                      (tx.raw as DetectedTransaction).matchedTradeId = trade.id;
                                    } else {
                                      const ttx = tx.raw as TreasuryTransaction;
                                      ttx.linkedTradeId = trade.id;
                                      ttx.linkedSettlementId = refundEntry.id;
                                      persistBankTxLink(ttx.id, trade.id, refundEntry.id);
                                    }
                                    totalRefunded += tx.amount;
                                  }
                                  recalcSettlement();
                                  const refundTrade: Trade = {
                                    id: Date.now(),
                                    counterpartyName: trade.counterpartyName,
                                    counterpartyId: trade.counterpartyId,
                                    direction: refundDir,
                                    usdtAmount: totalRefunded,
                                    rate: trade.rate,
                                    ngnAmount: trade.rate ? totalRefunded * trade.rate : null,
                                    txHash: null,
                                    chain: trade.chain,
                                    viaExchange: false,
                                    exchangeName: null,
                                    usdtVerified: false,
                                    nairaPaymentMethod: null,
                                    nairaBankVerified: false,
                                    nairaReceiptPath: null,
                                    nairaBankRef: null,
                                    senderConfirmed: false,
                                    receiverConfirmed: false,
                                    status: "pending",
                                    verificationLevel: "unverified",
                                    settlementTimeSecs: null,
                                    createdAt: new Date().toISOString(),
                                    settledAt: null,
                                    senderAddress: null,
                                    senderEntity: null,
                                    coinStatus: "pending",
                                    coinAmountSettled: 0,
                                    nairaStatus: "pending",
                                    nairaAmountSettled: 0,
                                    isRefund: true,
                                    refundOfTradeId: trade.id,
                                  };
                                  onCreateTrade(refundTrade);
                                  postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "refund_added", details: `Refund of ${totalRefunded.toLocaleString()} USDT (${selected.length} tx) — refund trade #${refundTrade.id} created as ${getDirectionConfig(refundDir).label}`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });
                                  setShowRefundSelect(null);
                                  setSelectedRefundTxIds(new Set());
                                  onUpdateTrade(trade.id, {});
                                  toast(`Refund of ${totalRefunded.toLocaleString()} USDT recorded — ${selected.length} transaction${selected.length !== 1 ? "s" : ""}`);
                                }}
                              >
                                Confirm Refund ({selectedRefundTxIds.size} selected{selectedRefundTxIds.size > 0 ? ` — ${rSelectableTxs.filter(t => selectedRefundTxIds.has(t.uid)).reduce((sum, t) => sum + t.amount, 0).toLocaleString()} USDT` : ""})
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Combine Receipts + Use Balance buttons */}
                      <div className="flex items-center gap-2 pt-1">
                        <button onClick={(e) => { e.stopPropagation(); setShowCombineReceipts(!showCombineReceipts); setSelectedCoinTxIds(new Set()); setCombineSearch(""); setCombineFilterDir("all"); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"><Layers className="w-3 h-3" />Combine Receipts</button>
                        {trade.counterpartyId && <button onClick={(e) => { e.stopPropagation(); setShowBalanceModal("coin"); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"><Wallet className="w-3 h-3" />Use Balance</button>}
                      </div>
                      {/* Combine Receipts — multi-select panel */}
                      {showCombineReceipts && (() => {
                        // Merge scanner transactions + treasury USDT transactions into one list
                        // Expected direction for this trade type
                        const expectedDir = (trade.direction === "buy" || trade.direction === "borrow") ? "incoming" : "outgoing";
                        const scannerTxs = scannerTransactions
                          .map(stx => ({
                            uid: `sc-${stx.id}`,
                            source: "scanner" as const,
                            amount: stx.amount,
                            label: truncateHash(stx.txHash, 4),
                            detail: `${stx.chain.toUpperCase()} · from ${truncateHash(stx.counterpartyAddress, 4)}`,
                            direction: stx.direction as "incoming" | "outgoing",
                            date: stx.timestamp,
                            tagged: stx.matchedTradeId ? stx.matchedTradeId : null,
                            searchText: `${stx.txHash} ${stx.counterpartyAddress} ${stx.chain}`.toLowerCase(),
                            raw: stx,
                          }));
                        const treasuryTxs = treasuryTransactions
                          .filter(tx => tx.currency === "USDT")
                          .map(tx => ({
                            uid: `tr-${tx.id}`,
                            source: "treasury" as const,
                            amount: tx.amount,
                            label: tx.narration.slice(0, 30),
                            detail: treasuryAccounts.find(a => a.id === tx.accountId)?.label || "Wallet",
                            direction: (tx.type === "credit" ? "incoming" : "outgoing") as "incoming" | "outgoing",
                            date: tx.date,
                            tagged: tx.linkedTradeId ? tx.linkedTradeId : null,
                            searchText: `${tx.narration} ${treasuryAccounts.find(a => a.id === tx.accountId)?.label || ""} ${tx.counterpartyName || ""}`.toLowerCase(),
                            raw: tx,
                          }));
                        // Apply search, direction filter, and sort
                        const rawTxs = [...scannerTxs, ...treasuryTxs];
                        const searchLower = combineSearch.toLowerCase().trim();
                        const filteredTxs = rawTxs.filter(t => {
                          if (searchLower && !t.searchText.includes(searchLower) && !t.label.toLowerCase().includes(searchLower) && !t.detail.toLowerCase().includes(searchLower) && !String(t.amount).includes(searchLower)) return false;
                          if (combineFilterDir !== "all" && t.direction !== combineFilterDir) return false;
                          return true;
                        });
                        const allTxs = filteredTxs.sort((a, b) => combineSortAsc
                          ? new Date(a.date).getTime() - new Date(b.date).getTime()
                          : new Date(b.date).getTime() - new Date(a.date).getTime());
                        // All visible transactions are selectable (tagged ones can be re-assigned)
                        const selectableTxs = allTxs;
                        const selectedSame = selectableTxs.filter(t => selectedCoinTxIds.has(t.uid) && t.direction === expectedDir);
                        const selectedOpposite = selectableTxs.filter(t => selectedCoinTxIds.has(t.uid) && t.direction !== expectedDir);
                        const selectedTotal = selectedSame.reduce((sum, t) => sum + t.amount, 0);
                        const refundTotal = selectedOpposite.reduce((sum, t) => sum + t.amount, 0);
                        const remaining = trade.usdtAmount - trade.coinAmountSettled;
                        const diff = selectedTotal - remaining;
                        const isExcess = diff > 0 && selectedCoinTxIds.size > 0;
                        const isShort = selectedTotal > 0 && selectedTotal < remaining && selectedCoinTxIds.size > 0;
                        const allSelected = selectableTxs.length > 0 && selectableTxs.every(t => selectedCoinTxIds.has(t.uid));
          
                        return (
                          <div className="bg-surface-3/30 rounded-lg p-3 space-y-3" onClick={(e) => e.stopPropagation()} style={{ border: "1px solid var(--border)" }}>
                            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Select USDT transactions to combine</p>
          
                            {/* Search bar */}
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted pointer-events-none" />
                              <input
                                type="text"
                                value={combineSearch}
                                onChange={(e) => setCombineSearch(e.target.value)}
                                placeholder="Search by tx hash, address, reference..."
                                className="w-full bg-surface-2 rounded px-2 pl-6 py-1.5 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/40"
                                style={{ border: "1px solid var(--border)" }}
                              />
                              {combineSearch && (
                                <button onClick={() => setCombineSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
          
                            {/* Direction filter + sort */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-0.5 bg-surface-2 rounded p-0.5" style={{ border: "1px solid var(--border)" }}>
                                {(["all", "incoming", "outgoing"] as const).map(dir => (
                                  <button
                                    key={dir}
                                    onClick={() => setCombineFilterDir(dir)}
                                    className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-all cursor-pointer ${
                                      combineFilterDir === dir ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"
                                    }`}
                                  >
                                    {dir === "all" ? "All" : dir === "incoming" ? "IN" : "OUT"}
                                  </button>
                                ))}
                              </div>
                              <button
                                onClick={() => setCombineSortAsc(!combineSortAsc)}
                                className="inline-flex items-center gap-1 text-[9px] font-semibold text-muted hover:text-foreground transition-colors"
                                title={combineSortAsc ? "Oldest first" : "Newest first"}
                              >
                                <ArrowUpDown className="w-3 h-3" />
                                {combineSortAsc ? "Oldest" : "Newest"}
                              </button>
                            </div>
          
                            {/* Running total bar */}
                            <div className={`flex items-center justify-between rounded px-2.5 py-1.5 ${isExcess ? "bg-warning/10" : "bg-surface-2"}`} style={{ border: `1px solid ${isExcess ? "var(--warning)" : "var(--border)"}` }}>
                              <span className={`text-[11px] font-semibold tabular-nums ${isExcess ? "text-warning" : "text-foreground"}`}>Selected: {selectedTotal.toLocaleString()} USDT</span>
                              <span className="text-[10px] text-muted tabular-nums">{selectedCoinTxIds.size} selected &middot; {remaining.toLocaleString()} remaining</span>
                            </div>
                            {isShort && <p className="text-[10px] text-muted">Shortfall of {(remaining - selectedTotal).toLocaleString()} USDT — you can add more later</p>}
                            {/* Refund warning overlay modal */}
                            <AnimatePresence>
                              {showRefundWarning && (
                                <>
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="fixed inset-0 z-[60] bg-black/50"
                                    onClick={() => setShowRefundWarning(false)}
                                  />
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.85, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                    className="fixed z-[61] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] bg-surface rounded-xl p-5 shadow-2xl"
                                    style={{ border: "1px solid var(--warning)" }}
                                  >
                                    <div className="flex flex-col items-center text-center gap-3">
                                      <motion.div
                                        initial={{ scale: 0, rotate: -30 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        transition={{ type: "spring", stiffness: 500, damping: 15, delay: 0.1 }}
                                        className="w-12 h-12 rounded-full bg-warning/15 flex items-center justify-center"
                                      >
                                        <AlertTriangle className="w-6 h-6 text-warning" />
                                      </motion.div>
                                      <h3 className="text-sm font-bold text-foreground">Opposite Direction Detected</h3>
                                      <p className="text-[12px] text-muted leading-relaxed">
                                        You selected a transaction going in the opposite direction of this trade. It will be <span className="font-semibold text-warning">filed as a refund</span> under this trade.
                                      </p>
                                      <div className="flex gap-2 w-full pt-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="flex-1"
                                          onClick={() => {
                                            // Undo — remove opposite-direction selections
                                            const next = new Set(selectedCoinTxIds);
                                            selectableTxs.filter(t => t.direction !== expectedDir).forEach(t => next.delete(t.uid));
                                            setSelectedCoinTxIds(next);
                                            setShowRefundWarning(false);
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          className="flex-1"
                                          onClick={() => setShowRefundWarning(false)}
                                        >
                                          Got it, proceed
                                        </Button>
                                      </div>
                                    </div>
                                  </motion.div>
                                </>
                              )}
                            </AnimatePresence>
                            {/* Inline refund indicator (after dismissing modal) */}
                            {selectedOpposite.length > 0 && !showRefundWarning && (
                              <div className="flex items-center gap-2 rounded px-2.5 py-1.5 bg-warning/10 text-[10px] font-semibold text-warning" style={{ border: "1px solid var(--warning)" }}>
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                {selectedOpposite.length} refund{selectedOpposite.length > 1 ? "s" : ""} · {refundTotal.toLocaleString()} USDT
                              </div>
                            )}
          
                            {/* Select All toggle */}
                            {selectableTxs.length > 0 && (
                              <button
                                onClick={() => {
                                  if (allSelected) {
                                    setSelectedCoinTxIds(new Set());
                                  } else {
                                    setSelectedCoinTxIds(new Set(selectableTxs.map(t => t.uid)));
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors"
                              >
                                <CheckSquare className="w-3 h-3" />{allSelected ? "Deselect All" : "Select All"}
                              </button>
                            )}
          
                            {/* Transaction list */}
                            <div className="max-h-64 overflow-auto space-y-0.5">
                              {allTxs.length === 0 ? (
                                <p className="text-[11px] text-muted py-2">{combineSearch || combineFilterDir !== "all" ? "No transactions match your filters" : "No USDT transactions found"}</p>
                              ) : allTxs.map(tx => {
                                const isOtherTag = !!tx.tagged && tx.tagged !== trade.id;
                                const isOpposite = tx.direction !== expectedDir;
                                const isSelected = selectedCoinTxIds.has(tx.uid);
                                return (
                                  <button
                                    key={tx.uid}
                                    onClick={() => {
                                      const next = new Set(selectedCoinTxIds);
                                      const isAdding = !next.has(tx.uid);
                                      if (isAdding) { next.add(tx.uid); } else { next.delete(tx.uid); }
                                      setSelectedCoinTxIds(next);
                                      // Show warning modal when first opposite-direction tx is selected
                                      if (isAdding && isOpposite && !selectedOpposite.some(s => s.uid !== tx.uid)) {
                                        setShowRefundWarning(true);
                                      }
                                    }}
                                    className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-colors ${
                                      isSelected && isOpposite ? "bg-warning/10" :
                                      isSelected ? "bg-accent/10" :
                                      "hover:bg-surface-2"
                                    }`}
                                  >
                                    <span className="flex-shrink-0">
                                      {isSelected
                                        ? <CheckSquare className={`w-3 h-3 ${isOpposite ? "text-warning" : "text-accent"}`} />
                                        : <CheckSquare className="w-3 h-3 text-muted/30" />}
                                    </span>
                                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.source === "scanner" ? "bg-accent/10 text-accent" : "bg-surface-3 text-muted"}`}>
                                      {tx.source === "scanner" ? "ON-CHAIN" : "WALLET"}
                                    </span>
                                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.direction === "incoming" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
                                      {tx.direction === "incoming" ? "IN" : "OUT"}
                                    </span>
                                    {isOtherTag && (
                                      <span className="text-[9px] font-bold text-warning bg-warning/10 px-1 py-0.5 rounded flex-shrink-0">
                                        Trade #{tx.tagged}
                                      </span>
                                    )}
                                    <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(tx.date)}</span>
                                    <span className="text-[11px] text-foreground truncate flex-1 min-w-0" title={tx.label}>{tx.label}</span>
                                    <span className="text-[10px] text-muted truncate max-w-[80px]">{tx.detail}</span>
                                    <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">{tx.amount.toLocaleString()} USDT</span>
                                  </button>
                                );
                              })}
                            </div>
          
                            {/* Action buttons */}
                            <div className="flex items-center justify-between pt-1">
                              <Button variant="ghost" size="sm" onClick={() => { setShowCombineReceipts(false); setSelectedCoinTxIds(new Set()); }}>Cancel</Button>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={selectedCoinTxIds.size === 0}
                                onClick={() => {
                                  const sameCount = selectedSame.length;
                                  const oppCount = selectedOpposite.length;
                                  const totalApply = selectedTotal;
                                  const totalRefund = refundTotal;
                                  // Store the execution function, show confirmation
                                  setCombineConfirm({
                                    sameDirCount: sameCount,
                                    oppDirCount: oppCount,
                                    totalApply,
                                    totalRefund,
                                    execute: () => {
                                      const sameDirSel = selectableTxs.filter(t => selectedCoinTxIds.has(t.uid) && t.direction === expectedDir);
                                      const oppDirSel = selectableTxs.filter(t => selectedCoinTxIds.has(t.uid) && t.direction !== expectedDir);
                                      const allSel = [...sameDirSel, ...oppDirSel];
                                      const batchId = allSel.length > 1
                                        ? `batch-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
                                        : undefined;
                                      let appliedAmt = 0;
                                      let refundedAmt = 0;
                                      for (const tx of allSel) {
                                        const isOpp = tx.direction !== expectedDir;
                                        const entry: SettlementEntry = {
                                          id: Date.now() + Math.random(),
                                          tradeId: trade.id,
                                          side: "coin",
                                          amount: tx.amount,
                                          currency: "USDT",
                                          paymentMethod: tx.source === "scanner" ? "crypto_wallet" : "bank_transfer",
                                          sourceDestination: tx.source === "scanner"
                                            ? (treasuryAccounts.find(a => a.type === "wallet")?.label || "Wallet")
                                            : (treasuryAccounts.find(a => a.id === (tx.raw as TreasuryTransaction).accountId)?.label || "Wallet"),
                                          reference: tx.source === "scanner"
                                            ? (tx.raw as DetectedTransaction).txHash.slice(0, 16)
                                            : (tx.raw as TreasuryTransaction).narration.slice(0, 40),
                                          treasuryAccountId: tx.source === "treasury" ? (tx.raw as TreasuryTransaction).accountId : undefined,
                                          batchId,
                                          isRefund: isOpp || undefined,
                                          createdAt: new Date().toISOString(),
                                        };
                                        settlementEntries.push(entry);
                                        if (tx.source === "scanner") {
                                          (tx.raw as DetectedTransaction).matchedTradeId = trade.id;
                                        } else {
                                          const ttx = tx.raw as TreasuryTransaction;
                                          ttx.linkedTradeId = trade.id;
                                          ttx.linkedSettlementId = entry.id;
                                          persistBankTxLink(ttx.id, trade.id, entry.id);
                                        }
                                        if (isOpp) refundedAmt += tx.amount;
                                        else appliedAmt += tx.amount;
                                      }
                                      recalcSettlement();
                                      if (oppDirSel.length > 0) {
                                        const oppDir = (trade.direction === "buy" || trade.direction === "borrow") ? "sell" : "buy";
                                        const refundTrade: Trade = {
                                          id: Date.now(),
                                          counterpartyName: trade.counterpartyName, counterpartyId: trade.counterpartyId,
                                          direction: oppDir as Trade["direction"], usdtAmount: refundedAmt,
                                          rate: trade.rate, ngnAmount: trade.rate ? refundedAmt * trade.rate : null,
                                          txHash: null, chain: trade.chain, viaExchange: false, exchangeName: null,
                                          usdtVerified: false, nairaPaymentMethod: null, nairaBankVerified: false,
                                          nairaReceiptPath: null, nairaBankRef: null, senderConfirmed: false,
                                          receiverConfirmed: false, status: "pending", verificationLevel: "unverified",
                                          settlementTimeSecs: null, createdAt: new Date().toISOString(), settledAt: null,
                                          senderAddress: null, senderEntity: null,
                                          coinStatus: "pending", coinAmountSettled: 0,
                                          nairaStatus: "pending", nairaAmountSettled: 0,
                                          isRefund: true, refundOfTradeId: trade.id,
                                        };
                                        onCreateTrade(refundTrade);
                                        postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "refund_added", details: `Refund of ${refundedAmt.toLocaleString()} USDT (${oppDirSel.length} tx) filed under trade #${trade.id}`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });
                                      }
                                      if (sameDirSel.length > 0) {
                                        postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "coin_receipts_combined", details: `Combined ${sameDirSel.length} USDT transaction${sameDirSel.length !== 1 ? "s" : ""} — ${appliedAmt.toLocaleString()} USDT`, changes: [], channel: "dashboard", timestamp: new Date().toISOString() });
                                      }
                                      setShowCombineReceipts(false);
                                      setSelectedCoinTxIds(new Set());
                                      onUpdateTrade(trade.id, {});
                                      // Build success message
                                      const parts: string[] = [];
                                      if (sameDirSel.length > 0) parts.push(`${sameDirSel.length} receipt${sameDirSel.length !== 1 ? "s" : ""} combined (${appliedAmt.toLocaleString()} USDT)`);
                                      if (oppDirSel.length > 0) parts.push(`${oppDirSel.length} refund${oppDirSel.length !== 1 ? "s" : ""} filed (${refundedAmt.toLocaleString()} USDT)`);
                                      setCombineSuccess(parts.join(" · "));
                                    },
                                  });
                                }}
                              >
                                {selectedOpposite.length > 0
                                  ? `Combine & File Refund (${selectedCoinTxIds.size} selected)`
                                  : `Combine (${selectedCoinTxIds.size} selected${selectedCoinTxIds.size > 0 ? ` — ${selectedTotal.toLocaleString()} USDT` : ""})`
                                }
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Linked USDT balance adjustments (refund payouts) for this trade */}
                      {(() => {
                        const usdtAdjs = tradeRefundAdjustments.filter(a => a.currency === "USDT");
                        if (usdtAdjs.length === 0) return null;
                        const usdtTotal = usdtAdjs.reduce((s, a) => s + a.amount, 0);
                        const expectedRefund = trade.coinAmountSettled - trade.usdtAmount;
                        const excess = usdtTotal - Math.max(0, expectedRefund);
                        const cpName = trade.counterpartyId ? (counterparties.find(c => c.id === trade.counterpartyId)?.name || "counterparty") : "counterparty";
                        return (
                          <div className="space-y-1.5">
                            {usdtAdjs.map(adj => {
                              const method = (adj.paymentMethod || "crypto_wallet").replace(/_/g, " ");
                              return (
                              <div key={adj.id} className="relative rounded-lg px-3 py-2 group/entry transition-colors duration-150 bg-surface-3/50 hover:bg-surface-3/60">
                                {/* Row 1: tag + amount */}
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded uppercase tracking-wide">Refund</span>
                                  <span className="text-[11px] font-semibold tabular-nums text-foreground">{adj.amount.toLocaleString()} USDT</span>
                                </div>
                                {/* Row 2: method · ref · destination */}
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[9px] text-muted">{method}</span>
                                  {adj.reference && <><span className="text-[9px] text-muted/30">·</span><button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(adj.reference); toast("Reference copied"); }}>{truncateHash(adj.reference, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button></>}
                                  {adj.destination && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted truncate">{adj.destination}</span></>}
                                </div>
                                {/* Row 3: refund breakdown */}
                                {expectedRefund > 0 && (
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[8px] text-muted/50 uppercase tracking-wide">Expected</span>
                                    <span className="text-[9px] text-foreground/80 font-medium tabular-nums">{Math.max(0, expectedRefund).toLocaleString()} USDT</span>
                                    {excess > 0 && <><span className="text-[8px] text-muted/30">·</span><span className="text-[8px] text-muted tabular-nums">excess {excess.toLocaleString()} USDT → {cpName}</span></>}
                                    {excess <= 0 && usdtTotal < expectedRefund && <><span className="text-[8px] text-muted/30">·</span><span className="text-[8px] text-muted tabular-nums">{(expectedRefund - usdtTotal).toLocaleString()} USDT remaining</span></>}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* USDT Settlement Summary — below entries + refund, above action buttons */}
                      {trade.coinAmountSettled > trade.usdtAmount && (() => {
                        const excessUsdt = trade.coinAmountSettled - trade.usdtAmount;
                        const usdtRefundAdjs = tradeRefundAdjustments.filter(a => a.currency === "USDT");
                        const usdtRefundTotal = usdtRefundAdjs.reduce((s, a) => s + a.amount, 0);
                        const legacyCoinRefundTotal = tradeEntries.filter(e => e.side === "coin" && e.isRefund).reduce((s, e) => s + e.amount, 0);
                        const totalCoinRefunded = usdtRefundTotal + legacyCoinRefundTotal;
                        if (totalCoinRefunded <= 0) return null;
                        const excessRefund = totalCoinRefunded - excessUsdt;
                        const cpName = trade.counterpartyId ? (counterparties.find(c => c.id === trade.counterpartyId)?.name || "counterparty") : "counterparty";
                        const tint = excessRefund > 0 ? "warning" : "success";
                        return (
                          <div className={`rounded-lg px-3 py-1.5 bg-${tint}/5`}>
                            <span className="text-[9px] text-muted/50 uppercase tracking-wide">Settlement summary</span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[10px] text-foreground/80 font-medium tabular-nums">Overpaid {excessUsdt.toLocaleString()} USDT</span>
                              <span className="text-[10px] text-muted/30">·</span>
                              <span className="text-[10px] text-foreground/80 font-medium tabular-nums">Refunded {totalCoinRefunded.toLocaleString()} USDT</span>
                            </div>
                            {excessRefund > 0 && (
                              <p className="text-[9px] text-warning mt-0.5">{excessRefund.toLocaleString()} USDT excess → {cpName}'s outstanding</p>
                            )}
                            {excessRefund < 0 && (
                              <p className="text-[9px] text-success mt-0.5">{Math.abs(excessRefund).toLocaleString()} USDT remaining on {cpName}'s credit</p>
                            )}
                            {excessRefund === 0 && (
                              <p className="text-[9px] text-success mt-0.5">Refund matches overpayment — fully settled</p>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Naira Payment Side */}
                    <div className="bg-surface-2/40 rounded-xl p-4 space-y-3" style={{ border: "1px solid var(--border)" }}>
                      {trade.direction === "middleman" ? (() => {
                        // Middleman naira settlement — different sections based on nairaFlow
                        const buyTotal = (trade.buyRate || 0) * trade.usdtAmount;
                        const sellTotal = (trade.sellRate || 0) * trade.usdtAmount;
                        const profit = trade.middlemanProfit || 0;
                        const mmNairaEntries = nairaEntries;
                        const buyLegEntries = mmNairaEntries.filter(e => e.middlemanLeg === "buy");
                        const sellLegEntries = mmNairaEntries.filter(e => e.middlemanLeg === "sell");
                        const profitEntries = mmNairaEntries.filter(e => e.middlemanLeg === "profit");
                        const untaggedEntries = mmNairaEntries.filter(e => !e.middlemanLeg);
                        const buySettled = buyLegEntries.reduce((s, e) => s + e.amount, 0);
                        const sellSettled = sellLegEntries.reduce((s, e) => s + e.amount, 0);
                        const profitSettled = profitEntries.reduce((s, e) => s + e.amount, 0);
          
                        const NairaEntryList = ({ entries }: { entries: SettlementEntry[] }) => entries.length > 0 ? (
                          <div className="space-y-1.5">
                            {entries.map(entry => {
                              const pd = entry.payerDetailId ? tradePayerDetails.find(p => p.id === entry.payerDetailId) : null;
                              const entryFlags = receiptFlags.filter(f => f.settlementId === entry.id);
                              const hasCritical = entryFlags.some(f => f.severity === "critical");
                              return (
                              <div key={entry.id} className={`relative rounded-lg px-3 py-2 group/entry transition-colors duration-150 ${entry.isCreditToBalance ? "bg-success/5 border border-success/20 hover:bg-success/8" : entry.isRefund ? "bg-warning/5 border border-warning/20 hover:bg-warning/8" : hasCritical ? "bg-danger/8 hover:bg-danger/12" : "bg-surface-3/50 hover:bg-surface-3/60"}`} style={!entry.isRefund && !entry.isCreditToBalance && hasCritical ? { border: "1px solid var(--danger)" } : undefined}>
                                {/* Unlink button — top right */}
                                {hasPermission(activeStaff, "remove_payment") && (
                                  <button onClick={(e) => { e.stopPropagation(); setConfirmRemove(entry); }} className="absolute top-1.5 right-2 inline-flex items-center gap-1 text-[8px] font-medium px-2 py-[3px] rounded transition-colors duration-150" style={{ color: "rgba(232,84,84,0.7)", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.05)" }}><Unlink className="w-3 h-3" />Unlink</button>
                                )}
                                {/* Row 1: tag + amount */}
                                <div className="flex items-center gap-2 pr-20">
                                  {entry.isCreditToBalance && <span className="text-[9px] font-bold text-success bg-success/10 px-1.5 py-0.5 rounded uppercase tracking-wide">Credit</span>}
                                  {entry.isRefund && !entry.isCreditToBalance && <span className="text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded uppercase tracking-wide">Refund</span>}
                                  <span className={`text-[11px] font-semibold tabular-nums ${entry.isCreditToBalance ? "text-success" : entry.isRefund ? "text-warning" : "text-foreground"}`}>{entry.isRefund && !entry.isCreditToBalance ? "-" : ""}₦{entry.amount.toLocaleString()}</span>
                                  {entryFlags.length > 0 && (
                                    <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${hasCritical ? "bg-danger/15 text-danger" : "bg-warning/15 text-warning"}`}>
                                      <AlertTriangle className="w-2.5 h-2.5" />{entryFlags.length} {entryFlags.length === 1 ? "flag" : "flags"}
                                    </span>
                                  )}
                                </div>
                                {/* Row 2: metadata */}
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[9px] text-muted">{entry.paymentMethod.replace(/_/g, " ")}</span>
                                  <span className="text-[9px] text-muted/30">·</span>
                                  <button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(entry.reference); toast("Reference copied"); }}>{truncateHash(entry.reference, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button>
                                  {entry.sourceDestination && !entry.isCreditToBalance && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted truncate">{entry.sourceDestination}</span></>}
                                </div>
                                {/* Payer details */}
                                {pd && (
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[8px] text-muted/50 uppercase tracking-wide">Payer</span>
                                    <span className="text-[9px] text-foreground/80 font-medium">{pd.payerName}</span>
                                    {pd.payerAccountNumber && <span className="text-[8px] text-muted font-mono">····{pd.payerAccountNumber.slice(-4)}</span>}
                                    {pd.payerBank && <span className="text-[8px] text-accent flex items-center gap-1"><BankIcon bankName={pd.payerBank} size="sm" />{pd.payerBank}</span>}
                                  </div>
                                )}
                                {/* Receipt flags */}
                                {entryFlags.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {entryFlags.map(flag => (
                                      <div key={flag.id} className={`flex items-start gap-1.5 text-[9px] px-2 py-1 rounded ${flag.severity === "critical" ? "bg-danger/10 text-danger" : "bg-warning/10 text-warning"}`}>
                                        <AlertTriangle className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                                        <span className="flex-1">{flag.message}</span>
                                        <button onClick={(e) => { e.stopPropagation(); fetch("/api/receipt-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "dismiss", flagId: flag.id }) }).then(() => setReceiptFlags(prev => prev.filter(f => f.id !== flag.id))).catch(() => {}); }} className="text-muted hover:text-foreground flex-shrink-0" title="Dismiss"><X className="w-2.5 h-2.5" /></button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        ) : null;
          
                        const mmHasCounterparty = trade.counterpartyId || trade.buyCounterpartyId || trade.sellCounterpartyId;
                        const AddPaymentButtons = ({ leg }: { leg: "buy" | "sell" | "profit" }) => (
                          mmHasCounterparty ? (
                          <div className="flex items-center gap-2 pt-1">
                            {hasPermission(activeStaff, "add_payment") && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowAddNaira(true);
                                  setShowBankPicker(false);
                                  setShowBalanceModal(false);
                                  (trade as Record<string, unknown>).__activeLeg = leg;
                                }}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"
                              >
                                <Plus className="w-3 h-3" />Add Payment
                              </button>
                            )}
                            {hasPermission(activeStaff, "select_from_bank") && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowBankPicker(true);
                                  setShowAddNaira(false);
                                  setShowBalanceModal(false);
                                  setSelectedBankId(null);
                                  setSelectedTxIds(new Set());
                                  (trade as Record<string, unknown>).__activeLeg = leg;
                                }}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"
                              >
                                <Building2 className="w-3 h-3" />Select from Bank
                              </button>
                            )}
                            {(trade.counterpartyId || trade.buyCounterpartyId || trade.sellCounterpartyId) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowBalanceModal("naira");
                                setShowAddNaira(false);
                                setShowBankPicker(false);
                                (trade as Record<string, unknown>).__activeLeg = leg;
                              }}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"
                            >
                              <Wallet className="w-3 h-3" />Use Balance
                            </button>
                            )}
                          </div>
                          ) : (
                          <p className="text-[11px] text-warning pt-1">Assign a counterparty to this trade before recording settlements.</p>
                          )
                        );

                        if (trade.nairaFlow === "direct") {
                          // Direct: buyer pays seller, you only receive profit
                          const pctDone = profit > 0 ? Math.min(100, Math.round((profitSettled / profit) * 100)) : 0;
                          return (
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <div className="section-icon section-icon-success"><CfProfit /></div>
                                <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">Profit Settlement</h4>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${pctDone >= 100 ? "text-success bg-success/10" : pctDone > 0 ? "text-warning bg-warning/10" : "text-muted bg-surface-3"}`}>{pctDone}%</span>
                              </div>
                              <div className="rounded-lg p-3 space-y-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted">Your profit</span>
                                  <span className={`text-sm font-bold ${profit >= 0 ? "text-success" : "text-danger"}`}>₦{Math.abs(profit).toLocaleString()}</span>
                                </div>
                                <ProgressBar percentage={pctDone} />
                                <p className="text-[10px] text-muted tabular-nums">₦{profitSettled.toLocaleString()} / ₦{Math.abs(profit).toLocaleString()} received</p>
                              </div>
                              <NairaEntryList entries={[...profitEntries, ...untaggedEntries]} />
                              <AddPaymentButtons leg="profit" />
                            </>
                          );
                        }
          
                        // Through you: two legs
                        const buyPct = buyTotal > 0 ? Math.min(100, Math.round((buySettled / buyTotal) * 100)) : 0;
                        const sellPct = sellTotal > 0 ? Math.min(100, Math.round((sellSettled / sellTotal) * 100)) : 0;
                        return (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="section-icon section-icon-success"><CfNaira /></div>
                              <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">NGN Settlement</h4>
                            </div>
          
                            {/* Buy leg — you pay seller */}
                            <div className="rounded-lg p-3 space-y-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-bold text-danger uppercase tracking-wider">Pay to Seller</span>
                                  <div className="relative">
                                    <button
                                      onClick={() => { setEditingMmCp(editingMmCp === "buy" ? null : "buy"); setMmCpSearch(""); }}
                                      className="text-[10px] text-muted hover:text-foreground transition-colors inline-flex items-center gap-0.5"
                                      title="Click to change seller"
                                    >
                                      {trade.buyCounterpartyName || "Seller"}<Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-50" />
                                    </button>
                                    {editingMmCp === "buy" && (
                                      <>
                                        <div className="fixed inset-0 z-40" onClick={() => setEditingMmCp(null)} />
                                        <div className="absolute top-full left-0 mt-1 z-50 bg-surface rounded-lg py-1 shadow-lg w-48" style={{ border: "1px solid var(--border)" }}>
                                          <input
                                            autoFocus
                                            type="text"
                                            placeholder="Search..."
                                            value={mmCpSearch}
                                            onChange={(e) => setMmCpSearch(e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-xs bg-transparent text-foreground placeholder:text-muted/50 focus:outline-none"
                                            style={{ borderBottom: "1px solid var(--border)" }}
                                          />
                                          <div className="max-h-32 overflow-auto">
                                            {counterparties.filter(cp => !mmCpSearch || cp.name.toLowerCase().includes(mmCpSearch.toLowerCase())).slice(0, 8).map(cp => (
                                              <button
                                                key={cp.id}
                                                onClick={() => {
                                                  onUpdateTrade(trade.id, { buyCounterpartyId: cp.id, buyCounterpartyName: cp.name });
                                                  toast(`Seller updated to ${cp.name}`);
                                                  setEditingMmCp(null);
                                                }}
                                                className="w-full text-left px-2.5 py-1.5 text-xs text-foreground hover:bg-surface-2 transition-colors"
                                              >
                                                {cp.name}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${buyPct >= 100 ? "text-success bg-success/10" : buyPct > 0 ? "text-warning bg-warning/10" : "text-muted bg-surface-3"}`}>{buyPct}%</span>
                                </div>
                                <span className="text-xs font-semibold text-foreground tabular-nums">₦{buyTotal.toLocaleString()}</span>
                              </div>
                              <ProgressBar percentage={buyPct} />
                              <p className="text-[10px] text-muted tabular-nums">₦{buySettled.toLocaleString()} / ₦{buyTotal.toLocaleString()}</p>
                              <NairaEntryList entries={buyLegEntries} />
                              <AddPaymentButtons leg="buy" />
                            </div>
          
                            {/* Sell leg — buyer pays you */}
                            <div className="rounded-lg p-3 space-y-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-bold text-success uppercase tracking-wider">Receive from Buyer</span>
                                  <div className="relative">
                                    <button
                                      onClick={() => { setEditingMmCp(editingMmCp === "sell" ? null : "sell"); setMmCpSearch(""); }}
                                      className="text-[10px] text-muted hover:text-foreground transition-colors inline-flex items-center gap-0.5"
                                      title="Click to change buyer"
                                    >
                                      {trade.sellCounterpartyName || "Buyer"}<Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-50" />
                                    </button>
                                    {editingMmCp === "sell" && (
                                      <>
                                        <div className="fixed inset-0 z-40" onClick={() => setEditingMmCp(null)} />
                                        <div className="absolute top-full left-0 mt-1 z-50 bg-surface rounded-lg py-1 shadow-lg w-48" style={{ border: "1px solid var(--border)" }}>
                                          <input
                                            autoFocus
                                            type="text"
                                            placeholder="Search..."
                                            value={mmCpSearch}
                                            onChange={(e) => setMmCpSearch(e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-xs bg-transparent text-foreground placeholder:text-muted/50 focus:outline-none"
                                            style={{ borderBottom: "1px solid var(--border)" }}
                                          />
                                          <div className="max-h-32 overflow-auto">
                                            {counterparties.filter(cp => !mmCpSearch || cp.name.toLowerCase().includes(mmCpSearch.toLowerCase())).slice(0, 8).map(cp => (
                                              <button
                                                key={cp.id}
                                                onClick={() => {
                                                  onUpdateTrade(trade.id, { sellCounterpartyId: cp.id, sellCounterpartyName: cp.name });
                                                  toast(`Buyer updated to ${cp.name}`);
                                                  setEditingMmCp(null);
                                                }}
                                                className="w-full text-left px-2.5 py-1.5 text-xs text-foreground hover:bg-surface-2 transition-colors"
                                              >
                                                {cp.name}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${sellPct >= 100 ? "text-success bg-success/10" : sellPct > 0 ? "text-warning bg-warning/10" : "text-muted bg-surface-3"}`}>{sellPct}%</span>
                                </div>
                                <span className="text-xs font-semibold text-foreground tabular-nums">₦{sellTotal.toLocaleString()}</span>
                              </div>
                              <ProgressBar percentage={sellPct} />
                              <p className="text-[10px] text-muted tabular-nums">₦{sellSettled.toLocaleString()} / ₦{sellTotal.toLocaleString()}</p>
                              <NairaEntryList entries={sellLegEntries} />
                              <AddPaymentButtons leg="sell" />
                            </div>
          
                            {/* Profit summary — actual profit derived from settled amounts */}
                            {(() => {
                              const actualProfit = sellSettled - buySettled;
                              const expectedProfit = profit;
                              const bothStarted = buySettled > 0 || sellSettled > 0;
                              return (
                                <div className="rounded-lg px-3 py-2 space-y-1" style={{ background: "color-mix(in srgb, var(--success) 5%, transparent)", border: "1px solid color-mix(in srgb, var(--success) 20%, transparent)" }}>
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-foreground">Expected Profit</span>
                                    <span className={`text-[12px] font-bold ${expectedProfit >= 0 ? "text-success" : "text-danger"}`}>₦{Math.abs(expectedProfit).toLocaleString()}</span>
                                  </div>
                                  {bothStarted && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-[11px] font-semibold text-foreground">Actual Profit</span>
                                      <span className={`text-sm font-bold ${actualProfit >= 0 ? "text-success" : "text-danger"}`}>{actualProfit >= 0 ? "" : "-"}₦{Math.abs(actualProfit).toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
          
                            {/* Untagged entries (legacy) */}
                            {untaggedEntries.length > 0 && <NairaEntryList entries={untaggedEntries} />}
                          </>
                        );
                      })() : (
                      <>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="section-icon section-icon-success"><CfBills /></div>
                          <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">NGN Payment</h4>
                          {drawerNgnTotal ? (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${trade.nairaStatus === "overpaid" ? "text-danger bg-danger/10" : trade.nairaStatus === "complete" ? "text-success bg-success/10" : trade.nairaStatus === "partial" ? "text-warning bg-warning/10" : "text-muted bg-surface-3"}`}>{trade.nairaStatus === "overpaid" ? `${nairaPct}% OVER` : `${nairaPct}%`}</span>
                          ) : (
                            <span className="text-[10px] text-muted bg-surface-3 px-1.5 py-0.5 rounded">N/A</span>
                          )}
                        </div>
                      </div>
                      {drawerNgnTotal ? (
                        <>
                          <ProgressBar percentage={nairaPct} />
                          <p className="text-[11px] text-muted tabular-nums">₦{trade.nairaAmountSettled.toLocaleString()} / ₦{drawerNgnTotal.toLocaleString()}</p>
                        </>
                      ) : (
                        <p className="text-[11px] text-muted">No NGN amount (USDT-only trade)</p>
                      )}
                      {/* Balance card — split: Credit | Outstanding — hidden when all zero */}
                      {trade.counterpartyId && (() => {
                        const cp = counterparties.find(c => c.id === trade.counterpartyId);
                        if (!cp) return null;
                        const hasCredit = cp.balanceNgn > 0 || cp.balanceUsdt > 0;
                        const hasOutstanding = cp.balanceNgn < 0 || cp.balanceUsdt < 0;
                        if (!hasCredit && !hasOutstanding) return null;
                        const creditNgn = cp.balanceNgn > 0 ? cp.balanceNgn : 0;
                        const creditUsdt = cp.balanceUsdt > 0 ? cp.balanceUsdt : 0;
                        const outstandingNgn = cp.balanceNgn < 0 ? Math.abs(cp.balanceNgn) : 0;
                        const outstandingUsdt = cp.balanceUsdt < 0 ? Math.abs(cp.balanceUsdt) : 0;
                        return (
                          <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-surface-2/50" style={{ borderBottom: "1px solid var(--border)" }}>
                              <Wallet className="w-3 h-3 text-muted flex-shrink-0" />
                              <span className="text-[10px] font-semibold text-foreground">{cp.name} — Balance</span>
                            </div>
                            <div className="grid grid-cols-2">
                              {/* Credit column */}
                              <div className="px-2.5 py-2" style={{ borderRight: "1px solid var(--border)" }}>
                                <p className="text-[9px] font-bold text-muted uppercase tracking-wider mb-1">Credit</p>
                                <p className="text-[9px] text-muted mb-1.5">They hold with you</p>
                                <div className="space-y-0.5">
                                  <p className={`text-[11px] font-bold tabular-nums ${creditNgn > 0 ? "text-warning" : "text-muted/40"}`}>{creditNgn > 0 ? `₦${creditNgn.toLocaleString()}` : "—"}</p>
                                  <p className={`text-[11px] font-bold tabular-nums ${creditUsdt > 0 ? "text-warning" : "text-muted/40"}`}>{creditUsdt > 0 ? `${creditUsdt.toLocaleString()} USDT` : "—"}</p>
                                </div>
                              </div>
                              {/* Outstanding column */}
                              <div className="px-2.5 py-2">
                                <p className="text-[9px] font-bold text-muted uppercase tracking-wider mb-1">Outstanding</p>
                                <p className="text-[9px] text-muted mb-1.5">They owe you</p>
                                <div className="space-y-0.5">
                                  <p className={`text-[11px] font-bold tabular-nums ${outstandingNgn > 0 ? "text-success" : "text-muted/40"}`}>{outstandingNgn > 0 ? `₦${outstandingNgn.toLocaleString()}` : "—"}</p>
                                  <p className={`text-[11px] font-bold tabular-nums ${outstandingUsdt > 0 ? "text-success" : "text-muted/40"}`}>{outstandingUsdt > 0 ? `${outstandingUsdt.toLocaleString()} USDT` : "—"}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* NGN overpayment — Refund button (only when no refund exists yet) */}
                      {drawerNgnTotal && trade.nairaAmountSettled > drawerNgnTotal && (() => {
                        const ngnRefundAdjs = tradeRefundAdjustments.filter(a => a.currency === "NGN");
                        const ngnRefundTotal = ngnRefundAdjs.reduce((s, a) => s + a.amount, 0);
                        const legacyRefundTotal = tradeEntries.filter(e => e.side === "naira" && e.isRefund).reduce((s, e) => s + e.amount, 0);
                        const totalRefunded = ngnRefundTotal + legacyRefundTotal;
                        const hasAnyRefund = totalRefunded > 0;
                        const hasCredited = tradeEntries.some(e => e.side === "naira" && e.isCreditToBalance);
                        if (hasCredited || hasAnyRefund) return null;
                        const excessNgn = trade.nairaAmountSettled - drawerNgnTotal;
                        return (
                          <div className="flex items-center justify-between gap-2 rounded px-2.5 py-1.5 bg-surface-3/50">
                            <span className="text-[10px] text-muted">Overpaid ₦{excessNgn.toLocaleString()}</span>
                            <button onClick={(e) => { e.stopPropagation(); setShowResolutionPanel({ side: "naira", type: "overpayment" }); setResolutionTab("from_bank"); setResolutionBankId(null); setResolutionSelectedTxIds(new Set()); setCreditSubAction(null); setCreditTargetTradeId(null); setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAddMethod("bank_transfer"); setResPayerName(""); setResPayerAccount(""); setResPayerBank(""); setResNotes(""); }} className="text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer">Refund</button>
                          </div>
                        );
                      })()}
                      {/* USDT overpayment — Refund button (only when no refund exists yet) */}
                      {trade.coinAmountSettled > trade.usdtAmount && (() => {
                        const usdtRefundTotal = tradeRefundAdjustments.filter(a => a.currency === "USDT").reduce((s, a) => s + a.amount, 0);
                        const legacyCoinRefundTotal = tradeEntries.filter(e => e.side === "coin" && e.isRefund).reduce((s, e) => s + e.amount, 0);
                        const hasAnyCoinRefund = (usdtRefundTotal + legacyCoinRefundTotal) > 0;
                        if (hasAnyCoinRefund) return null;
                        const excessUsdt = trade.coinAmountSettled - trade.usdtAmount;
                        return (
                          <div className="flex items-center justify-between gap-2 rounded px-2.5 py-1.5 bg-surface-3/50">
                            <span className="text-[10px] text-muted">Overpaid {excessUsdt.toLocaleString()} USDT</span>
                            <button onClick={(e) => { e.stopPropagation(); setShowResolutionPanel({ side: "coin", type: "overpayment" }); setResolutionTab("from_scanner"); setResolutionBankId(null); setResolutionSelectedTxIds(new Set()); setCreditSubAction(null); setCreditTargetTradeId(null); setResCombineSearch(""); setResCombineFilterDir("all"); setResCombineSortAsc(false); }} className="text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer">Refund</button>
                          </div>
                        );
                      })()}
                      {/* NGN underpayment indicator */}
                      {drawerNgnTotal && trade.nairaAmountSettled > 0 && trade.nairaAmountSettled < drawerNgnTotal && (() => {
                        const shortNgn = drawerNgnTotal - trade.nairaAmountSettled;
                        const cp = trade.counterpartyId ? counterparties.find(c => c.id === trade.counterpartyId) : null;
                        const hasCredit = cp && cp.balanceNgn > 0;
                        return (
                          <div className="flex items-center justify-between gap-2 rounded px-2.5 py-1.5 bg-warning/10" style={{ border: "1px solid var(--warning)" }}>
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />
                              <span className="text-[10px] font-semibold text-warning">Short ₦{shortNgn.toLocaleString()}</span>
                            </div>
                            {hasCredit && (
                              <button onClick={(e) => { e.stopPropagation(); setShowResolutionPanel({ side: "naira", type: "underpayment" }); setResolutionTab("use_credit"); setCreditSubAction(null); }} className="btn-mini-accent text-[10px] font-bold text-accent hover:text-accent-hover px-2 py-0.5 rounded transition-all cursor-pointer">Resolve</button>
                            )}
                          </div>
                        );
                      })()}
                      {/* USDT underpayment indicator */}
                      {trade.coinAmountSettled > 0 && trade.coinAmountSettled < trade.usdtAmount && (() => {
                        const shortUsdt = trade.usdtAmount - trade.coinAmountSettled;
                        const cp = trade.counterpartyId ? counterparties.find(c => c.id === trade.counterpartyId) : null;
                        const hasCredit = cp && cp.balanceUsdt > 0;
                        return (
                          <div className="flex items-center justify-between gap-2 rounded px-2.5 py-1.5 bg-warning/10" style={{ border: "1px solid var(--warning)" }}>
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />
                              <span className="text-[10px] font-semibold text-warning">Short {shortUsdt.toLocaleString()} USDT</span>
                            </div>
                            {hasCredit && (
                              <button onClick={(e) => { e.stopPropagation(); setShowResolutionPanel({ side: "coin", type: "underpayment" }); setResolutionTab("use_credit"); setCreditSubAction(null); }} className="btn-mini-accent text-[10px] font-bold text-accent hover:text-accent-hover px-2 py-0.5 rounded transition-all cursor-pointer">Resolve</button>
                            )}
                          </div>
                        );
                      })()}
                      {/* Resolution action buttons + panels — mirrors Add Payment / Select from Bank / Combine Receipts pattern */}
                      {/* Resolution panel is rendered as a modal below */}
                      {/* Receipt verification flags summary */}
                      {receiptFlags.length > 0 && (() => {
                        const critCount = receiptFlags.filter(f => f.severity === "critical").length;
                        const warnCount = receiptFlags.filter(f => f.severity === "warning").length;
                        return (
                          <div className={`flex items-center gap-2 rounded px-2.5 py-1.5 ${critCount > 0 ? "bg-danger/10" : "bg-warning/10"}`} style={{ border: `1px solid var(--${critCount > 0 ? "danger" : "warning"})` }}>
                            <Shield className={`w-3 h-3 ${critCount > 0 ? "text-danger" : "text-warning"} flex-shrink-0`} />
                            <span className={`text-[10px] font-semibold ${critCount > 0 ? "text-danger" : "text-warning"}`}>
                              Receipt verification: {critCount > 0 ? `${critCount} critical` : ""}{critCount > 0 && warnCount > 0 ? ", " : ""}{warnCount > 0 ? `${warnCount} warning${warnCount > 1 ? "s" : ""}` : ""}
                            </span>
                          </div>
                        );
                      })()}
                      {/* Naira entry list */}
                      {nairaEntries.length > 0 && (
                        <div className="space-y-1.5">
                          {nairaEntries.map(entry => {
                            const pd = entry.payerDetailId ? tradePayerDetails.find(p => p.id === entry.payerDetailId) : null;
                            const entryFlags = receiptFlags.filter(f => f.settlementId === entry.id);
                            const hasCritical = entryFlags.some(f => f.severity === "critical");
                            return (
                            <div key={entry.id} className={`relative rounded-lg px-3 py-2 group/entry transition-colors duration-150 ${entry.isCreditToBalance ? "bg-success/5 border border-success/20 hover:bg-success/8" : entry.isRefund ? "bg-warning/5 border border-warning/20 hover:bg-warning/8" : hasCritical ? "bg-danger/8 hover:bg-danger/12" : "bg-surface-3/50 hover:bg-surface-3/60"}`} style={!entry.isRefund && !entry.isCreditToBalance && hasCritical ? { border: "1px solid var(--danger)" } : undefined}>
                              {/* Unlink button — top right */}
                              {hasPermission(activeStaff, "remove_payment") && (
                                <button onClick={(e) => { e.stopPropagation(); setConfirmRemove(entry); }} className="absolute top-1.5 right-2 inline-flex items-center gap-1 text-[8px] font-medium px-2 py-[3px] rounded transition-colors duration-150" style={{ color: "rgba(232,84,84,0.7)", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.05)" }}><Unlink className="w-3 h-3" />Unlink</button>
                              )}
                              {/* Row 1: tag + amount */}
                              <div className="flex items-center gap-2 pr-20">
                                {entry.isCreditToBalance && <span className="text-[9px] font-bold text-success bg-success/10 px-1.5 py-0.5 rounded uppercase tracking-wide">Credit</span>}
                                {entry.isRefund && !entry.isCreditToBalance && <span className="text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded uppercase tracking-wide">Refund</span>}
                                <span className={`text-[11px] font-semibold tabular-nums ${entry.isCreditToBalance ? "text-success" : entry.isRefund ? "text-warning" : "text-foreground"}`}>{entry.isRefund && !entry.isCreditToBalance ? "-" : ""}₦{entry.amount.toLocaleString()}</span>
                                {entryFlags.length > 0 && (
                                  <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${hasCritical ? "bg-danger/15 text-danger" : "bg-warning/15 text-warning"}`}>
                                    <AlertTriangle className="w-2.5 h-2.5" />{entryFlags.length} {entryFlags.length === 1 ? "flag" : "flags"}
                                  </span>
                                )}
                              </div>
                              {/* Row 2: metadata */}
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-[9px] text-muted">{entry.paymentMethod.replace(/_/g, " ")}</span>
                                <span className="text-[9px] text-muted/30">·</span>
                                <button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(entry.reference); toast("Reference copied"); }}>{truncateHash(entry.reference, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button>
                                {entry.sourceDestination && !entry.isCreditToBalance && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted truncate">{entry.sourceDestination}</span></>}
                              </div>
                              {/* Row 3: credit explanation — why this entry exists */}
                              {entry.isCreditToBalance && (
                                <div className="mt-1 text-[9px] text-success/60 leading-snug">
                                  <span>Excess from overpayment — settled ₦{trade.nairaAmountSettled.toLocaleString()} on ₦{trade.ngnAmount?.toLocaleString()} trade → ₦{entry.amount.toLocaleString()} credited to {entry.sourceDestination?.replace("Credit to ", "")?.replace("'s balance", "") || "counterparty"}'s account</span>
                                </div>
                              )}
                              {pd && (
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[8px] text-muted/50 uppercase tracking-wide">Payer</span>
                                  <span className="text-[9px] text-foreground/80 font-medium">{pd.payerName}</span>
                                  {pd.payerAccountNumber && <span className="text-[8px] text-muted font-mono">····{pd.payerAccountNumber.slice(-4)}</span>}
                                  {pd.payerBank && <span className="text-[8px] text-accent flex items-center gap-1"><BankIcon bankName={pd.payerBank} size="sm" />{pd.payerBank}</span>}
                                </div>
                              )}
                              {entryFlags.length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {entryFlags.map(flag => (
                                    <div key={flag.id} className={`flex items-start gap-1.5 text-[9px] px-2 py-1 rounded ${flag.severity === "critical" ? "bg-danger/10 text-danger" : "bg-warning/10 text-warning"}`}>
                                      <AlertTriangle className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                                      <span className="flex-1">{flag.message}</span>
                                      <button onClick={(e) => { e.stopPropagation(); fetch("/api/receipt-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "dismiss", flagId: flag.id }) }).then(() => setReceiptFlags(prev => prev.filter(f => f.id !== flag.id))).catch(() => {}); }} className="text-muted hover:text-foreground flex-shrink-0" title="Dismiss"><X className="w-2.5 h-2.5" /></button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Linked balance adjustments (refund payouts) for this trade — NGN */}
                      {(() => {
                        const ngnAdjs = tradeRefundAdjustments.filter(a => a.currency === "NGN");
                        if (ngnAdjs.length === 0) return null;
                        const ngnTotal = ngnAdjs.reduce((s, a) => s + a.amount, 0);
                        const expectedRefund = drawerNgnTotal ? trade.nairaAmountSettled - drawerNgnTotal : 0;
                        const excess = ngnTotal - Math.max(0, expectedRefund);
                        const cpName = trade.counterpartyId ? (counterparties.find(c => c.id === trade.counterpartyId)?.name || "counterparty") : "counterparty";
                        return (
                          <div className="space-y-1.5">
                            {ngnAdjs.map(adj => {
                              const method = (adj.paymentMethod || "bank_transfer").replace(/_/g, " ");
                              return (
                              <div key={adj.id} className="relative rounded-lg px-3 py-2 group/entry transition-colors duration-150 bg-surface-3/50 hover:bg-surface-3/60">
                                {/* Row 1: tag + amount */}
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded uppercase tracking-wide">Refund</span>
                                  <span className="text-[11px] font-semibold tabular-nums text-foreground">₦{adj.amount.toLocaleString()}</span>
                                </div>
                                {/* Row 2: method · ref · destination */}
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[9px] text-muted">{method}</span>
                                  {adj.reference && <><span className="text-[9px] text-muted/30">·</span><button className="text-[9px] text-muted font-mono hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); copyToClipboard(adj.reference); toast("Reference copied"); }}>{truncateHash(adj.reference, 4)}<Copy className="w-2 h-2 inline ml-0.5 opacity-40" /></button></>}
                                  {adj.destination && <><span className="text-[9px] text-muted/30">·</span><span className="text-[9px] text-muted truncate">{adj.destination}</span></>}
                                </div>
                                {/* Row 3: refund breakdown — styled like Payer row */}
                                {expectedRefund > 0 && (
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[8px] text-muted/50 uppercase tracking-wide">Expected</span>
                                    <span className="text-[9px] text-foreground/80 font-medium tabular-nums">₦{Math.max(0, expectedRefund).toLocaleString()}</span>
                                    {excess > 0 && <><span className="text-[8px] text-muted/30">·</span><span className="text-[8px] text-muted tabular-nums">excess ₦{excess.toLocaleString()} → {cpName}</span></>}
                                    {excess <= 0 && ngnTotal < expectedRefund && <><span className="text-[8px] text-muted/30">·</span><span className="text-[8px] text-muted tabular-nums">₦{(expectedRefund - ngnTotal).toLocaleString()} remaining</span></>}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* NGN Settlement Summary — below entries + refund, above action buttons */}
                      {drawerNgnTotal && trade.nairaAmountSettled > drawerNgnTotal && (() => {
                        const excessNgn = trade.nairaAmountSettled - drawerNgnTotal;
                        const ngnRefundAdjs = tradeRefundAdjustments.filter(a => a.currency === "NGN");
                        const ngnRefundTotal = ngnRefundAdjs.reduce((s, a) => s + a.amount, 0);
                        const legacyRefundTotal = tradeEntries.filter(e => e.side === "naira" && e.isRefund).reduce((s, e) => s + e.amount, 0);
                        const totalRefunded = ngnRefundTotal + legacyRefundTotal;
                        if (totalRefunded <= 0) return null;
                        const excessRefund = totalRefunded - excessNgn;
                        const cpName = trade.counterpartyId ? (counterparties.find(c => c.id === trade.counterpartyId)?.name || "counterparty") : "counterparty";
                        const tint = excessRefund > 0 ? "warning" : "success";
                        return (
                          <div className={`rounded-lg px-3 py-1.5 bg-${tint}/5`}>
                            <span className="text-[9px] text-muted/50 uppercase tracking-wide">Settlement summary</span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[10px] text-foreground/80 font-medium tabular-nums">Overpaid ₦{excessNgn.toLocaleString()}</span>
                              <span className="text-[10px] text-muted/30">·</span>
                              <span className="text-[10px] text-foreground/80 font-medium tabular-nums">Refunded ₦{totalRefunded.toLocaleString()}</span>
                            </div>
                            {excessRefund > 0 && (
                              <p className="text-[9px] text-warning mt-0.5">₦{excessRefund.toLocaleString()} excess → {cpName}'s outstanding</p>
                            )}
                            {excessRefund < 0 && (
                              <p className="text-[9px] text-success mt-0.5">₦{Math.abs(excessRefund).toLocaleString()} remaining on {cpName}'s credit</p>
                            )}
                            {excessRefund === 0 && (
                              <p className="text-[9px] text-success mt-0.5">Refund matches overpayment — fully settled</p>
                            )}
                          </div>
                        );
                      })()}
                      {/* Add Payment / Select from Bank / Use Balance buttons */}
                      {drawerNgnTotal && (
                        trade.counterpartyId ? (
                        <div className="flex items-center gap-2 pt-1">
                          {hasPermission(activeStaff, "add_payment") && <button onClick={(e) => { e.stopPropagation(); setShowAddNaira(true); setShowBankPicker(false); setShowBalanceModal(false); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"><Plus className="w-3 h-3" />Add Payment</button>}
                          {hasPermission(activeStaff, "select_from_bank") && <button onClick={(e) => { e.stopPropagation(); setShowBankPicker(true); setShowAddNaira(false); setShowBalanceModal(false); setSelectedBankId(null); setSelectedTxIds(new Set()); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"><Building2 className="w-3 h-3" />Select from Bank</button>}
                          <button onClick={(e) => { e.stopPropagation(); setShowBalanceModal("naira"); setShowAddNaira(false); setShowBankPicker(false); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors active:scale-[0.97]"><Wallet className="w-3 h-3" />Use Balance</button>
                        </div>
                        ) : (
                        <p className="text-[11px] text-warning pt-1">Assign a counterparty to this trade before recording settlements.</p>
                        )
                      )}
                    </>
                    )}
                    {/* Add Payment modal */}
                    <AnimatePresence>
                    {showAddNaira && (() => {
                      const ngnRemaining = drawerNgnTotal - trade.nairaAmountSettled;
                      const parsedAddAmt = parseFloat(addAmount.replace(/,/g, "")) || 0;
                      const isOverpay = parsedAddAmt > 0 && ngnRemaining > 0 && parsedAddAmt > ngnRemaining;
                      return (
                      <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]" onClick={() => { resetAddPaymentState(); setShowAddNaira(false); }} />
                        <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ type: "spring", stiffness: 350, damping: 22 }} className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[480px] bg-surface rounded-2xl shadow-2xl max-h-[90vh] overflow-auto" style={{ border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-between px-5 pt-5 pb-3">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-foreground">Add Payment</h3>
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-success/10 text-success">CREDIT</span>
                              <button
                                onClick={() => resetAddPaymentState()}
                                className="w-6 h-6 rounded-md flex items-center justify-center text-muted hover:text-accent hover:bg-accent/10 transition-all"
                                title="Reset form"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </button>
                            </div>
                            <button onClick={() => { resetAddPaymentState(); setShowAddNaira(false); }} className="text-muted hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
                          </div>
                          <div className="px-5 pb-5 space-y-3">
                        {/* Screenshot Upload */}
                        <div
                          className={`group flex items-center gap-3 w-full rounded-lg px-3.5 py-3.5 cursor-pointer transition-all duration-200 ${tradeAttachments.length > 0 ? "bg-success/5 hover:bg-success/8" : "bg-surface-2/40 hover:bg-surface-2/70"} ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                          style={{ border: `1px solid ${tradeAttachments.length > 0 ? "color-mix(in srgb, var(--success) 25%, transparent)" : "var(--border)"}` }}
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const file = e.dataTransfer.files?.[0]; if (file) handleScreenshotUpload(file); }}
                          onClick={() => { screenshotInputRef.current?.click(); }}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tradeAttachments.length > 0 ? "bg-success/10" : "bg-accent/10"}`}>
                            {uploading ? <Loader2 className="w-4 h-4 text-accent animate-spin" /> : tradeAttachments.length > 0 ? <CheckCircle2 className="w-4 h-4 text-success" /> : <Upload className="w-4 h-4 text-accent" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-foreground">{uploading ? ((tradeAttachments[0] as Record<string, unknown>)?.isPdf ? "Validating PDF..." : "Uploading...") : tradeAttachments.length > 0 ? ((tradeAttachments[0] as Record<string, unknown>)?.isPdf ? "PDF receipt attached" : `${tradeAttachments.length} receipt${tradeAttachments.length > 1 ? "s" : ""} attached`) : <>Drop receipt or <span className="text-accent">browse</span></>}</p>
                            <p className="text-[10px] text-muted mt-0.5">{tradeAttachments.length > 0 ? ((tradeAttachments[0] as Record<string, unknown>)?.isPdf ? "PDF attached · fill in details below" : "Click to replace · hover thumbnail to preview") : "Screenshot or PDF receipt · auto-extracts from images"}</p>
                          </div>
                          {tradeAttachments.length > 0 ? (
                            <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                              {tradeAttachments.slice(0, 3).map((att) => (
                                <div key={att.id} className="relative group/att w-8 h-8 rounded overflow-hidden bg-surface-2 cursor-pointer flex-shrink-0" style={{ border: "1px solid var(--border)" }} onClick={(e) => { e.stopPropagation(); if (!(att as Record<string, unknown>).isPdf) setLightboxUrl(att.url); }}>
                                  {(att as Record<string, unknown>).isPdf ? (
                                    <div className="w-full h-full flex items-center justify-center bg-danger/10">
                                      <FileText className="w-4 h-4 text-danger" />
                                    </div>
                                  ) : (
                                    <img src={att.url} alt={att.originalName} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                  )}
                                  {!(att as Record<string, unknown>).isPdf && (
                                    <div className="absolute inset-0 bg-black/0 group-hover/att:bg-black/30 transition-all flex items-center justify-center">
                                      <Eye className="w-3 h-3 text-white opacity-0 group-hover/att:opacity-100 transition-opacity" />
                                    </div>
                                  )}
                                </div>
                              ))}
                              {tradeAttachments.length > 3 && (
                                <span className="text-[9px] font-semibold text-muted cursor-pointer" onClick={(e) => { e.stopPropagation(); setLightboxUrl(tradeAttachments[0].url); }}>+{tradeAttachments.length - 3}</span>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); setTradeAttachments([]); setOcrResult(null); setOcrImageHash(null); setDuplicateWarning(null); setMatchWarning(null); setBankMismatchWarning(null); setBankDetectedConfirm(null); setOcrDetectedBankRaw(null); setPayerName(""); setPayerAccount(""); setPayerBank(""); setAddAmount(""); setAddRef(""); setAddSource(""); setAddAccountId(null); setAddSessionId(""); setAddDate(localNow()); setPayerExpanded(true); }} className="w-6 h-6 rounded-md flex items-center justify-center text-muted hover:text-danger hover:bg-danger/10 transition-all" title="Clear all"><X className="w-3 h-3" /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 shrink-0">
                              {["JPG", "PNG", "PDF"].map(f => <span key={f} className="text-[8px] font-medium text-muted bg-surface-3 px-1.5 py-0.5 rounded">{f}</span>)}
                            </div>
                          )}
                          <input ref={screenshotInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleScreenshotUpload(file); e.target.value = ""; }} />
                        </div>
                        {/* OCR status */}
                        {ocrLoading && <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-accent/8"><Loader2 className="w-3 h-3 text-accent animate-spin" /><span className="text-[10px] text-accent font-semibold">Extracting details from screenshot...</span></div>}
                        {ocrResult && !ocrLoading && (() => {
                          const filled = [ocrResult.amount, ocrResult.senderName, ocrResult.senderAccountNumber, ocrResult.sessionId, ocrResult.reference, ocrResult.transactionDate].filter(Boolean).length;
                          return <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded ${ocrResult.confidence >= 80 ? "bg-success/10" : ocrResult.confidence >= 40 ? "bg-warning/10" : "bg-danger/10"}`}><span className={`text-[10px] font-semibold ${ocrResult.confidence >= 80 ? "text-success" : ocrResult.confidence >= 40 ? "text-warning" : "text-danger"}`}>AI extracted {filled}/6 fields — verify below</span></div>;
                        })()}
                        {/* Duplicate + match warning overlays rendered via portal at component bottom */}
                        {/* Bank detection feedback */}
                        <AnimatePresence mode="wait">
                          {bankDetectedConfirm && !bankMismatchWarning && (
                            <motion.div key="bank-confirm" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ type: "spring", stiffness: 400, damping: 28 }} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-success/8" style={{ border: "1px solid rgba(34,197,94,0.2)" }}>
                              <CheckCircle2 className="w-3.5 h-3.5 text-success shrink-0" />
                              <p className="flex-1 text-[11px] text-success font-semibold">Detected as {bankDetectedConfirm} receipt — auto-selected</p>
                              <button onClick={() => setBankDetectedConfirm(null)} className="text-success/50 hover:text-success transition-colors shrink-0"><X className="w-3 h-3" /></button>
                            </motion.div>
                          )}
                          {bankMismatchWarning && (
                            <motion.div key="bank-mismatch" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ type: "spring", stiffness: 400, damping: 28 }} className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-warning/8" style={{ border: "1px solid rgba(234,179,8,0.2)" }}>
                              <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] text-warning font-semibold leading-tight">{bankMismatchWarning}</p>
                                {ocrDetectedBankRaw && (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    <button onClick={() => { const det = ocrDetectedBankRaw.toLowerCase().replace(/[^a-z0-9]/g, ""); const match = treasuryAccounts.filter(a => a.type === "bank").find((acc) => { const accName = (acc.bankName + " " + acc.label).toLowerCase().replace(/[^a-z0-9]/g, ""); return accName.includes(det) || det.includes(accName.split(" ")[0]); }); if (match) { setAddSource(match.label); setAddAccountId(match.id); setBankDetectedConfirm(match.bankName || ocrDetectedBankRaw || "matched bank"); } setBankMismatchWarning(null); }} className="text-[10px] font-semibold text-accent bg-accent/10 hover:bg-accent/20 px-2 py-0.5 rounded transition-colors">Switch to {ocrDetectedBankRaw}</button>
                                    <button onClick={() => { setBankMismatchWarning(null); setBankDetectedConfirm(null); }} className="text-[10px] font-semibold text-muted bg-surface-3 hover:bg-surface-3/80 px-2 py-0.5 rounded transition-colors">Keep current</button>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* Amount + remaining */}
                        {ngnRemaining > 0 && <p className="text-[10px] text-muted tabular-nums">Remaining: <span className="font-semibold text-foreground">₦{ngnRemaining.toLocaleString()}</span></p>}
                        {ngnRemaining <= 0 && drawerNgnTotal && <p className="text-[10px] text-success font-semibold">NGN fully settled</p>}
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" inputMode="numeric" placeholder="Amount (NGN)" value={addAmount} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ""); const parts = raw.split("."); const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","); setAddAmount(parts.length > 1 ? `${whole}.${parts[1]}` : whole); }} className={`bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground tabular-nums placeholder:text-muted/40 focus:outline-none focus:ring-1 ${isOverpay ? "focus:ring-warning/50 ring-1 ring-warning/30" : "focus:ring-accent/30"}`} style={{ border: `1px solid ${isOverpay ? "var(--warning)" : "var(--border)"}` }} />
                          <div className="relative flex items-center">
                            <BankIcon bankName={treasuryAccounts.find(a => a.id === addAccountId)?.bankName} size="sm" className="absolute left-2 z-[1] pointer-events-none" />
                            <select value={addAccountId ?? ""} onChange={(e) => {
                              const newId = Number(e.target.value);
                              setAddAccountId(newId || null);
                              const selectedAcc = treasuryAccounts.find(a => a.id === newId);
                              setAddSource(selectedAcc?.label || "");
                              if (ocrDetectedBankRaw && selectedAcc) {
                                const det = ocrDetectedBankRaw.toLowerCase().replace(/[^a-z0-9]/g, "");
                                const accName = (selectedAcc.bankName + " " + selectedAcc.label).toLowerCase().replace(/[^a-z0-9]/g, "");
                                if (accName.includes(det) || det.includes(accName.split(" ")[0])) {
                                  setBankMismatchWarning(null);
                                  setBankDetectedConfirm(selectedAcc.bankName || "matched bank");
                                } else {
                                  setBankDetectedConfirm(null);
                                  setBankMismatchWarning(`Screenshot shows "${ocrDetectedBankRaw}" but you selected "${selectedAcc.bankName || "another bank"}". Is this correct?`);
                                }
                              }
                            }} className={`w-full bg-surface-2 rounded pl-8 pr-2 py-1.5 text-[12px] text-foreground focus:outline-none ${bankMismatchWarning ? "ring-1 ring-warning/50" : bankDetectedConfirm ? "ring-1 ring-success/40" : ""}`} style={{ border: `1px solid ${bankMismatchWarning ? "rgba(234,179,8,0.4)" : bankDetectedConfirm ? "rgba(34,197,94,0.3)" : "var(--border)"}` }}>
                              <option value="">Select bank account...</option>
                              {treasuryAccounts.filter(a => a.type === "bank").map(a => <option key={a.id} value={a.id}>{a.label} ({a.bankName})</option>)}
                            </select>
                          </div>
                        </div>
                        {/* Narration / Reference */}
                        <input type="text" placeholder="Narration / Transaction reference" value={addRef} onChange={(e) => setAddRef(e.target.value)} className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                        {/* NIP Session ID */}
                        <div>
                          <input type="text" placeholder="NIP Session ID (30 digits)" value={addSessionId}
                            onChange={(e) => setAddSessionId(e.target.value.replace(/\D/g, "").slice(0, 30))}
                            className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                          {addSessionId && addSessionId.length !== 30 && (
                            <p className="text-[10px] text-warning mt-0.5">{addSessionId.length}/30 digits — NIP Session ID must be exactly 30 digits</p>
                          )}
                          {addSessionId && addSessionId.length === 30 && (() => {
                            const nibssPrefix = addSessionId.substring(0, 6);
                            const sessionBank = NIBSS_CODE_MAP[nibssPrefix];
                            const selectedAcc = treasuryAccounts.find(a => a.id === addAccountId);
                            const selectedBankName = selectedAcc?.bankName || "";
                            const isMatch = sessionBank && selectedBankName && sessionBank.toLowerCase().replace(/[^a-z0-9]/g, "").includes(selectedBankName.toLowerCase().replace(/[^a-z0-9]/g, "").split(" ")[0]);
                            const isReverse = sessionBank && selectedBankName && selectedBankName.toLowerCase().replace(/[^a-z0-9]/g, "").includes(sessionBank.toLowerCase().replace(/[^a-z0-9]/g, "").split(" ")[0]);
                            if (sessionBank && !isMatch && !isReverse) {
                              return <p className="text-[10px] text-warning mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3 inline" /> Session ID belongs to <strong>{sessionBank}</strong> but you selected <strong>{selectedBankName || "another bank"}</strong></p>;
                            }
                            if (sessionBank) {
                              return <p className="text-[10px] text-success mt-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 inline" /> Session ID verified — {sessionBank}</p>;
                            }
                            return null;
                          })()}
                        </div>
                        {/* Payer Details section — collapsible, auto-collapses when OCR fills */}
                        <div className="rounded-lg bg-surface-2/40 overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                          <button
                            type="button"
                            onClick={() => setPayerExpanded(!payerExpanded)}
                            className="w-full flex items-center justify-between px-2.5 py-2 hover:bg-surface-2/60 transition-colors"
                          >
                            <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Payer Details</span>
                            <div className="flex items-center gap-2">
                              {!payerExpanded && (payerName || payerAccount || payerBank) && (
                                <span className="text-[10px] text-success flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  {[payerName, payerAccount && `••${payerAccount.slice(-4)}`, payerBank].filter(Boolean).join(" · ")}
                                </span>
                              )}
                              <motion.div animate={{ rotate: payerExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                <ChevronDown className="w-3 h-3 text-muted" />
                              </motion.div>
                            </div>
                          </button>
                          <AnimatePresence initial={false}>
                            {payerExpanded && (
                              <motion.div
                                key="payer-fields"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                                className="overflow-hidden"
                              >
                                <div className="px-2.5 pb-2.5 space-y-2">
                                  <div className="grid grid-cols-2 gap-2">
                                    <input type="text" placeholder="Payer name" value={payerName} onChange={(e) => setPayerName(e.target.value)} className="bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                                    <input type="text" placeholder="Account number" value={payerAccount} onChange={(e) => setPayerAccount(e.target.value.replace(/[^0-9*]/g, "").slice(0, 10))} className="bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                                  </div>
                                  <select value={payerBank} onChange={(e) => setPayerBank(e.target.value)} className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground focus:outline-none" style={{ border: "1px solid var(--border)" }}>
                                    <option value="">Select payer bank...</option>
                                    {NIGERIAN_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                                  </select>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        {/* Note field */}
                        <div>
                          <input type="text" placeholder="e.g. From Ahmed, Personal transfer..."
                            value={tradeNotes} onChange={(e) => setTradeNotes(e.target.value)} onBlur={handleSaveNotes}
                            className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                            style={{ border: "1px solid var(--border)" }} />
                          <p className="text-[10px] text-muted mt-1">A memo for your reference — does not affect any accounts</p>
                        </div>
                        {/* Receipt date — when the payment hit the account */}
                        <div>
                          <label className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1 block">Payment Date & Time (from receipt)</label>
                          <input type="datetime-local" step="1" value={addDate} onChange={(e) => setAddDate(e.target.value)}
                            className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)", colorScheme: "dark" }} />
                          <p className="text-[10px] text-muted mt-1">When the payment hit the account — auto-filled from receipt if uploaded</p>
                        </div>
                        {isOverpay && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-warning/10" style={{ border: "1px solid var(--warning)" }}>
                            <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />
                            <span className="text-[10px] text-warning font-semibold">Overpayment: ₦{(parsedAddAmt - ngnRemaining).toLocaleString()} excess</span>
                          </div>
                        )}
                        {/* Actions */}
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <Button variant="ghost" size="sm" onClick={() => {
                            resetAddPaymentState(); setShowAddNaira(false);
                          }}>Cancel</Button>
                          <Button variant="primary" size="sm" onClick={() => {
                            if (duplicateWarning) { toast(duplicateWarning, "error"); return; }
                            const parsed = parseFloat(addAmount.replace(/,/g, ""));
                            if (isNaN(parsed) || parsed <= 0) { toast("Enter a valid amount", "error"); return; }
                            setShowAddSaveConfirm(true);
                          }} disabled={!!duplicateWarning}>Save</Button>
                        </div>
                        {/* Save confirmation overlay */}
                        <AnimatePresence>
                          {showAddSaveConfirm && (() => {
                            const acct = treasuryAccounts.find(a => a.id === addAccountId);
                            const acctLabel = acct ? `${acct.label} (${acct.bankName || "Bank"})` : addSource || "selected account";
                            const amt = addAmount;
                            const hasMismatch = ocrDetectedBankRaw && acct?.bankName && (() => {
                              const det = ocrDetectedBankRaw.toLowerCase().replace(/[^a-z0-9]/g, "");
                              const accName = (acct.bankName + " " + acct.label).toLowerCase().replace(/[^a-z0-9]/g, "");
                              return !(accName.includes(det) || det.includes(accName.split(" ")[0]));
                            })();
                            return (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px] rounded-2xl"
                              >
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                  className="bg-surface rounded-xl px-5 py-4 max-w-[340px] w-full mx-4 space-y-3"
                                  style={{ border: "1px solid var(--border)" }}
                                >
                                  <div className="text-center space-y-1.5">
                                    <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center bg-success/12">
                                      <ArrowDownLeft className="w-5 h-5 text-success" />
                                    </div>
                                    <p className="text-[14px] font-bold text-foreground">Confirm Payment</p>
                                  </div>
                                  <div className="space-y-2 rounded-lg p-3 bg-surface-2/50" style={{ border: "1px solid var(--border)" }}>
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">Type</span>
                                      <span className="text-[12px] font-bold text-success">Credit — Money received</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">Amount</span>
                                      <span className="text-[12px] font-bold text-foreground tabular-nums">₦{amt}</span>
                                    </div>
                                    {addSource && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">Bank</span>
                                        <span className="text-[12px] font-semibold text-foreground inline-flex items-center gap-1.5">
                                          <BankIcon bankName={acct?.bankName} size="sm" />
                                          {acctLabel}
                                        </span>
                                      </div>
                                    )}
                                    {ocrDetectedBankRaw && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">OCR Detected</span>
                                        <span className={`text-[12px] font-semibold ${hasMismatch ? "text-warning" : "text-success"}`}>{ocrDetectedBankRaw} {hasMismatch ? "\u26A0" : "\u2713"}</span>
                                      </div>
                                    )}
                                  </div>
                                  {hasMismatch && (
                                    <div className="flex items-start gap-1.5 px-2 py-1.5 rounded bg-warning/8" style={{ border: "1px solid rgba(234,179,8,0.2)" }}>
                                      <AlertTriangle className="w-3 h-3 text-warning shrink-0 mt-0.5" />
                                      <p className="text-[10px] text-warning font-semibold">Screenshot shows {ocrDetectedBankRaw} but you&apos;re logging to {acct?.bankName}. Double-check this is correct.</p>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 pt-1">
                                    <Button variant="ghost" size="sm" className="flex-1" onClick={() => setShowAddSaveConfirm(false)}>Go Back</Button>
                                    <Button variant="primary" size="sm" className="flex-1" onClick={() => { setShowAddSaveConfirm(false); handleAddEntry("naira"); }}>
                                      Yes, Log It
                                    </Button>
                                  </div>
                                </motion.div>
                              </motion.div>
                            );
                          })()}
                        </AnimatePresence>
                          </div>
                        </motion.div>
                      </>
                      );
                    })()}
                    </AnimatePresence>
                    {/* Select from Bank modal */}
                    <AnimatePresence>
                    {showBankPicker && (
                        <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]" onClick={() => { setShowBankPicker(false); setSelectedBankId(null); setSelectedTxIds(new Set()); }} />
                        <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ type: "spring", stiffness: 350, damping: 22 }} className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[520px] bg-surface rounded-2xl shadow-2xl max-h-[90vh] overflow-auto" style={{ border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-between px-5 pt-5 pb-3">
                            <h3 className="text-sm font-bold text-foreground">Select from Bank</h3>
                            <button onClick={() => { setShowBankPicker(false); setSelectedBankId(null); setSelectedTxIds(new Set()); }} className="text-muted hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
                          </div>
                          <div className="px-5 pb-5 space-y-3">
                          {selectedBankId === null ? (
                            <>
                              {/* Phase 1: Bank Account Picker */}
                              <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Select Bank Account</p>
                              <div className="flex flex-wrap gap-2">
                                {treasuryAccounts.filter(a => a.type === "bank").map(acct => {
                                  // Determine expected tx direction: middleman buy leg = debit (paying out), sell/profit = credit (receiving)
                                  // Regular trades: buy/borrow = debit (paying NGN), sell/lend = credit (receiving NGN)
                                  const pickerLeg = (trade as Record<string, unknown>).__activeLeg as "buy" | "sell" | "profit" | undefined;
                                  const expectedTxType = trade.direction === "middleman"
                                    ? (pickerLeg === "buy" ? "debit" : "credit")
                                    : (trade.direction === "sell" || trade.direction === "lend" ? "credit" : "debit");
                                  const availableCount = monoBankStatements.filter(s => s.accountId === acct.id && s.type === expectedTxType && !s.linkedTradeId).length;
                                  return (
                                    <button
                                      key={acct.id}
                                      onClick={() => { setSelectedBankId(acct.id); setSelectedTxIds(new Set()); }}
                                      className="flex items-center gap-2 bg-surface-2 hover:bg-surface-3 rounded-lg px-3 py-2 transition-colors"
                                      style={{ border: "1px solid var(--border)" }}
                                    >
                                      <BankIcon bankName={acct.bankName} size="sm" />
                                      <div className="text-left">
                                        <p className="text-[11px] font-semibold text-foreground">{acct.label}</p>
                                        <p className="text-[10px] text-muted">****{acct.accountNumber?.slice(-4)}</p>
                                      </div>
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${availableCount > 0 ? "bg-success/15 text-success" : "bg-surface-3 text-muted/50"}`}>{availableCount}</span>
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex justify-end pt-1">
                                <Button variant="ghost" size="sm" onClick={() => setShowBankPicker(false)}>Cancel</Button>
                              </div>
                            </>
                          ) : (() => {
                            const bankAcct = treasuryAccounts.find(a => a.id === selectedBankId)!;
                            // Filter by correct direction: middleman buy leg = debit, sell/profit = credit
                            // Regular trades: buy/borrow = debit, sell/lend = credit
                            const bankActiveLegForCalc = (trade as Record<string, unknown>).__activeLeg as "buy" | "sell" | "profit" | undefined;
                            const expectedType = trade.direction === "middleman"
                              ? (bankActiveLegForCalc === "buy" ? "debit" : "credit")
                              : (trade.direction === "sell" || trade.direction === "lend" ? "credit" : "debit");
                            const allStmts = monoBankStatements.filter(s => s.accountId === selectedBankId);
                            const dirStmts = allStmts.filter(s => s.type === expectedType);
                            const selectableStmts = dirStmts.filter(s => !s.linkedTradeId);
                            const selectedTotal = selectableStmts.filter(s => selectedTxIds.has(s.id)).reduce((sum, s) => sum + s.amount, 0);
                            let remaining: number;
                            if (trade.direction === "middleman" && bankActiveLegForCalc) {
                              const legEntries = settlementEntries.filter(e => e.tradeId === trade.id && e.side === "naira" && e.middlemanLeg === bankActiveLegForCalc);
                              const legSettled = legEntries.reduce((s, e) => s + e.amount, 0);
                              const legTotal = bankActiveLegForCalc === "buy" ? (trade.buyRate || 0) * trade.usdtAmount
                                : bankActiveLegForCalc === "sell" ? (trade.sellRate || 0) * trade.usdtAmount
                                : Math.abs(trade.middlemanProfit || 0);
                              remaining = legTotal - legSettled;
                            } else {
                              remaining = drawerNgnTotal - trade.nairaAmountSettled;
                            }
                            const isOverpay = selectedTotal > remaining && remaining > 0 && selectedTxIds.size > 0;
                            const allSelectableSelected = selectableStmts.length > 0 && selectableStmts.every(s => selectedTxIds.has(s.id));

                            return (
                              <>
                                {/* Phase 2: Multi-Select Transaction List */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => { setSelectedBankId(null); setSelectedTxIds(new Set()); }} className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors"><ArrowLeft className="w-3 h-3" />Change bank</button>
                                    <span className="text-muted/30">|</span>
                                    <BankIcon bankName={bankAcct.bankName} size="sm" />
                                    <p className="text-[11px] font-bold text-foreground">{bankAcct.label}</p>
                                  </div>
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${expectedType === "credit" ? "bg-success/15 text-success" : "bg-danger/15 text-danger"}`}>{expectedType === "credit" ? "↓ Credits" : "↑ Debits"}</span>
                                </div>

                                {/* Running total bar */}
                                <div className={`flex items-center justify-between rounded px-2.5 py-1.5 ${isOverpay ? "bg-warning/10" : "bg-surface-2"}`} style={{ border: `1px solid ${isOverpay ? "var(--warning)" : "var(--border)"}` }}>
                                  <span className={`text-[11px] font-semibold tabular-nums ${isOverpay ? "text-warning" : "text-foreground"}`}>Selected: ₦{selectedTotal.toLocaleString()}</span>
                                  <span className="text-[10px] text-muted tabular-nums">{selectedTxIds.size} of {selectableStmts.length} available &middot; ₦{remaining.toLocaleString()} remaining</span>
                                </div>
                                {isOverpay && <p className="text-[10px] text-warning font-semibold">Selection exceeds remaining amount by ₦{(selectedTotal - remaining).toLocaleString()}</p>}
          
                                {/* Select All toggle */}
                                {selectableStmts.length > 0 && (
                                  <button
                                    onClick={() => {
                                      if (allSelectableSelected) {
                                        setSelectedTxIds(new Set());
                                      } else {
                                        setSelectedTxIds(new Set(selectableStmts.map(s => s.id)));
                                      }
                                    }}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors"
                                  >
                                    <CheckSquare className="w-3 h-3" />{allSelectableSelected ? "Deselect All" : "Select All"}
                                  </button>
                                )}
          
                                {/* Transaction list */}
                                <div className="max-h-48 overflow-auto space-y-0.5">
                                  {dirStmts.length === 0 ? (
                                    <p className="text-[11px] text-muted py-2">No {expectedType} transactions for this account</p>
                                  ) : (
                                    dirStmts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(stmt => {
                                      const isLinked = !!stmt.linkedTradeId;
                                      const isSelected = selectedTxIds.has(stmt.id);
                                      return (
                                        <button
                                          key={stmt.id}
                                          disabled={isLinked}
                                          onClick={() => {
                                            const next = new Set(selectedTxIds);
                                            if (next.has(stmt.id)) { next.delete(stmt.id); } else { next.add(stmt.id); }
                                            setSelectedTxIds(next);
                                          }}
                                          className={`btn-select-item w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-all ${isLinked ? "opacity-40 cursor-not-allowed" : isSelected ? "bg-accent/10" : "hover:bg-surface-2"}`} data-selected={isSelected && !isLinked}
                                        >
                                          <span className="flex-shrink-0">
                                            {isLinked ? (
                                              <CheckSquare className="w-3 h-3 text-muted/30" />
                                            ) : isSelected ? (
                                              <CheckSquare className="w-3 h-3 text-accent" />
                                            ) : (
                                              <CheckSquare className="w-3 h-3 text-muted/30" />
                                            )}
                                          </span>
                                          <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(stmt.date)}</span>
                                          <span className="text-[11px] text-foreground truncate flex-1 min-w-0">{stmt.narration.slice(0, 35)}</span>
                                          <span className={`text-[11px] font-semibold tabular-nums flex-shrink-0 ${stmt.type === "credit" ? "text-success" : "text-danger"}`}>{stmt.type === "credit" ? "+" : "−"}₦{stmt.amount.toLocaleString()}</span>
                                          {isLinked && <span className="text-[9px] font-bold text-muted bg-surface-3 px-1 py-0.5 rounded flex-shrink-0">Trade #{stmt.linkedTradeId}</span>}
                                        </button>
                                      );
                                    })
                                  )}
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center justify-between pt-1">
                                  <Button variant="ghost" size="sm" onClick={() => { setShowBankPicker(false); setSelectedBankId(null); setSelectedTxIds(new Set()); }}>Cancel</Button>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={async () => {
                                      const selected = monoBankStatements.filter(s => selectedTxIds.has(s.id));
                                      const bankBeforeSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
                                      let totalApplied = 0;
                                      const bankActiveLeg = (trade as Record<string, unknown>).__activeLeg as "buy" | "sell" | "profit" | undefined;
                                      const bankNewEntries: SettlementEntry[] = [];
                                      const bankPayerDetails: PayerDetail[] = [];
                                      for (const stmt of selected) {
                                        const acctLabel = treasuryAccounts.find(a => a.id === stmt.accountId)?.label || "Unknown";
                                        const entry: SettlementEntry = {
                                          id: Date.now() + Math.random(),
                                          tradeId: trade.id,
                                          side: "naira",
                                          amount: stmt.amount,
                                          currency: "NGN",
                                          paymentMethod: "bank_transfer",
                                          sourceDestination: acctLabel,
                                          reference: stmt.narration.slice(0, 40),
                                          treasuryAccountId: stmt.accountId,
                                          createdAt: new Date().toISOString(),
                                          ...(trade.direction === "middleman" && bankActiveLeg ? { middlemanLeg: bankActiveLeg } : {}),
                                        };
                                        // Extract payer details from bank statement narration
                                        if (stmt.narration) {
                                          let pName: string | null = null;
                                          let pAcct: string | null = null;
                                          const nipMatch = /(?:NIP|MC|NIBSS)\s*\/?\s*(?:TRF\s+)?(?:FRM|FROM)?\s*([A-Za-z\s.'-]{2,40})\s*\/\s*(\d{10})/i.exec(stmt.narration);
                                          if (nipMatch) { pName = nipMatch[1].trim(); pAcct = nipMatch[2]; }
                                          if (!pName) { const trfMatch = /(?:TRF|TRANSFER)\s+FROM\s+([A-Za-z\s.'-]{2,40})/i.exec(stmt.narration); if (trfMatch) pName = trfMatch[1].trim(); }
                                          if (!pAcct) { const acctMatch = /\b(\d{10})\b/.exec(stmt.narration); if (acctMatch) pAcct = acctMatch[1]; }
                                          if (!pName) pName = (stmt as Record<string, unknown>).counterpartyName as string || null;
                                          if (pName) {
                                            const payerDetailId = genId();
                                            entry.payerDetailId = payerDetailId;
                                            const bankName = treasuryAccounts.find(a => a.id === stmt.accountId)?.bankName || acctLabel;
                                            bankPayerDetails.push({ id: payerDetailId, settlementId: entry.id, tradeId: trade.id, payerName: pName, payerAccountNumber: pAcct || "", payerBank: bankName, amount: stmt.amount, reference: stmt.narration.slice(0, 40), source: "mono", createdAt: new Date().toISOString() });
                                          }
                                        }
                                        settlementEntries.push(entry);
                                        bankNewEntries.push(entry);
                                        stmt.linkedTradeId = trade.id;
                                        stmt.linkedSettlementId = entry.id;
                                        trade.nairaAmountSettled += stmt.amount;
                                        totalApplied += stmt.amount;
                                      }
                                      // Compute naira status
                                      if (trade.direction === "middleman") {
                                        const allNaira = settlementEntries.filter(e => e.tradeId === trade.id && e.side === "naira");
                                        if (trade.nairaFlow === "direct") {
                                          const profitSettled = allNaira.filter(e => e.middlemanLeg === "profit" || !e.middlemanLeg).reduce((s, e) => s + e.amount, 0);
                                          trade.nairaStatus = profitSettled >= Math.abs(trade.middlemanProfit || 0) ? "complete" : "partial";
                                        } else {
                                          const buySettled = allNaira.filter(e => e.middlemanLeg === "buy").reduce((s, e) => s + e.amount, 0);
                                          const sellSettled = allNaira.filter(e => e.middlemanLeg === "sell").reduce((s, e) => s + e.amount, 0);
                                          const buyDone = buySettled >= (trade.buyRate || 0) * trade.usdtAmount;
                                          const sellDone = sellSettled >= (trade.sellRate || 0) * trade.usdtAmount;
                                          trade.nairaStatus = (buyDone && sellDone) ? "complete" : (buySettled > 0 || sellSettled > 0) ? "partial" : "pending";
                                        }
                                      } else {
                                        const total = getTradeNgnTotal(trade);
                                        trade.nairaStatus = trade.nairaAmountSettled <= 0 ? "pending" : trade.nairaAmountSettled > total ? "overpaid" : trade.nairaAmountSettled >= total ? "complete" : "partial";
                                      }
                                      const coinDone = trade.coinStatus === "complete" || trade.coinStatus === "overpaid";
                                      const nairaDone = trade.nairaStatus === "complete" || trade.nairaStatus === "overpaid";
                                      if (coinDone && nairaDone) {
                                        trade.status = "settled";
                                        trade.settledAt = trade.settledAt || new Date().toISOString();
                                      }
                                      // Update payment metadata from the bank entries
                                      if (bankNewEntries.length > 0) {
                                        trade.nairaPaymentMethod = "bank_transfer";
                                        trade.nairaBankRef = bankNewEntries[bankNewEntries.length - 1].reference;
                                        trade.nairaBankVerified = bankNewEntries.some(e => !!e.treasuryAccountId);
                                      }
                                      // Persist everything — await all calls and catch failures
                                      const bankSyncData = { nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, status: trade.status, settledAt: trade.settledAt, nairaPaymentMethod: trade.nairaPaymentMethod, nairaBankRef: trade.nairaBankRef, nairaBankVerified: trade.nairaBankVerified };
                                      const persistOps: Promise<unknown>[] = [];
                                      if (bankNewEntries.length > 0) {
                                        persistOps.push(fetch("/api/trades/settlements", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: bankNewEntries }) }).then(r => { if (!r.ok) throw new Error("Settlement save failed"); }));
                                      }
                                      for (const stmt of selected) {
                                        persistOps.push(persistBankTxLink(stmt.id, trade.id, bankNewEntries.find(e => e.reference === stmt.narration.slice(0, 40))?.id));
                                      }
                                      persistOps.push(fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...bankSyncData }) }).then(r => { if (!r.ok) throw new Error("Trade sync failed"); }));
                                      persistOps.push(fetch("/api/scanner/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: trade.id, ...bankSyncData }) }).then(r => { if (!r.ok) throw new Error("Scanner sync failed"); }));
                                      if (bankPayerDetails.length > 0) {
                                        setTradePayerDetails((prev) => [...prev, ...bankPayerDetails]);
                                        persistOps.push(fetch("/api/payer-details", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ details: bankPayerDetails }) }).then(r => { if (!r.ok) throw new Error("Payer details save failed"); }));
                                      }
                                      try {
                                        await Promise.all(persistOps);
                                      } catch (persistErr) {
                                        console.error("Bank picker persist failed:", persistErr);
                                        toast("Settlement saved locally but failed to sync to disk — refresh and retry", "error");
                                      }
                                      setShowBankPicker(false);
                                      setSelectedBankId(null);
                                      setSelectedTxIds(new Set());
                                      const bankLabel = treasuryAccounts.find(a => a.id === selectedBankId)?.label || "Unknown";
                                      const bankAfterSnap = { coinAmountSettled: trade.coinAmountSettled, coinStatus: trade.coinStatus, nairaAmountSettled: trade.nairaAmountSettled, nairaStatus: trade.nairaStatus, status: trade.status };
                                      postAuditEntry({ tradeId: trade.id, staffId: activeStaff.id, staffName: activeStaff.name, action: "bank_txns_applied", details: `Applied ${selected.length} bank transaction${selected.length !== 1 ? "s" : ""} — ₦${totalApplied.toLocaleString()} from ${bankLabel}`, changes: diffChanges(bankBeforeSnap, bankAfterSnap, ["coinAmountSettled", "coinStatus", "nairaAmountSettled", "nairaStatus", "status"]), channel: "dashboard", timestamp: new Date().toISOString() });
                                      onUpdateTrade(trade.id, {});
                                      toast(`${selected.length} payment${selected.length !== 1 ? "s" : ""} applied — ₦${totalApplied.toLocaleString()} settled`);
                                    }}
                                    disabled={selectedTxIds.size === 0}
                                  >
                                    Apply ({selectedTxIds.size} selected{selectedTxIds.size > 0 ? ` — ₦${selectableStmts.filter(s => selectedTxIds.has(s.id)).reduce((sum, s) => sum + s.amount, 0).toLocaleString()}` : ""})
                                  </Button>
                                </div>
                              </>
                            );
                          })()}
                          </div>
                        </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                    {/* Use Balance modal */}
                    <AnimatePresence>
                    {showBalanceModal && trade.counterpartyId && (() => {
                      const cp = counterparties.find(c => c.id === trade.counterpartyId);
                      if (!cp) return null;
                      const isNaira = showBalanceModal === "naira";
                      const symbol = isNaira ? "₦" : "";
                      const unit = isNaira ? "" : " USDT";
                      const creditAvailable = isNaira ? cp.balanceNgn : cp.balanceUsdt;
                      const remaining = isNaira
                        ? drawerNgnTotal - trade.nairaAmountSettled
                        : trade.usdtAmount - trade.coinAmountSettled;
                      const hasCredit = creditAvailable > 0;
                      const hasRemaining = remaining > 0;
                      const canApply = hasCredit && hasRemaining;
                      const applyAmt = canApply ? Math.min(creditAvailable, remaining) : 0;
                      const fmtAmt = (v: number) => `${symbol}${v.toLocaleString()}${unit}`;
                      return (
                        <>
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]" onClick={() => setShowBalanceModal(false)} />
                          <motion.div role="dialog" aria-modal="true" aria-label={`Use balance — ${isNaira ? "NGN" : "USDT"}`} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ type: "spring", stiffness: 350, damping: 22 }} className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[420px] bg-surface rounded-2xl shadow-2xl" style={{ border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-5 pt-5 pb-3">
                              <h3 className="text-sm font-bold text-foreground">Use Balance — {isNaira ? "NGN" : "USDT"}</h3>
                              <button onClick={() => setShowBalanceModal(false)} aria-label="Close" className="text-muted hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="px-5 pb-5 space-y-4">
                              <div className="rounded-lg bg-surface-2/60 p-3 space-y-2" style={{ border: "1px solid var(--border)" }}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted">Counterparty {isNaira ? "NGN" : "USDT"} credit</span>
                                  <span className={`text-[13px] font-bold tabular-nums ${hasCredit ? "text-success" : "text-muted"}`}>{fmtAmt(creditAvailable)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted">{isNaira ? "NGN" : "USDT"} remaining</span>
                                  <span className={`text-[13px] font-bold tabular-nums ${hasRemaining ? "text-foreground" : "text-muted"}`}>{fmtAmt(remaining)}</span>
                                </div>
                              </div>
                              {!hasCredit && (
                                <p className="text-[12px] text-warning leading-relaxed">
                                  <span className="font-semibold text-foreground">{cp.name}</span> has no {isNaira ? "NGN" : "USDT"} credit available.
                                </p>
                              )}
                              {hasCredit && !hasRemaining && (
                                <p className="text-[12px] text-muted leading-relaxed">
                                  {isNaira ? "NGN" : "USDT"} is already fully settled on this trade.
                                </p>
                              )}
                              {canApply && (
                                <>
                                  <p className="text-[12px] text-muted leading-relaxed">
                                    <span className="font-semibold text-foreground">{cp.name}</span> has <span className="font-semibold text-success">{fmtAmt(creditAvailable)}</span> credit.{" "}
                                    {fmtAmt(remaining)} remaining on this trade.
                                  </p>
                                  <div className="flex items-center justify-between rounded-lg bg-accent/8 px-3 py-2" style={{ border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)" }}>
                                    <span className="text-[11px] font-semibold text-accent">Amount to apply</span>
                                    <span className="text-[14px] font-bold text-accent tabular-nums">{fmtAmt(applyAmt)}</span>
                                  </div>
                                </>
                              )}
                              <div className="flex gap-3 pt-1">
                                <Button variant="ghost" size="sm" className="flex-1" onClick={() => setShowBalanceModal(false)}>{canApply ? "Cancel" : "Close"}</Button>
                                {canApply && <Button variant="primary" size="sm" className="flex-1" onClick={async () => { await applyCounterpartyCredit(isNaira ? "naira" : "coin"); setShowBalanceModal(false); }}>Apply {fmtAmt(applyAmt)}</Button>}
                              </div>
                            </div>
                          </motion.div>
                        </>
                      );
                    })()}
                    </AnimatePresence>
                    </div>
                  </motion.div>

                  <div className="space-y-3 mt-4">
                  {/* Collapsible Details */}
                  <motion.div className="bg-surface-2/40 rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }} variants={drawerItemVariants}>
                    <button onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }} className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-2/60 transition-colors duration-150">
                      <div className="flex items-center gap-2">
                        <div className="section-icon section-icon-accent"><CfClipboard /></div>
                        <span className="text-[11px] font-semibold text-foreground">Details</span>
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform duration-200 ${showDetails ? "rotate-180" : ""}`} />
                    </button>
                    {showDetails && (
                      <div className="px-4 pb-4 grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          {trade.txHash && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted w-16 flex-shrink-0">TX Hash</span>
                              <button className="btn-secondary inline-flex items-center gap-1.5 rounded px-2 py-1 transition-all" onClick={(e) => { e.stopPropagation(); copyToClipboard(trade.txHash || ""); toast("TX hash copied"); }} title={trade.txHash}>
                                <code className="text-xs text-foreground font-mono">{truncateHash(trade.txHash)}</code>
                                <Copy className="w-3 h-3 text-muted/60" />
                              </button>
                              <motion.button {...iconSpring} className="p-1.5 rounded hover:bg-surface-3 transition-colors duration-150 text-muted hover:text-foreground" onClick={(e) => { e.stopPropagation(); const explorers: Record<string, string> = { tron: "https://tronscan.org/#/transaction/", bsc: "https://bscscan.com/tx/", ethereum: "https://etherscan.io/tx/" }; window.open(`${explorers[trade.chain]}${trade.txHash}`, "_blank"); }}>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </motion.button>
                            </div>
                          )}
                          <div className="flex items-center gap-2"><span className="text-xs text-muted w-16">Chain</span><span className="text-xs text-foreground uppercase font-semibold">{trade.chain}</span></div>
                          <div className="flex items-center gap-2"><span className="text-xs text-muted w-16">Verified</span>{trade.usdtVerified ? <span className="text-xs text-success font-semibold">Yes</span> : <span className="text-xs text-warning font-semibold">No</span>}</div>
                        </div>
                        <div className="space-y-2">
                          {trade.nairaBankRef && <div className="flex items-center gap-2"><span className="text-xs text-muted w-16">Bank Ref</span><button className="text-xs font-mono text-foreground" onClick={(e) => { e.stopPropagation(); copyToClipboard(trade.nairaBankRef!); toast("Bank ref copied"); }}>{trade.nairaBankRef}</button></div>}
                          <div className="flex items-center gap-2"><span className="text-xs text-muted w-16">Method</span><span className="text-xs text-foreground">{trade.nairaPaymentMethod?.replace(/_/g, " ") || "N/A"}</span></div>
                          {trade.settlementTimeSecs && <div className="flex items-center gap-2"><span className="text-xs text-muted w-16">Settled in</span><span className="text-xs text-success font-semibold tabular-nums">{Math.floor(trade.settlementTimeSecs / 60)}m {trade.settlementTimeSecs % 60}s</span></div>}
                        </div>
                      </div>
                    )}
                  </motion.div>
          
                  {/* Activity Log */}
                  {hasPermission(activeStaff, "view_audit_log") && (
                    <motion.div className="bg-surface-2/40 rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }} variants={drawerItemVariants}>
                      <button onClick={(e) => { e.stopPropagation(); setShowActivityLog(!showActivityLog); }} className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-2/60 transition-colors duration-150">
                        <div className="flex items-center gap-2">
                          <div className="section-icon section-icon-accent"><CfActivityLog /></div>
                          <span className="text-[11px] font-semibold text-foreground">Activity Log</span>
                        </div>
                        <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform duration-200 ${showActivityLog ? "rotate-180" : ""}`} />
                      </button>
                      {showActivityLog && (() => {
                        const tradeAudit = [...auditEntries].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                        return tradeAudit.length > 0 ? (
                          <div className="px-4 pb-4 max-h-56 overflow-auto space-y-1">
                            {tradeAudit.map(entry => (
                              <div key={entry.id} className="flex items-start gap-2 bg-surface-3/30 rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-surface-3/50">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center mt-0.5">
                                  <span className="text-[9px] font-bold text-accent">{entry.staffName.split(" ").map(n => n[0]).join("")}</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-semibold text-foreground">{entry.staffName}</span>
                                    <span className="text-[10px] text-muted">{entry.details}</span>
                                  </div>
                                  {entry.changes && entry.changes.length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                      {entry.changes.map((c, ci) => (
                                        <div key={ci} className="text-[10px] font-mono text-muted">
                                          <span className="text-foreground/70">{c.field}</span>{": "}
                                          <span className="text-red-400">{String(c.from ?? "null")}</span>
                                          {" → "}
                                          <span className="text-green-400">{String(c.to ?? "null")}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-muted tabular-nums">{timeAgo(entry.timestamp)}</span>
                                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${entry.channel === "dashboard" ? "bg-accent/10 text-accent" : entry.channel === "whatsapp" ? "bg-success/10 text-success" : entry.channel === "telegram" ? "bg-blue-500/10 text-blue-400" : "bg-surface-3 text-muted"}`}>{entry.channel}</span>
                                    <span className="text-[9px] font-mono text-muted/50" title={`seq: ${entry.seq} · hash: ${entry.hash}`}>#{entry.seq} · {entry.hash?.slice(0, 8)}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="px-4 pb-4 text-[11px] text-muted">No activity recorded for this trade</p>
                        );
                      })()}
                    </motion.div>
                  )}

                  {/* Scanner Suggestions */}
                  {(() => {
                    const relevantTxs = scannerTransactions.filter(
                      (stx) =>
                        !stx.matchedTradeId &&
                        stx.matchSuggestions?.some((s) => s.tradeId === trade.id)
                    );
                    if (relevantTxs.length === 0) return null;
                    return (
                      <motion.div className="bg-surface-2/40 rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }} variants={drawerItemVariants}>
                        <div className="flex items-center gap-2 px-4 py-3">
                          <div className="section-icon section-icon-accent"><CfScanner /></div>
                          <span className="text-[11px] font-bold text-foreground uppercase tracking-wider">Scanner Suggestions</span>
                          <span className="text-[10px] text-accent font-bold bg-accent/10 px-1.5 py-0.5 rounded-full">{relevantTxs.length}</span>
                        </div>
                        <div className="px-4 pb-4 space-y-1.5">
                          {relevantTxs.map((stx) => {
                            const suggestion = stx.matchSuggestions?.find((s) => s.tradeId === trade.id);
                            const chainBadge: Record<string, string> = {
                              tron: "text-red-400 bg-red-500/10 border-red-500/20",
                              bsc: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
                              ethereum: "text-blue-400 bg-blue-500/10 border-blue-500/20",
                            };
                            return (
                              <div key={stx.id} className="flex items-center justify-between bg-surface-3/50 rounded-lg px-3 py-2 group/scanner transition-colors duration-150 hover:bg-surface-3/60">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${chainBadge[stx.chain]}`}>{stx.chain}</span>
                                  <button
                                    className="text-[11px] text-foreground font-mono hover:text-accent transition-colors"
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(stx.txHash); toast("TX hash copied"); }}
                                    title={stx.txHash}
                                  >
                                    {truncateHash(stx.txHash, 4)}
                                    <Copy className="w-2.5 h-2.5 inline ml-1 opacity-50" />
                                  </button>
                                  <span className="text-[11px] font-semibold text-foreground tabular-nums">
                                    {stx.amount.toLocaleString("en-US", { maximumFractionDigits: 2 })} USDT
                                  </span>
                                  <span className="text-[10px] text-muted">from {truncateHash(stx.counterpartyAddress, 4)}</span>
                                  {suggestion && (
                                    <div className="flex items-center gap-1.5">
                                      <div className="w-16 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                                        <div className="h-full bg-accent rounded-full" style={{ width: `${Math.min(suggestion.score, 100)}%` }} />
                                      </div>
                                      <span className="text-[10px] font-bold text-accent tabular-nums">{suggestion.score}%</span>
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await onScannerMatch(stx.id, trade.id);
                                    onUpdateTrade(trade.id, {});
                                  }}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors opacity-0 group-hover/scanner:opacity-100"
                                >
                                  <Link2 className="w-3 h-3" />Link
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    );
                  })()}
          
                  {/* Timeline */}
                  <motion.div className="bg-surface-2/40 rounded-xl px-4 py-3" style={{ border: "1px solid var(--border)" }} variants={drawerItemVariants}>
                    <div className="flex items-center gap-6 text-xs">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-muted" />
                        <span className="text-muted">Created</span>
                        <span className="font-semibold text-foreground tabular-nums">{formatDateTime(trade.createdAt)}</span>
                      </div>
                      {trade.settledAt ? (
                        <>
                          <span className="text-muted/40">&#8594;</span>
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                            <span className="text-muted">Settled</span>
                            <span className="font-semibold text-foreground tabular-nums">{formatDateTime(trade.settledAt)}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="text-muted/40">&#8594;</span>
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-warning animate-pulse" />
                            <span className="text-muted">Not settled</span>
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                  </div>
          </motion.div>
        </div>
        {/* Review Card — centered modal */}
        <AnimatePresence>
          {showReviewCard && !reviewSubmitted && trade.status === "settled" && trade.counterpartyId && !reviewSuccess && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]"
                onClick={() => setShowReviewCard(false)}
              />
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Trade review"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 350, damping: 22 }}
                className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[480px] bg-surface rounded-2xl shadow-2xl max-h-[90vh] overflow-auto"
                style={{ border: "1px solid var(--border)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 pt-5 pb-4 space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  <span className="text-sm font-semibold text-foreground">
                    Trade settled
                    <span className="text-muted font-normal">
                      {" "}&mdash; {trade.usdtAmount.toLocaleString()} USDT with {trade.counterpartyName}
                      {trade.settlementTimeSecs != null && (
                        <> &middot; {Math.floor(trade.settlementTimeSecs / 60)}m {trade.settlementTimeSecs % 60}s</>
                      )}
                    </span>
                  </span>
                </div>
                <button
                  onClick={() => setShowReviewCard(false)}
                  aria-label="Close"
                  className="text-muted hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Positive tags */}
              <div className="mb-2">
                <span className="text-[10px] text-muted uppercase tracking-wider font-semibold mb-1.5 block">Positive</span>
                <div className="flex flex-wrap gap-1.5">
                  {POSITIVE_TAGS.map((tag) => {
                    const isSelected = selectedTags.has(tag.id);
                    const isSuggested = smartSuggestions.has(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => {
                          setSelectedTags((prev) => {
                            const next = new Set(prev);
                            if (next.has(tag.id)) next.delete(tag.id);
                            else next.add(tag.id);
                            return next;
                          });
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          isSelected
                            ? "bg-success/20 text-success border border-success/40"
                            : "bg-surface-2 text-muted border border-border hover:border-success/30 hover:text-foreground"
                        } ${isSuggested && !isSelected ? "ring-1 ring-success/40 ring-offset-1 ring-offset-surface" : ""}`}
                      >
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Negative tags */}
              <div className="mb-3">
                <span className="text-[10px] text-muted uppercase tracking-wider font-semibold mb-1.5 block">Negative</span>
                <div className="flex flex-wrap gap-1.5">
                  {NEGATIVE_TAGS.map((tag) => {
                    const isSelected = selectedTags.has(tag.id);
                    const isSuggested = smartSuggestions.has(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => {
                          setSelectedTags((prev) => {
                            const next = new Set(prev);
                            if (next.has(tag.id)) next.delete(tag.id);
                            else next.add(tag.id);
                            return next;
                          });
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          isSelected
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            : "bg-surface-2 text-muted border border-border hover:border-amber-500/30 hover:text-foreground"
                        } ${isSuggested && !isSelected ? "ring-1 ring-amber-500/40 ring-offset-1 ring-offset-surface" : ""}`}
                      >
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Note toggle + textarea */}
              <div className="mb-3">
                {!showNoteField ? (
                  <button
                    onClick={() => setShowNoteField(true)}
                    className="text-xs text-muted hover:text-foreground transition-colors"
                  >
                    + Add a note
                  </button>
                ) : (
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Optional note..."
                    rows={2}
                    className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted/50 resize-none focus:outline-none focus:ring-1 focus:ring-accent/40"
                  />
                )}
              </div>

              {/* Anonymous + Submit */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={anonymous}
                    onChange={(e) => setAnonymous(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-xs text-muted">Post anonymously</span>
                </label>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  disabled={selectedTags.size === 0 || submittingReview}
                  onClick={handleSubmitReview}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-accent text-zinc-950 hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submittingReview ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                  Submit Review
                </motion.button>
              </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Review Success Overlay */}
        <AnimatePresence>
          {reviewSuccess && (
            <motion.div
              variants={reviewSuccessVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute inset-0 z-20 flex items-center justify-center bg-surface/90 backdrop-blur-sm"
              onAnimationComplete={() => playSuccessSound()}
            >
              <div className="flex flex-col items-center gap-3">
                <motion.div
                  initial={{ scale: 0, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  className="w-14 h-14 rounded-full bg-success/15 flex items-center justify-center"
                >
                  <CheckCircle2 className="w-7 h-7 text-success" />
                </motion.div>
                <span className="text-sm font-semibold text-foreground">Review submitted</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sticky footer actions */}
        <div className="sticky bottom-0 z-10 px-5 py-3 bg-surface border-t border-border backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <VerificationBadge level={trade.verificationLevel} />
            {trade.senderConfirmed && <span className="text-[10px] font-semibold text-success bg-success/10 px-1.5 py-0.5 rounded">Sender confirmed</span>}
            {trade.receiverConfirmed && <span className="text-[10px] font-semibold text-success bg-success/10 px-1.5 py-0.5 rounded">Receiver confirmed</span>}
            {tradeCp && (
              <>
                <span className="text-muted/30">|</span>
                {tradeCp.isBlocked ? (
                  <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); setShowTradeUnblockConfirm(true); }}>
                    <Ban className="w-3 h-3" />Unblock
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowTradeBlockConfirm(true); }}>
                    <Ban className="w-3 h-3" />Block
                  </Button>
                )}
                {!tradeCp.isBlacklisted ? (
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowTradeBlacklistConfirm(true); }}>
                    Blacklist
                  </Button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-danger bg-danger/10 px-1.5 py-0.5 rounded border border-danger/20">
                    <Ban className="w-2.5 h-2.5" />Blacklisted
                  </span>
                )}
                {tradeCp.isBlocked && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-danger bg-danger/10 px-1.5 py-0.5 rounded border border-danger/20" title={tradeCp.blockReason || "Blocked"}>
                    <Ban className="w-2.5 h-2.5" />Blocked
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {trade.status === "settled" && reviewExists === true && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold text-success bg-success/10 border border-success/20">
                <CheckCircle2 className="w-3 h-3" />
                Reviewed
              </span>
            )}
            {trade.status === "settled" && reviewExists === false && !showReviewCard && (
              <motion.div whileTap={{ scale: 0.97 }}>
                <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setShowReviewCard(true); setReviewSubmitted(false); setSelectedTags(new Set()); setReviewNote(""); setReviewSuccess(false); }}>
                  <Star className="w-3 h-3" />Leave Review
                </Button>
              </motion.div>
            )}
            <motion.div whileTap={{ scale: 0.97 }}><Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); generateForensicReport(trade); toast("Generating forensic report...", "success"); }}><FileText className="w-3 h-3" />Forensic Report</Button></motion.div>
            <motion.div whileTap={{ scale: 0.97 }}><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toast("Trade editing coming soon", "info"); }}><Pencil className="w-3 h-3" />Edit</Button></motion.div>
            {hasPermission(activeStaff, "dispute") && <motion.div whileTap={{ scale: 0.97 }}><Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); setShowDisputeModal(true); setDisputeReason(""); setDisputeDescription(""); setDisputePriority("medium"); setDisputeEvidence([]); }}><AlertTriangle className="w-3 h-3" />Dispute</Button></motion.div>}
          </div>
        </div>
      </motion.div>
      {/* Remove payment confirmation modal */}
      <AnimatePresence>
        {confirmRemove && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]"
              onClick={() => setConfirmRemove(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", stiffness: 350, damping: 22 }}
              className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[420px] bg-surface rounded-2xl p-8 shadow-2xl"
              style={{ border: "1px solid var(--danger)" }}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  className="w-12 h-12 rounded-full bg-danger/15 flex items-center justify-center"
                >
                  <Unlink className="w-6 h-6 text-danger" />
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-base font-bold text-foreground"
                >
                  Remove Payment
                </motion.h3>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <p className="text-[13px] text-muted leading-relaxed">
                    Remove <span className="font-semibold text-foreground">{confirmRemove.side === "coin" ? confirmRemove.amount.toLocaleString() + " USDT" : "₦" + confirmRemove.amount.toLocaleString()}</span> {confirmRemove.paymentMethod.replace(/_/g, " ")} via <span className="font-semibold text-foreground">{confirmRemove.sourceDestination}</span>?
                    {confirmRemove.isRefund && <span className="block mt-1.5 text-warning font-semibold">This is a refund entry.</span>}
                  </p>
                  <p className="text-[11px] text-muted mt-2">This action will be logged.</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex gap-3 w-full pt-2"
                >
                  <button
                    onClick={() => setConfirmRemove(null)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-muted hover:text-foreground hover:bg-surface-2 transition-colors"
                    style={{ border: "1px solid var(--border)" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { handleRemoveEntry(confirmRemove); setConfirmRemove(null); }}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-danger text-white hover:bg-danger/90 transition-colors"
                  >
                    Yes, remove
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Resolution modal — 3 tabs: Bank (from bank + add payment), Crypto (scanner), Credit */}
      <AnimatePresence>
        {showResolutionPanel && (() => {
          const isNairaSide = showResolutionPanel.side === "naira";
          const isOverpayment = showResolutionPanel.type === "overpayment";
          // Determine if this is a "Refund" (you owe them) or "Return" (they owe you)
          // Buy: you send USDT, they send NGN. Coin overpaid = you sent too much = they owe you (return). Naira overpaid = they sent too much = you owe them (refund).
          // Sell: you send NGN, they send USDT. Coin overpaid = they sent too much = you owe them (refund). Naira overpaid = you sent too much = they owe you (return).
          const isBuyLike = trade.direction === "buy" || trade.direction === "borrow";
          const theyOweYou = isOverpayment && ((isBuyLike && !isNairaSide) || (!isBuyLike && isNairaSide));
          const actionWord = isOverpayment ? (theyOweYou ? "Return" : "Refund") : "";
          const resNgnTotal = getTradeNgnTotal(trade);
          const excessNgn = resNgnTotal ? trade.nairaAmountSettled - resNgnTotal : 0;
          const excessUsdt = trade.coinAmountSettled - trade.usdtAmount;
          const shortNgn = resNgnTotal ? resNgnTotal - trade.nairaAmountSettled : 0;
          const shortUsdt = trade.usdtAmount - trade.coinAmountSettled;
          const cp = trade.counterpartyId ? counterparties.find(c => c.id === trade.counterpartyId) : null;
          const resolveAmt = isOverpayment
            ? (isNairaSide ? excessNgn : excessUsdt)
            : (isNairaSide ? shortNgn : shortUsdt);
          const closeResolution = () => { setShowResolutionPanel(null); setResolutionTab(null); setResolutionBankId(null); setResolutionSelectedTxIds(new Set()); setCreditSubAction(null); setCreditTargetTradeId(null); setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAddMethod("bank_transfer"); setResPayerName(""); setResPayerAccount(""); setResPayerBank(""); setResNotes(""); setResCombineSearch(""); setResCombineFilterDir("all"); setResCombineSortAsc(false); setResCrossRate(""); setResCrossRateMode("trade"); setResDuplicateWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null); setResAccountId(null); setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true); setShowResSaveConfirm(false); setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null); };

          // Smart tab ordering based on context
          const tabs: { id: "from_bank" | "from_scanner" | "use_credit"; label: string; icon: typeof Building2 }[] = [];
          const hasCredit = cp && ((isNairaSide && cp.balanceNgn > 0) || (!isNairaSide && cp.balanceUsdt > 0));
          // Build tabs in smart order
          if (hasCredit) {
            // Credit first when counterparty has available credit
            tabs.push({ id: "use_credit", label: "Credit", icon: Wallet });
          }
          if (isNairaSide) {
            // NGN: Bank tab first (or after credit)
            tabs.push({ id: "from_bank", label: "Bank", icon: Building2 });
            tabs.push({ id: "from_scanner", label: "Crypto", icon: Layers });
          } else {
            // USDT: Crypto first (or after credit)
            tabs.push({ id: "from_scanner", label: "Crypto", icon: Layers });
            tabs.push({ id: "from_bank", label: "Bank", icon: Building2 });
          }
          if (cp && !hasCredit) {
            // Credit last if no balance available
            tabs.push({ id: "use_credit", label: "Credit", icon: Wallet });
          }

          return (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-[3px]"
                onClick={closeResolution}
              />
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Resolve settlement"
                initial={{ opacity: 0, scale: 0.92, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                transition={{ type: "spring", stiffness: 380, damping: 26 }}
                className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[520px] max-h-[85vh] bg-surface rounded-2xl flex flex-col overflow-hidden"
                style={{ border: "1px solid var(--border)", boxShadow: "0 25px 60px -12px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="relative px-6 pt-5 pb-4" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--surface-2) 60%, transparent) 0%, transparent 100%)" }}>
                  <button onClick={closeResolution} aria-label="Close" className="btn-icon absolute top-4 right-4 p-1.5 rounded-lg text-muted hover:text-foreground transition-all"><X className="w-4 h-4" /></button>
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.05 }}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${isOverpayment ? "bg-danger/12" : "bg-warning/12"}`}
                      style={{ border: `1px solid color-mix(in srgb, var(--${isOverpayment ? "danger" : "warning"}) 25%, transparent)` }}
                    >
                      <AlertTriangle className={`w-5 h-5 ${isOverpayment ? "text-danger" : "text-warning"}`} />
                    </motion.div>
                    <div>
                      <h3 className="text-[15px] font-bold text-foreground tracking-tight">
                        {isOverpayment ? `Resolve Overpayment — ${actionWord}` : "Resolve Shortfall"}
                      </h3>
                      <p className="text-[12px] text-muted mt-0.5">
                        Trade #{trade.id} &middot; <span className={`font-semibold tabular-nums ${isOverpayment ? "text-danger" : "text-warning"}`}>{isNairaSide ? `₦${resolveAmt.toLocaleString()}` : `${resolveAmt.toLocaleString()} USDT`}</span> {isOverpayment ? "excess" : "short"}
                      </p>
                    </div>
                  </div>

                  {/* Smart tab bar */}
                  <div className="flex gap-1 mt-4" role="tablist">
                    {tabs.map(tab => {
                      const Icon = tab.icon;
                      const isActive = resolutionTab === tab.id;
                      return (
                        <button key={tab.id} role="tab" aria-selected={isActive} onClick={() => { setResolutionTab(tab.id); setResolutionBankId(null); setResolutionSelectedTxIds(new Set()); if (tab.id === "use_credit") { setCreditSubAction(null); setCreditTargetTradeId(null); } if (tab.id === "from_scanner") { setResCombineSearch(""); setResCombineFilterDir("all"); } }} className={`flex-1 flex items-center justify-center gap-1.5 text-[12px] font-semibold py-2 rounded-lg transition-all ${isActive ? "bg-foreground text-surface btn-tab-full-active" : "text-muted hover:text-foreground hover:bg-surface-3/60"}`}>
                          <Icon className="w-3.5 h-3.5" />{tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto px-6 py-5">

                  {/* Bank tab: From Bank picker + "or enter manually" + Add Payment form */}
                  {resolutionTab === "from_bank" && (() => {
                    // Cross-currency: when USDT side excess, convert to NGN equivalent for bank comparison
                    const isCrossCurrency = !isNairaSide;
                    const crossRate = resCrossRateMode === "trade" ? (trade.rate || 0) : (parseFloat(resCrossRate.replace(/,/g, "")) || 0);
                    const ngnEquivalent = isCrossCurrency && crossRate > 0 ? resolveAmt * crossRate : 0;
                    const ngnRemaining = isCrossCurrency ? ngnEquivalent : (isOverpayment ? resolveAmt : (resNgnTotal - trade.nairaAmountSettled));
                    const parsedResAmt = parseFloat(resAddAmount.replace(/,/g, "")) || 0;
                    const isFormOverpay = parsedResAmt > 0 && ngnRemaining > 0 && parsedResAmt > ngnRemaining;
                    return (
                      <div className="space-y-4">
                        {/* Cross-currency rate picker — shown when resolving USDT excess via bank */}
                        {isCrossCurrency && (
                          <div className="rounded-xl p-3.5 space-y-2.5" style={{ background: "color-mix(in srgb, var(--accent) 4%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)" }}>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Cross-Currency Conversion</span>
                              <span className="text-[10px] text-muted">Resolving {resolveAmt.toLocaleString()} USDT via NGN</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <button onClick={() => { setResCrossRateMode("trade"); setResCrossRate(""); }} className={`text-[10px] font-semibold px-2 py-0.5 rounded transition-all cursor-pointer ${resCrossRateMode === "trade" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}>
                                    Trade rate{trade.rate ? ` (₦${trade.rate.toLocaleString()})` : ""}
                                  </button>
                                  <button onClick={() => { setResCrossRateMode("custom"); setResCrossRate(trade.rate ? trade.rate.toLocaleString() : ""); }} className={`text-[10px] font-semibold px-2 py-0.5 rounded transition-all cursor-pointer ${resCrossRateMode === "custom" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}>
                                    Custom rate
                                  </button>
                                </div>
                                {resCrossRateMode === "custom" && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] text-muted">₦</span>
                                    <input type="text" inputMode="numeric" placeholder="Rate" value={resCrossRate} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ""); const parts = raw.split("."); const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","); setResCrossRate(parts.length > 1 ? `${whole}.${parts[1]}` : whole); }} className="w-28 bg-surface-2 rounded px-2 py-1 text-[12px] text-foreground tabular-nums placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                                    <span className="text-[10px] text-muted">per USDT</span>
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-muted">NGN Equivalent</p>
                                <p className={`text-[14px] font-bold tabular-nums ${crossRate > 0 ? "text-accent" : "text-muted/40"}`}>{crossRate > 0 ? `₦${ngnEquivalent.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}</p>
                              </div>
                            </div>
                            {crossRate <= 0 && <p className="text-[10px] text-warning font-semibold">Set a rate to continue</p>}
                          </div>
                        )}

                        {/* Phase 1: Bank picker — select from bank statements */}
                        <div className="space-y-3">
                          <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Select from Bank Statement</p>
                          {resolutionBankId === null ? (
                            <div className="flex flex-wrap gap-2">
                              {treasuryAccounts.filter(a => a.type === "bank").map(acct => {
                                // Overpayment refund: buy → credit (they return excess), sell → debit (you return excess)
                                // Underpayment collection: buy → credit (they pay remainder), sell → debit (you pay remainder)... no:
                                // Actually: for resolution, the direction is about who sends money to whom.
                                // Buy overpayment: you overpaid → counterparty refunds you → credit (money IN)
                                // Sell overpayment: they overpaid → you refund them → debit (money OUT)
                                // Buy underpayment: they owe naira → they pay you → credit (money IN)
                                // Sell underpayment: you owe naira → you pay them → debit (money OUT)
                                const txType = isBuyLike ? "credit" : "debit";
                                const availableCount = monoBankStatements.filter(s => s.accountId === acct.id && s.type === txType && !s.linkedTradeId).length;
                                return (
                                  <button key={acct.id} onClick={() => { setResolutionBankId(acct.id); setResolutionSelectedTxIds(new Set()); }} className="flex items-center gap-2 bg-surface-2 hover:bg-surface-3 rounded-lg px-3 py-2 transition-colors" style={{ border: "1px solid var(--border)" }}>
                                    <BankIcon bankName={acct.bankName} size="sm" />
                                    <div className="text-left">
                                      <p className="text-[11px] font-semibold text-foreground">{acct.label}</p>
                                      <p className="text-[10px] text-muted">****{acct.accountNumber?.slice(-4)}</p>
                                    </div>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${availableCount > 0 ? "bg-success/15 text-success" : "bg-surface-3 text-muted/50"}`}>{availableCount}</span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (() => {
                            const txType = isBuyLike ? "credit" : "debit";
                            const bankAcct = treasuryAccounts.find(a => a.id === resolutionBankId)!;
                            const allStmts = monoBankStatements.filter(s => s.accountId === resolutionBankId && s.type === txType);
                            const selectableStmts = allStmts.filter(s => !s.linkedTradeId);
                            const selectedTotal = selectableStmts.filter(s => resolutionSelectedTxIds.has(s.id)).reduce((sum, s) => sum + s.amount, 0);
                            // Use ngnRemaining from outer scope — already handles cross-currency conversion
                            const remaining = ngnRemaining;
                            const isOverpay = selectedTotal > remaining && remaining > 0 && resolutionSelectedTxIds.size > 0;
                            const allSelectableSelected = selectableStmts.length > 0 && selectableStmts.every(s => resolutionSelectedTxIds.has(s.id));
                            return (
                              <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => { setResolutionBankId(null); setResolutionSelectedTxIds(new Set()); }} className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors"><ArrowLeft className="w-3 h-3" />Change bank</button>
                                    <span className="text-muted/30">|</span>
                                    <BankIcon bankName={bankAcct.bankName} size="sm" />
                                    <p className="text-[11px] font-bold text-foreground">{bankAcct.label}</p>
                                  </div>
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${txType === "credit" ? "bg-success/15 text-success" : "bg-danger/15 text-danger"}`}>{txType === "credit" ? "↓ Credits" : "↑ Debits"}</span>
                                </div>
                                <div className={`flex items-center justify-between rounded px-2.5 py-1.5 ${isOverpay ? "bg-warning/10" : "bg-surface-2"}`} style={{ border: `1px solid ${isOverpay ? "var(--warning)" : "var(--border)"}` }}>
                                  <span className={`text-[11px] font-semibold tabular-nums ${isOverpay ? "text-warning" : "text-foreground"}`}>Selected: ₦{selectedTotal.toLocaleString()}</span>
                                  <span className="text-[10px] text-muted tabular-nums">{resolutionSelectedTxIds.size} of {selectableStmts.length} &middot; {remaining > 0 ? `₦${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"} {isOverpayment ? "excess" : "remaining"}{isCrossCurrency ? ` (${resolveAmt.toLocaleString()} USDT)` : ""}</span>
                                </div>
                                {selectableStmts.length > 0 && (
                                  <button onClick={() => setResolutionSelectedTxIds(allSelectableSelected ? new Set() : new Set(selectableStmts.map(s => s.id)))} className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors">
                                    <CheckSquare className="w-3 h-3" />{allSelectableSelected ? "Deselect All" : "Select All"}
                                  </button>
                                )}
                                <div className="max-h-40 overflow-auto space-y-0.5">
                                  {allStmts.length === 0 ? (
                                    <p className="text-[11px] text-muted py-2">No {txType} transactions</p>
                                  ) : (
                                    allStmts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(stmt => {
                                      const isLinked = !!stmt.linkedTradeId;
                                      const isSelected = resolutionSelectedTxIds.has(stmt.id);
                                      return (
                                        <button key={stmt.id} disabled={isLinked} onClick={() => { const next = new Set(resolutionSelectedTxIds); if (next.has(stmt.id)) next.delete(stmt.id); else next.add(stmt.id); setResolutionSelectedTxIds(next); }} className={`btn-select-item w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-all ${isLinked ? "opacity-40 cursor-not-allowed" : isSelected ? "bg-accent/10" : "hover:bg-surface-2"}`} data-selected={isSelected && !isLinked}>
                                          <CheckSquare className={`w-3 h-3 flex-shrink-0 ${isSelected && !isLinked ? "text-accent" : "text-muted/30"}`} />
                                          <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(stmt.date)}</span>
                                          <span className="text-[11px] text-foreground truncate flex-1 min-w-0">{stmt.narration.slice(0, 35)}</span>
                                          <span className={`text-[11px] font-semibold tabular-nums flex-shrink-0 ${stmt.type === "credit" ? "text-success" : "text-danger"}`}>{stmt.type === "credit" ? "+" : "−"}₦{stmt.amount.toLocaleString()}</span>
                                          {isLinked && <span className="text-[9px] font-bold text-muted bg-surface-3 px-1 py-0.5 rounded flex-shrink-0">#{stmt.linkedTradeId}</span>}
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                                {resolutionSelectedTxIds.size > 0 && (
                                  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-accent/8" style={{ border: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)" }}>
                                    <span className="text-[11px] font-semibold text-accent tabular-nums">{resolutionSelectedTxIds.size} selected &middot; ₦{selectedTotal.toLocaleString()}{isCrossCurrency && crossRate > 0 ? ` (${(selectedTotal / crossRate).toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT)` : ""}</span>
                                    <Button variant="primary" size="sm" disabled={isCrossCurrency && crossRate <= 0} onClick={handleRefundFromTransaction}>{isOverpayment ? `Apply ${actionWord}` : "Apply"}</Button>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Collapsible Manual Entry */}
                        <div className="flex items-center gap-2">
                          <button onClick={() => setResManualExpanded(v => !v)} className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-3/30 hover:bg-surface-3/50 transition-colors" style={{ border: "1px solid var(--border)" }}>
                            <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${resManualExpanded ? "rotate-0" : "-rotate-90"}`} />
                            <span className="text-[11px] font-semibold text-muted uppercase tracking-wider">Enter Manually</span>
                            <div className="flex-1" />
                            {resTradeAttachments.length > 0 && <span className="text-[9px] font-bold text-success bg-success/10 px-1.5 py-0.5 rounded-full">{resTradeAttachments.length} screenshot{resTradeAttachments.length > 1 ? "s" : ""}</span>}
                          </button>
                          <button
                            onClick={() => {
                              setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null);
                              setResDuplicateWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null);
                              setResPayerName(""); setResPayerAccount(""); setResPayerBank("");
                              setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAccountId(null);
                              setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true); setResNotes("");
                            }}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted hover:text-accent hover:bg-accent/10 transition-all bg-surface-3/30"
                            style={{ border: "1px solid var(--border)" }}
                            title="Reset form"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {resManualExpanded && <div className="bg-surface-3/30 rounded-lg p-3 space-y-3" style={{ border: "1px solid var(--border)" }}>
                          {/* Screenshot Upload */}
                          <div
                            className={`group flex items-center gap-3 w-full rounded-lg px-3.5 py-3.5 cursor-pointer transition-all duration-200 ${resTradeAttachments.length > 0 ? "bg-success/5 hover:bg-success/8" : "bg-surface-2/40 hover:bg-surface-2/70"} ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                            style={{ border: `1px solid ${resTradeAttachments.length > 0 ? "color-mix(in srgb, var(--success) 25%, transparent)" : "var(--border)"}` }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const file = e.dataTransfer.files?.[0]; if (file) handleResScreenshotUpload(file); }}
                            onClick={() => { resScreenshotInputRef.current?.click(); }}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${resTradeAttachments.length > 0 ? "bg-success/10" : "bg-accent/10"}`}>
                              {uploading ? <Loader2 className="w-4 h-4 text-accent animate-spin" /> : resTradeAttachments.length > 0 ? <CheckCircle2 className="w-4 h-4 text-success" /> : <Upload className="w-4 h-4 text-accent" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-semibold text-foreground">{uploading ? ((resTradeAttachments[0] as Record<string, unknown>)?.isPdf ? "Validating PDF..." : "Uploading...") : resTradeAttachments.length > 0 ? ((resTradeAttachments[0] as Record<string, unknown>)?.isPdf ? "PDF receipt attached" : `${resTradeAttachments.length} receipt${resTradeAttachments.length > 1 ? "s" : ""} attached`) : <>Drop receipt or <span className="text-accent">browse</span></>}</p>
                              <p className="text-[10px] text-muted mt-0.5">{resTradeAttachments.length > 0 ? ((resTradeAttachments[0] as Record<string, unknown>)?.isPdf ? "PDF attached · fill in details below" : "Click to replace · hover thumbnail to preview") : "Screenshot or PDF receipt · auto-extracts from images"}</p>
                            </div>
                            {/* Thumbnails inline when attached, format pills when empty */}
                            {resTradeAttachments.length > 0 ? (
                              <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                {resTradeAttachments.slice(0, 3).map((att) => (
                                  <div key={att.id} className="relative group/att w-8 h-8 rounded overflow-hidden bg-surface-2 cursor-pointer flex-shrink-0" style={{ border: "1px solid var(--border)" }} onClick={(e) => { e.stopPropagation(); if (!(att as Record<string, unknown>).isPdf) setLightboxUrl(att.url); }}>
                                    {(att as Record<string, unknown>).isPdf ? (
                                      <div className="w-full h-full flex items-center justify-center bg-danger/10">
                                        <FileText className="w-4 h-4 text-danger" />
                                      </div>
                                    ) : (
                                      <img src={att.url} alt={att.originalName} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                    )}
                                    {!(att as Record<string, unknown>).isPdf && (
                                      <div className="absolute inset-0 bg-black/0 group-hover/att:bg-black/30 transition-all flex items-center justify-center">
                                        <Eye className="w-3 h-3 text-white opacity-0 group-hover/att:opacity-100 transition-opacity" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {resTradeAttachments.length > 3 && (
                                  <span className="text-[9px] font-semibold text-muted cursor-pointer" onClick={(e) => { e.stopPropagation(); setLightboxUrl(resTradeAttachments[0].url); }}>+{resTradeAttachments.length - 3}</span>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null); setResDuplicateWarning(null); setResMatchWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null); setResPayerName(""); setResPayerAccount(""); setResPayerBank(""); setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAccountId(null); setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true); }} className="w-6 h-6 rounded-md flex items-center justify-center text-muted hover:text-danger hover:bg-danger/10 transition-all" title="Clear all"><X className="w-3 h-3" /></button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 shrink-0">
                                {["JPG", "PNG", "PDF"].map(f => <span key={f} className="text-[8px] font-medium text-muted bg-surface-3 px-1.5 py-0.5 rounded">{f}</span>)}
                              </div>
                            )}
                            <input ref={resScreenshotInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleResScreenshotUpload(file); e.target.value = ""; }} />
                          </div>
                          {/* OCR status */}
                          {ocrLoading && <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-accent/8"><Loader2 className="w-3 h-3 text-accent animate-spin" /><span className="text-[10px] text-accent font-semibold">Extracting details from screenshot...</span></div>}
                          {resOcrResult && !ocrLoading && (() => {
                            const filled = [resOcrResult.amount, resOcrResult.senderName, resOcrResult.senderAccountNumber, resOcrResult.sessionId, resOcrResult.reference, resOcrResult.transactionDate].filter(Boolean).length;
                            return <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded ${resOcrResult.confidence >= 80 ? "bg-success/10" : resOcrResult.confidence >= 40 ? "bg-warning/10" : "bg-danger/10"}`}><span className={`text-[10px] font-semibold ${resOcrResult.confidence >= 80 ? "text-success" : resOcrResult.confidence >= 40 ? "text-warning" : "text-danger"}`}>AI extracted {filled}/6 fields — verify below</span></div>;
                          })()}
                          {/* Duplicate + match warning overlays rendered via portal at component bottom */}
                          {/* Bank detection feedback */}
                          <AnimatePresence mode="wait">
                            {resBankDetectedConfirm && !resBankMismatchWarning && (
                              <motion.div key="res-bank-confirm" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ type: "spring", stiffness: 400, damping: 28 }} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-success/8" style={{ border: "1px solid rgba(34,197,94,0.2)" }}>
                                <CheckCircle2 className="w-3.5 h-3.5 text-success shrink-0" />
                                <p className="flex-1 text-[11px] text-success font-semibold">Detected as {resBankDetectedConfirm} receipt — auto-selected</p>
                                <button onClick={() => setResBankDetectedConfirm(null)} className="text-success/50 hover:text-success transition-colors shrink-0"><X className="w-3 h-3" /></button>
                              </motion.div>
                            )}
                            {resBankMismatchWarning && (
                              <motion.div key="res-bank-mismatch" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ type: "spring", stiffness: 400, damping: 28 }} className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-warning/8" style={{ border: "1px solid rgba(234,179,8,0.2)" }}>
                                <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] text-warning font-semibold leading-tight">{resBankMismatchWarning}</p>
                                  {resOcrDetectedBankRaw && (
                                    <div className="flex items-center gap-1.5 mt-1.5">
                                      <button onClick={() => { const det = resOcrDetectedBankRaw.toLowerCase().replace(/[^a-z0-9]/g, ""); const match = treasuryAccounts.filter(a => a.type === "bank").find((acc) => { const accName = (acc.bankName + " " + acc.label).toLowerCase().replace(/[^a-z0-9]/g, ""); return accName.includes(det) || det.includes(accName.split(" ")[0]); }); if (match) { setResAddSource(match.label); setResAccountId(match.id); setResBankDetectedConfirm(match.bankName || resOcrDetectedBankRaw || "matched bank"); } setResBankMismatchWarning(null); }} className="text-[10px] font-semibold text-accent bg-accent/10 hover:bg-accent/20 px-2 py-0.5 rounded transition-colors">Switch to {resOcrDetectedBankRaw}</button>
                                      <button onClick={() => { setResBankMismatchWarning(null); setResBankDetectedConfirm(null); }} className="text-[10px] font-semibold text-muted bg-surface-3 hover:bg-surface-3/80 px-2 py-0.5 rounded transition-colors">Keep current</button>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                          {/* Amount + Remaining info */}
                          {ngnRemaining > 0 && <p className="text-[10px] text-muted tabular-nums">{isOverpayment ? "Excess" : "Remaining"}: <span className="font-semibold text-foreground">₦{ngnRemaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>{isCrossCurrency ? <span className="text-muted/60"> ({resolveAmt.toLocaleString()} USDT)</span> : ""}</p>}
                          <div className="grid grid-cols-2 gap-2">
                            <input type="text" inputMode="numeric" placeholder="Amount (NGN)" value={resAddAmount} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ""); const parts = raw.split("."); const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","); setResAddAmount(parts.length > 1 ? `${whole}.${parts[1]}` : whole); }} className={`bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground tabular-nums placeholder:text-muted/40 focus:outline-none focus:ring-1 ${isFormOverpay ? "focus:ring-warning/50 ring-1 ring-warning/30" : "focus:ring-accent/30"}`} style={{ border: `1px solid ${isFormOverpay ? "var(--warning)" : "var(--border)"}` }} />
                            <div className="relative flex items-center">
                              <BankIcon bankName={treasuryAccounts.find(a => a.id === resAccountId)?.bankName} size="sm" className="absolute left-2 z-[1] pointer-events-none" />
                              <select value={resAccountId ?? ""} onChange={(e) => {
                                const newId = Number(e.target.value);
                                setResAccountId(newId || null);
                                const selectedAcc = treasuryAccounts.find(a => a.id === newId);
                                setResAddSource(selectedAcc?.label || "");
                                if (resOcrDetectedBankRaw && selectedAcc) {
                                  const det = resOcrDetectedBankRaw.toLowerCase().replace(/[^a-z0-9]/g, "");
                                  const accName = (selectedAcc.bankName + " " + selectedAcc.label).toLowerCase().replace(/[^a-z0-9]/g, "");
                                  if (accName.includes(det) || det.includes(accName.split(" ")[0])) {
                                    setResBankMismatchWarning(null);
                                    setResBankDetectedConfirm(selectedAcc.bankName || "matched bank");
                                  } else {
                                    setResBankDetectedConfirm(null);
                                    setResBankMismatchWarning(`Screenshot shows "${resOcrDetectedBankRaw}" but you selected "${selectedAcc.bankName || "another bank"}". Is this correct?`);
                                  }
                                }
                              }} className={`w-full bg-surface-2 rounded pl-8 pr-2 py-1.5 text-[12px] text-foreground focus:outline-none ${resBankMismatchWarning ? "ring-1 ring-warning/50" : resBankDetectedConfirm ? "ring-1 ring-success/40" : ""}`} style={{ border: `1px solid ${resBankMismatchWarning ? "rgba(234,179,8,0.4)" : resBankDetectedConfirm ? "rgba(34,197,94,0.3)" : "var(--border)"}` }}>
                                <option value="">Select bank account...</option>
                                {treasuryAccounts.filter(a => a.type === "bank").map(a => <option key={a.id} value={a.id}>{a.label} ({a.bankName})</option>)}
                              </select>
                            </div>
                          </div>
                          {/* Narration / Reference */}
                          <input type="text" placeholder="Narration / Transaction reference" value={resAddRef} onChange={(e) => setResAddRef(e.target.value)} className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                          {/* NIP Session ID */}
                          <div>
                            <input type="text" placeholder="NIP Session ID (30 digits)" value={resSessionId}
                              onChange={(e) => setResSessionId(e.target.value.replace(/\D/g, "").slice(0, 30))}
                              className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                            {resSessionId && resSessionId.length !== 30 && (
                              <p className="text-[10px] text-warning mt-0.5">{resSessionId.length}/30 digits — NIP Session ID must be exactly 30 digits</p>
                            )}
                            {resSessionId && resSessionId.length === 30 && (() => {
                              const nibssPrefix = resSessionId.substring(0, 6);
                              const sessionBank = NIBSS_CODE_MAP[nibssPrefix];
                              const selectedAcc = treasuryAccounts.find(a => a.id === resAccountId);
                              const selectedBankName = selectedAcc?.bankName || "";
                              const isMatch = sessionBank && selectedBankName && sessionBank.toLowerCase().replace(/[^a-z0-9]/g, "").includes(selectedBankName.toLowerCase().replace(/[^a-z0-9]/g, "").split(" ")[0]);
                              const isReverse = sessionBank && selectedBankName && selectedBankName.toLowerCase().replace(/[^a-z0-9]/g, "").includes(sessionBank.toLowerCase().replace(/[^a-z0-9]/g, "").split(" ")[0]);
                              if (sessionBank && !isMatch && !isReverse) {
                                return <p className="text-[10px] text-warning mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3 inline" /> Session ID belongs to <strong>{sessionBank}</strong> but you selected <strong>{selectedBankName || "another bank"}</strong></p>;
                              }
                              if (sessionBank) {
                                return <p className="text-[10px] text-success mt-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 inline" /> Session ID verified — {sessionBank}</p>;
                              }
                              return null;
                            })()}
                          </div>
                          {/* Payer Details section — collapsible, auto-collapses when OCR fills */}
                          <div className="rounded-lg bg-surface-2/40 overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                            <button
                              type="button"
                              onClick={() => setResPayerExpanded(!resPayerExpanded)}
                              className="w-full flex items-center justify-between px-2.5 py-2 hover:bg-surface-2/60 transition-colors"
                            >
                              <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Payer Details</span>
                              <div className="flex items-center gap-2">
                                {!resPayerExpanded && (resPayerName || resPayerAccount || resPayerBank) && (
                                  <span className="text-[10px] text-success flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {[resPayerName, resPayerAccount && `••${resPayerAccount.slice(-4)}`, resPayerBank].filter(Boolean).join(" · ")}
                                  </span>
                                )}
                                <motion.div animate={{ rotate: resPayerExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                  <ChevronDown className="w-3 h-3 text-muted" />
                                </motion.div>
                              </div>
                            </button>
                            <AnimatePresence initial={false}>
                              {resPayerExpanded && (
                                <motion.div
                                  key="res-payer-fields"
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-2.5 pb-2.5 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                      <input type="text" placeholder="Payer name" value={resPayerName} onChange={(e) => setResPayerName(e.target.value)} className="bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                                      <input type="text" placeholder="Account number" value={resPayerAccount} onChange={(e) => setResPayerAccount(e.target.value.replace(/[^0-9*]/g, "").slice(0, 10))} className="bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                                    </div>
                                    <select value={resPayerBank} onChange={(e) => setResPayerBank(e.target.value)} className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground focus:outline-none" style={{ border: "1px solid var(--border)" }}>
                                      <option value="">Select payer bank...</option>
                                      {NIGERIAN_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                          {/* Note field */}
                          <div>
                            <input type="text" placeholder="e.g. From Ahmed, Personal transfer..."
                              value={resNotes} onChange={(e) => setResNotes(e.target.value)}
                              className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                              style={{ border: "1px solid var(--border)" }} />
                            <p className="text-[10px] text-muted mt-1">A memo for your reference — does not affect any accounts</p>
                          </div>
                          {/* Receipt date — when the payment hit the account */}
                          <div>
                            <label className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1 block">Payment Date & Time (from receipt)</label>
                            <input type="datetime-local" step="1" value={resDate} onChange={(e) => setResDate(e.target.value)}
                              className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)", colorScheme: "dark" }} />
                            <p className="text-[10px] text-muted mt-1">When the payment hit the account — auto-filled from receipt if uploaded</p>
                          </div>
                          {isFormOverpay && (
                            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-warning/10" style={{ border: "1px solid var(--warning)" }}>
                              <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />
                              <span className="text-[10px] text-warning font-semibold">Overpayment: ₦{(parsedResAmt - ngnRemaining).toLocaleString()} excess</span>
                            </div>
                          )}
                          {/* Actions */}
                          <div className="flex items-center justify-end gap-2 pt-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setTradeAttachments([]); setOcrResult(null); setOcrImageHash(null);
                              setResDuplicateWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null);
                              setResPayerName(""); setResPayerAccount(""); setResPayerBank("");
                              setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAccountId(null);
                              setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true); setResNotes("");
                              closeResolution();
                            }}>Cancel</Button>
                            <Button variant="primary" size="sm" onClick={() => {
                              if (resDuplicateWarning) { toast(resDuplicateWarning, "error"); return; }
                              const parsed = parseFloat(resAddAmount.replace(/,/g, ""));
                              if (isNaN(parsed) || parsed <= 0) { toast("Enter a valid amount", "error"); return; }
                              setShowResSaveConfirm(true);
                            }} disabled={!!resDuplicateWarning}>Save</Button>
                          </div>
                          {/* Save confirmation overlay */}
                          <AnimatePresence>
                            {showResSaveConfirm && (() => {
                              const acct = treasuryAccounts.find(a => a.id === resAccountId);
                              const acctLabel = acct ? `${acct.label} (${acct.bankName || "Bank"})` : resAddSource || "selected account";
                              const amt = resAddAmount;
                              const hasMismatch = resOcrDetectedBankRaw && acct?.bankName && (() => {
                                const det = resOcrDetectedBankRaw.toLowerCase().replace(/[^a-z0-9]/g, "");
                                const accName = (acct.bankName + " " + acct.label).toLowerCase().replace(/[^a-z0-9]/g, "");
                                return !(accName.includes(det) || det.includes(accName.split(" ")[0]));
                              })();
                              return (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="absolute inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px] rounded-2xl"
                                >
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                    className="bg-surface rounded-xl px-5 py-4 max-w-[340px] w-full mx-4 space-y-3"
                                    style={{ border: "1px solid var(--border)" }}
                                  >
                                    <div className="text-center space-y-1.5">
                                      <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center bg-success/12">
                                        <ArrowDownLeft className="w-5 h-5 text-success" />
                                      </div>
                                      <p className="text-[14px] font-bold text-foreground">Confirm Payment</p>
                                    </div>
                                    <div className="space-y-2 rounded-lg p-3 bg-surface-2/50" style={{ border: "1px solid var(--border)" }}>
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">Type</span>
                                        <span className="text-[12px] font-bold text-success">Credit — Money received</span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">Amount</span>
                                        <span className="text-[12px] font-bold text-foreground tabular-nums">₦{amt}</span>
                                      </div>
                                      {resAddSource && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">Bank</span>
                                          <span className="text-[12px] font-semibold text-foreground inline-flex items-center gap-1.5">
                                            <BankIcon bankName={acct?.bankName} size="sm" />
                                            {acctLabel}
                                          </span>
                                        </div>
                                      )}
                                      {resOcrDetectedBankRaw && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-[10px] text-muted uppercase tracking-wider font-semibold">OCR Detected</span>
                                          <span className={`text-[12px] font-semibold ${hasMismatch ? "text-warning" : "text-success"}`}>{resOcrDetectedBankRaw} {hasMismatch ? "\u26A0" : "\u2713"}</span>
                                        </div>
                                      )}
                                    </div>
                                    {hasMismatch && (
                                      <div className="flex items-start gap-1.5 px-2 py-1.5 rounded bg-warning/8" style={{ border: "1px solid rgba(234,179,8,0.2)" }}>
                                        <AlertTriangle className="w-3 h-3 text-warning shrink-0 mt-0.5" />
                                        <p className="text-[10px] text-warning font-semibold">Screenshot shows {resOcrDetectedBankRaw} but you&apos;re logging to {acct?.bankName}. Double-check this is correct.</p>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 pt-1">
                                      <Button variant="ghost" size="sm" className="flex-1" onClick={() => setShowResSaveConfirm(false)}>Go Back</Button>
                                      <Button variant="primary" size="sm" className="flex-1" onClick={() => { setShowResSaveConfirm(false); handleResolutionAddPayment(); }}>
                                        Yes, Log It
                                      </Button>
                                    </div>
                                  </motion.div>
                                </motion.div>
                              );
                            })()}
                          </AnimatePresence>
                        </div>}
                      </div>
                    );
                  })()}

                  {/* Crypto tab: From Scanner — with cross-currency rate when resolving NGN via crypto */}
                  {resolutionTab === "from_scanner" && (() => {
                    // Cross-currency: when isNairaSide, we need to convert NGN → USDT
                    const isCrossCurrency = isNairaSide;
                    const crossRate = resCrossRateMode === "trade" ? (trade.rate || 0) : (parseFloat(resCrossRate.replace(/,/g, "")) || 0);
                    const usdtEquivalent = isCrossCurrency && crossRate > 0 ? resolveAmt / crossRate : 0;

                    // Resolution direction: buy/borrow overpayment refund = incoming (they return), sell overpayment = outgoing (you return)
                    // Underpayment: buy = incoming (they owe you), sell = outgoing (you owe them)
                    const expectedDir = isBuyLike ? "incoming" : "outgoing";
                    const scannerTxs = scannerTransactions.map(stx => ({
                      uid: `rsc-${stx.id}`,
                      source: "scanner" as const,
                      amount: stx.amount,
                      label: truncateHash(stx.txHash, 4),
                      detail: `${stx.chain.toUpperCase()} · from ${truncateHash(stx.counterpartyAddress, 4)}`,
                      direction: stx.direction as "incoming" | "outgoing",
                      date: stx.timestamp,
                      tagged: stx.matchedTradeId ? stx.matchedTradeId : null,
                      searchText: `${stx.txHash} ${stx.counterpartyAddress} ${stx.chain}`.toLowerCase(),
                      raw: stx,
                    }));
                    const treasuryTxs = treasuryTransactions.filter(tx => tx.currency === "USDT").map(tx => ({
                      uid: `rtr-${tx.id}`,
                      source: "treasury" as const,
                      amount: tx.amount,
                      label: tx.narration.slice(0, 30),
                      detail: treasuryAccounts.find(a => a.id === tx.accountId)?.label || "Wallet",
                      direction: (tx.type === "credit" ? "incoming" : "outgoing") as "incoming" | "outgoing",
                      date: tx.date,
                      tagged: tx.linkedTradeId ? tx.linkedTradeId : null,
                      searchText: `${tx.narration} ${treasuryAccounts.find(a => a.id === tx.accountId)?.label || ""} ${tx.counterpartyName || ""}`.toLowerCase(),
                      raw: tx,
                    }));
                    const rawTxs = [...scannerTxs, ...treasuryTxs];
                    const searchLower = resCombineSearch.toLowerCase().trim();
                    const filteredTxs = rawTxs.filter(t => {
                      if (searchLower && !t.searchText.includes(searchLower) && !t.label.toLowerCase().includes(searchLower) && !t.detail.toLowerCase().includes(searchLower) && !String(t.amount).includes(searchLower)) return false;
                      if (resCombineFilterDir !== "all" && t.direction !== resCombineFilterDir) return false;
                      return true;
                    });
                    const allTxs = filteredTxs.sort((a, b) => resCombineSortAsc
                      ? new Date(a.date).getTime() - new Date(b.date).getTime()
                      : new Date(b.date).getTime() - new Date(a.date).getTime());
                    const selectableTxs = allTxs;
                    const selectedAll = selectableTxs.filter(t => resolutionSelectedTxIds.has(t.uid));
                    const selectedTotal = selectedAll.reduce((sum, t) => sum + t.amount, 0);
                    // For cross-currency, compare against USDT equivalent; otherwise normal USDT remaining
                    const remaining = isCrossCurrency ? usdtEquivalent : (isOverpayment ? resolveAmt : (trade.usdtAmount - trade.coinAmountSettled));
                    const isExcess = selectedTotal > remaining && remaining > 0 && resolutionSelectedTxIds.size > 0;
                    const allSelected = selectableTxs.length > 0 && selectableTxs.every(t => resolutionSelectedTxIds.has(t.uid));

                    return (
                      <div className="space-y-3">
                        {/* Cross-currency rate picker — shown when resolving NGN via crypto */}
                        {isCrossCurrency && (
                          <div className="rounded-xl p-3.5 space-y-2.5" style={{ background: "color-mix(in srgb, var(--accent) 4%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)" }}>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Cross-Currency Conversion</span>
                              <span className="text-[10px] text-muted">Resolving ₦{resolveAmt.toLocaleString()} via USDT</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <button onClick={() => { setResCrossRateMode("trade"); setResCrossRate(""); }} className={`text-[10px] font-semibold px-2 py-0.5 rounded transition-all cursor-pointer ${resCrossRateMode === "trade" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}>
                                    Trade rate{trade.rate ? ` (₦${trade.rate.toLocaleString()})` : ""}
                                  </button>
                                  <button onClick={() => { setResCrossRateMode("custom"); setResCrossRate(trade.rate ? trade.rate.toLocaleString() : ""); }} className={`text-[10px] font-semibold px-2 py-0.5 rounded transition-all cursor-pointer ${resCrossRateMode === "custom" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}>
                                    Custom rate
                                  </button>
                                </div>
                                {resCrossRateMode === "custom" && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] text-muted">₦</span>
                                    <input type="text" inputMode="numeric" placeholder="Rate" value={resCrossRate} onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ""); const parts = raw.split("."); const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","); setResCrossRate(parts.length > 1 ? `${whole}.${parts[1]}` : whole); }} className="w-28 bg-surface-2 rounded px-2 py-1 text-[12px] text-foreground tabular-nums placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30" style={{ border: "1px solid var(--border)" }} />
                                    <span className="text-[10px] text-muted">per USDT</span>
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-muted">Equivalent</p>
                                <p className={`text-[14px] font-bold tabular-nums ${crossRate > 0 ? "text-accent" : "text-muted/40"}`}>{crossRate > 0 ? `${usdtEquivalent.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT` : "—"}</p>
                              </div>
                            </div>
                            {crossRate <= 0 && <p className="text-[10px] text-warning font-semibold">Set a rate to continue</p>}
                          </div>
                        )}

                        <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Select USDT transactions</p>

                        {/* Search bar */}
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted pointer-events-none" />
                          <input type="text" value={resCombineSearch} onChange={(e) => setResCombineSearch(e.target.value)} placeholder="Search by tx hash, address, reference..." className="w-full bg-surface-2 rounded px-2 pl-6 py-1.5 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/40" style={{ border: "1px solid var(--border)" }} />
                          {resCombineSearch && <button onClick={() => setResCombineSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"><X className="w-3 h-3" /></button>}
                        </div>

                        {/* Direction filter + sort */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-0.5 bg-surface-2 rounded p-0.5" style={{ border: "1px solid var(--border)" }}>
                            {(["all", "incoming", "outgoing"] as const).map(dir => (
                              <button key={dir} onClick={() => setResCombineFilterDir(dir)} className={`text-[9px] font-bold uppercase px-2 py-1 rounded transition-all cursor-pointer ${resCombineFilterDir === dir ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}>
                                {dir === "all" ? "All" : dir === "incoming" ? "IN" : "OUT"}
                              </button>
                            ))}
                          </div>
                          <button onClick={() => setResCombineSortAsc(!resCombineSortAsc)} className="inline-flex items-center gap-1 text-[9px] font-semibold text-muted hover:text-foreground transition-colors">
                            <ArrowUpDown className="w-3 h-3" />{resCombineSortAsc ? "Oldest" : "Newest"}
                          </button>
                        </div>

                        {/* Running total bar */}
                        <div className={`flex items-center justify-between rounded px-2.5 py-1.5 ${isExcess ? "bg-warning/10" : "bg-surface-2"}`} style={{ border: `1px solid ${isExcess ? "var(--warning)" : "var(--border)"}` }}>
                          <span className={`text-[11px] font-semibold tabular-nums ${isExcess ? "text-warning" : "text-foreground"}`}>Selected: {selectedTotal.toLocaleString()} USDT</span>
                          <span className="text-[10px] text-muted tabular-nums">{resolutionSelectedTxIds.size} selected &middot; {remaining.toLocaleString()} {isOverpayment ? "excess" : "remaining"}</span>
                        </div>

                        {/* Select All */}
                        {selectableTxs.length > 0 && (
                          <button onClick={() => setResolutionSelectedTxIds(allSelected ? new Set() : new Set(selectableTxs.map(t => t.uid)))} className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors">
                            <CheckSquare className="w-3 h-3" />{allSelected ? "Deselect All" : "Select All"}
                          </button>
                        )}

                        {/* Transaction list */}
                        <div className="max-h-64 overflow-auto space-y-0.5">
                          {allTxs.length === 0 ? (
                            <p className="text-[11px] text-muted py-2">{resCombineSearch || resCombineFilterDir !== "all" ? "No transactions match" : "No USDT transactions found"}</p>
                          ) : allTxs.map(tx => {
                            const isOtherTag = !!tx.tagged && tx.tagged !== trade.id;
                            const isWrongDir = tx.direction !== expectedDir;
                            const isSelected = resolutionSelectedTxIds.has(tx.uid);
                            return (
                              <button key={tx.uid} disabled={isWrongDir} onClick={() => { if (isWrongDir) return; const next = new Set(resolutionSelectedTxIds); if (next.has(tx.uid)) next.delete(tx.uid); else next.add(tx.uid); setResolutionSelectedTxIds(next); }} className={`btn-select-item w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-all ${isWrongDir ? "opacity-35 cursor-not-allowed" : isSelected ? "bg-accent/10" : "hover:bg-surface-2"}`} data-selected={isSelected}>
                                <CheckSquare className={`w-3 h-3 flex-shrink-0 ${isWrongDir ? "text-muted/20" : isSelected ? "text-accent" : "text-muted/30"}`} />
                                <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.source === "scanner" ? "bg-accent/10 text-accent" : "bg-surface-3 text-muted"}`}>
                                  {tx.source === "scanner" ? "ON-CHAIN" : "WALLET"}
                                </span>
                                <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${tx.direction === "incoming" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
                                  {tx.direction === "incoming" ? "IN" : "OUT"}
                                </span>
                                {isOtherTag && <span className="text-[9px] font-bold text-warning bg-warning/10 px-1 py-0.5 rounded flex-shrink-0">#{tx.tagged}</span>}
                                <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(tx.date)}</span>
                                <span className="text-[11px] text-foreground truncate flex-1 min-w-0" title={tx.label}>{tx.label}</span>
                                <span className="text-[10px] text-muted truncate max-w-[80px]">{tx.detail}</span>
                                <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">{tx.amount.toLocaleString()} USDT</span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center justify-between pt-1">
                          <Button variant="ghost" size="sm" onClick={closeResolution}>Cancel</Button>
                          <Button variant="primary" size="sm" disabled={resolutionSelectedTxIds.size === 0} onClick={handleRefundFromTransaction}>
                            {isOverpayment ? `Apply ${actionWord}` : "Combine"} ({resolutionSelectedTxIds.size} selected{resolutionSelectedTxIds.size > 0 ? ` — ${selectedTotal.toLocaleString()} USDT` : ""})
                          </Button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Credit tab */}
                  {resolutionTab === "use_credit" && (() => {
                    if (!cp) return <p className="text-[12px] text-muted py-6 text-center">Assign a counterparty to use credit features</p>;
                    const creditNgn = cp.balanceNgn;
                    const creditUsdt = cp.balanceUsdt;
                    return (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 rounded-xl px-4 py-3" style={{ background: "color-mix(in srgb, var(--success) 4%, transparent)", border: "1px solid color-mix(in srgb, var(--success) 18%, transparent)" }}>
                          <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center"><Wallet className="w-4 h-4 text-success" /></div>
                          <div className="flex items-center gap-4 flex-1">
                            <div>
                              <p className="text-[10px] text-muted font-medium">NGN</p>
                              <p className={`text-[14px] font-bold tabular-nums ${creditNgn > 0 ? "text-success" : "text-muted/50"}`}>₦{creditNgn.toLocaleString()}</p>
                            </div>
                            <div className="w-px h-8 bg-border" />
                            <div>
                              <p className="text-[10px] text-muted font-medium">USDT</p>
                              <p className={`text-[14px] font-bold tabular-nums ${creditUsdt > 0 ? "text-success" : "text-muted/50"}`}>{creditUsdt.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>

                        {isOverpayment ? (
                          <div className="space-y-3">
                            {(() => {
                              const underpaidTrades = allTrades.filter(t =>
                                t.counterpartyId === trade.counterpartyId && t.id !== trade.id &&
                                ((isNairaSide && t.ngnAmount && t.nairaAmountSettled < t.ngnAmount) ||
                                 (!isNairaSide && t.coinAmountSettled < t.usdtAmount))
                              );
                              if (underpaidTrades.length === 0) return null;
                              return (
                                <div className="rounded-xl p-3.5 space-y-2.5" style={{ background: "color-mix(in srgb, var(--surface-2) 40%, transparent)", border: "1px solid var(--border)" }}>
                                  <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Or apply to underpaid trade</p>
                                  <div className="space-y-1 max-h-36 overflow-auto">
                                    {underpaidTrades.map(t => {
                                      const shortAmt = isNairaSide ? (t.ngnAmount || 0) - t.nairaAmountSettled : t.usdtAmount - t.coinAmountSettled;
                                      const isSelected = creditTargetTradeId === t.id;
                                      return (
                                        <button key={t.id} onClick={() => setCreditTargetTradeId(isSelected ? null : t.id)} className={`btn-select-item w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-all ${isSelected ? "bg-accent/10 ring-1 ring-accent/30" : "hover:bg-surface-3"}`} data-selected={isSelected}>
                                          <span className="text-[11px] font-semibold text-foreground">Trade #{t.id}</span>
                                          <span className="text-[10px] text-warning font-bold tabular-nums">Short {isNairaSide ? `₦${shortAmt.toLocaleString()}` : `${shortAmt.toLocaleString()} USDT`}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {creditTargetTradeId && (
                                    <Button variant="primary" size="sm" onClick={handleCreditToTrade} className="w-full">
                                      Apply to Trade #{creditTargetTradeId}
                                    </Button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        ) : (() => {
                          const availableCredit = isNairaSide ? creditNgn : creditUsdt;
                          const shortfall = isNairaSide ? shortNgn : shortUsdt;
                          if (availableCredit <= 0) return (
                            <div className="flex flex-col items-center py-6 gap-2">
                              <div className="w-10 h-10 rounded-full bg-surface-2 flex items-center justify-center"><Wallet className="w-4 h-4 text-muted/40" /></div>
                              <p className="text-[12px] text-muted">No credit available</p>
                            </div>
                          );
                          const applyAmount = Math.min(shortfall, availableCredit);
                          const isPartial = availableCredit < shortfall;
                          return (
                            <div className="space-y-3">
                              {isPartial && (
                                <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 bg-warning/8" style={{ border: "1px solid color-mix(in srgb, var(--warning) 25%, transparent)" }}>
                                  <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
                                  <span className="text-[11px] text-warning font-semibold">Partial: covers {isNairaSide ? `₦${applyAmount.toLocaleString()}` : `${applyAmount.toLocaleString()} USDT`} of {isNairaSide ? `₦${shortfall.toLocaleString()}` : `${shortfall.toLocaleString()} USDT`}</span>
                                </div>
                              )}
                              <button onClick={() => handleApplyCreditToShortfall()} className="btn-primary w-full flex items-center justify-center gap-2 text-[12px] font-medium rounded-lg px-4 py-2 transition-all active:scale-[0.99] cursor-pointer">
                                Apply {isNairaSide ? `₦${applyAmount.toLocaleString()}` : `${applyAmount.toLocaleString()} USDT`} from credit
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            </>
          );
        })()}
      </AnimatePresence>
      {/* Screenshot lightbox */}
      <AnimatePresence>
        {lightboxUrl && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm cursor-pointer"
              onClick={() => setLightboxUrl(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="fixed z-[81] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[90vw] max-h-[90vh]"
              onClick={() => setLightboxUrl(null)}
            >
              <img src={lightboxUrl} alt="Screenshot" className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain" />
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
                className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-surface flex items-center justify-center shadow-lg hover:bg-surface-2 transition-colors"
                style={{ border: "1px solid var(--border)" }}
              >
                <X className="w-4 h-4 text-foreground" />
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Combine confirmation modal */}
      <AnimatePresence>
        {combineConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]"
              onClick={() => setCombineConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", stiffness: 350, damping: 22 }}
              className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[420px] bg-surface rounded-2xl p-8 shadow-2xl"
              style={{ border: "1px solid var(--border)" }}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center"
                >
                  <Layers className="w-6 h-6 text-accent" />
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="text-base font-bold text-foreground"
                >
                  Confirm Combine
                </motion.h3>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-[13px] text-muted leading-relaxed space-y-1.5"
                >
                  {combineConfirm.sameDirCount > 0 && (
                    <p><span className="font-semibold text-foreground">{combineConfirm.sameDirCount}</span> receipt{combineConfirm.sameDirCount !== 1 ? "s" : ""} — <span className="font-semibold text-foreground">{combineConfirm.totalApply.toLocaleString()} USDT</span></p>
                  )}
                  {combineConfirm.oppDirCount > 0 && (
                    <p><span className="font-semibold text-warning">{combineConfirm.oppDirCount}</span> refund{combineConfirm.oppDirCount !== 1 ? "s" : ""} — <span className="font-semibold text-warning">{combineConfirm.totalRefund.toLocaleString()} USDT</span></p>
                  )}
                  <p className="pt-1">Applied to trade <span className="font-semibold text-foreground">#{trade.id}</span></p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex gap-3 w-full pt-2"
                >
                  <button
                    onClick={() => setCombineConfirm(null)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-muted hover:text-foreground hover:bg-surface-2 transition-colors"
                    style={{ border: "1px solid var(--border)" }}
                  >
                    Cancel
                  </button>
                  <Button variant="primary" size="sm" className="flex-1 !py-2.5 !rounded-xl !text-[13px]" onClick={() => { combineConfirm.execute(); setCombineConfirm(null); }}>
                    Yes, combine
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Combine success overlay */}
      <AnimatePresence>
        {combineSuccess && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-[2px]"
              onClick={() => setCombineSuccess(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[420px] bg-surface rounded-2xl p-8 shadow-2xl"
              style={{ border: "1px solid color-mix(in srgb, var(--success) 30%, var(--border))" }}
              onAnimationComplete={() => playSuccessSound()}
            >
              <div className="flex flex-col items-center text-center gap-5">
                <motion.div
                  initial={{ scale: 0, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  className="w-14 h-14 rounded-full bg-success/15 flex items-center justify-center"
                >
                  <CheckCircle2 className="w-7 h-7 text-success" />
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="text-lg font-bold text-foreground"
                >
                  Receipts Combined
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="text-[13px] text-muted leading-relaxed"
                >
                  {combineSuccess}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="w-full pt-1"
                >
                  <button
                    onClick={() => setCombineSuccess(null)}
                    className="w-full px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-success text-white hover:bg-success/90 transition-colors"
                  >
                    Done
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Dispute Creation Modal */}
      <AnimatePresence>
        {showDisputeModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-[3px]"
              onClick={() => setShowDisputeModal(false)}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="File dispute"
              initial={{ opacity: 0, scale: 0.92, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: "spring", stiffness: 380, damping: 26 }}
              className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[520px] max-h-[85vh] bg-surface rounded-2xl flex flex-col overflow-hidden"
              style={{ border: "1px solid var(--danger)", boxShadow: "0 25px 60px -12px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative px-6 pt-5 pb-4" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--danger) 8%, var(--surface-2)) 0%, transparent 100%)" }}>
                <button onClick={() => setShowDisputeModal(false)} aria-label="Close" className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-surface-3/80 text-muted hover:text-foreground transition-all"><X className="w-4 h-4" /></button>
                <div className="flex items-center gap-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.05 }}
                    className="w-10 h-10 rounded-xl bg-danger/12 flex items-center justify-center"
                    style={{ border: "1px solid color-mix(in srgb, var(--danger) 25%, transparent)" }}
                  >
                    <AlertTriangle className="w-5 h-5 text-danger" />
                  </motion.div>
                  <div>
                    <h3 className="text-[15px] font-bold text-foreground tracking-tight">File Dispute</h3>
                    <p className="text-[12px] text-muted mt-0.5">Trade #{trade.id} · {trade.counterpartyName ?? "Unknown"}</p>
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="flex-1 overflow-auto px-6 py-5 space-y-5">
                {/* Reason selector */}
                <div>
                  <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Reason</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {DISPUTE_REASONS.map((reason) => (
                      <button
                        key={reason}
                        onClick={() => setDisputeReason(reason)}
                        className={`px-3 py-2 rounded-lg text-[12px] font-medium text-left transition-all ${
                          disputeReason === reason
                            ? "bg-danger/15 text-danger ring-1 ring-danger/30"
                            : "bg-surface-2 text-muted hover:text-foreground hover:bg-surface-3"
                        }`}
                        style={{ border: disputeReason === reason ? "none" : "1px solid var(--border)" }}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Description */}
                <div>
                  <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Description</label>
                  <textarea
                    value={disputeDescription}
                    onChange={(e) => setDisputeDescription(e.target.value)}
                    placeholder="Describe the issue in detail..."
                    rows={3}
                    className="w-full bg-surface-2 rounded-lg px-3 py-2.5 text-[13px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-danger/30 resize-none"
                    style={{ border: "1px solid var(--border)" }}
                  />
                </div>
                {/* Priority */}
                <div>
                  <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Priority</label>
                  <div className="flex gap-1.5">
                    {(["low", "medium", "high", "critical"] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => setDisputePriority(p)}
                        className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                          disputePriority === p
                            ? p === "critical" ? "bg-danger/20 text-danger ring-1 ring-danger/40"
                            : p === "high" ? "bg-warning/20 text-warning ring-1 ring-warning/40"
                            : p === "medium" ? "bg-accent/20 text-accent ring-1 ring-accent/40"
                            : "bg-success/20 text-success ring-1 ring-success/40"
                            : "bg-surface-2 text-muted hover:text-foreground"
                        }`}
                        style={disputePriority !== p ? { border: "1px solid var(--border)" } : undefined}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Evidence upload */}
                <div>
                  <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Evidence (optional)</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {disputeEvidence.map((url, i) => (
                      <div key={i} className="relative group/evmod">
                        <img src={url} alt={`Evidence ${i + 1}`} className="w-14 h-14 rounded-lg object-cover" style={{ border: "1px solid var(--border)" }} />
                        <button
                          onClick={() => setDisputeEvidence((prev) => prev.filter((_, idx) => idx !== i))}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-danger text-white flex items-center justify-center opacity-0 group-hover/evmod:opacity-100 transition-opacity shadow-sm"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => disputeFileInputRef.current?.click()}
                      disabled={disputeUploading}
                      className="w-14 h-14 rounded-lg bg-surface-2 flex items-center justify-center hover:bg-surface-3 transition-colors"
                      style={{ border: "1px dashed var(--border)" }}
                    >
                      {disputeUploading ? <Loader2 className="w-4 h-4 text-muted animate-spin" /> : <Upload className="w-4 h-4 text-muted" />}
                    </button>
                  </div>
                  <input
                    ref={disputeFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleDisputeEvidenceUpload(file);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
              {/* Footer */}
              <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
                <Button variant="ghost" size="sm" onClick={() => setShowDisputeModal(false)}>Cancel</Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!disputeReason || !disputeDescription.trim() || submittingDispute}
                  onClick={handleSubmitDispute}
                >
                  {submittingDispute ? <><Loader2 className="w-3 h-3 animate-spin" />Filing...</> : <><AlertTriangle className="w-3 h-3" />File Dispute</>}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Dispute Resolution Modal */}
      <AnimatePresence>
        {showResolveModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-[3px]"
              onClick={() => setShowResolveModal(null)}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Resolve dispute"
              initial={{ opacity: 0, scale: 0.92, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: "spring", stiffness: 380, damping: 26 }}
              className="fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[480px] max-h-[85vh] bg-surface rounded-2xl flex flex-col overflow-hidden"
              style={{ border: "1px solid var(--border)", boxShadow: "0 25px 60px -12px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative px-6 pt-5 pb-4" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--surface-2) 60%, transparent) 0%, transparent 100%)" }}>
                <button onClick={() => setShowResolveModal(null)} aria-label="Close" className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-surface-3/80 text-muted hover:text-foreground transition-all"><X className="w-4 h-4" /></button>
                <div className="flex items-center gap-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.05 }}
                    className="w-10 h-10 rounded-xl bg-accent/12 flex items-center justify-center"
                    style={{ border: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)" }}
                  >
                    <Scale className="w-5 h-5 text-accent" />
                  </motion.div>
                  <div>
                    <h3 className="text-[15px] font-bold text-foreground tracking-tight">Resolve Dispute</h3>
                    <p className="text-[12px] text-muted mt-0.5">Trade #{trade.id}</p>
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="flex-1 overflow-auto px-6 py-5 space-y-5">
                {/* Resolution type */}
                <div>
                  <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Resolution Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { id: "refund" as const, label: "Refund", desc: "Full refund to counterparty" },
                      { id: "partial_refund" as const, label: "Partial Refund", desc: "Partial amount refunded" },
                      { id: "dismissed" as const, label: "Dismiss", desc: "No action required" },
                    ]).map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setResolveType(opt.id)}
                        className={`p-3 rounded-xl text-left transition-all ${
                          resolveType === opt.id
                            ? "bg-accent/10 ring-1 ring-accent/30"
                            : "bg-surface-2 hover:bg-surface-3"
                        }`}
                        style={resolveType !== opt.id ? { border: "1px solid var(--border)" } : undefined}
                      >
                        <span className={`text-[12px] font-bold block ${resolveType === opt.id ? "text-accent" : "text-foreground"}`}>{opt.label}</span>
                        <span className="text-[10px] text-muted block mt-0.5">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {/* Amount + currency (hidden for dismiss) */}
                {resolveType !== "dismissed" && (
                  <div>
                    <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Amount</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={resolveAmount}
                        onChange={(e) => setResolveAmount(e.target.value)}
                        placeholder="0.00"
                        className="flex-1 bg-surface-2 rounded-lg px-3 py-2 text-[13px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30 tabular-nums"
                        style={{ border: "1px solid var(--border)" }}
                      />
                      <div className="flex rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                        <button
                          onClick={() => setResolveCurrency("NGN")}
                          className={`px-3 py-2 text-[11px] font-bold transition-colors ${resolveCurrency === "NGN" ? "bg-foreground text-surface" : "bg-surface-2 text-muted hover:text-foreground"}`}
                        >NGN</button>
                        <button
                          onClick={() => setResolveCurrency("USDT")}
                          className={`px-3 py-2 text-[11px] font-bold transition-colors ${resolveCurrency === "USDT" ? "bg-foreground text-surface" : "bg-surface-2 text-muted hover:text-foreground"}`}
                        >USDT</button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Resolution note */}
                <div>
                  <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block mb-2">Resolution Note <span className="text-danger">*</span></label>
                  <textarea
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value)}
                    placeholder="Explain the resolution..."
                    rows={3}
                    className="w-full bg-surface-2 rounded-lg px-3 py-2.5 text-[13px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30 resize-none"
                    style={{ border: "1px solid var(--border)" }}
                  />
                </div>
              </div>
              {/* Footer */}
              <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
                <Button variant="ghost" size="sm" onClick={() => setShowResolveModal(null)}>Cancel</Button>
                <Button
                  size="sm"
                  disabled={!resolveNote.trim() || submittingResolve}
                  onClick={() => { if (showResolveModal) handleResolveDispute(showResolveModal); }}
                >
                  {submittingResolve ? <><Loader2 className="w-3 h-3 animate-spin" />Confirming...</> : "Confirm Resolution"}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Evidence Lightbox */}
      {lightboxImages && (
        <EvidenceLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxImages(null)}
        />
      )}

      {tradeCp && (
        <>
          <ConfirmDialog
            open={showTradeBlockConfirm}
            title={`Block ${tradeCp.name}?`}
            message="Personal block — instant and reversible. They will still appear in your records and trade dropdowns."
            confirmLabel="Block"
            confirmDisabled={!tradeBlockReason.trim()}
            icon={Ban}
            onConfirm={handleTradeBlock}
            onCancel={() => { setShowTradeBlockConfirm(false); setTradeBlockReason(""); }}
          >
            <textarea
              value={tradeBlockReason}
              onChange={(e) => setTradeBlockReason(e.target.value)}
              placeholder="Reason for blocking (required)..."
              rows={2}
              autoFocus
              className="w-full bg-surface-2 rounded-lg px-3 py-2 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-danger/30 resize-none"
              style={{ border: "1px solid var(--border)" }}
            />
          </ConfirmDialog>

          <ConfirmDialog
            open={showTradeUnblockConfirm}
            title={`Unblock ${tradeCp.name}?`}
            message="This will remove the personal block. You can re-block at any time."
            confirmLabel="Unblock"
            variant="warning"
            icon={Ban}
            onConfirm={handleTradeUnblock}
            onCancel={() => setShowTradeUnblockConfirm(false)}
          />

          <ConfirmDialog
            open={showTradeBlacklistConfirm}
            title={`Blacklist ${tradeCp.name}?`}
            message="This will flag the counterparty as blacklisted. They will still appear in your records but will be marked as high risk."
            confirmLabel="Blacklist"
            icon={Ban}
            onConfirm={handleTradeBlacklist}
            onCancel={() => setShowTradeBlacklistConfirm(false)}
          />
        </>
      )}
    {/* Match warning overlay — rendered via portal so it escapes modal transform context */}
    {matchWarning && !duplicateWarning && typeof document !== "undefined" && createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setMatchWarning(null)}>
        <div className="animate-pulse-warning bg-surface rounded-xl px-6 py-5 max-w-sm w-full mx-4 flex flex-col items-center gap-3 text-center" style={{ border: "2px solid rgba(234,179,8,0.5)" }} onClick={(e) => e.stopPropagation()}>
          <div className="w-12 h-12 rounded-full bg-warning/15 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-warning" />
          </div>
          <p className="text-[15px] font-bold text-warning">Possible Duplicate</p>
          <p className="text-[12px] text-muted leading-relaxed">{matchWarning}</p>
          <p className="text-[11px] text-muted/60">You can still save if this is a different transaction.</p>
          <div className="flex gap-2 w-full mt-1">
            <Button variant="ghost" size="sm" className="flex-1" onClick={() => setMatchWarning(null)}>Continue Anyway</Button>
            <Button variant="warning" size="sm" className="flex-1" onClick={() => { resetAddPaymentState(); }}>Remove Receipt</Button>
          </div>
        </div>
      </div>,
      document.body
    )}
    {/* Duplicate warning overlay (Add Payment) — rendered via portal */}
    {duplicateWarning && typeof document !== "undefined" && createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="animate-shake-hard animate-pulse-danger bg-surface rounded-xl px-6 py-5 max-w-sm w-full mx-4 flex flex-col items-center gap-3 text-center" style={{ border: "2px solid rgba(239,68,68,0.5)" }}>
          <div className="w-12 h-12 rounded-full bg-danger/15 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-danger" />
          </div>
          <p className="text-[15px] font-bold text-danger">Duplicate Screenshot</p>
          <p className="text-[12px] text-muted leading-relaxed">{duplicateWarning}</p>
          <p className="text-[11px] text-muted/60">Remove this screenshot and upload a different one to continue.</p>
          <Button variant="danger" size="sm" className="mt-1 w-full" onClick={() => resetAddPaymentState()}>Remove Screenshot</Button>
        </div>
      </div>,
      document.body
    )}
    {/* Resolution match warning overlay */}
    {resMatchWarning && !resDuplicateWarning && typeof document !== "undefined" && createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setResMatchWarning(null)}>
        <div className="animate-pulse-warning bg-surface rounded-xl px-6 py-5 max-w-sm w-full mx-4 flex flex-col items-center gap-3 text-center" style={{ border: "2px solid rgba(234,179,8,0.5)" }} onClick={(e) => e.stopPropagation()}>
          <div className="w-12 h-12 rounded-full bg-warning/15 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-warning" />
          </div>
          <p className="text-[15px] font-bold text-warning">Possible Duplicate</p>
          <p className="text-[12px] text-muted leading-relaxed">{resMatchWarning}</p>
          <p className="text-[11px] text-muted/60">You can still save if this is a different transaction.</p>
          <div className="flex gap-2 w-full mt-1">
            <Button variant="ghost" size="sm" className="flex-1" onClick={() => setResMatchWarning(null)}>Continue Anyway</Button>
            <Button variant="warning" size="sm" className="flex-1" onClick={() => {
              setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null);
              setResDuplicateWarning(null); setResMatchWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null);
              setResPayerName(""); setResPayerAccount(""); setResPayerBank("");
              setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAccountId(null);
              setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true);
            }}>Remove Receipt</Button>
          </div>
        </div>
      </div>,
      document.body
    )}
    {/* Resolution duplicate warning overlay */}
    {resDuplicateWarning && typeof document !== "undefined" && createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="animate-shake-hard animate-pulse-danger bg-surface rounded-xl px-6 py-5 max-w-sm w-full mx-4 flex flex-col items-center gap-3 text-center" style={{ border: "2px solid rgba(239,68,68,0.5)" }}>
          <div className="w-12 h-12 rounded-full bg-danger/15 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-danger" />
          </div>
          <p className="text-[15px] font-bold text-danger">Duplicate Screenshot</p>
          <p className="text-[12px] text-muted leading-relaxed">{resDuplicateWarning}</p>
          <p className="text-[11px] text-muted/60">Remove this screenshot and upload a different one to continue.</p>
          <Button variant="danger" size="sm" className="mt-1 w-full" onClick={() => {
            setResTradeAttachments([]); setResOcrResult(null); setResOcrImageHash(null);
            setResDuplicateWarning(null); setResMatchWarning(null); setResBankMismatchWarning(null); setResBankDetectedConfirm(null); setResOcrDetectedBankRaw(null);
            setResPayerName(""); setResPayerAccount(""); setResPayerBank("");
            setResAddAmount(""); setResAddRef(""); setResAddSource(""); setResAccountId(null);
            setResSessionId(""); setResDate(localNow()); setResPayerExpanded(true);
          }}>Remove Screenshot</Button>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}

// --- Component ---

export default function TradeLedgerPage() {
  return (
    <Suspense>
      <TradeLedger />
    </Suspense>
  );
}

function TradeLedger() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [activeTab, setActiveTab] = useState<string>(searchParams.get("tab") || "All");
  const [expandedId, setExpandedId] = useState<number | null>(() => {
    const t = searchParams.get("trade");
    return t ? Number(t) : null;
  });
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [sortCol, setSortCol] = useState<SortColumn>((searchParams.get("sort") as SortColumn) || "createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">((searchParams.get("dir") as "asc" | "desc") || "desc");
  const [dateRange, setDateRange] = useState<string>(searchParams.get("range") || "today");
  const [customFrom, setCustomFrom] = useState(searchParams.get("from") || "");
  const [customTo, setCustomTo] = useState(searchParams.get("to") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 0);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [assignTradeId, setAssignTradeId] = useState<number | null>(null);
  const [cpSearch, setCpSearch] = useState("");
  const [cpList, setCpList] = useState<Counterparty[]>(counterparties);
  const [registeringNew, setRegisteringNew] = useState(false);
  const [newCpName, setNewCpName] = useState("");
  const [newCpAddress, setNewCpAddress] = useState("");
  const [newCpChain, setNewCpChain] = useState<"tron" | "bsc" | "ethereum">("tron");
  const [dataVersion, setDataVersion] = useState(0);
  const [showFinancialSummary, setShowFinancialSummary] = useState(false);
  const [headerPeriod, setHeaderPeriod] = useState<"allTime" | "today" | "yesterday" | "7d" | "30d">("today");
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showLockPicker, setShowLockPicker] = useState(false);
  const [showProfitCalc, setShowProfitCalc] = useState(false);
  const [showSmartSelect, setShowSmartSelect] = useState(false);
  const [linkToLockTrade, setLinkToLockTrade] = useState<Trade | null>(null);
  const [linkedTradeMap, setLinkedTradeMap] = useState<Map<number, { lockId: number; lockType: string }>>(new Map());
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const [expandedWarningType, setExpandedWarningType] = useState<AccountingWarning["type"] | null>(null);
  const [hashNotFound, setHashNotFound] = useState<{ hash: string; lookupResult?: { found: boolean; chain?: string; amount?: number; from?: string; to?: string; error?: string } } | null>(null);
  const [hashLookupLoading, setHashLookupLoading] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletCount, setWalletCount] = useState(0);
  const [disputePriorityFilter, setDisputePriorityFilter] = useState<string>("all");
  const [disputeStatusFilter, setDisputeStatusFilter] = useState<string>("all");
  const [allDisputesByTrade, setAllDisputesByTrade] = useState<Record<number, Dispute[]>>({});
  const [showMiddlemanModal, setShowMiddlemanModal] = useState(false);
  const [middlemanForm, setMiddlemanForm] = useState({
    txHash: "",
    chain: "tron" as "tron" | "bsc" | "ethereum",
    amount: "",
    buyCpId: null as number | null,
    buyCpName: "",
    buyRate: "",
    sellCpId: null as number | null,
    sellCpName: "",
    sellRate: "",
    nairaFlow: "through_you" as "through_you" | "direct",
  });
  const [mmBuyCpSearch, setMmBuyCpSearch] = useState("");
  const [mmSellCpSearch, setMmSellCpSearch] = useState("");
  const [mmBuyCpMode, setMmBuyCpMode] = useState<"selected" | "search" | "create" | "idle">("idle");
  const [mmSellCpMode, setMmSellCpMode] = useState<"selected" | "search" | "create" | "idle">("idle");
  const [mmNewCpName, setMmNewCpName] = useState("");
  const [mmCpExpanded, setMmCpExpanded] = useState(false);
  const [mmHashLooking, setMmHashLooking] = useState(false);
  const [mmHashStatus, setMmHashStatus] = useState<"" | "found" | "not_found" | "existing">("");

  const [directionFilter, setDirectionFilter] = useState<"all" | "middleman">("all");
  const [addressLookup, setAddressLookup] = useState<{
    address: string;
    loading: boolean;
    result?: {
      found: boolean;
      chain?: string;
      counterpartyName?: string | null;
      transactions: Array<{
        txHash: string;
        chain: string;
        amount: number;
        from: string;
        to: string;
        timestamp: string;
        direction: "incoming" | "outgoing";
      }>;
      error?: string;
    };
    amountFilter: string;
  } | null>(null);
  const { toast } = useToast();
  const tableRef = useRef<HTMLDivElement>(null);
  const prevSortKeyRef = useRef({ col: "", dir: "" });
  const sortedIdsRef = useRef<number[]>([]);
  const scanner = useScanner();

  // User-created trades (middleman, manual) — persisted via API
  const [userTrades, setUserTrades] = useState<Trade[]>([]);
  const [userSettlements, setUserSettlements] = useState<import("@/lib/mock-data").SettlementEntry[]>([]);
  useEffect(() => {
    fetch("/api/trades")
      .then((r) => r.json())
      .then((data) => {
        if (data.trades) {
          setUserTrades(data.trades);
          // Merge user settlements into the module-level array so the UI can find them
          if (data.settlements) {
            for (const s of data.settlements) {
              if (!settlementEntries.some(e => e.id === s.id)) {
                settlementEntries.push(s);
              }
            }
            setUserSettlements(data.settlements);
          }
        }
      })
      .catch(() => {});
  }, []);

  // Hydrate counterparty balance fields from persisted data
  useEffect(() => {
    fetch("/api/counterparties")
      .then((r) => r.json())
      .then((data) => {
        if (data.counterparties) {
          for (const persisted of data.counterparties) {
            const cp = counterparties.find(c => c.id === persisted.id);
            if (cp) {
              if (persisted.balanceNgn !== undefined) cp.balanceNgn = persisted.balanceNgn;
              if (persisted.balanceUsdt !== undefined) cp.balanceUsdt = persisted.balanceUsdt;
              if (persisted.isBlocked !== undefined) cp.isBlocked = persisted.isBlocked;
              if (persisted.blockReason !== undefined) cp.blockReason = persisted.blockReason;
              if (persisted.blockedAt !== undefined) cp.blockedAt = persisted.blockedAt;
              if (persisted.isBlacklisted !== undefined) cp.isBlacklisted = persisted.isBlacklisted;
            }
          }
          setDataVersion(v => v + 1); // trigger re-render
        }
      })
      .catch(() => {});
  }, []);

  // Hydrate bank transactions from persisted store (so Bank Ledger entries appear in pickers)
  useEffect(() => {
    Promise.all([hydrateBankTransactions(), hydrateTreasuryAccounts()]).then(() => setDataVersion(v => v + 1));
  }, []);

  // Fetch wallet count for header button
  useEffect(() => {
    fetch("/api/treasury/accounts").then(r => r.json())
      .then(d => setWalletCount((d.accounts || []).filter((a: { type: string; status?: string }) => a.type === "wallet" && a.status !== "inactive").length))
      .catch(() => {});
  }, [walletModalOpen]);

  // Live USDT wallet balance from blockchain
  const [walletBalance, setWalletBalance] = useState<{
    totalUsdt: number;
    balances: Array<{ chain: string; chainLabel: string; address: string; balance: number; label: string; error?: string }>;
    loading: boolean;
    lastUpdated: string | null;
  }>({ totalUsdt: 0, balances: [], loading: true, lastUpdated: null });
  const fetchWalletBalance = useCallback(() => {
    setWalletBalance(prev => ({ ...prev, loading: true }));
    fetch("/api/wallets/balances")
      .then(r => r.json())
      .then(data => {
        setWalletBalance({
          totalUsdt: data.totalUsdt ?? 0,
          balances: data.balances ?? [],
          loading: false,
          lastUpdated: new Date().toISOString(),
        });
      })
      .catch(() => {
        setWalletBalance(prev => ({ ...prev, loading: false }));
      });
  }, []);
  useEffect(() => {
    fetchWalletBalance();
    // Refresh every 60s
    const id = setInterval(fetchWalletBalance, 60_000);
    return () => clearInterval(id);
  }, [fetchWalletBalance]);

  // Track current calendar date so "Today" metrics reset at midnight
  const [calendarDate, setCalendarDate] = useState(() => new Date().toDateString());
  useEffect(() => {
    const check = () => {
      const now = new Date().toDateString();
      if (now !== calendarDate) setCalendarDate(now);
    };
    // Check every 30s; also fires immediately after midnight
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [calendarDate]);

  // Fetch expenses for P&L
  useEffect(() => {
    fetch("/api/expenses").then(r => r.json()).then(d => setExpenses(d.expenses || [])).catch(() => {});
  }, []);

  // Fetch all disputes for filtering
  useEffect(() => {
    fetch("/api/disputes")
      .then((r) => r.json())
      .then((data) => {
        const map: Record<number, Dispute[]> = {};
        for (const d of (data.disputes || []) as Dispute[]) {
          if (!map[d.tradeId]) map[d.tradeId] = [];
          map[d.tradeId].push(d);
        }
        setAllDisputesByTrade(map);
      })
      .catch(() => {});
  }, []);

  // Fetch linked trade → lock mapping
  const fetchLinkedTrades = useCallback(() => {
    fetch("/api/locks/linked-trades")
      .then((r) => r.json())
      .then((data) => {
        if (data.links) {
          const map = new Map<number, { lockId: number; lockType: string }>();
          for (const link of data.links) {
            map.set(link.tradeId, { lockId: link.lockId, lockType: link.lockType });
          }
          setLinkedTradeMap(map);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchLinkedTrades();
  }, [fetchLinkedTrades]);

  // Fetch counterparties from API when assign modal opens
  useEffect(() => {
    if (assignTradeId !== null) {
      fetch("/api/counterparties")
        .then((r) => r.json())
        .then((data) => {
          if (data.counterparties) setCpList(data.counterparties);
        })
        .catch(() => {});
      setRegisteringNew(false);
      setNewCpName("");
      setNewCpAddress("");
      setNewCpChain("tron");
    }
  }, [assignTradeId]);

  // Update trade — persists to API for scanner/user trades
  const handleUpdateTrade = useCallback((id: number, updates: { rate?: number; direction?: Trade["direction"]; status?: Trade["status"]; buyRate?: number; sellRate?: number; middlemanProfit?: number; buyCounterpartyId?: number | null; buyCounterpartyName?: string; sellCounterpartyId?: number | null; sellCounterpartyName?: string }) => {
    // If no real updates, just trigger re-render (used after settlement changes)
    if (Object.keys(updates).length === 0) {
      setDataVersion(v => v + 1);
      return;
    }

    // Try user trade
    const userTrade = userTrades.find(t => t.id === id);
    if (userTrade) {
      if (updates.rate !== undefined) {
        userTrade.rate = updates.rate;
        userTrade.ngnAmount = Math.round(userTrade.usdtAmount * updates.rate);
      }
      if (updates.direction !== undefined) {
        userTrade.direction = updates.direction;
      }
      if (updates.status !== undefined) {
        userTrade.status = updates.status;
      }
      if (updates.buyRate !== undefined) {
        (userTrade as Record<string, unknown>).buyRate = updates.buyRate;
      }
      if (updates.sellRate !== undefined) {
        (userTrade as Record<string, unknown>).sellRate = updates.sellRate;
      }
      if (updates.middlemanProfit !== undefined) {
        (userTrade as Record<string, unknown>).middlemanProfit = updates.middlemanProfit;
      }
      if (updates.buyCounterpartyId !== undefined) {
        (userTrade as Record<string, unknown>).buyCounterpartyId = updates.buyCounterpartyId;
        (userTrade as Record<string, unknown>).buyCounterpartyName = updates.buyCounterpartyName;
      }
      if (updates.sellCounterpartyId !== undefined) {
        (userTrade as Record<string, unknown>).sellCounterpartyId = updates.sellCounterpartyId;
        (userTrade as Record<string, unknown>).sellCounterpartyName = updates.sellCounterpartyName;
      }
      // Persist via API
      fetch("/api/trades", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tradeId: id, ...updates }),
      }).catch(() => {});
      setDataVersion(v => v + 1);
      return;
    }

    // If no real updates, just reload data (used for re-render triggers after settlement changes)
    if (Object.keys(updates).length === 0) {
      scanner.reload().then(() => setDataVersion(v => v + 1));
      return;
    }

    // Scanner trade — update in-memory immediately for instant UI feedback
    const scannerTrade = scannerTrades.find(t => t.id === id);
    if (scannerTrade) {
      if (updates.rate !== undefined) {
        scannerTrade.rate = updates.rate;
        scannerTrade.ngnAmount = updates.rate ? Math.round(scannerTrade.usdtAmount * updates.rate) : null;
      }
      if (updates.direction !== undefined) scannerTrade.direction = updates.direction;
      if (updates.status !== undefined) scannerTrade.status = updates.status;
    }
    setDataVersion(v => v + 1);
    // Persist via API
    fetch("/api/scanner/trades", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tradeId: id, ...updates }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to update");
        return r.json();
      })
      .then(() => {
        scanner.reload();
        setDataVersion(v => v + 1);
      })
      .catch(() => {
        toast("Failed to save rate", "error");
      });
  }, [scanner, toast, userTrades]);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== "All") params.set("tab", activeTab);
    if (searchQuery) params.set("q", searchQuery);
    if (sortCol !== "createdAt") params.set("sort", sortCol);
    if (sortDir !== "desc") params.set("dir", sortDir);
    if (dateRange !== "all") params.set("range", dateRange);
    if (dateRange === "custom" && customFrom) params.set("from", customFrom);
    if (dateRange === "custom" && customTo) params.set("to", customTo);
    if (page > 0) params.set("page", String(page));
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [activeTab, searchQuery, sortCol, sortDir, dateRange, customFrom, customTo, page, router, pathname]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [activeTab, searchQuery, sortCol, sortDir, dateRange, customFrom, customTo]);

  // Scanner trades are real Trade objects created server-side (with coinStatus: "complete")
  const scannerTrades = scanner.scannerTrades;

  // --- Data Pipeline ---

  // Step 1: Filter by tab (scanner + user trades)
  const tabFiltered = useMemo(() => {
    const scFiltered = activeTab === "All"
      ? scannerTrades
      : scannerTrades.filter((t) => t.status === activeTab.toLowerCase());
    const utFiltered = activeTab === "All"
      ? userTrades
      : userTrades.filter((t) => t.status === activeTab.toLowerCase());
    let combined = [...utFiltered, ...scFiltered];
    // Sub-filter by dispute priority/status when on Disputed tab
    if (activeTab === "Disputed") {
      if (disputePriorityFilter !== "all") {
        combined = combined.filter((t) => {
          const disputes = allDisputesByTrade[t.id];
          return disputes?.some((d) => d.priority === disputePriorityFilter);
        });
      }
      if (disputeStatusFilter !== "all") {
        combined = combined.filter((t) => {
          const disputes = allDisputesByTrade[t.id];
          return disputes?.some((d) => d.status === disputeStatusFilter);
        });
      }
    }
    return combined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataVersion, scannerTrades, userTrades, disputePriorityFilter, disputeStatusFilter, allDisputesByTrade]);

  // Step 2: Filter by date range
  const dateFiltered = useMemo(() => {
    if (dateRange === "all") return tabFiltered;
    if (dateRange === "custom") {
      const from = customFrom ? new Date(customFrom + "T00:00:00") : null;
      const to = customTo ? new Date(customTo + "T23:59:59") : null;
      if (!from && !to) return tabFiltered;
      return tabFiltered.filter(t => {
        const d = new Date(t.createdAt);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }
    if (dateRange === "today") {
      // Use WAT date key comparison for consistency with day grouping
      const todayKey = watDateKey(new Date());
      return tabFiltered.filter(t => watDateKey(new Date(t.createdAt)) === todayKey);
    }
    const now = new Date();
    const cutoff = new Date();
    if (dateRange === "7d") cutoff.setDate(now.getDate() - 7);
    else if (dateRange === "30d") cutoff.setDate(now.getDate() - 30);
    return tabFiltered.filter(t => new Date(t.createdAt) >= cutoff);
  }, [tabFiltered, dateRange, customFrom, customTo]);

  // Step 3: Filter by search
  // Detect if search query is a USDT amount (digits, commas, optional decimal)
  const searchAmountNum = useMemo(() => {
    const cleaned = searchQuery.replace(/,/g, "").trim();
    if (/^\d+(\.\d{0,2})?$/.test(cleaned) && cleaned.length >= 2) return parseFloat(cleaned);
    return null;
  }, [searchQuery]);

  const searched = useMemo(() => {
    let filtered = dateFiltered;
    // Apply direction filter
    if (directionFilter === "middleman") {
      filtered = filtered.filter(t => t.direction === "middleman");
    }
    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.toLowerCase();
    // For TX hash searches, match exact txHash
    if (looksLikeTxHash(searchQuery)) {
      return filtered.filter(t =>
        t.txHash && t.txHash.toLowerCase() === q.trim()
      );
    }
    // Amount search — show amounts >= searched amount, sorted by proximity (closest first)
    if (searchAmountNum !== null) {
      const matches = filtered.filter(t => t.usdtAmount >= searchAmountNum);
      return matches.sort((a, b) => Math.abs(a.usdtAmount - searchAmountNum) - Math.abs(b.usdtAmount - searchAmountNum));
    }
    return filtered.filter(t =>
      (t.counterpartyName?.toLowerCase().includes(q)) ||
      (t.exchangeName?.toLowerCase().includes(q)) ||
      (t.txHash?.toLowerCase().includes(q)) ||
      (t.senderAddress?.toLowerCase().includes(q)) ||
      (t.senderEntity?.toLowerCase().includes(q)) ||
      (t.buyCounterpartyName?.toLowerCase().includes(q)) ||
      (t.sellCounterpartyName?.toLowerCase().includes(q)) ||
      t.usdtAmount.toString().includes(q) ||
      t.direction.toLowerCase().includes(q)
    );
  }, [dateFiltered, searchQuery, directionFilter, searchAmountNum]);

  // Step 4: Sort by column (skip when amount search active — keep proximity sort)
  // When only trade content changes (e.g. counterparty assignment) but the set of
  // trades and sort criteria are unchanged, preserve previous row order so the row
  // the user just edited doesn't jump to a different position.
  const sorted = useMemo(() => {
    if (searchAmountNum !== null) return searched;
    const arr = [...searched];
    const dir = sortDir === "asc" ? 1 : -1;

    const sortChanged = prevSortKeyRef.current.col !== sortCol || prevSortKeyRef.current.dir !== sortDir;
    prevSortKeyRef.current = { col: sortCol, dir: sortDir };

    const currentIds = new Set(arr.map(t => t.id));
    const prevIds = sortedIdsRef.current;
    const sameSet = currentIds.size === prevIds.length && prevIds.every(id => currentIds.has(id));

    if (!sortChanged && sameSet) {
      // Content-only update — preserve previous row order
      const orderMap = new Map(prevIds.map((id, idx) => [id, idx]));
      arr.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
    } else {
      arr.sort((a, b) => {
        switch (sortCol) {
          case "counterparty": return dir * (getName(a)).localeCompare(getName(b));
          case "direction": return dir * a.direction.localeCompare(b.direction);
          case "usdtAmount": return dir * (a.usdtAmount - b.usdtAmount);
          case "rate": return dir * ((a.rate ?? 0) - (b.rate ?? 0));
          case "ngnAmount": return dir * ((a.ngnAmount ?? 0) - (b.ngnAmount ?? 0));
          case "chain": return dir * a.chain.localeCompare(b.chain);
          case "status": return dir * a.status.localeCompare(b.status);
          case "createdAt": return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          default: return 0;
        }
      });
    }

    sortedIdsRef.current = arr.map(t => t.id);
    return arr;
  }, [searched, sortCol, sortDir, searchAmountNum]);

  // Step 5: Paginate
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = useMemo(() => sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sorted, page]);

  const selectedTrade = expandedId ? sorted.find(t => t.id === expandedId) ?? null : null;

  // Navigate to a specific trade: switch page, expand row, scroll into view, flash highlight
  const navigateToTrade = useCallback((tradeId: number) => {
    const idx = sorted.findIndex((t) => t.id === tradeId);
    if (idx === -1) return;
    const targetPage = Math.floor(idx / PAGE_SIZE);
    setPage(targetPage);
    setExpandedId(tradeId);
    setShowFinancialSummary(false);
    // Wait for DOM to reflect new page, then scroll and highlight
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = document.querySelector(`[data-trade-id="${tradeId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setHighlightedId(tradeId);
          setTimeout(() => setHighlightedId(null), 1500);
        }
      }, 50);
    });
  }, [sorted]);

  // Step 6: Group by date (re-group on midnight rollover)
  const groupedTrades = useMemo(
    () => groupTradesByDate(paginated),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paginated, calendarDate]
  );

  // --- Keyboard Navigation ---
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIdx(prev => prev === null ? 0 : Math.min(prev + 1, paginated.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIdx(prev => prev === null ? 0 : Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && focusedIdx !== null) {
        e.preventDefault();
        const trade = paginated[focusedIdx];
        if (trade) setExpandedId(expandedId === trade.id ? null : trade.id);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setExpandedId(null);
        setFocusedIdx(null);
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [paginated, focusedIdx, expandedId]);

  // --- Batch Select ---
  const handleSelectToggle = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === paginated.length && paginated.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map(t => t.id)));
    }
  }, [selectedIds.size, paginated]);

  const smartSelect = useCallback((mode: string) => {
    let ids: number[] = [];

    if (mode === "buys_since_last_sell") {
      // Walk sorted (newest-first by default) — find last sell, select all buys after it
      const chronological = [...sorted].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      let lastSellIdx = -1;
      for (let i = chronological.length - 1; i >= 0; i--) {
        if (chronological[i].direction === "sell") { lastSellIdx = i; break; }
      }
      const startIdx = lastSellIdx >= 0 ? lastSellIdx + 1 : 0;
      ids = chronological.slice(startIdx).filter(t => t.direction === "buy").map(t => t.id);
    } else if (mode === "buys_today") {
      ids = sorted.filter(t => t.direction === "buy" && isToday(t.createdAt)).map(t => t.id);
    } else if (mode === "sells_today") {
      ids = sorted.filter(t => t.direction === "sell" && isToday(t.createdAt)).map(t => t.id);
    } else if (mode === "buys_yesterday") {
      ids = sorted.filter(t => t.direction === "buy" && isYesterday(t.createdAt)).map(t => t.id);
    } else if (mode === "sells_yesterday") {
      ids = sorted.filter(t => t.direction === "sell" && isYesterday(t.createdAt)).map(t => t.id);
    } else if (mode === "same_counterparty") {
      const selected = sorted.filter(t => selectedIds.has(t.id));
      const cpIds = new Set(selected.map(t => t.counterpartyId).filter(Boolean));
      if (cpIds.size > 0) {
        ids = sorted.filter(t => t.counterpartyId != null && cpIds.has(t.counterpartyId)).map(t => t.id);
      }
    } else if (mode === "same_rate") {
      const selected = sorted.filter(t => selectedIds.has(t.id));
      const rates = new Set(selected.map(t => t.rate).filter((r): r is number => r != null));
      if (rates.size > 0) {
        ids = sorted.filter(t => t.rate != null && rates.has(t.rate)).map(t => t.id);
      }
    }

    if (ids.length > 0) {
      setSelectedIds(new Set(ids));
    } else {
      toast("No matching trades found", "info");
    }
    setShowSmartSelect(false);
  }, [sorted, selectedIds, toast]);

  // --- Accounting computation (recompute on dataVersion change or midnight rollover) ---
  void dataVersion;
  const accounting = useMemo(() => {
    const all = [...scannerTrades, ...userTrades];
    return computeLedgerAccounting(all);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion, scannerTrades, userTrades, calendarDate]);

  // Period-specific P&L for header cards
  const periodPnL: PeriodPnL = useMemo(() => {
    if (headerPeriod === "allTime") return accounting.allTime;
    if (headerPeriod === "today") return accounting.today;
    const all = [...scannerTrades, ...userTrades];
    const filter =
      headerPeriod === "yesterday"
        ? (d: string) => isYesterday(d)
        : (d: string) => isWithinDays(d, headerPeriod === "7d" ? 7 : 30);
    return computePeriodPnL(all, accounting.trades, filter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerPeriod, accounting, calendarDate]);

  // Period-filtered expenses for P&L
  const periodExpenses = useMemo(() => {
    if (headerPeriod === "allTime") return expenses;
    return expenses.filter(e => {
      if (headerPeriod === "today") return isToday(e.date);
      if (headerPeriod === "yesterday") return isYesterday(e.date);
      return isWithinDays(e.date, headerPeriod === "7d" ? 7 : 30);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerPeriod, expenses, calendarDate]);

  const totalExpenses = periodExpenses.reduce((s, e) => s + e.amount, 0);

  const expensesByCategory = useMemo(() => {
    const map: Partial<Record<ExpenseCategory, number>> = {};
    for (const e of periodExpenses) {
      map[e.category] = (map[e.category] || 0) + e.amount;
    }
    return map;
  }, [periodExpenses]);

  // Derived values for summary bar (always today — independent of header period)
  const todayBought = accounting.today.totalBought;
  const todaySold = accounting.today.totalSold;
  const netPosition = todayBought - todaySold;
  // Avg rates for header cards: use period-specific values
  const avgBuyRate = periodPnL.avgBuyRate ? Math.round(periodPnL.avgBuyRate) : null;
  const avgSellRate = periodPnL.avgSellRate ? Math.round(periodPnL.avgSellRate) : null;
  const spread = (avgSellRate && avgBuyRate) ? avgSellRate - avgBuyRate : null;

  // Comma-format helper for number inputs (preserves raw digits, adds commas for display)
  const formatWithCommas = useCallback((val: string): string => {
    const stripped = val.replace(/[^0-9.]/g, "");
    const parts = stripped.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
  }, []);

  // Derive last buy/sell rates for prefill
  const lastRates = useMemo(() => {
    const all = [...scannerTrades, ...userTrades].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const lastBuy = all.find(t => t.direction === "buy" && t.rate != null);
    const lastSell = all.find(t => t.direction === "sell" && t.rate != null);
    const lastMm = all.find(t => t.direction === "middleman" && t.buyRate != null && t.sellRate != null);
    return {
      buyRate: lastMm?.buyRate ?? lastBuy?.rate ?? null,
      sellRate: lastMm?.sellRate ?? lastSell?.rate ?? null,
    };
  }, [scannerTrades, userTrades]);

  // Hash paste handler for middleman modal — auto-detects chain & amount
  const handleMmHashPaste = useCallback((val: string) => {
    const hash = val.trim();
    setMiddlemanForm(f => ({ ...f, txHash: hash }));
    if (!looksLikeTxHash(hash)) {
      setMmHashStatus("");
      return;
    }
    // Check if already exists
    const allTr = [...scannerTrades, ...userTrades];
    const exists = allTr.find(t => t.txHash && t.txHash.toLowerCase() === hash.toLowerCase());
    if (exists) {
      setMmHashStatus("existing");
      return;
    }
    // Lookup on blockchain
    setMmHashLooking(true);
    setMmHashStatus("");
    fetch(`/api/scanner/lookup?hash=${encodeURIComponent(hash)}`)
      .then(r => r.json())
      .then(data => {
        if (data.found && data.amount) {
          setMiddlemanForm(f => ({
            ...f,
            amount: formatWithCommas(String(data.amount)),
            chain: (data.chain ?? "tron") as "tron" | "bsc" | "ethereum",
          }));
          setMmHashStatus("found");
        } else {
          setMmHashStatus("not_found");
        }
      })
      .catch(() => setMmHashStatus("not_found"))
      .finally(() => setMmHashLooking(false));
  }, [scannerTrades, userTrades, formatWithCommas]);

  // Tab badge counts (includes scanner trades)
  const allTrades = useMemo(() => [...scannerTrades, ...userTrades], [scannerTrades, userTrades, dataVersion]);

  const tabCounts = useMemo(() => ({
    All: allTrades.length,
    Pending: allTrades.filter(t => t.status === "pending").length,
    Settled: allTrades.filter(t => t.status === "settled").length,
    Disputed: allTrades.filter(t => t.status === "disputed").length,
    Expired: allTrades.filter(t => t.status === "expired").length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [allTrades, dataVersion]);

  // Clear-all-filters flag
  const hasActiveFilters = activeTab !== "All" || searchQuery !== "" || sortCol !== "createdAt" || sortDir !== "desc" || dateRange !== "today" || customFrom !== "" || customTo !== "";

  // --- Sort Header Component ---
  function SortHeader({ col, label, align, className: extraClass }: { col: SortColumn; label: string; align?: "right" | "left"; className?: string }) {
    const active = sortCol === col;
    return (
      <th
        className={`text-[13px] font-semibold text-muted px-2.5 py-3 cursor-pointer select-none hover:text-foreground transition-colors ${align === "right" ? "text-right" : "text-left"} ${extraClass || ""}`}
        onClick={() => {
          if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
          else { setSortCol(col); setSortDir("asc"); }
        }}
      >
        <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
          {label}
          {active && <ChevronUp className={`w-3 h-3 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`} />}
        </span>
      </th>
    );
  }

  // --- Export ---
  function handleExportCsv(trades?: Trade[]) {
    const toExport = trades || sorted;
    const csv = generateCsv(toExport);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chainfiler-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast("CSV exported");
  }

  // --- Compute flat index offset per date group ---
  function computeGroupOffsets(): Map<string, number> {
    const offsets = new Map<string, number>();
    let offset = 0;
    for (const [dateLabel, trades] of groupedTrades.entries()) {
      offsets.set(dateLabel, offset);
      offset += trades.length;
    }
    return offsets;
  }
  const groupOffsets = computeGroupOffsets();

  return (
    <PageWrapper>
      <PageHeader
        title="Trade Ledger"
        subtitle="Complete history of all your OTC trades"
        actions={
          <div className="flex items-center gap-1.5">
            <Button variant="secondary" size="sm" onClick={() => setWalletModalOpen(true)}>
              <Wallet className="w-3.5 h-3.5" />
              Wallets{walletCount > 0 ? ` · ${walletCount}` : ""}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleExportCsv()}>
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>
        }
      />

      {/* Period Selector + Balance Cards */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1], delay: 0.05 }}
        className="mb-4"
      >
        {/* Period pills */}
        <div className="flex items-center gap-1.5 mb-3">
          {([
            ["allTime", "All Time"],
            ["today", "Today"],
            ["yesterday", "Yesterday"],
            ["7d", "7 Days"],
            ["30d", "30 Days"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setHeaderPeriod(key)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                headerPeriod === key
                  ? "bg-accent text-white"
                  : "bg-surface-2 text-muted hover:text-foreground hover:bg-surface-3"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* USDT Balance — live from blockchain */}
          <div className="bg-surface rounded-lg p-4" style={{ border: "1px solid var(--border)" }}>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-bold text-muted uppercase tracking-wider"><Tooltip text="Live USDT balance from your wallets on-chain">USDT Balance</Tooltip></p>
              <button
                onClick={fetchWalletBalance}
                disabled={walletBalance.loading}
                className="text-[10px] text-muted hover:text-accent transition-colors disabled:opacity-50"
                title="Refresh balance"
              >
                <RefreshCw className={`w-3 h-3 ${walletBalance.loading ? "animate-spin" : ""}`} />
              </button>
            </div>
            <p className="text-xl font-bold text-foreground tabular-nums font-mono">
              {walletBalance.loading && walletBalance.totalUsdt === 0 ? "—" : walletBalance.totalUsdt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-xs text-muted ml-1">USDT</span>
            </p>
            {walletBalance.balances.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {walletBalance.balances.map((b) => (
                  <p key={b.address} className="text-[10px] text-muted tabular-nums truncate" title={b.address}>
                    {b.label} <span className="text-foreground/60">{b.chainLabel}</span> — {b.balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Volume — period-specific */}
          <div className="bg-surface rounded-lg p-4" style={{ border: "1px solid var(--border)" }}>
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5"><Tooltip text="Total USDT bought and sold in this period">Volume</Tooltip></p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-success tabular-nums font-mono">
                {formatCurrency(Math.round(periodPnL.totalBought))}
              </span>
              <span className="text-xs text-muted">/</span>
              <span className="text-xl font-bold text-danger tabular-nums font-mono">
                {formatCurrency(Math.round(periodPnL.totalSold))}
              </span>
              <span className="text-xs text-muted">USDT</span>
            </div>
            <p className="text-[10px] text-muted tabular-nums mt-1">
              {periodPnL.buyCount} buy{periodPnL.buyCount !== 1 ? "s" : ""}
              <span className="mx-1">/</span>
              {periodPnL.sellCount} sell{periodPnL.sellCount !== 1 ? "s" : ""}
            </p>
          </div>

          {/* Avg Rates — period-specific */}
          <div className="bg-surface rounded-lg p-4" style={{ border: "1px solid var(--border)" }}>
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5"><Tooltip text="Average buy and sell rates for this period, with the spread between them">Avg Rates</Tooltip></p>
            <div className="flex items-baseline gap-3">
              <span className="text-xl font-bold text-foreground tabular-nums font-mono">
                {avgBuyRate ? formatCurrency(avgBuyRate, "NGN") : "--"}
              </span>
              <span className="text-xs text-muted">/</span>
              <span className="text-xl font-bold text-foreground tabular-nums font-mono">
                {avgSellRate ? formatCurrency(avgSellRate, "NGN") : "--"}
              </span>
            </div>
            <p className="text-[10px] text-muted mt-1">
              Buy / Sell
              {spread != null && (
                <span className={`ml-2 ${spread >= 0 ? "text-success" : "text-danger"}`}>
                  Spread {spread >= 0 ? "+" : ""}{formatCurrency(spread, "NGN")}
                </span>
              )}
            </p>
          </div>

          {/* Gross Profit — period-specific */}
          <div className="bg-surface rounded-lg p-4" style={{ border: "1px solid var(--border)" }}>
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1.5"><Tooltip text="Sales Revenue minus Cost of Sales plus Middleman profits — your total trading margin before expenses">Gross Profit</Tooltip></p>
            <p className={`text-xl font-bold tabular-nums font-mono ${(periodPnL.grossProfit + periodPnL.middlemanProfit) >= 0 ? "text-success" : "text-danger"}`}>
              {(periodPnL.grossProfit + periodPnL.middlemanProfit) >= 0 ? "+" : ""}{formatCurrency(Math.round(Math.abs(periodPnL.grossProfit + periodPnL.middlemanProfit)), "NGN")}
            </p>
            <p className="text-[10px] text-muted tabular-nums mt-1">
              {periodPnL.marginPct != null ? `${periodPnL.marginPct.toFixed(2)}% margin` : "--"}
              <span className="mx-1">/</span>
              {periodPnL.sellCount} sell{periodPnL.sellCount !== 1 ? "s" : ""}
              {periodPnL.middlemanProfit !== 0 && (
                <span className="ml-1">
                  · <span className={periodPnL.middlemanProfit >= 0 ? "text-success/70" : "text-danger/70"}>
                    {periodPnL.middlemanProfit >= 0 ? "+" : ""}₦{Math.abs(Math.round(periodPnL.middlemanProfit)).toLocaleString()} MM
                  </span>
                </span>
              )}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Trading Activity + View Financials */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1], delay: 0.1 }}
        className="mb-6"
      >
        <div className="bg-surface rounded-lg px-5 py-3" style={{ border: "1px solid var(--border)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5 text-[12px] tabular-nums">
              <span className="text-[10px] font-bold text-muted uppercase tracking-wider">{{ allTime: "All Time", today: "Today", yesterday: "Yesterday", "7d": "7 Days", "30d": "30 Days" }[headerPeriod]}</span>
              <span>
                <span className="text-success font-medium">+{formatCurrency(Math.round(periodPnL.totalBought))} USDT</span>
                <span className="text-muted ml-1">bought</span>
              </span>
              <span>
                <span className="text-danger font-medium">-{formatCurrency(Math.round(periodPnL.totalSold))} USDT</span>
                <span className="text-muted ml-1">sold</span>
              </span>
              <span className="text-muted">{periodPnL.buyCount + periodPnL.sellCount} trade{(periodPnL.buyCount + periodPnL.sellCount) !== 1 ? "s" : ""}</span>
            </div>
            <button
              onClick={() => setShowFinancialSummary(!showFinancialSummary)}
              className="flex items-center gap-1.5 text-[12px] text-accent hover:text-accent/80 transition-colors font-semibold shrink-0"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFinancialSummary ? "rotate-180" : ""}`} />
              {showFinancialSummary ? "Hide" : "View"} Financials
            </button>
          </div>

          {/* Collapsible Financial Summary */}
          <AnimatePresence>
            {showFinancialSummary && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-border">

                  {/* ── Income Statement + Balance Sheet ── */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Income Statement (P&L) — period-specific */}
                    <div
                      className="rounded-xl p-5"
                      style={{ background: "linear-gradient(160deg, var(--surface-2), var(--surface))", border: "1px solid var(--border)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                    >
                      <h3 className="text-[10px] font-extrabold text-muted uppercase tracking-widest mb-4 flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5" />
                        Income Statement
                        <span className="text-muted/50 normal-case font-normal tracking-normal">
                          · {{ allTime: "All Time", today: "Today", yesterday: "Yesterday", "7d": "7 Days", "30d": "30 Days" }[headerPeriod]}
                        </span>
                      </h3>

                      {/* Revenue section */}
                      <div className="space-y-2 text-[13px]">
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="Total naira received from selling USDT in this period">Sales Revenue</Tooltip></span>
                          <span className="text-foreground font-semibold tabular-nums">{formatCurrency(Math.round(periodPnL.grossRevenue), "NGN")}</span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="What the sold USDT originally cost you to buy (WAC × quantity)">Cost of Sales</Tooltip></span>
                          <span className="text-danger/80 font-semibold tabular-nums">({formatCurrency(Math.round(periodPnL.costOfSales), "NGN")})</span>
                        </div>
                      </div>

                      {/* Gross Profit result */}
                      <div className="mt-3 pt-3 border-t border-border/40 space-y-1.5 text-[13px]">
                        <div className="flex justify-between items-baseline">
                          <span className="text-foreground font-bold"><Tooltip text="Sales Revenue minus Cost of Sales — your trading margin">Gross Profit</Tooltip></span>
                          <span className={`text-[15px] font-extrabold tabular-nums ${periodPnL.grossProfit >= 0 ? "text-success" : "text-danger"}`}>
                            {periodPnL.grossProfit >= 0 ? "" : "-"}{formatCurrency(Math.round(Math.abs(periodPnL.grossProfit)), "NGN")}
                          </span>
                        </div>
                        {periodPnL.marginPct != null && (
                          <div className="flex justify-between items-baseline">
                            <span className="text-muted pl-2 text-[12px]"><Tooltip text="Gross Profit as a percentage of Sales Revenue">Gross Margin</Tooltip></span>
                            <span className="text-muted tabular-nums text-[12px]">{periodPnL.marginPct.toFixed(1)}%</span>
                          </div>
                        )}
                        {periodPnL.middlemanProfit !== 0 && (
                          <div className="flex justify-between items-baseline mt-1">
                            <span className="text-muted text-[12px]"><Tooltip text="Total profit from middleman deals (sell rate - buy rate) × quantity">Middleman Profit</Tooltip></span>
                            <span className={`font-semibold tabular-nums text-[13px] ${periodPnL.middlemanProfit >= 0 ? "text-success" : "text-danger"}`}>
                              {periodPnL.middlemanProfit >= 0 ? "+" : "-"}{formatCurrency(Math.round(Math.abs(periodPnL.middlemanProfit)), "NGN")}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Operating Expenses sub-card */}
                      <div className="mt-4 rounded-lg p-3.5 space-y-2 text-[13px]" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <p className="text-[9px] text-muted font-extrabold uppercase tracking-widest mb-1.5"><Tooltip text="Business costs like fees, salaries, and overheads">Operating Expenses</Tooltip></p>
                        {(Object.entries(expensesByCategory) as [ExpenseCategory, number][])
                          .filter(([, amt]) => amt > 0)
                          .sort(([, a], [, b]) => b - a)
                          .map(([cat, amt]) => (
                            <div key={cat} className="flex justify-between items-baseline">
                              <span className="text-muted text-[12px]">{EXPENSE_CATEGORY_LABELS[cat]}</span>
                              <span className="text-danger/70 tabular-nums text-[12px]">({formatCurrency(Math.round(amt), "NGN")})</span>
                            </div>
                          ))
                        }
                        <div className="flex justify-between items-baseline border-t border-border/50 pt-2">
                          <span className="text-muted font-semibold"><Tooltip text="Sum of all operating costs for this period">Total</Tooltip></span>
                          <span className="text-danger/80 font-semibold tabular-nums">({formatCurrency(Math.round(totalExpenses), "NGN")})</span>
                        </div>
                      </div>

                      {/* Net Profit result */}
                      <div className="mt-4 pt-3 border-t border-border space-y-1.5 text-[13px]">
                        <div className="flex justify-between items-baseline">
                          <span className="text-foreground font-extrabold"><Tooltip text="What remains after subtracting all expenses from Gross Profit">Net Profit</Tooltip></span>
                          <span className={`text-[16px] font-extrabold tabular-nums ${(periodPnL.grossProfit + periodPnL.middlemanProfit - totalExpenses) >= 0 ? "text-success" : "text-danger"}`}>
                            {(periodPnL.grossProfit + periodPnL.middlemanProfit - totalExpenses) >= 0 ? "" : "-"}{formatCurrency(Math.round(Math.abs(periodPnL.grossProfit + periodPnL.middlemanProfit - totalExpenses)), "NGN")}
                          </span>
                        </div>
                        {periodPnL.grossRevenue > 0 && (
                          <div className="flex justify-between items-baseline">
                            <span className="text-muted pl-2 text-[12px]"><Tooltip text="Net Profit as a percentage of Sales Revenue">Net Margin</Tooltip></span>
                            <span className="text-muted tabular-nums text-[12px]">{(((periodPnL.grossProfit - totalExpenses) / periodPnL.grossRevenue) * 100).toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Balance Sheet — always all-time */}
                    <div
                      className="rounded-xl p-5 flex flex-col"
                      style={{ background: "linear-gradient(160deg, var(--surface-2), var(--surface))", border: "1px solid var(--border)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                    >
                      <h3 className="text-[10px] font-extrabold text-muted uppercase tracking-widest mb-4 flex items-center gap-1.5">
                        <Coins className="w-3.5 h-3.5" />
                        Balance Sheet
                        <span className="text-muted/50 normal-case font-normal tracking-normal">· All Time</span>
                      </h3>

                      {/* Assets sub-card */}
                      <div className="rounded-lg p-3.5 space-y-2 text-[13px]" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <p className="text-[9px] text-muted font-extrabold uppercase tracking-widest mb-1.5">Assets</p>
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="Total naira you spent acquiring your current USDT holdings">Inventory Cost</Tooltip></span>
                          <span className="text-foreground font-semibold tabular-nums">{formatCurrency(Math.round(accounting.inventory.totalCost), "NGN")}</span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="Total USDT currently in your inventory">USDT On Hand</Tooltip></span>
                          <span className="text-foreground font-semibold tabular-nums">{formatCurrency(Math.round(accounting.inventory.totalQuantity))} USDT</span>
                        </div>
                        {accounting.inventory.uncostedQuantity > 0 && (
                          <div className="flex justify-between items-baseline">
                            <span className="text-warning/80 text-[12px]"><Tooltip text="USDT bought without a rate — cost unknown, not included in WAC">Uncosted USDT</Tooltip></span>
                            <span className="text-warning/80 tabular-nums text-[12px]">{formatCurrency(Math.round(accounting.inventory.uncostedQuantity))} USDT</span>
                          </div>
                        )}
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="Weighted Average Cost — your average buy price per USDT">WAC</Tooltip></span>
                          <span className="text-foreground font-semibold tabular-nums">{formatCurrency(Math.round(accounting.inventory.wac), "NGN")}/USDT</span>
                        </div>
                        {accounting.position.lentUsdt > 0 && (
                          <div className="flex justify-between items-baseline">
                            <span className="text-accent"><Tooltip text="USDT lent to others — a receivable, not in your inventory">Lent Out</Tooltip></span>
                            <span className="text-accent font-semibold tabular-nums">{formatCurrency(Math.round(accounting.position.lentUsdt))} USDT</span>
                          </div>
                        )}
                        {accounting.fiat.pendingIn > 0 && (
                          <div className="flex justify-between items-baseline">
                            <span className="text-muted"><Tooltip text="Naira expected from unsettled sell trades">Pending Inflows</Tooltip></span>
                            <span className="text-foreground font-semibold tabular-nums">{formatCurrency(Math.round(accounting.fiat.pendingIn), "NGN")}</span>
                          </div>
                        )}
                      </div>

                      {/* Liabilities sub-card */}
                      <div className="mt-3 rounded-lg p-3.5 space-y-2 text-[13px]" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <p className="text-[9px] text-muted font-extrabold uppercase tracking-widest mb-1.5">Liabilities</p>
                        {accounting.position.borrowedUsdt > 0 ? (
                          <div className="flex justify-between items-baseline">
                            <span className="text-warning"><Tooltip text="USDT borrowed from others — a liability you owe back">Borrowed USDT</Tooltip></span>
                            <span className="text-warning font-semibold tabular-nums">{formatCurrency(Math.round(accounting.position.borrowedUsdt))} USDT</span>
                          </div>
                        ) : (
                          <div className="flex justify-between items-baseline">
                            <span className="text-muted text-[12px]">No outstanding liabilities</span>
                          </div>
                        )}
                        {accounting.fiat.pendingOut > 0 && (
                          <div className="flex justify-between items-baseline">
                            <span className="text-muted"><Tooltip text="Naira owed for unsettled buy trades">Pending Outflows</Tooltip></span>
                            <span className="text-foreground font-semibold tabular-nums">{formatCurrency(Math.round(accounting.fiat.pendingOut), "NGN")}</span>
                          </div>
                        )}
                      </div>

                      {/* Cash Flow sub-card */}
                      <div className="mt-3 rounded-lg p-3.5 space-y-2 text-[13px]" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <p className="text-[9px] text-muted font-extrabold uppercase tracking-widest mb-1.5">Cash Flow</p>
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="Total naira spent buying USDT (settled trades)">Total Outflows</Tooltip></span>
                          <span className="text-danger/80 font-semibold tabular-nums">({formatCurrency(Math.round(accounting.fiat.ngnOut), "NGN")})</span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted"><Tooltip text="Total naira received from selling USDT (settled trades)">Total Inflows</Tooltip></span>
                          <span className="text-success/80 font-semibold tabular-nums">{formatCurrency(Math.round(accounting.fiat.ngnIn), "NGN")}</span>
                        </div>
                        <div className="flex justify-between items-baseline border-t border-border/50 pt-2">
                          <span className="text-foreground font-bold"><Tooltip text="Inflows minus Outflows — net cash position">Net</Tooltip></span>
                          <span className={`font-bold tabular-nums ${accounting.fiat.netFlow >= 0 ? "text-success" : "text-danger"}`}>
                            {accounting.fiat.netFlow >= 0 ? "+" : ""}{formatCurrency(Math.round(accounting.fiat.netFlow), "NGN")}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* ── Accounting Warnings ── */}
                  {accounting.warnings.length > 0 && (() => {
                    const WARNING_CONFIG: Record<AccountingWarning["type"], { label: string; hint: string; color: "warning" | "muted" | "danger" }> = {
                      no_rate: { label: "Missing Rate", hint: "Add a rate to these trades so they count toward your P&L", color: "warning" },
                      no_cost_basis: { label: "No Cost Basis", hint: "Add rates to earlier buy trades so cost basis can be calculated", color: "warning" },
                      partial_cost_basis: { label: "Partial Cost Basis", hint: "Only part of the sell had known cost — review these trades", color: "muted" },
                      negative_inventory: { label: "Negative Inventory", hint: "Sold more than available — a trade may be missing from your ledger", color: "danger" },
                    };
                    const grouped = new Map<AccountingWarning["type"], number[]>();
                    for (const w of accounting.warnings) {
                      const list = grouped.get(w.type);
                      if (list) list.push(w.tradeId);
                      else grouped.set(w.type, [w.tradeId]);
                    }
                    const typeOrder: AccountingWarning["type"][] = ["no_rate", "no_cost_basis", "partial_cost_basis", "negative_inventory"];
                    return (
                      <div className="mt-5 rounded-xl p-4" style={{ background: "linear-gradient(160deg, var(--surface-2), var(--surface))", border: "1px solid var(--border)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                        <p className="text-[9px] font-extrabold text-warning uppercase tracking-widest mb-3 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" /> Accounting Notes ({accounting.warnings.length})
                        </p>
                        <div className="space-y-2">
                          {typeOrder.filter((t) => grouped.has(t)).map((type) => {
                            const tradeIds = grouped.get(type)!;
                            const cfg = WARNING_CONFIG[type];
                            const isOpen = expandedWarningType === type;
                            const colorClass = cfg.color === "danger" ? "text-danger" : cfg.color === "warning" ? "text-warning" : "text-muted";
                            return (
                              <div key={type} className="rounded-lg relative" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                                <button
                                  onClick={() => setExpandedWarningType(isOpen ? null : type)}
                                  className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left"
                                >
                                  {cfg.color === "danger" ? (
                                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-danger/20 flex items-center justify-center">
                                      <span className="w-2 h-2 rounded-full bg-danger" />
                                    </span>
                                  ) : (
                                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${colorClass}`} />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-[12px] font-bold ${colorClass}`}>
                                      {cfg.label}
                                    </span>
                                    <span className="text-[11px] text-muted ml-1.5">
                                      {tradeIds.length} trade{tradeIds.length !== 1 ? "s" : ""}
                                    </span>
                                    <p className="text-[10px] text-muted/60 mt-0.5 leading-tight">{cfg.hint}</p>
                                  </div>
                                  <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                </button>
                                {/* Nested dropdown popover for trade chips */}
                                <AnimatePresence>
                                  {isOpen && (
                                    <motion.div
                                      initial={{ opacity: 0, y: -4 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: -4 }}
                                      transition={{ duration: 0.15 }}
                                      className="absolute left-3 right-3 z-20 rounded-lg p-3 max-h-[180px] overflow-y-auto"
                                      style={{ background: "var(--surface-2)", border: "1px solid var(--border)", boxShadow: "0 8px 24px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)", top: "100%" }}
                                    >
                                      <div className="flex flex-wrap gap-1.5">
                                        {tradeIds.map((id) => (
                                          <button
                                            key={id}
                                            onClick={() => navigateToTrade(id)}
                                            className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold tabular-nums hover:scale-105 active:scale-95 transition-all cursor-pointer"
                                            style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--accent)" }}
                                          >
                                            #{id}
                                          </button>
                                        ))}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Filter Tabs */}
      <motion.div
        {...sectionFadeIn(0.1)}
        className="mb-6"
      >
        <div className="flex items-center gap-3">
          <TabBar
            tabs={FILTER_TABS.map(t => ({ label: t, count: tabCounts[t] }))}
            active={activeTab}
            onChange={(tab) => { setActiveTab(tab); setSelectedIds(new Set()); if (tab === "Disputed" || tab === "Expired") setDateRange("all"); }}
          />
          {activeTab === "Disputed" && (
            <>
              <select
                value={disputePriorityFilter}
                onChange={(e) => setDisputePriorityFilter(e.target.value)}
                className="bg-surface-2 text-[11px] text-foreground rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent/30"
                style={{ border: "1px solid var(--border)" }}
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={disputeStatusFilter}
                onChange={(e) => setDisputeStatusFilter(e.target.value)}
                className="bg-surface-2 text-[11px] text-foreground rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent/30"
                style={{ border: "1px solid var(--border)" }}
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="under_review">Under Review</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </>
          )}
          <button
            onClick={() => setDirectionFilter(directionFilter === "middleman" ? "all" : "middleman")}
            className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${
              directionFilter === "middleman"
                ? "bg-purple-500/15 text-purple-400"
                : "text-muted hover:text-foreground hover:bg-surface-2"
            }`}
          >
            Middleman
          </button>
          <button
            onClick={() => {
              setMiddlemanForm({
                txHash: "", chain: "tron", amount: "",
                buyCpId: null, buyCpName: "",
                buyRate: lastRates.buyRate != null ? formatWithCommas(String(lastRates.buyRate)) : "",
                sellCpId: null, sellCpName: "",
                sellRate: lastRates.sellRate != null ? formatWithCommas(String(lastRates.sellRate)) : "",
                nairaFlow: "through_you",
              });
              setMmBuyCpMode("idle"); setMmSellCpMode("idle");
              setMmBuyCpSearch(""); setMmSellCpSearch("");
              setMmCpExpanded(false);
              setMmHashStatus(""); setMmHashLooking(false);
              setShowMiddlemanModal(true);
            }}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-md bg-purple-500/15 text-purple-400 hover:bg-purple-500/25 transition-colors inline-flex items-center gap-1"
          >
            <ArrowUpDown className="w-3 h-3" />+ Middleman
          </button>
        </div>
      </motion.div>

      {/* Search Bar + Date Presets + Clear Filters */}
      <motion.div {...sectionFadeIn(0.15)}>
        <div className="flex items-center gap-4 mb-4">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="text"
              placeholder="Search trades, paste TX hash or address..."
              value={searchQuery}
              onChange={(e) => {
                const val = e.target.value;
                setSearchQuery(val);
                if (looksLikeTxHash(val)) {
                  const cleaned = val.trim().toLowerCase();
                  const allTrades = [...(scanner.scannerTrades || []), ...userTrades];
                  const found = allTrades.some(
                    (t) => t.txHash && t.txHash.toLowerCase() === cleaned
                  );
                  if (found) {
                    setHashNotFound(null);
                    setHashLookupLoading(false);
                  } else {
                    setHashNotFound({ hash: val.trim() });
                    // Query blockchain for TX details
                    setHashLookupLoading(true);
                    fetch(`/api/scanner/lookup?hash=${encodeURIComponent(val.trim())}`)
                      .then(r => r.json())
                      .then(data => {
                        setHashNotFound(prev => prev?.hash === val.trim() ? { ...prev, lookupResult: data } : prev);
                      })
                      .catch(() => {})
                      .finally(() => setHashLookupLoading(false));
                  }
                  setAddressLookup(null);
                } else if (looksLikeAddress(val)) {
                  // Address detected — just set the address, don't auto-trigger blockchain lookup
                  setAddressLookup({ address: val.trim(), loading: false, amountFilter: "" });
                  setHashNotFound(null);
                  setHashLookupLoading(false);
                } else {
                  setHashNotFound(null);
                  setHashLookupLoading(false);
                  setAddressLookup(null);
                }
              }}
              className={`w-full bg-surface rounded-lg pl-10 py-2.5 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all ${searchAmountNum !== null ? "pr-32" : "pr-4"}`}
              style={{ border: `1px solid ${searchAmountNum !== null ? "color-mix(in srgb, var(--accent) 50%, var(--border))" : "var(--border)"}` }}
            />
            {searchAmountNum !== null && (
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-accent/10 text-accent text-[11px] font-semibold tabular-nums pointer-events-none"
                style={{ border: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)" }}>
                {searchAmountNum.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}+ USDT
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {DATE_PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => { setDateRange(p.key); setCustomFrom(""); setCustomTo(""); setPage(0); }}
                className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all cursor-pointer ${
                  dateRange === p.key
                    ? "btn-pill-active text-accent"
                    : "text-muted hover:text-foreground hover:bg-surface-2"
                }`}
              >
                {p.label}
              </button>
            ))}
            <div className="relative">
              <button
                onClick={() => {
                  if (dateRange === "custom") {
                    setShowDatePicker(prev => !prev);
                  } else {
                    setDateRange("custom");
                    setShowDatePicker(true);
                    setPage(0);
                  }
                }}
                className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all cursor-pointer inline-flex items-center gap-1 ${
                  dateRange === "custom"
                    ? "btn-pill-active text-accent"
                    : "text-muted hover:text-foreground hover:bg-surface-2"
                }`}
              >
                <Calendar className="w-3 h-3" />
                {dateRange === "custom" && (customFrom || customTo)
                  ? `${customFrom || "..."} – ${customTo || "..."}`
                  : "Custom"}
              </button>
              <AnimatePresence>
                {showDatePicker && dateRange === "custom" && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowDatePicker(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 z-50 bg-surface rounded-lg p-4 shadow-lg"
                      style={{ border: "1px solid var(--border)" }}
                    >
                      <p className="text-[11px] font-bold text-muted uppercase tracking-wider mb-3">Custom Date Range</p>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2">
                          <span className="text-[12px] text-muted w-10">From</span>
                          <input
                            type="date"
                            value={customFrom}
                            onChange={(e) => { setCustomFrom(e.target.value); setPage(0); }}
                            className="bg-surface-2 rounded-md px-2.5 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
                            style={{ border: "1px solid var(--border)", colorScheme: "dark" }}
                          />
                        </label>
                        <label className="flex items-center gap-2">
                          <span className="text-[12px] text-muted w-10">To</span>
                          <input
                            type="date"
                            value={customTo}
                            onChange={(e) => { setCustomTo(e.target.value); setPage(0); }}
                            className="bg-surface-2 rounded-md px-2.5 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
                            style={{ border: "1px solid var(--border)", colorScheme: "dark" }}
                          />
                        </label>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <button
                          onClick={() => { setCustomFrom(""); setCustomTo(""); setPage(0); }}
                          className="text-[11px] text-muted hover:text-foreground transition-colors"
                        >
                          Clear dates
                        </button>
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setActiveTab("All");
                setSearchQuery("");
                setSortCol("createdAt");
                setSortDir("desc");
                setDateRange("today");
                setCustomFrom("");
                setCustomTo("");
                setPage(0);
                setSelectedIds(new Set());
              }}
              className="text-[12px] text-muted hover:text-foreground transition-colors flex items-center gap-1"
            >
              <X className="w-3 h-3" />Clear filters
            </button>
          )}
        </div>
      </motion.div>

      {/* Hash Not Found Banner */}
      {hashNotFound && (
        <div className="mb-3 rounded-xl border border-purple-400/30 bg-purple-500/5 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10 shrink-0">
              {hashLookupLoading ? (
                <Loader2 className="h-4 w-4 text-purple-400 animate-spin" />
              ) : (
                <ArrowUpDown className="h-4 w-4 text-purple-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground mb-1">This hash isn&apos;t in your ledger</p>
              {hashLookupLoading ? (
                <p className="text-xs text-muted mb-3">Checking blockchain for transaction details...</p>
              ) : hashNotFound.lookupResult?.found && hashNotFound.lookupResult.amount ? (
                <div className="mb-3">
                  <p className="text-xs text-muted mb-2">Found on-chain — did you broker a middleman deal?</p>
                  <div className="flex items-center gap-3 rounded-lg px-3 py-2 text-xs" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                    <span className="font-semibold text-foreground">{hashNotFound.lookupResult.amount.toLocaleString()} USDT</span>
                    <span className="text-muted">on</span>
                    <span className="font-semibold text-foreground uppercase">{hashNotFound.lookupResult.chain === "bsc" ? "BEP-20" : hashNotFound.lookupResult.chain === "ethereum" ? "ERC-20" : "TRC-20"}</span>
                    {hashNotFound.lookupResult.from && (
                      <>
                        <span className="text-muted">from</span>
                        <span className="font-mono text-[11px] text-muted">{hashNotFound.lookupResult.from.slice(0, 8)}...{hashNotFound.lookupResult.from.slice(-6)}</span>
                      </>
                    )}
                    {hashNotFound.lookupResult.to && (
                      <>
                        <span className="text-muted">&rarr;</span>
                        <span className="font-mono text-[11px] text-muted">{hashNotFound.lookupResult.to.slice(0, 8)}...{hashNotFound.lookupResult.to.slice(-6)}</span>
                      </>
                    )}
                  </div>
                </div>
              ) : hashNotFound.lookupResult?.found && !hashNotFound.lookupResult.amount ? (
                <p className="text-xs text-muted mb-3">Transaction found on {hashNotFound.lookupResult.chain?.toUpperCase()} but it&apos;s not a USDT transfer. You can still log it manually.</p>
              ) : (
                <p className="text-xs text-muted mb-3">Did you broker a middleman deal? The coin went from seller to buyer directly.</p>
              )}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const hash = hashNotFound.hash;
                    const lr = hashNotFound.lookupResult;
                    // Auto-fill from blockchain lookup result or scanner
                    const matchedTx = scanner.transactions.find(
                      (tx) => tx.txHash.toLowerCase() === hash.toLowerCase()
                    );
                    const detectedAmount = lr?.amount ?? (matchedTx ? matchedTx.amount : undefined);
                    const detectedChain = (lr?.chain ?? matchedTx?.chain ?? "tron") as "tron" | "bsc" | "ethereum";
                    setMiddlemanForm((f) => ({
                      ...f,
                      txHash: hash,
                      amount: detectedAmount ? formatWithCommas(String(detectedAmount)) : "",
                      chain: detectedChain,
                      buyRate: lastRates.buyRate != null ? formatWithCommas(String(lastRates.buyRate)) : f.buyRate,
                      sellRate: lastRates.sellRate != null ? formatWithCommas(String(lastRates.sellRate)) : f.sellRate,
                    }));
                    setMmBuyCpMode("idle"); setMmSellCpMode("idle");
                    setMmBuyCpSearch(""); setMmSellCpSearch("");
                    setMmCpExpanded(false);
                    setShowMiddlemanModal(true);
                    setHashNotFound(null);
                    setSearchQuery("");
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors"
                >
                  Log as Middleman Deal
                </button>
                <button
                  onClick={() => {
                    setHashNotFound(null);
                    setSearchQuery("");
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-muted hover:text-foreground transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
            <button onClick={() => setHashNotFound(null)} className="text-muted hover:text-foreground shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Address Lookup — "Look up on blockchain" link + results banner */}
      {addressLookup && !addressLookup.result && !addressLookup.loading && (
        <div className="mb-3 flex items-center gap-2 px-1">
          <Radio className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs text-muted">Address detected:</span>
          <span className="text-xs font-mono text-foreground">{addressLookup.address.slice(0, 10)}...{addressLookup.address.slice(-6)}</span>
          <span className="text-xs text-muted">&middot;</span>
          <button
            onClick={() => {
              setAddressLookup((prev) => prev ? { ...prev, loading: true } : prev);
              fetch(`/api/scanner/lookup-address?address=${encodeURIComponent(addressLookup.address)}`)
                .then((r) => r.json())
                .then((data) => {
                  setAddressLookup((prev) =>
                    prev?.address === addressLookup.address
                      ? { ...prev, loading: false, result: data }
                      : prev
                  );
                })
                .catch(() => {
                  setAddressLookup((prev) =>
                    prev ? { ...prev, loading: false, result: { found: false, transactions: [], error: "Lookup failed" } } : prev
                  );
                });
            }}
            className="text-xs font-semibold text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />Look up on blockchain
          </button>
        </div>
      )}
      {addressLookup?.loading && (
        <div className="mb-3 flex items-center gap-2 px-1">
          <Loader2 className="w-3.5 h-3.5 text-purple-400 animate-spin" />
          <span className="text-xs text-muted">Checking blockchain for USDT transactions...</span>
        </div>
      )}
      {addressLookup?.result && (
        <div className="mb-3 rounded-xl border border-purple-400/30 bg-purple-500/5 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10 shrink-0">
              <Radio className="h-4 w-4 text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-foreground">
                  {addressLookup.result.counterpartyName
                    ? `${addressLookup.result.counterpartyName}`
                    : "Address Lookup"}
                </p>
                <span className="text-[10px] font-mono text-muted bg-surface-2 px-1.5 py-0.5 rounded">
                  {addressLookup.address.slice(0, 8)}...{addressLookup.address.slice(-6)}
                </span>
                {addressLookup.result.chain && (
                  <span className="text-[10px] font-bold text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded uppercase">
                    {addressLookup.result.chain === "bsc" ? "BEP-20" : addressLookup.result.chain === "ethereum" ? "ERC-20" : "TRC-20"}
                  </span>
                )}
              </div>

              {addressLookup.result.transactions.length === 0 ? (
                <p className="text-xs text-muted mb-2">
                  {addressLookup.result.error || "No recent USDT transactions found for this address."}
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted mb-2">
                    Last {addressLookup.result.transactions.length} USDT transaction{addressLookup.result.transactions.length !== 1 ? "s" : ""}:
                  </p>
                  {/* Amount filter */}
                  <div className="relative mb-2 max-w-[200px]">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Filter by amount..."
                      value={addressLookup.amountFilter}
                      onChange={(e) => setAddressLookup((prev) => prev ? { ...prev, amountFilter: e.target.value } : prev)}
                      className="w-full rounded-lg pl-7 pr-3 py-1 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-purple-400/30"
                      style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                    />
                  </div>
                  {/* Transaction list */}
                  <div className="max-h-[200px] overflow-auto space-y-1">
                    {addressLookup.result.transactions
                      .filter((tx) => {
                        if (!addressLookup.amountFilter.trim()) return true;
                        return tx.amount.toString().includes(addressLookup.amountFilter.trim());
                      })
                      .map((tx) => {
                        const otherAddr = tx.direction === "incoming" ? tx.from : tx.to;
                        return (
                          <button
                            key={tx.txHash}
                            onClick={() => {
                              // Pre-fill middleman modal with this transaction
                              setMiddlemanForm((f) => ({
                                ...f,
                                txHash: tx.txHash,
                                amount: formatWithCommas(String(tx.amount)),
                                chain: tx.chain as "tron" | "bsc" | "ethereum",
                                buyRate: lastRates.buyRate != null ? formatWithCommas(String(lastRates.buyRate)) : f.buyRate,
                                sellRate: lastRates.sellRate != null ? formatWithCommas(String(lastRates.sellRate)) : f.sellRate,
                              }));
                              setMmBuyCpMode("idle");
                              setMmSellCpMode("idle");
                              setMmBuyCpSearch("");
                              setMmSellCpSearch("");
                              setMmCpExpanded(false);
                              setShowMiddlemanModal(true);
                              setAddressLookup(null);
                              setSearchQuery("");
                            }}
                            className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-surface-2 transition-colors group"
                            style={{ border: "1px solid var(--border)" }}
                          >
                            <span className={`text-sm font-semibold tabular-nums ${tx.direction === "incoming" ? "text-success" : "text-danger"}`}>
                              {tx.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT
                            </span>
                            <span className={`text-[11px] ${tx.direction === "incoming" ? "text-success" : "text-danger"}`}>
                              {tx.direction === "incoming" ? "←" : "→"}
                            </span>
                            <span className="text-[11px] font-mono text-muted truncate">
                              {otherAddr.slice(0, 8)}...{otherAddr.slice(-6)}
                            </span>
                            <span className="text-[10px] text-muted ml-auto whitespace-nowrap">
                              {relativeTime(tx.timestamp)}
                            </span>
                            <span className="text-[10px] font-semibold text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              Log deal ▸
                            </span>
                          </button>
                        );
                      })}
                    {addressLookup.result.transactions.filter((tx) => {
                      if (!addressLookup.amountFilter.trim()) return true;
                      return tx.amount.toString().includes(addressLookup.amountFilter.trim());
                    }).length === 0 && (
                      <p className="text-[11px] text-muted py-2 text-center">No transactions match that amount.</p>
                    )}
                  </div>
                </>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setAddressLookup(null);
                    setSearchQuery("");
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-muted hover:text-foreground transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
            <button onClick={() => { setAddressLookup(null); setSearchQuery(""); }} className="text-muted hover:text-foreground shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Middleman Summary — shown when filter is active */}
      {directionFilter === "middleman" && (() => {
        const mmAll = sorted.filter(t => t.direction === "middleman");
        const mmVolume = mmAll.reduce((s, t) => s + t.usdtAmount, 0);
        const mmProfitTotal = mmAll.reduce((s, t) => s + (t.middlemanProfit ?? 0), 0);
        const mmWithRates = mmAll.filter(t => t.buyRate && t.sellRate);
        const mmWtdBuy = mmVolume > 0 ? mmWithRates.reduce((s, t) => s + (t.buyRate ?? 0) * t.usdtAmount, 0) / mmVolume : 0;
        const mmWtdSell = mmVolume > 0 ? mmWithRates.reduce((s, t) => s + (t.sellRate ?? 0) * t.usdtAmount, 0) / mmVolume : 0;
        const mmSpd = mmWtdSell - mmWtdBuy;
        return (
          <motion.div {...sectionFadeIn(0.15)}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="bg-surface rounded-lg p-3" style={{ border: "1px solid var(--border)" }}>
                <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">Deals</p>
                <p className="text-lg font-bold text-foreground tabular-nums">{mmAll.length}</p>
                <p className="text-xs text-muted tabular-nums">{mmVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</p>
              </div>
              <div className="bg-surface rounded-lg p-3" style={{ border: "1px solid var(--border)" }}>
                <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">Avg Rates</p>
                <p className="text-lg font-bold text-foreground tabular-nums">₦{mmWtdBuy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-xs text-muted tabular-nums">/ ₦{mmWtdSell.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-surface rounded-lg p-3" style={{ border: "1px solid var(--border)" }}>
                <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">Avg Spread</p>
                <p className={`text-lg font-bold tabular-nums ${mmSpd >= 0 ? "text-success" : "text-danger"}`}>
                  {mmSpd >= 0 ? "+" : "-"}₦{Math.abs(mmSpd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted">per USDT</p>
              </div>
              <div className="bg-surface rounded-lg p-3" style={{ border: "1px solid var(--border)" }}>
                <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">Total Profit</p>
                <p className={`text-lg font-bold tabular-nums ${mmProfitTotal >= 0 ? "text-success" : "text-danger"}`}>
                  {mmProfitTotal >= 0 ? "+" : "-"}₦{Math.abs(mmProfitTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted tabular-nums">
                  {mmVolume > 0 ? `${((mmProfitTotal / (mmWtdBuy * mmVolume)) * 100).toFixed(2)}% return` : "--"}
                </p>
              </div>
            </div>
          </motion.div>
        );
      })()}

      {/* Trade List */}
      <motion.div {...sectionFadeIn(0.2)}>
      <SectionCard>
        {paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-muted" />
            </div>
            <h3 className="text-sm font-bold text-foreground mb-1">
              No trades found
            </h3>
            <p className="text-xs text-muted max-w-xs">
              {searchQuery ? "No trades match your search." : `No ${activeTab.toLowerCase()} trades to display.`}
            </p>
          </div>
        ) : (
          <div ref={tableRef} tabIndex={0} className="outline-none overflow-auto max-h-[calc(100vh-320px)]">
            <table className="w-full border-collapse min-w-[800px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-surface-2 border-b border-border">
                  {/* Select all checkbox + smart select */}
                  <th className="px-3 py-3 w-10">
                    <div className="relative flex items-center gap-0.5">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center justify-center w-5 h-5 rounded"
                      >
                        {selectedIds.size === paginated.length && paginated.length > 0
                          ? <CheckSquare className="w-4 h-4 text-accent" />
                          : <CheckSquare className="w-4 h-4 text-muted/40" />}
                      </button>
                      <button
                        onClick={() => setShowSmartSelect(!showSmartSelect)}
                        className="flex items-center justify-center w-4 h-4 rounded text-muted/40 hover:text-accent transition-colors"
                        title="Smart select"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <AnimatePresence>
                        {showSmartSelect && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setShowSmartSelect(false)} />
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.12 }}
                              className="absolute top-full left-0 mt-1 z-50 bg-surface rounded-xl py-1.5 shadow-lg min-w-[220px]"
                              style={{ border: "1px solid var(--border)" }}
                            >
                              <div className="px-3 py-1.5 mb-0.5">
                                <span className="text-[9px] font-bold text-muted uppercase tracking-wider">Smart Select</span>
                              </div>
                              <div className="h-px bg-border mx-2 mb-0.5" />
                              <button onClick={() => smartSelect("buys_since_last_sell")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                <ArrowDownLeft className="w-3.5 h-3.5 text-success shrink-0" />
                                Buys since last sell
                              </button>
                              <div className="h-px bg-border mx-2 my-0.5" />
                              <button onClick={() => smartSelect("buys_today")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                <ArrowDownLeft className="w-3.5 h-3.5 text-success shrink-0" />
                                All buys today
                              </button>
                              <button onClick={() => smartSelect("sells_today")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                <ArrowUpRight className="w-3.5 h-3.5 text-danger shrink-0" />
                                All sells today
                              </button>
                              <button onClick={() => smartSelect("buys_yesterday")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                <ArrowDownLeft className="w-3.5 h-3.5 text-success shrink-0" />
                                All buys yesterday
                              </button>
                              <button onClick={() => smartSelect("sells_yesterday")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                <ArrowUpRight className="w-3.5 h-3.5 text-danger shrink-0" />
                                All sells yesterday
                              </button>
                              {selectedIds.size > 0 && (
                                <>
                                  <div className="h-px bg-border mx-2 my-0.5" />
                                  <button onClick={() => smartSelect("same_counterparty")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                    <Users className="w-3.5 h-3.5 text-muted shrink-0" />
                                    Same counterparty
                                  </button>
                                  <button onClick={() => smartSelect("same_rate")} className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-2 transition-colors flex items-center gap-2.5">
                                    <Coins className="w-3.5 h-3.5 text-muted shrink-0" />
                                    Same rate
                                  </button>
                                </>
                              )}
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </th>
                  <SortHeader col="counterparty" label="Name" />
                  <th className="text-left text-[13px] font-semibold text-muted pl-0 pr-2.5 py-3">Address</th>
                  <SortHeader col="direction" label="Dir" />
                  <SortHeader col="usdtAmount" label="Amount" align="right" />
                  <SortHeader col="rate" label="Rate" align="right" />
                  <SortHeader col="ngnAmount" label="NGN" align="right" />
                  <SortHeader col="chain" label="Chain" />
                  <th className="text-left text-[13px] font-semibold text-muted px-2.5 py-3">Hash</th>
                  <SortHeader col="status" label="Settlement" />
                  <SortHeader col="createdAt" label="Created At" align="right" />
                </tr>
              </thead>
              <tbody>
                {Array.from(groupedTrades.entries()).map(
                  ([dateLabel, trades]) => (
                    <TradeGroup
                      key={dateLabel}
                      dateLabel={dateLabel}
                      trades={trades}
                      expandedId={expandedId}
                      setExpandedId={setExpandedId}
                      toast={toast}
                      selectedIds={selectedIds}
                      onSelectToggle={handleSelectToggle}
                      focusedIdx={focusedIdx}
                      flatIndexOffset={groupOffsets.get(dateLabel) ?? 0}
                      onAssignCounterparty={(tradeId) => { setAssignTradeId(tradeId); setCpSearch(""); }}
                      onUpdateTrade={handleUpdateTrade}
                      onLinkToLock={(trade) => setLinkToLockTrade(trade)}
                      linkedTradeMap={linkedTradeMap}
                      accountingMap={accounting.trades}
                      highlightedId={highlightedId}
                      lastRates={lastRates}
                    />
                  )
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <span className="text-xs text-muted">
              {page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-xs text-muted tabular-nums">Page {page + 1} of {totalPages}</span>
              <Button variant="secondary" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </SectionCard>
      </motion.div>

      {/* Floating Batch Action Bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-surface-2 rounded-lg px-5 py-3 flex items-center gap-4 shadow-lg"
            style={{ border: "1px solid var(--border)" }}
          >
            <span className="text-sm font-semibold text-foreground">{selectedIds.size} selected</span>
            <Button variant="secondary" size="sm" onClick={() => {
              const selected = sorted.filter(t => selectedIds.has(t.id));
              handleExportCsv(selected);
            }}>
              <Download className="w-3 h-3" />Export Selected
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowLockPicker(true)}>
              <Lock className="w-3 h-3" />Lock
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowProfitCalc(true)}>
              <TrendingUp className="w-3 h-3" />Avg Rate
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lock Picker Modal */}
      <AnimatePresence>
        {showLockPicker && (() => {
          const selectedTrades = sorted.filter(t => selectedIds.has(t.id));
          const selectedTotal = selectedTrades.reduce((sum, t) => sum + t.usdtAmount, 0);
          return (
            <LockPickerModal
              selectedTrades={selectedTrades}
              selectedTotal={selectedTotal}
              onClose={() => setShowLockPicker(false)}
              onLinked={() => {
                setShowLockPicker(false);
                setSelectedIds(new Set());
                scanner.reload();
                setDataVersion(v => v + 1);
              }}
              toast={toast}
            />
          );
        })()}
      </AnimatePresence>

      {/* Profit Calculator Modal */}
      <AnimatePresence>
        {showProfitCalc && (
          <ProfitCalculatorModal
            trades={sorted.filter(t => selectedIds.has(t.id))}
            accountingMap={accounting.trades}
            onClose={() => setShowProfitCalc(false)}
          />
        )}
      </AnimatePresence>

      {/* Trade Detail Drawer */}
      <AnimatePresence>
        {selectedTrade && (
          <TradeDetailDrawer
            key={selectedTrade.id}
            trade={selectedTrade}
            onClose={() => setExpandedId(null)}
            toast={toast}
            onUpdateTrade={handleUpdateTrade}
            onCreateTrade={(trade) => {
              setUserTrades(prev => [trade, ...prev]);
              fetch("/api/trades", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ trade }) }).catch(() => {});
              setDataVersion(v => v + 1);
            }}
            onAssignCounterparty={(tradeId) => { setAssignTradeId(tradeId); setCpSearch(""); }}
            onLinkToLock={(trade) => setLinkToLockTrade(trade)}
            linkedLock={expandedId ? linkedTradeMap.get(expandedId) : undefined}
            scannerTransactions={scanner.transactions}
            onScannerMatch={async (txId, tradeId) => { await scanner.confirmMatch(txId, tradeId); setDataVersion(v => v + 1); }}
            tradeAccounting={expandedId ? accounting.trades.get(expandedId) : undefined}
            allTrades={allTrades}
          />
        )}
      </AnimatePresence>

      {/* Assign Counterparty Modal */}
      <AnimatePresence>
        {assignTradeId !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setAssignTradeId(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface rounded-xl w-full max-w-md mx-4 overflow-hidden shadow-xl"
              style={{ border: "1px solid var(--border)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-border">
                <h3 className="text-sm font-bold text-foreground">Assign Counterparty</h3>
                <p className="text-xs text-muted mt-0.5">Select an existing counterparty or register a new one</p>
              </div>
              <div className="px-5 py-3">
                {!registeringNew ? (
                  <>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                      <input
                        type="text"
                        placeholder="Search counterparties..."
                        value={cpSearch}
                        onChange={(e) => setCpSearch(e.target.value)}
                        className="w-full bg-surface-2 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)" }}
                        autoFocus
                      />
                    </div>
                    <div className="max-h-[240px] overflow-auto space-y-1">
                      {cpList
                        .filter(cp => !cp.isBlacklisted)
                        .filter(cp => {
                          if (!cpSearch.trim()) return true;
                          const q = cpSearch.toLowerCase();
                          return cp.name.toLowerCase().includes(q) || (cp.alias?.toLowerCase().includes(q));
                        })
                        .map(cp => (
                          <button
                            key={cp.id}
                            className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors flex items-center justify-between group/cp"
                            onClick={async () => {
                              // Check if it's a scanner trade or a user trade
                              const isScannerTrade = scanner.scannerTrades.some(t => t.id === assignTradeId);
                              if (isScannerTrade) {
                                await scanner.updateScannerTrade(assignTradeId!, { counterpartyName: cp.name, counterpartyId: cp.id });
                              } else {
                                // User trade — update locally and persist
                                const trade = userTrades.find(t => t.id === assignTradeId);
                                if (trade) {
                                  trade.counterpartyName = cp.name;
                                  trade.counterpartyId = cp.id;
                                  fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: assignTradeId, counterpartyName: cp.name, counterpartyId: cp.id }) }).catch(() => {});
                                  setDataVersion(v => v + 1);
                                }
                              }
                              toast(`Assigned "${cp.name}" as counterparty`, "success");
                              setAssignTradeId(null);
                            }}
                          >
                            <div>
                              <span className="text-sm font-semibold text-foreground">{cp.name}</span>
                              {cp.alias && <span className="text-xs text-muted ml-2">{cp.alias}</span>}
                              <div className="text-[10px] text-muted mt-0.5">{cp.city} &middot; {cp.tradeCount} trades &middot; {cp.reviewScore}/5</div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {cp.kycVerified && <span className="text-[9px] font-bold text-success bg-success/10 px-1.5 py-0.5 rounded">KYC</span>}
                              <ChevronDown className="w-3 h-3 text-muted -rotate-90 opacity-0 group-hover/cp:opacity-100 transition-opacity" />
                            </div>
                          </button>
                        ))}
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-muted mb-1 block">Name *</label>
                      <input
                        type="text"
                        placeholder="Counterparty name"
                        value={newCpName}
                        onChange={(e) => setNewCpName(e.target.value)}
                        className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)" }}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted mb-1 block">Wallet Address (optional)</label>
                      <input
                        type="text"
                        placeholder="T... or 0x..."
                        value={newCpAddress}
                        onChange={(e) => setNewCpAddress(e.target.value)}
                        className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30 font-mono"
                        style={{ border: "1px solid var(--border)" }}
                      />
                    </div>
                    {newCpAddress && (
                      <div>
                        <label className="text-xs font-semibold text-muted mb-1 block">Chain</label>
                        <select
                          value={newCpChain}
                          onChange={(e) => setNewCpChain(e.target.value as "tron" | "bsc" | "ethereum")}
                          className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30"
                          style={{ border: "1px solid var(--border)" }}
                        >
                          <option value="tron">TRC-20 (TRON)</option>
                          <option value="bsc">BEP-20 (BSC)</option>
                          <option value="ethereum">ERC-20 (Ethereum)</option>
                        </select>
                      </div>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full"
                      onClick={async () => {
                        if (!newCpName.trim()) { toast("Name is required", "error"); return; }
                        try {
                          const body: Record<string, unknown> = { name: newCpName.trim() };
                          if (newCpAddress.trim()) {
                            body.addresses = [{ address: newCpAddress.trim(), chain: newCpChain }];
                          }
                          const res = await fetch("/api/counterparties", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(body),
                          });
                          const data = await res.json();
                          if (data.counterparty) {
                            setCpList(prev => [...prev, data.counterparty]);
                            // Auto-assign to current trade
                            const cp = data.counterparty;
                            const isScannerTrade = scanner.scannerTrades.some(t => t.id === assignTradeId);
                            if (isScannerTrade) {
                              await scanner.updateScannerTrade(assignTradeId!, { counterpartyName: cp.name, counterpartyId: cp.id });
                            } else {
                              const trade = userTrades.find(t => t.id === assignTradeId);
                              if (trade) {
                                trade.counterpartyName = cp.name;
                                trade.counterpartyId = cp.id;
                                fetch("/api/trades", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tradeId: assignTradeId, counterpartyName: cp.name, counterpartyId: cp.id }) }).catch(() => {});
                                setDataVersion(v => v + 1);
                              }
                            }
                            toast(`Created "${cp.name}" and assigned to trade`, "success");
                            setAssignTradeId(null);
                          } else {
                            toast(data.error || "Failed to create counterparty", "error");
                          }
                        } catch (err) {
                          toast((err as Error).message, "error");
                        }
                      }}
                    >
                      Create & Assign
                    </Button>
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t border-border flex items-center justify-between">
                {!registeringNew ? (
                  <Button variant="secondary" size="sm" onClick={() => setRegisteringNew(true)}>
                    <UserPlus className="w-3 h-3" />Register New
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => setRegisteringNew(false)}>
                    <ArrowLeft className="w-3 h-3" />Back to List
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setAssignTradeId(null)}>Cancel</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Link Trade to Lock Modal */}
      <AnimatePresence>
        {linkToLockTrade && (
          <LinkTradeToLockModal
            trade={linkToLockTrade}
            onClose={() => setLinkToLockTrade(null)}
            onLinked={() => {
              setLinkToLockTrade(null);
              fetchLinkedTrades();
              setDataVersion((v) => v + 1);
            }}
            toast={toast}
          />
        )}
      </AnimatePresence>

      {/* Middleman Deal Modal */}
      <AnimatePresence>
        {showMiddlemanModal && (() => {
          const mmAmount = parseFloat(middlemanForm.amount.replace(/,/g, "")) || 0;
          const mmBuyRate = parseFloat(middlemanForm.buyRate.replace(/,/g, "")) || 0;
          const mmSellRate = parseFloat(middlemanForm.sellRate.replace(/,/g, "")) || 0;
          const mmBuyNgn = mmAmount * mmBuyRate;
          const mmSellNgn = mmAmount * mmSellRate;
          const mmSpread = mmSellRate - mmBuyRate;
          const mmProfit = mmSpread * mmAmount;
          const mmSpreadPct = mmBuyRate > 0 ? (mmSpread / mmBuyRate) * 100 : 0;
          const canSubmit = mmAmount > 0 && mmBuyRate > 0 && mmSellRate > 0;

          const filteredBuyCps = cpList.filter(cp => !cp.isBlacklisted).filter(cp => {
            if (!mmBuyCpSearch.trim()) return true;
            const q = mmBuyCpSearch.toLowerCase();
            return cp.name.toLowerCase().includes(q) || (cp.alias?.toLowerCase().includes(q));
          });
          const filteredSellCps = cpList.filter(cp => !cp.isBlacklisted).filter(cp => {
            if (!mmSellCpSearch.trim()) return true;
            const q = mmSellCpSearch.toLowerCase();
            return cp.name.toLowerCase().includes(q) || (cp.alias?.toLowerCase().includes(q));
          });

          const handleCreateCp = async (side: "buy" | "sell") => {
            if (!mmNewCpName.trim()) { toast("Name is required", "error"); return; }
            try {
              const res = await fetch("/api/counterparties", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: mmNewCpName.trim() }),
              });
              const data = await res.json();
              if (data.counterparty) {
                const cp = data.counterparty;
                setCpList(prev => [...prev, cp]);
                if (side === "buy") {
                  setMiddlemanForm(f => ({ ...f, buyCpId: cp.id, buyCpName: cp.name }));
                  setMmBuyCpMode("selected");
                } else {
                  setMiddlemanForm(f => ({ ...f, sellCpId: cp.id, sellCpName: cp.name }));
                  setMmSellCpMode("selected");
                }
                setMmNewCpName("");
                toast(`Created "${cp.name}"`);
              } else {
                toast(data.error || "Failed to create", "error");
              }
            } catch (err) { toast((err as Error).message, "error"); }
          };

          const handleMiddlemanSave = async () => {
            if (!canSubmit) return;
            // Duplicate detection
            const allTrades = [...scannerTrades, ...userTrades];
            if (middlemanForm.txHash) {
              const hashMatch = allTrades.find(t => t.txHash === middlemanForm.txHash);
              if (hashMatch) {
                const proceed = window.confirm(`A trade with hash ${middlemanForm.txHash.slice(0, 10)}... already exists (Trade #${hashMatch.id}, ${hashMatch.direction}). Create anyway?`);
                if (!proceed) return;
              }
            }
            const amountDup = allTrades.find(t =>
              t.direction === "middleman" &&
              t.usdtAmount === mmAmount &&
              t.buyRate === mmBuyRate &&
              t.sellRate === mmSellRate
            );
            if (amountDup) {
              const proceed = window.confirm(`A middleman deal with the same amount (${mmAmount} USDT) and rates (₦${mmBuyRate}/₦${mmSellRate}) already exists (Trade #${amountDup.id}). Create anyway?`);
              if (!proceed) return;
            }
            const newTrade: Trade = {
              id: Date.now(),
              counterpartyName: null,
              counterpartyId: null,
              direction: "middleman",
              usdtAmount: mmAmount,
              rate: null,
              ngnAmount: null,
              txHash: middlemanForm.txHash || null,
              chain: middlemanForm.chain,
              viaExchange: false,
              exchangeName: null,
              usdtVerified: true,
              nairaPaymentMethod: null,
              nairaBankVerified: false,
              nairaReceiptPath: null,
              nairaBankRef: null,
              senderConfirmed: true,
              receiverConfirmed: true,
              status: "pending",
              verificationLevel: "unverified",
              settlementTimeSecs: null,
              createdAt: new Date().toISOString(),
              settledAt: null,
              senderAddress: null,
              senderEntity: null,
              coinStatus: "complete",
              coinAmountSettled: mmAmount,
              nairaStatus: "pending",
              nairaAmountSettled: 0,
              buyRate: mmBuyRate,
              sellRate: mmSellRate,
              buyCounterpartyName: middlemanForm.buyCpName || null,
              buyCounterpartyId: middlemanForm.buyCpId || null,
              sellCounterpartyName: middlemanForm.sellCpName || null,
              sellCounterpartyId: middlemanForm.sellCpId || null,
              middlemanProfit: mmProfit,
              nairaFlow: middlemanForm.nairaFlow,
            };
            // Persist to disk via API
            try {
              await fetch("/api/trades", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ trade: newTrade }),
              });
            } catch {
              // Fall through — still show in UI for this session
            }
            setUserTrades(prev => [newTrade, ...prev]);
            setDataVersion((v) => v + 1);
            setShowMiddlemanModal(false);
            setMiddlemanForm({ txHash: "", chain: "tron", amount: "", buyCpId: null, buyCpName: "", buyRate: "", sellCpId: null, sellCpName: "", sellRate: "", nairaFlow: "through_you" });
            toast("Middleman deal logged");
            // Rate deviation warnings
            if (lastRates.buyRate != null && lastRates.buyRate > 0 && mmBuyRate > 0) {
              const pct = ((mmBuyRate - lastRates.buyRate) / lastRates.buyRate) * 100;
              if (Math.abs(pct) > 5) toast(`Buy rate ₦${mmBuyRate.toLocaleString()} is ${Math.abs(pct).toFixed(1)}% ${pct > 0 ? "above" : "below"} your last buy rate (₦${lastRates.buyRate.toLocaleString()})`, "info");
            }
            if (lastRates.sellRate != null && lastRates.sellRate > 0 && mmSellRate > 0) {
              const pct = ((mmSellRate - lastRates.sellRate) / lastRates.sellRate) * 100;
              if (Math.abs(pct) > 5) toast(`Sell rate ₦${mmSellRate.toLocaleString()} is ${Math.abs(pct).toFixed(1)}% ${pct > 0 ? "above" : "below"} your last sell rate (₦${lastRates.sellRate.toLocaleString()})`, "info");
            }
          };

          // Reusable counterparty selector — starts as a compact "Select" button
          const CpSelector = ({ side, mode, setMode, search, setSearch, filtered, selectedName }: {
            side: "buy" | "sell";
            mode: "selected" | "search" | "create" | "idle";
            setMode: (m: "selected" | "search" | "create" | "idle") => void;
            search: string;
            setSearch: (s: string) => void;
            filtered: typeof cpList;
            selectedName: string;
          }) => (
            <div>
              {mode === "selected" && selectedName ? (
                <button
                  onClick={() => setMode("search")}
                  className="w-full text-left rounded-lg px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-surface-2 transition-colors truncate"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                >
                  {selectedName}
                </button>
              ) : mode === "create" ? (
                <div className="space-y-1.5">
                  <input
                    type="text"
                    placeholder="Name"
                    value={mmNewCpName}
                    onChange={(e) => setMmNewCpName(e.target.value)}
                    className="w-full rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button onClick={() => setMode("search")} className="text-[10px] text-muted hover:text-foreground">Back</button>
                    <button onClick={() => handleCreateCp(side)} className="text-[10px] font-semibold text-accent hover:underline">Create</button>
                  </div>
                </div>
              ) : mode === "search" ? (
                <div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full rounded-lg pl-6 pr-2 py-1.5 text-[11px] text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-[90px] overflow-auto mt-1 space-y-0.5">
                    {(search.trim() ? filtered.slice(0, 4) : cpList.filter(cp => !cp.isBlacklisted).sort((a, b) => b.tradeCount - a.tradeCount).slice(0, 3)).map(cp => (
                      <button
                        key={cp.id}
                        className="w-full text-left px-2 py-1 rounded text-[11px] font-medium text-foreground hover:bg-surface-2 transition-colors truncate"
                        onClick={() => {
                          if (side === "buy") {
                            setMiddlemanForm(f => ({ ...f, buyCpId: cp.id, buyCpName: cp.name }));
                            setMmBuyCpMode("selected");
                          } else {
                            setMiddlemanForm(f => ({ ...f, sellCpId: cp.id, sellCpName: cp.name }));
                            setMmSellCpMode("selected");
                          }
                        }}
                      >
                        {cp.name}
                      </button>
                    ))}
                    {search.trim() && filtered.length === 0 && (
                      <p className="text-[10px] text-muted text-center py-1">No matches</p>
                    )}
                  </div>
                  <button
                    onClick={() => { setMode("create"); setMmNewCpName(search); }}
                    className="mt-1 text-[10px] font-semibold text-accent hover:underline inline-flex items-center gap-0.5"
                  >
                    <UserPlus className="w-2.5 h-2.5" />New
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setMode("search")}
                  className="w-full rounded-lg px-2.5 py-1.5 text-[11px] text-muted hover:text-foreground transition-colors text-left"
                  style={{ background: "var(--surface-2)", border: "1px dashed var(--border)" }}
                >
                  Select...
                </button>
              )}
            </div>
          );

          return (
            <motion.div
              key="middleman-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4"
              style={{ background: "rgba(0,0,0,0.6)" }}
              onClick={() => setShowMiddlemanModal(false)}
            >
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Middleman deal"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-5">
                  {/* Compact Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                      <ArrowUpDown className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-foreground">Middleman Deal</h2>
                      <p className="text-[10px] text-muted">You earn the spread between buy & sell rate</p>
                    </div>
                  </div>

                  {/* TX Hash — auto-detects chain & amount on paste */}
                  <div className="mb-3">
                    <label className="text-[10px] font-medium text-muted mb-1 block">TX Hash <span className="text-muted/50">(optional)</span></label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Paste transaction hash to auto-fill amount..."
                        value={middlemanForm.txHash}
                        onChange={(e) => handleMmHashPaste(e.target.value)}
                        className={`w-full rounded-lg px-2.5 py-1.5 text-xs font-mono text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 transition-colors ${
                          mmHashStatus === "found" ? "focus:ring-success/30 ring-1 ring-success/30" :
                          mmHashStatus === "existing" ? "focus:ring-warning/30 ring-1 ring-warning/30" :
                          "focus:ring-accent/30"
                        }`}
                        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                      />
                      {mmHashLooking && (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                          <div className="w-3 h-3 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {mmHashStatus === "found" && (
                      <p className="text-[10px] text-success mt-1">Detected — amount & chain auto-filled</p>
                    )}
                    {mmHashStatus === "existing" && (
                      <p className="text-[10px] text-warning mt-1">This hash already exists in your trades</p>
                    )}
                    {mmHashStatus === "not_found" && (
                      <p className="text-[10px] text-muted mt-1">Not found on chain — fill in manually</p>
                    )}
                  </div>

                  {/* Core inputs — 3 columns: Amount | Buy Rate | Sell Rate */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <label className="text-[10px] font-medium text-muted mb-1 block">USDT Amount</label>
                      <input
                        type="text"
                        placeholder="5,000"
                        value={middlemanForm.amount}
                        onChange={(e) => setMiddlemanForm((f) => ({ ...f, amount: formatWithCommas(e.target.value) }))}
                        className="w-full rounded-lg px-2.5 py-2 text-sm font-semibold text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-success mb-1 block">Buy Rate ₦</label>
                      <input
                        type="text"
                        placeholder="1,660"
                        value={middlemanForm.buyRate}
                        onChange={(e) => setMiddlemanForm((f) => ({ ...f, buyRate: formatWithCommas(e.target.value) }))}
                        className="w-full rounded-lg px-2.5 py-2 text-sm font-semibold text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-success/30"
                        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-danger mb-1 block">Sell Rate ₦</label>
                      <input
                        type="text"
                        placeholder="1,672"
                        value={middlemanForm.sellRate}
                        onChange={(e) => setMiddlemanForm((f) => ({ ...f, sellRate: formatWithCommas(e.target.value) }))}
                        className="w-full rounded-lg px-2.5 py-2 text-sm font-semibold text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-danger/30"
                        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                      />
                    </div>
                  </div>

                  {/* Live profit preview — always visible when rates are set */}
                  {canSubmit && (
                    <div className="rounded-lg px-3 py-2.5 mb-3 flex items-center justify-between" style={{ background: mmProfit >= 0 ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${mmProfit >= 0 ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}` }}>
                      <div className="text-xs text-muted">
                        <span>₦{mmSpread.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/USDT spread</span>
                        <span className="mx-1.5">·</span>
                        <span>{mmSpreadPct.toFixed(2)}%</span>
                      </div>
                      <span className={`text-base font-bold ${mmProfit >= 0 ? "text-success" : "text-danger"}`}>
                        {mmProfit >= 0 ? "+" : ""}₦{Math.abs(mmProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}

                  {/* NGN breakdown — subtle, only when rates entered */}
                  {canSubmit && (
                    <div className="flex gap-2 mb-3 text-[11px]">
                      <div className="flex-1 rounded-lg px-2.5 py-1.5 text-center" style={{ background: "var(--surface-2)" }}>
                        <span className="text-muted">Buy total </span>
                        <span className="font-semibold text-foreground">₦{mmBuyNgn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex-1 rounded-lg px-2.5 py-1.5 text-center" style={{ background: "var(--surface-2)" }}>
                        <span className="text-muted">Sell total </span>
                        <span className="font-semibold text-foreground">₦{mmSellNgn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}

                  {/* Chain + Naira Flow — single compact row */}
                  <div className="flex gap-2 mb-3">
                    <div className="flex-1">
                      <label className="text-[10px] font-medium text-muted mb-1 block">Chain</label>
                      <div className="flex gap-1">
                        {(["tron", "bsc", "ethereum"] as const).map((c) => (
                          <button
                            key={c}
                            onClick={() => setMiddlemanForm((f) => ({ ...f, chain: c }))}
                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer ${
                              middlemanForm.chain === c
                                ? "btn-pill-active text-accent"
                                : "bg-surface-2 text-muted hover:text-foreground"
                            }`}
                          >
                            {c === "tron" ? "TRC-20" : c === "bsc" ? "BEP-20" : "ERC-20"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] font-medium text-muted mb-1 block">Naira Flow</label>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setMiddlemanForm((f) => ({ ...f, nairaFlow: "through_you" }))}
                          className={`flex-1 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer ${
                            middlemanForm.nairaFlow === "through_you"
                              ? "btn-pill-active text-accent"
                              : "bg-surface-2 text-muted hover:text-foreground"
                          }`}
                        >
                          Through you
                        </button>
                        <button
                          onClick={() => setMiddlemanForm((f) => ({ ...f, nairaFlow: "direct" }))}
                          className={`flex-1 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer ${
                            middlemanForm.nairaFlow === "direct"
                              ? "btn-pill-active text-accent"
                              : "bg-surface-2 text-muted hover:text-foreground"
                          }`}
                        >
                          Direct
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Counterparties — side by side */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="rounded-lg p-2.5" style={{ border: "1px solid var(--border)" }}>
                      <p className="text-[10px] font-bold uppercase text-success tracking-wider mb-1.5">Bought from</p>
                      <CpSelector
                        side="buy"
                        mode={mmBuyCpMode}
                        setMode={setMmBuyCpMode}
                        search={mmBuyCpSearch}
                        setSearch={setMmBuyCpSearch}
                        filtered={filteredBuyCps}
                        selectedName={middlemanForm.buyCpName}
                      />
                    </div>
                    <div className="rounded-lg p-2.5" style={{ border: "1px solid var(--border)" }}>
                      <p className="text-[10px] font-bold uppercase text-danger tracking-wider mb-1.5">Sold to</p>
                      <CpSelector
                        side="sell"
                        mode={mmSellCpMode}
                        setMode={setMmSellCpMode}
                        search={mmSellCpSearch}
                        setSearch={setMmSellCpSearch}
                        filtered={filteredSellCps}
                        selectedName={middlemanForm.sellCpName}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowMiddlemanModal(false)}
                      className="flex-1 py-2 rounded-lg text-sm font-medium bg-surface-2 text-muted hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleMiddlemanSave}
                      disabled={!canSubmit}
                      className="flex-1 py-2 rounded-lg text-sm font-semibold bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Log Deal
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Wallet Management Modal */}
      <WalletManagementModal open={walletModalOpen} onClose={() => setWalletModalOpen(false)} />
    </PageWrapper>
  );
}

// =============================================================================
// Profit Calculator Modal — rate analysis & profit projection
// =============================================================================

function ProfitCalculatorModal({
  trades,
  accountingMap,
  onClose,
}: {
  trades: Trade[];
  accountingMap: Map<number, TradeAccounting>;
  onClose: () => void;
}) {
  const [targetRate, setTargetRate] = useState("");

  const buys = trades.filter(t => t.direction === "buy" && t.rate != null && t.ngnAmount != null);
  const sells = trades.filter(t => t.direction === "sell" && t.rate != null && t.ngnAmount != null);

  const buyTotalUsdt = buys.reduce((s, t) => s + t.usdtAmount, 0);
  const buyTotalNgn = buys.reduce((s, t) => s + (t.ngnAmount ?? 0), 0);
  const buyAvgRate = buyTotalUsdt > 0 ? buyTotalNgn / buyTotalUsdt : 0;

  const sellTotalUsdt = sells.reduce((s, t) => s + t.usdtAmount, 0);
  const sellTotalNgn = sells.reduce((s, t) => s + (t.ngnAmount ?? 0), 0);
  const sellAvgRate = sellTotalUsdt > 0 ? sellTotalNgn / sellTotalUsdt : 0;

  const actualPnL = sells.reduce((s, t) => {
    const acc = accountingMap.get(t.id);
    return s + (acc?.grossProfit ?? 0);
  }, 0);
  const actualPnLPct = sellTotalNgn > 0 && actualPnL !== 0
    ? (actualPnL / (sellTotalNgn - actualPnL)) * 100
    : 0;

  // Middleman trades
  const mmTrades = trades.filter(t => t.direction === "middleman" && t.buyRate && t.sellRate);
  const mmTotalUsdt = mmTrades.reduce((s, t) => s + t.usdtAmount, 0);
  const mmTotalProfit = mmTrades.reduce((s, t) => s + (t.middlemanProfit ?? 0), 0);
  const mmAvgBuyRate = mmTotalUsdt > 0 ? mmTrades.reduce((s, t) => s + (t.buyRate ?? 0) * t.usdtAmount, 0) / mmTotalUsdt : 0;
  const mmAvgSellRate = mmTotalUsdt > 0 ? mmTrades.reduce((s, t) => s + (t.sellRate ?? 0) * t.usdtAmount, 0) / mmTotalUsdt : 0;
  const mmAvgSpread = mmAvgSellRate - mmAvgBuyRate;

  const totalUsdt = trades.reduce((s, t) => s + t.usdtAmount, 0);

  // NGN spread: sell avg rate minus buy avg rate
  const hasSpread = buyAvgRate > 0 && sellAvgRate > 0;
  const ngnSpread = sellAvgRate - buyAvgRate;
  const spreadPct = buyAvgRate > 0 ? (ngnSpread / buyAvgRate) * 100 : 0;

  const parsedTarget = parseFloat(targetRate);
  const hasProjection = buyTotalUsdt > 0 && !isNaN(parsedTarget) && parsedTarget > 0;
  const projectedRevenue = hasProjection ? buyTotalUsdt * parsedTarget : 0;
  const projectedCost = hasProjection ? buyTotalNgn : 0;
  const projectedProfit = projectedRevenue - projectedCost;
  const projectedProfitPct = projectedCost > 0 ? (projectedProfit / projectedCost) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Rate analysis"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-surface rounded-2xl w-full max-w-xl mx-4 overflow-hidden shadow-2xl"
        style={{ border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
              <TrendingUp className="w-4.5 h-4.5 text-accent" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Rate Analysis</h3>
              <p className="text-xs text-muted mt-0.5">
                {trades.length} trade{trades.length !== 1 ? "s" : ""}
                {buys.length > 0 && <> · {buyTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT bought</>}
                {sells.length > 0 && <> · {sellTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT sold</>}
                {mmTrades.length > 0 && <> · {mmTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT middleman</>}
              </p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="btn-icon w-8 h-8 rounded-lg flex items-center justify-center text-muted hover:text-foreground transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-auto">

          {/* Hero: Avg Buy Rate — the primary reason for opening this modal */}
          {buys.length > 0 && (
            <div className="text-center py-2">
              <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Average Buy Rate</p>
              <p className="text-3xl font-bold tabular-nums text-foreground">
                ₦{buyAvgRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-muted tabular-nums mt-1.5">
                {buyTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT · ₦{buyTotalNgn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent
              </p>
            </div>
          )}

          {/* Context cards: Sell rate, Spread, Actual P&L — only when data exists */}
          {(sells.length > 0 || hasSpread || actualPnL !== 0) && (
            <div className={`grid gap-3 ${sells.length > 0 && (hasSpread || actualPnL !== 0) ? "grid-cols-2" : "grid-cols-1"}`}>
              {sells.length > 0 && (
                <div className="rounded-xl p-4" style={{ border: "1px solid var(--border)", background: "var(--surface-2)" }}>
                  <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1">Avg Sell Rate</p>
                  <p className="text-lg font-bold tabular-nums text-foreground">
                    ₦{sellAvgRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-muted tabular-nums mt-0.5">
                    {sellTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT · ₦{sellTotalNgn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}

              {hasSpread && (
                <div className="rounded-xl p-4" style={{ border: "1px solid var(--border)", background: "var(--surface-2)" }}>
                  <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1">NGN Spread</p>
                  <p className={`text-lg font-bold tabular-nums ${ngnSpread >= 0 ? "text-success" : "text-danger"}`}>
                    {ngnSpread >= 0 ? "+" : ""}₦{Math.abs(ngnSpread).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className={`text-xs tabular-nums mt-0.5 ${spreadPct >= 0 ? "text-success/70" : "text-danger/70"}`}>
                    {spreadPct >= 0 ? "+" : ""}{spreadPct.toFixed(2)}% markup
                  </p>
                </div>
              )}

              {actualPnL !== 0 && (
                <div className="rounded-xl p-4" style={{ border: "1px solid var(--border)", background: "var(--surface-2)" }}>
                  <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1">Realized P&L</p>
                  <p className={`text-lg font-bold tabular-nums ${actualPnL >= 0 ? "text-success" : "text-danger"}`}>
                    {actualPnL >= 0 ? "+" : "-"}₦{Math.abs(actualPnL).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className={`text-xs tabular-nums mt-0.5 ${actualPnLPct >= 0 ? "text-success/70" : "text-danger/70"}`}>
                    {actualPnLPct >= 0 ? "+" : ""}{actualPnLPct.toFixed(2)}% profit
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Middleman deals */}
          {mmTrades.length > 0 && (
            <div className="rounded-xl p-4 space-y-2" style={{ border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))", background: "var(--surface-2)" }}>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-xs font-semibold text-foreground">Middleman ({mmTrades.length})</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-lg font-bold tabular-nums text-foreground">
                  ₦{mmAvgBuyRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-muted">/</span>
                <span className="text-lg font-bold tabular-nums text-foreground">
                  ₦{mmAvgSellRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-xs text-muted tabular-nums">
                {mmTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                · Spread {mmAvgSpread >= 0 ? "+" : "-"}₦{Math.abs(mmAvgSpread).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/USDT
              </p>
              <p className={`text-sm font-bold tabular-nums ${mmTotalProfit >= 0 ? "text-success" : "text-danger"}`}>
                {mmTotalProfit >= 0 ? "+" : "-"}₦{Math.abs(mmTotalProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} profit
              </p>
            </div>
          )}

          {/* Profit Projection — the main interactive section */}
          {buyTotalUsdt > 0 && (
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <div className="px-4 py-3 flex items-center gap-3" style={{ background: "var(--surface-2)" }}>
                <label className="text-xs text-muted whitespace-nowrap">Sell at</label>
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted">₦</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={targetRate}
                    onChange={(e) => setTargetRate(e.target.value)}
                    placeholder="target rate"
                    className="w-full bg-surface rounded-lg pl-6 pr-3 py-1.5 text-sm text-foreground tabular-nums placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                    style={{ border: "1px solid var(--border)" }}
                    autoFocus
                  />
                </div>
                <span className="text-xs text-muted whitespace-nowrap tabular-nums">
                  {buyTotalUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                </span>
              </div>

              {hasProjection && (
                <div className="px-5 py-5 text-center" style={{ background: "var(--surface-2)" }}>
                  <p className={`text-3xl font-bold tabular-nums ${projectedProfit >= 0 ? "text-success" : "text-danger"}`}>
                    {projectedProfit >= 0 ? "+" : "-"}₦{Math.abs(projectedProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className={`text-sm font-semibold tabular-nums mt-1 ${projectedProfitPct >= 0 ? "text-success/80" : "text-danger/80"}`}>
                    {projectedProfitPct >= 0 ? "+" : ""}{projectedProfitPct.toFixed(2)}% profit
                  </p>
                  <div className="flex justify-center gap-5 mt-3 text-xs text-muted tabular-nums">
                    <span>Revenue ₦{projectedRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span>Cost ₦{projectedCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className={parsedTarget - buyAvgRate >= 0 ? "text-success/70" : "text-danger/70"}>
                      Spread {parsedTarget - buyAvgRate >= 0 ? "+" : "-"}₦{Math.abs(parsedTarget - buyAvgRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Lock Picker Modal — transaction-first flow
// =============================================================================

type LockRecord = {
  id: number;
  reference?: string;
  lockType: "lock_in" | "lock_out";
  parentLockId: number | null;
  counterpartyAddress: string;
  counterpartyName: string | null;
  usdtAmount: number;
  rate: number | null;
  ngnAmount: number | null;
  nairaFunded: number;
  usdtSourced: number;
  usdtDelivered: number;
  nairaPaid: number;
  usdtReceived: number;
  status: string;
  createdAt: string;
};

function LockPickerModal({
  selectedTrades,
  selectedTotal,
  onClose,
  onLinked,
  toast,
}: {
  selectedTrades: Trade[];
  selectedTotal: number;
  onClose: () => void;
  onLinked: () => void;
  toast: (msg: string, variant?: "success" | "error" | "info") => void;
}) {
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [lockSearch, setLockSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [linking, setLinking] = useState(false);

  // Create-new form state
  const [newType, setNewType] = useState<"lock_in" | "lock_out">("lock_in");
  const [newAddress, setNewAddress] = useState("");
  const [newName, setNewName] = useState("");
  const [newAmount, setNewAmount] = useState(String(selectedTotal));
  const [newRate, setNewRate] = useState("");
  const [newExpiry, setNewExpiry] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [createError, setCreateError] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch("/api/locks?status=open").then(r => r.json()),
      fetch("/api/locks?status=partial").then(r => r.json()),
    ]).then(([openData, partialData]) => {
      const combined = [...(openData.locks || []), ...(partialData.locks || [])];
      // Deduplicate by id
      const seen = new Set<number>();
      const unique = combined.filter((l: LockRecord) => {
        if (seen.has(l.id)) return false;
        seen.add(l.id);
        return true;
      });
      setLocks(unique);
    }).finally(() => setLoading(false));
  }, []);

  const filteredLocks = locks.filter(l => {
    if (!lockSearch.trim()) return true;
    const q = lockSearch.toLowerCase();
    return (
      (l.counterpartyName?.toLowerCase().includes(q)) ||
      l.counterpartyAddress.toLowerCase().includes(q) ||
      String(l.id).includes(q)
    );
  });

  const determineEntryType = (lock: LockRecord, trade: Trade): string => {
    const isIncoming = trade.direction === "buy" || trade.direction === "borrow" || trade.direction === "return";
    if (lock.lockType === "lock_in") {
      return isIncoming ? "usdt_sourced" : "usdt_delivered";
    } else {
      return "usdt_received";
    }
  };

  const linkTradesToLock = async (lockId: number, lock: LockRecord) => {
    setLinking(true);
    try {
      // Group trades by entry type
      const byType: Record<string, number[]> = {};
      for (const trade of selectedTrades) {
        const et = determineEntryType(lock, trade);
        if (!byType[et]) byType[et] = [];
        byType[et].push(trade.id);
      }

      for (const [entryType, tradeIds] of Object.entries(byType)) {
        const res = await fetch(`/api/locks/${lockId}/entries`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entryType, tradeIds }),
        });
        if (!res.ok) {
          const data = await res.json();
          toast(data.error || "Failed to link trades", "error");
          setLinking(false);
          return;
        }
      }

      toast(`Linked ${selectedTrades.length} trade${selectedTrades.length !== 1 ? "s" : ""} to Lock #${lockId}`);
      onLinked();
    } catch {
      toast("Failed to link trades", "error");
    }
    setLinking(false);
  };

  const handleCreateAndLink = async () => {
    if (!newAddress.trim()) { setCreateError("Counterparty address required"); return; }
    const parsedAmount = parseFloat(newAmount);
    if (!parsedAmount || parsedAmount <= 0) { setCreateError("Valid USDT amount required"); return; }
    setCreateError("");
    setLinking(true);

    try {
      const payload: Record<string, unknown> = {
        lockType: newType,
        counterpartyAddress: newAddress.trim(),
        counterpartyName: newName.trim() || null,
        usdtAmount: parsedAmount,
        rate: newRate ? parseFloat(newRate) : null,
        expiryAt: newExpiry || null,
        notes: newNotes.trim() || null,
      };

      const res = await fetch("/api/locks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        setCreateError(data.error || "Failed to create lock");
        setLinking(false);
        return;
      }

      const data = await res.json();
      const newLock = data.lock as LockRecord;
      await linkTradesToLock(newLock.id, newLock);
    } catch {
      toast("Failed to create lock", "error");
      setLinking(false);
    }
  };

  const lockFill = (l: LockRecord) => {
    if (l.lockType === "lock_in") return l.usdtSourced + l.usdtDelivered;
    return l.usdtReceived;
  };

  const lockFillPct = (l: LockRecord) => {
    const fill = lockFill(l);
    const target = l.lockType === "lock_in" ? l.usdtAmount * 2 : l.usdtAmount;
    return target > 0 ? Math.min((fill / target) * 100, 100) : 0;
  };

  const computedNgn = newAmount && newRate ? Math.round(parseFloat(newAmount) * parseFloat(newRate)) : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Link trades to lock"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-surface rounded-xl w-full max-w-lg mx-4 overflow-hidden shadow-xl"
        style={{ border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground">Link Trades to Lock</h3>
          <p className="text-xs text-muted mt-0.5">
            Selected: {selectedTrades.length} trade{selectedTrades.length !== 1 ? "s" : ""} — {formatCurrency(selectedTotal)} USDT
          </p>
        </div>

        <div className="px-5 py-3 max-h-[60vh] overflow-auto">
          {!showCreate ? (
            <>
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                <input
                  type="text"
                  placeholder="Search locks..."
                  value={lockSearch}
                  onChange={(e) => setLockSearch(e.target.value)}
                  className="w-full bg-surface-2 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  style={{ border: "1px solid var(--border)" }}
                  autoFocus
                />
              </div>

              {/* Existing locks */}
              {loading ? (
                <p className="text-xs text-muted text-center py-6">Loading locks...</p>
              ) : filteredLocks.length === 0 ? (
                <p className="text-xs text-muted text-center py-6">No open/partial locks found</p>
              ) : (
                <div className="max-h-[280px] overflow-auto space-y-1 mb-3">
                  {filteredLocks.map(l => {
                    const isIn = l.lockType === "lock_in";
                    const fill = lockFill(l);
                    const wouldExceed = fill + selectedTotal > (isIn ? l.usdtAmount * 2 : l.usdtAmount);
                    return (
                      <button
                        key={l.id}
                        disabled={linking}
                        onClick={() => linkTradesToLock(l.id, l)}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors"
                        style={{ border: "1px solid var(--border)" }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider ${isIn ? "text-accent" : "text-warning"}`}>
                              {isIn ? "LOCK IN" : "LOCK OUT"}
                            </span>
                            <span className="text-[10px] text-muted">#{l.id}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${l.status === "open" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
                              {l.status.toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-foreground tabular-nums">{formatCurrency(l.usdtAmount)}</span>
                        </div>
                        <p className="text-[13px] text-foreground mb-1.5">
                          {l.counterpartyName || truncateHash(l.counterpartyAddress)}
                        </p>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1">
                            <ProgressBar percentage={lockFillPct(l)} />
                          </div>
                          <span className="text-[10px] text-muted tabular-nums whitespace-nowrap">
                            {formatCurrency(fill)} / {formatCurrency(isIn ? l.usdtAmount * 2 : l.usdtAmount)}
                          </span>
                        </div>
                        {wouldExceed && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertTriangle className="w-3 h-3 text-warning" />
                            <span className="text-[10px] text-warning font-semibold">
                              Would exceed target by {formatCurrency(fill + selectedTotal - (isIn ? l.usdtAmount * 2 : l.usdtAmount))}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* Create new lock form */
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-muted block mb-1">Lock Type</label>
                <div className="flex gap-2">
                  {(["lock_in", "lock_out"] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setNewType(t)}
                      className={`flex-1 px-3 py-2 rounded-md text-[13px] font-medium transition-colors cursor-pointer ${
                        newType === t
                          ? t === "lock_in"
                            ? "bg-accent/15 text-accent border border-accent/30"
                            : "bg-warning/15 text-warning border border-warning/30"
                          : "bg-surface-2 text-muted border border-border hover:text-foreground"
                      }`}
                    >
                      {t === "lock_in" ? "Lock IN" : "Lock OUT"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] text-muted block mb-1">Counterparty Address *</label>
                <input
                  placeholder="Wallet address..."
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30 font-mono"
                  style={{ border: "1px solid var(--border)" }}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted block mb-1">Counterparty Name (optional)</label>
                <input
                  placeholder="Label or name..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  style={{ border: "1px solid var(--border)" }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-muted block mb-1">USDT Amount *</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                    style={{ border: "1px solid var(--border)" }}
                  />
                </div>
                <div>
                  <label className="text-[11px] text-muted block mb-1">Rate (NGN/USDT)</label>
                  <input
                    type="number"
                    placeholder="Optional"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                    style={{ border: "1px solid var(--border)" }}
                  />
                </div>
              </div>
              {computedNgn && (
                <p className="text-[11px] text-muted">NGN equivalent: <span className="text-foreground font-medium">{formatCurrency(computedNgn, "NGN")}</span></p>
              )}
              <div>
                <label className="text-[11px] text-muted block mb-1">Expiry (optional)</label>
                <input
                  type="datetime-local"
                  value={newExpiry}
                  onChange={(e) => setNewExpiry(e.target.value)}
                  className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30"
                  style={{ border: "1px solid var(--border)" }}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted block mb-1">Notes (optional)</label>
                <input
                  placeholder="Lock notes..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  style={{ border: "1px solid var(--border)" }}
                />
              </div>
              {createError && <p className="text-[12px] text-danger">{createError}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" size="sm" onClick={() => setShowCreate(false)}>Back</Button>
                <Button size="sm" onClick={handleCreateAndLink} disabled={linking}>
                  {linking ? "Creating..." : "Create & Link Trades"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          {!showCreate ? (
            <Button variant="secondary" size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-3 h-3" />Create New Lock
            </Button>
          ) : (
            <span />
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Link Trade to Lock Modal — single-trade flow with counterparty step
// =============================================================================

function LinkTradeToLockModal({
  trade,
  onClose,
  onLinked,
  toast,
}: {
  trade: Trade;
  onClose: () => void;
  onLinked: () => void;
  toast: (msg: string, variant?: "success" | "error" | "info") => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [cpList, setCpList] = useState<Counterparty[]>([]);
  const [cpSearch, setCpSearch] = useState("");
  const [selectedCp, setSelectedCp] = useState<Counterparty | null>(null);
  const [loadingCp, setLoadingCp] = useState(true);
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");

  // Step 2 state
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [loadingLocks, setLoadingLocks] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newType, setNewType] = useState<"lock_in" | "lock_out">(
    trade.direction === "sell" || trade.direction === "lend" ? "lock_out" : "lock_in"
  );
  const [newAmount, setNewAmount] = useState(String(trade.usdtAmount));
  const [newRate, setNewRate] = useState(trade.rate ? String(trade.rate) : "");
  const [newExpiry, setNewExpiry] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [createError, setCreateError] = useState("");

  // Fetch counterparties
  useEffect(() => {
    setLoadingCp(true);
    fetch("/api/counterparties")
      .then((r) => r.json())
      .then((data) => {
        const list: Counterparty[] = data.counterparties || [];
        setCpList(list);
        // Pre-select if trade already has a counterparty
        if (trade.counterpartyName) {
          const match = list.find(
            (cp) => cp.name.toLowerCase() === trade.counterpartyName!.toLowerCase()
          );
          if (match) setSelectedCp(match);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCp(false));
  }, [trade.counterpartyName]);

  // Fetch locks when entering step 2
  useEffect(() => {
    if (step !== 2) return;
    setLoadingLocks(true);
    Promise.all([
      fetch("/api/locks?status=open").then((r) => r.json()),
      fetch("/api/locks?status=partial").then((r) => r.json()),
    ])
      .then(([openData, partialData]) => {
        const combined = [...(openData.locks || []), ...(partialData.locks || [])];
        const seen = new Set<number>();
        const unique = combined.filter((l: LockRecord) => {
          if (seen.has(l.id)) return false;
          seen.add(l.id);
          return true;
        });
        // Sort: counterparty-matching locks first
        unique.sort((a: LockRecord, b: LockRecord) => {
          const aMatch = selectedCp && a.counterpartyName?.toLowerCase() === selectedCp.name.toLowerCase() ? 0 : 1;
          const bMatch = selectedCp && b.counterpartyName?.toLowerCase() === selectedCp.name.toLowerCase() ? 0 : 1;
          return aMatch - bMatch;
        });
        setLocks(unique);
      })
      .finally(() => setLoadingLocks(false));
  }, [step, selectedCp]);

  const determineEntryType = (lockType: "lock_in" | "lock_out"): string => {
    const isIncoming = trade.direction === "buy" || trade.direction === "borrow" || trade.direction === "return";
    if (lockType === "lock_in") {
      return isIncoming ? "usdt_sourced" : "usdt_delivered";
    }
    return "usdt_received";
  };

  const getActionLabel = (lockType: "lock_in" | "lock_out"): string => {
    const isIncoming = trade.direction === "buy" || trade.direction === "borrow" || trade.direction === "return";
    if (lockType === "lock_in") {
      return isIncoming
        ? "Add as USDT sourced \u2014 you bought coin for this lock"
        : "Add as USDT delivered \u2014 you sent coin for this lock";
    }
    return "Add as USDT received \u2014 counterparty sent you coin";
  };

  const lockFill = (l: LockRecord) => {
    if (l.lockType === "lock_in") return l.usdtSourced + l.usdtDelivered;
    return l.usdtReceived;
  };

  const lockFillPct = (l: LockRecord) => {
    const fill = lockFill(l);
    const target = l.lockType === "lock_in" ? l.usdtAmount * 2 : l.usdtAmount;
    return target > 0 ? Math.min((fill / target) * 100, 100) : 0;
  };

  const linkToLock = async (lockId: number, lockType: "lock_in" | "lock_out") => {
    setLinking(true);
    try {
      const entryType = determineEntryType(lockType);
      const res = await fetch(`/api/locks/${lockId}/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entryType, tradeIds: [trade.id] }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast(data.error || "Failed to link trade", "error");
        setLinking(false);
        return;
      }
      toast(`Linked trade to Lock #${lockId}`, "success");
      onLinked();
    } catch {
      toast("Failed to link trade", "error");
    }
    setLinking(false);
  };

  const handleCreateAndLink = async () => {
    const parsedAmount = parseFloat(newAmount);
    if (!parsedAmount || parsedAmount <= 0) { setCreateError("Valid USDT amount required"); return; }
    setCreateError("");
    setLinking(true);

    try {
      const payload: Record<string, unknown> = {
        lockType: newType,
        counterpartyAddress: selectedCp?.name || trade.counterpartyName || "Unknown",
        counterpartyName: selectedCp?.name || trade.counterpartyName || null,
        usdtAmount: parsedAmount,
        rate: newRate ? parseFloat(newRate) : null,
        expiryAt: newExpiry || null,
        notes: newNotes.trim() || null,
      };

      const res = await fetch("/api/locks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        setCreateError(data.error || "Failed to create lock");
        setLinking(false);
        return;
      }

      const data = await res.json();
      const newLock = data.lock as LockRecord;
      await linkToLock(newLock.id, newLock.lockType);
    } catch {
      toast("Failed to create lock", "error");
      setLinking(false);
    }
  };

  const handleAddCounterparty = async () => {
    if (!newName.trim()) { toast("Name is required", "error"); return; }
    try {
      const body: Record<string, unknown> = { name: newName.trim() };
      if (newPhone.trim()) body.phone = newPhone.trim();
      const res = await fetch("/api/counterparties", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.counterparty) {
        setCpList((prev) => [...prev, data.counterparty]);
        setSelectedCp(data.counterparty);
        setAddingNew(false);
        setNewName("");
        setNewPhone("");
        toast(`Created "${data.counterparty.name}"`, "success");
      } else {
        toast(data.error || "Failed to create counterparty", "error");
      }
    } catch {
      toast("Failed to create counterparty", "error");
    }
  };

  const filteredCp = cpList
    .filter((cp) => !cp.isBlacklisted)
    .filter((cp) => {
      if (!cpSearch.trim()) return true;
      const q = cpSearch.toLowerCase();
      return cp.name.toLowerCase().includes(q) || cp.alias?.toLowerCase().includes(q);
    });

  const computedNgn = newAmount && newRate ? Math.round(parseFloat(newAmount) * parseFloat(newRate)) : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Link trade to lock"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-surface rounded-xl w-full max-w-lg mx-4 overflow-hidden shadow-xl"
        style={{ border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-foreground">Link Trade to Lock</h3>
              <p className="text-xs text-muted mt-0.5">
                {trade.counterpartyName || "Unknown"} &middot; {trade.usdtAmount.toLocaleString()} USDT &middot; {getDirectionConfig(trade.direction).label}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 1 ? "bg-accent text-white" : "bg-surface-3 text-muted"}`}>1</span>
              <span className="w-4 h-px bg-border" />
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 2 ? "bg-accent text-white" : "bg-surface-3 text-muted"}`}>2</span>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 max-h-[60vh] overflow-auto">
          {step === 1 ? (
            /* ===== STEP 1: Counterparty ===== */
            <>
              {selectedCp && !addingNew && (
                <div className="flex items-center justify-between bg-success/5 rounded-lg px-3 py-2.5 mb-3" style={{ border: "1px solid color-mix(in srgb, var(--success) 20%, transparent)" }}>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{selectedCp.name}</span>
                      {selectedCp.alias && <span className="text-xs text-muted ml-2">{selectedCp.alias}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedCp(null); setCpSearch(""); }}
                    className="text-[11px] font-semibold text-accent hover:text-accent-hover transition-colors"
                  >
                    Change
                  </button>
                </div>
              )}

              {!selectedCp && !addingNew && (
                <>
                  <p className="text-[11px] font-bold text-muted uppercase tracking-wider mb-2">Select Counterparty</p>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                    <input
                      type="text"
                      placeholder="Search counterparties..."
                      value={cpSearch}
                      onChange={(e) => setCpSearch(e.target.value)}
                      className="w-full bg-surface-2 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                      autoFocus
                    />
                  </div>
                  {loadingCp ? (
                    <p className="text-xs text-muted text-center py-6">Loading...</p>
                  ) : (
                    <div className="max-h-[240px] overflow-auto space-y-1">
                      {filteredCp.map((cp) => (
                        <button
                          key={cp.id}
                          className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors flex items-center justify-between"
                          onClick={() => setSelectedCp(cp)}
                        >
                          <div>
                            <span className="text-sm font-semibold text-foreground">{cp.name}</span>
                            {cp.alias && <span className="text-xs text-muted ml-2">{cp.alias}</span>}
                            <div className="text-[10px] text-muted mt-0.5">{cp.city} &middot; {cp.tradeCount} trades</div>
                          </div>
                          {cp.kycVerified && <span className="text-[9px] font-bold text-success bg-success/10 px-1.5 py-0.5 rounded">KYC</span>}
                        </button>
                      ))}
                      {filteredCp.length === 0 && (
                        <p className="text-xs text-muted text-center py-4">No counterparties found</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {addingNew && (
                <div className="space-y-3">
                  <p className="text-[11px] font-bold text-muted uppercase tracking-wider">Add New Counterparty</p>
                  <div>
                    <label className="text-xs font-semibold text-muted mb-1 block">Name *</label>
                    <input
                      type="text"
                      placeholder="Counterparty name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted mb-1 block">Phone (optional)</label>
                    <input
                      type="text"
                      placeholder="+234..."
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button variant="primary" size="sm" onClick={handleAddCounterparty}>Create</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setAddingNew(false); setNewName(""); setNewPhone(""); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ===== STEP 2: Pick Lock or Create ===== */
            <>
              {!showCreate ? (
                <>
                  <p className="text-[11px] font-bold text-muted uppercase tracking-wider mb-3">Pick a Lock</p>
                  {loadingLocks ? (
                    <p className="text-xs text-muted text-center py-6">Loading locks...</p>
                  ) : locks.length === 0 ? (
                    <p className="text-xs text-muted text-center py-6">No open or partial locks found. Create one below.</p>
                  ) : (
                    <div className="max-h-[280px] overflow-auto space-y-1.5 mb-3">
                      {locks.map((l) => {
                        const isIn = l.lockType === "lock_in";
                        const fill = lockFill(l);
                        const target = isIn ? l.usdtAmount * 2 : l.usdtAmount;
                        const wouldExceed = fill + trade.usdtAmount > target;
                        const cpMatch = selectedCp && l.counterpartyName?.toLowerCase() === selectedCp.name.toLowerCase();
                        return (
                          <button
                            key={l.id}
                            disabled={linking}
                            onClick={() => linkToLock(l.id, l.lockType)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors ${cpMatch ? "ring-1 ring-accent/30" : ""}`}
                            style={{ border: "1px solid var(--border)" }}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${isIn ? "text-accent" : "text-warning"}`}>
                                  {isIn ? "LOCK IN" : "LOCK OUT"}
                                </span>
                                <span className="text-[10px] text-muted">#{l.id}</span>
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${l.status === "open" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
                                  {l.status.toUpperCase()}
                                </span>
                                {cpMatch && <span className="text-[9px] font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded">Match</span>}
                              </div>
                              <span className="text-sm font-semibold text-foreground tabular-nums">{formatCurrency(l.usdtAmount)}</span>
                            </div>
                            <p className="text-[13px] text-foreground mb-1">
                              {l.counterpartyName || truncateHash(l.counterpartyAddress)}
                            </p>
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex-1">
                                <ProgressBar percentage={lockFillPct(l)} />
                              </div>
                              <span className="text-[10px] text-muted tabular-nums whitespace-nowrap">
                                {formatCurrency(fill)} / {formatCurrency(target)}
                              </span>
                            </div>
                            <p className="text-[10px] text-muted italic">{getActionLabel(l.lockType)}</p>
                            {wouldExceed && (
                              <div className="flex items-center gap-1 mt-1">
                                <AlertTriangle className="w-3 h-3 text-warning" />
                                <span className="text-[10px] text-warning font-semibold">
                                  Would exceed target by {formatCurrency(fill + trade.usdtAmount - target)}
                                </span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                /* Create new lock form */
                <div className="space-y-3">
                  <p className="text-[11px] font-bold text-muted uppercase tracking-wider">Create New Lock</p>
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Lock Type</label>
                    <div className="flex gap-2">
                      {(["lock_in", "lock_out"] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setNewType(t)}
                          className={`flex-1 px-3 py-2 rounded-md text-[13px] font-medium transition-colors cursor-pointer ${
                            newType === t
                              ? t === "lock_in"
                                ? "bg-accent/15 text-accent border border-accent/30"
                                : "bg-warning/15 text-warning border border-warning/30"
                              : "bg-surface-2 text-muted border border-border hover:text-foreground"
                          }`}
                        >
                          {t === "lock_in" ? "Lock IN" : "Lock OUT"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-muted block mb-1">USDT Amount</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={newAmount}
                        onChange={(e) => setNewAmount(e.target.value)}
                        className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)" }}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted block mb-1">Rate (NGN/USDT)</label>
                      <input
                        type="number"
                        placeholder="Optional"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                        className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                        style={{ border: "1px solid var(--border)" }}
                      />
                    </div>
                  </div>
                  {computedNgn && (
                    <p className="text-[11px] text-muted">NGN equivalent: <span className="text-foreground font-medium">{formatCurrency(computedNgn, "NGN")}</span></p>
                  )}
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Expiry (optional)</label>
                    <input
                      type="datetime-local"
                      value={newExpiry}
                      onChange={(e) => setNewExpiry(e.target.value)}
                      className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Notes (optional)</label>
                    <input
                      placeholder="Lock notes..."
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      className="w-full bg-surface-2 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                    />
                  </div>
                  {createError && <p className="text-[12px] text-danger">{createError}</p>}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="secondary" size="sm" onClick={() => setShowCreate(false)}>Back</Button>
                    <Button size="sm" onClick={handleCreateAndLink} disabled={linking}>
                      {linking ? "Creating..." : "Create & Link"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          {step === 1 ? (
            <>
              {!addingNew ? (
                <Button variant="secondary" size="sm" onClick={() => setAddingNew(true)}>
                  <UserPlus className="w-3 h-3" />Add New
                </Button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
                <Button
                  size="sm"
                  disabled={!selectedCp}
                  onClick={() => setStep(2)}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <>
              {!showCreate ? (
                <Button variant="secondary" size="sm" onClick={() => setShowCreate(true)}>
                  <Plus className="w-3 h-3" />Create New Lock
                </Button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => { setStep(1); setShowCreate(false); }}>
                  <ArrowLeft className="w-3 h-3" />Back
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
